import React, { useState, useEffect, useContext, useRef, useMemo, useCallback, lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import axios from 'axios';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'moment/locale/is'; // Icelandic locale
import * as XLSX from 'xlsx';
import Webcam from 'react-webcam';
import jsQR from 'jsqr';
import { jsPDF } from 'jspdf';

// Setup calendar localizer and drag-and-drop
moment.locale('is');
const localizer = momentLocalizer(moment);
const DragAndDropCalendar = withDragAndDrop(Calendar);

import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table';
import { Badge } from './components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Checkbox } from './components/ui/checkbox';
import { Textarea } from './components/ui/textarea';
import { Users, Settings, LogOut, Calendar as CalendarIcon, Clock, DollarSign, Car, FileText, Building2, UserPlus, Languages, Pencil, Eye, EyeOff, Trash2, Plus, Package } from 'lucide-react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useAuth, AuthProvider } from './contexts/AuthContext';

// Lazy load heavy components
const CustomersManagement = lazy(() => import('./components/CustomersManagement'));
const Store = lazy(() => import('./components/Store'));
const MonthlyInvoicesManager = lazy(() => import('./components/MonthlyInvoicesManager'));
const MobileRegistration = lazy(() => import('./components/MobileRegistration'));

// Lazy load views for code splitting  
const CalendarView = lazy(() => import('./views/CalendarView'));
const JobsView = lazy(() => import('./views/JobsView'));
const PaymentsPage = lazy(() => import('./views/PaymentsPage'));
const ReportsPage = lazy(() => import('./views/ReportsPage'));
const DealershipsPage = lazy(() => import('./views/DealershipsPage'));

import { LoginPage } from './components/LoginPage';
import { SetPasswordPage } from './components/SetPasswordPage';
import RebookPage from './components/RebookPage';
import QRScanner from './components/QRScanner';
import CorporateApplicationForm from './components/CorporateApplicationForm';
import CorporateManagement from './components/CorporateManagement';
import CorporateDashboard from './components/CorporateDashboard';
import OrdersManagement from './components/OrdersManagement';
import GiftCardLookup from './components/GiftCardLookup';
import { useSwipeableCard, usePullToRefresh } from './hooks/useSwipe';
import { t } from './utils/translations';
import { API } from './utils/constants';
import { API_BASE_URL } from './config';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

// Inline QR Scanner for gift card payments
const GiftcardQRScanner = ({ onCodeDetected, onClose }) => {
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);
  const scanIntervalRef = useRef(null);
  const [scanning, setScanning] = useState(true);

  useEffect(() => {
    if (scanning) {
      scanIntervalRef.current = setInterval(() => {
        if (webcamRef.current && canvasRef.current) {
          const video = webcamRef.current.video;
          if (video && video.readyState === 4) {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const qrCode = jsQR(imageData.data, imageData.width, imageData.height);
            
            if (qrCode) {
              clearInterval(scanIntervalRef.current);
              setScanning(false);
              toast.success('QR kóði fundinn!');
              onCodeDetected(qrCode.data);
            }
          }
        }
      }, 200);
    }
    
    return () => {
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current);
      }
    };
  }, [scanning, onCodeDetected]);

  return (
    <div className="bg-purple-50 p-3">
      <div className="text-center mb-2">
        <p className="text-sm font-medium text-purple-800">📸 Beindu myndavélinni að QR kóðanum</p>
      </div>
      <div className="relative bg-black rounded-lg overflow-hidden">
        <Webcam
          ref={webcamRef}
          audio={false}
          screenshotFormat="image/jpeg"
          videoConstraints={{
            facingMode: { ideal: 'environment' },
            width: { ideal: 640 },
            height: { ideal: 480 }
          }}
          className="w-full"
          style={{ maxHeight: '250px' }}
          onUserMediaError={(error) => {
            console.error('Camera error:', error);
            toast.error('Villa við að opna myndavél');
            onClose();
          }}
        />
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-40 h-40 border-4 border-purple-400 rounded-lg animate-pulse"></div>
        </div>
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </div>
      <p className="text-xs text-center text-purple-600 mt-2">
        Skannið sjálfkrafa þegar QR kóði finnst
      </p>
    </div>
  );
};

// Simple translations for 5 languages: Icelandic, English, Polish, Lithuanian, Russian

// User Header Component
function UserHeader({ 
  user, onLogout, onNavigate, currentView, language, onLanguageChange, darkMode, onDarkModeToggle, 
  pendingApprovalJobs = [], pendingApprovalCount = 0, notificationDropdownOpen, setNotificationDropdownOpen, onApproveJob, onRejectJob,
  generalChatOpen, setGeneralChatOpen, dealershipsWithChat = [], totalGeneralUnread = 0, 
  selectedDealershipChat, setSelectedDealershipChat, generalChatReply, setGeneralChatReply, 
  onSendGeneralChatReply, sendingGeneralChat,
  dealershipPopupNotifications = [], onShowDealershipPopup,
  pendingPickups = [], pickupDropdownOpen, setPickupDropdownOpen, onMarkPickupComplete,
  pendingDeliveries = [], deliveryDropdownOpen, setDeliveryDropdownOpen, onMarkDeliveryComplete
}) {
  const dropdownRef = useRef(null);
  const pickupDropdownRef = useRef(null);
  const deliveryDropdownRef = useRef(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    if (!setNotificationDropdownOpen) return; // Guard against undefined
    
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setNotificationDropdownOpen(false);
      }
    }
    
    if (notificationDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [notificationDropdownOpen, setNotificationDropdownOpen]);
  
  // Close mobile menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    }
    
    if (mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Prevent body scroll when menu is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);
  
  // Handle navigation and close mobile menu
  const handleMobileNavigate = (view) => {
    onNavigate(view);
    setMobileMenuOpen(false);
  };
  
  return (
    <>
      {/* Mobile Menu Backdrop */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={(e) => {
            e.stopPropagation();
            setMobileMenuOpen(false);
          }}
          style={{ cursor: 'pointer' }}
        ></div>
      )}
      
      {/* Mobile Sidebar Menu - Only render when open or animating */}
      {mobileMenuOpen && (
        <div 
          ref={mobileMenuRef}
          className="fixed top-0 left-0 h-full w-80 bg-white dark:bg-slate-900 shadow-2xl z-50 transform transition-transform duration-300 ease-in-out lg:hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-col h-full">
            {/* Mobile Menu Header */}
            <div className="flex items-center justify-between p-3 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center">
                <div className="bg-white dark:bg-slate-900 rounded-lg px-2 py-1">
                  <img src="/alvorubon-top.png" alt="Alvörubón" className="h-6 object-contain" />
                </div>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setMobileMenuOpen(false);
                }}
                className="w-8 h-8 p-0 flex items-center justify-center rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                aria-label="Close menu"
              >
                <span className="text-xl text-slate-700 dark:text-slate-300">✕</span>
              </button>
            </div>
          
          {/* User Info */}
          <div className="p-3 border-b border-slate-200 dark:border-slate-700">
            <div className="text-sm font-medium text-slate-900 dark:text-slate-100">{user.name}</div>
            <div className="text-xs text-slate-500 dark:text-slate-400">{user.role}</div>
          </div>
          
          {/* Navigation Menu Items */}
          <div className="flex-1 overflow-y-auto py-2">
            <nav className="space-y-0.5">
              <button
                onClick={() => handleMobileNavigate('registration')}
                className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors flex items-center space-x-3 ${
                  currentView === 'registration'
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-r-4 border-blue-600 dark:border-blue-400'
                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                }`}
              >
                <span>📝</span>
                <span>{t('registration', language)}</span>
              </button>
              
              {user.role !== 'viewer' && (
                <>
                  {(user.role === 'admin' || user.role === 'staff' || user.permissions?.jobs) && (
                    <>
                      <button 
                        onClick={() => handleMobileNavigate('calendar')} 
                        className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors flex items-center space-x-3 ${
                          currentView === 'calendar'
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-r-4 border-blue-600 dark:border-blue-400'
                            : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                        }`}
                      >
                        <span>📅</span>
                        <span>{t('calendar', language)}</span>
                      </button>
                      <button 
                        onClick={() => handleMobileNavigate('jobs')} 
                        className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors flex items-center space-x-3 ${
                          currentView === 'jobs'
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-r-4 border-blue-600 dark:border-blue-400'
                            : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                        }`}
                      >
                        <span>📋</span>
                        <span>{t('jobs', language)}</span>
                      </button>
                    </>
                  )}
                  {(user.role === 'admin' || user.role === 'staff' || user.permissions?.payments) && (
                    <button 
                      onClick={() => handleMobileNavigate('payments')} 
                      className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors flex items-center space-x-3 ${
                        currentView === 'payments'
                          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-r-4 border-blue-600 dark:border-blue-400'
                          : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                      }`}
                    >
                      <span>💰</span>
                      <span>{t('payments', language)}</span>
                    </button>
                  )}
                  {user.role !== 'staff' && (user.role === 'admin' || user.permissions?.dealerships) && (
                    <button 
                      onClick={() => handleMobileNavigate('dealerships')} 
                      className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors flex items-center space-x-3 ${
                        currentView === 'dealerships'
                          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-r-4 border-blue-600 dark:border-blue-400'
                          : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                      }`}
                    >
                      <span>🏢</span>
                      <span>{t('dealerships', language)}</span>
                    </button>
                  )}
                  {user.role !== 'staff' && (
                    <>
                      {(user.role === 'admin' || user.permissions?.reports) && (
                        <button 
                          onClick={() => handleMobileNavigate('reports')} 
                          className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors flex items-center space-x-3 ${
                            currentView === 'reports'
                              ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-r-4 border-blue-600 dark:border-blue-400'
                              : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                          }`}
                        >
                          <span>📊</span>
                          <span>{t('reports', language)}</span>
                        </button>
                      )}
                      {(user.role === 'admin' || user.permissions?.customers) && (
                        <button 
                          onClick={() => handleMobileNavigate('customers')} 
                          className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors flex items-center space-x-3 ${
                            currentView === 'customers'
                              ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-r-4 border-blue-600 dark:border-blue-400'
                              : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                          }`}
                        >
                          <span>👥</span>
                          <span>{t('customers', language)}</span>
                        </button>
                      )}
                      {(user.role === 'admin' || user.permissions?.customers) && (
                        <button 
                          onClick={() => handleMobileNavigate('store')} 
                          className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors flex items-center space-x-3 ${
                            currentView === 'store'
                              ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-r-4 border-blue-600 dark:border-blue-400'
                              : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                          }`}
                        >
                          <span>🏪</span>
                          <span>{t('store', language)}</span>
                        </button>
                      )}
                    </>
                  )}
                  {(user.role === 'admin' || (user.role === 'manager' && (user.permissions?.payday || user.permissions?.accounting))) && (
                    <button
                      onClick={() => handleMobileNavigate('accounting')}
                      className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors flex items-center space-x-3 ${
                        currentView === 'accounting'
                          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-r-4 border-blue-600 dark:border-blue-400'
                          : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                      }`}
                    >
                      <span>📊</span>
                      <span>{t('accounting', language)}</span>
                    </button>
                  )}
                </>
              )}
              
              {/* Price List - Available to all */}
              <button 
                onClick={() => handleMobileNavigate('pricelist')} 
                className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors flex items-center space-x-3 ${
                  currentView === 'pricelist'
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-r-4 border-blue-600 dark:border-blue-400'
                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                }`}
              >
                <span>📋</span>
                <span>{t('pricelist', language)}</span>
              </button>
              
              {/* QR Scanner - Available to staff and admin */}
              {(user.role === 'admin' || user.role === 'staff' || user.role === 'manager') && (
                <button 
                  onClick={() => handleMobileNavigate('qrscanner')} 
                  className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors flex items-center space-x-3 ${
                    currentView === 'qrscanner'
                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-r-4 border-blue-600 dark:border-blue-400'
                      : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                  }`}
                >
                  <span>📱</span>
                  <span>QR Skanni</span>
                </button>
              )}
              
              {(user.role === 'admin' || (user.role === 'manager' && user.permissions?.settings)) && (
                <>
                  <button 
                    onClick={() => handleMobileNavigate('products')} 
                    className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors flex items-center space-x-3 ${
                      currentView === 'products'
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-r-4 border-blue-600 dark:border-blue-400'
                        : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                    }`}
                  >
                    <span>📦</span>
                    <span>{t('products', language)}</span>
                  </button>
                  <button 
                    onClick={() => handleMobileNavigate('inventory')} 
                    className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors flex items-center space-x-3 ${
                      currentView === 'inventory'
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-r-4 border-blue-600 dark:border-blue-400'
                        : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                    }`}
                  >
                    <span>📦</span>
                    <span>{t('inventory', language)}</span>
                  </button>
                  <button 
                    onClick={() => handleMobileNavigate('corporate')} 
                    className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors flex items-center space-x-3 ${
                      currentView === 'corporate'
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-r-4 border-blue-600 dark:border-blue-400'
                        : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                    }`}
                  >
                    <Building2 className="h-5 w-5" />
                    <span>Fyrirtæki</span>
                  </button>
                  <button 
                    onClick={() => handleMobileNavigate('orders')} 
                    className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors flex items-center space-x-3 ${
                      currentView === 'orders'
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-r-4 border-blue-600 dark:border-blue-400'
                        : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                    }`}
                  >
                    <Package className="h-5 w-5" />
                    <span>Pantanir</span>
                  </button>
                  <button 
                    onClick={() => handleMobileNavigate('settings')} 
                    className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors flex items-center space-x-3 ${
                      currentView === 'settings'
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-r-4 border-blue-600 dark:border-blue-400'
                        : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                    }`}
                  >
                    <Settings className="h-5 w-5" />
                    <span>{t('settings', language)}</span>
                  </button>
                </>
              )}
            </nav>
          </div>
          
          {/* Mobile Menu Footer */}
          <div className="p-3 border-t border-slate-200 dark:border-slate-700 space-y-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setMobileMenuOpen(false);
                onLogout();
              }}
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-md text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors flex items-center justify-center"
            >
              <LogOut className="h-4 w-4 mr-2" />
              {t('logout', language)}
            </button>
          </div>
        </div>
      </div>
      )}
      
      <div className="bg-white dark:bg-slate-900 shadow-sm border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 sm:space-x-8">
              {/* Hamburger Menu Button - Mobile Only */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMobileMenuOpen(true)}
                className="lg:hidden w-10 h-10 p-0 mr-2"
              >
                <span className="text-2xl">☰</span>
              </Button>
              
              <div className="flex items-center">
                <div className="bg-white dark:bg-slate-900 rounded-lg px-3 py-1.5">
                  <img src="/alvorubon-top.png" alt="Alvörubón" className="h-8 sm:h-10 object-contain" />
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 sm:space-x-4">
            {/* Pending Pickups Notification - Cars that need to be picked up */}
            {pendingPickups.length > 0 && setPickupDropdownOpen && (
              <div className="relative" ref={pickupDropdownRef}>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setPickupDropdownOpen(!pickupDropdownOpen)}
                  className="h-10 px-3 relative animate-bounce bg-gradient-to-r from-orange-400 to-red-500 border-0 hover:from-orange-500 hover:to-red-600 text-white shadow-lg"
                  title="Bílar til að sækja"
                >
                  <span className="text-lg mr-1">🚚</span>
                  <span className="font-bold">{pendingPickups.length} SÆKJA</span>
                </Button>
                
                {/* Pickup Dropdown */}
                {pickupDropdownOpen && (
                  <div className="fixed sm:absolute left-2 right-2 sm:right-0 sm:left-auto mt-2 sm:w-96 bg-white dark:bg-slate-800 rounded-lg shadow-xl border-2 border-orange-400 dark:border-orange-500 z-50 max-h-96 overflow-y-auto">
                    <div className="p-4 border-b border-orange-200 dark:border-orange-700 bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/30 dark:to-red-900/30">
                      <h3 className="font-bold text-orange-900 dark:text-orange-100 text-lg">
                        🚚 Bílar til að sækja!
                      </h3>
                      <p className="text-sm text-orange-700 dark:text-orange-300">
                        {pendingPickups.length} bíl{pendingPickups.length !== 1 ? 'ar' : 'l'} bíð{pendingPickups.length !== 1 ? 'a' : 'ur'} eftir að vera sótt{pendingPickups.length !== 1 ? 'ir' : ''}
                      </p>
                    </div>
                    
                    <div className="divide-y divide-slate-200 dark:divide-slate-700">
                      {pendingPickups.map((job) => (
                        <div key={job.id} className="p-4 hover:bg-orange-50 dark:hover:bg-slate-700/50">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1 min-w-0">
                              {/* Today/Tomorrow badge */}
                              <div className="mb-1">
                                {job.is_today ? (
                                  <span className="inline-block px-2 py-1 text-xs font-bold bg-red-600 text-white rounded">
                                    🔴 Í DAG {job.scheduled_time && `kl. ${job.scheduled_time}`}
                                  </span>
                                ) : (
                                  <span className="inline-block px-2 py-1 text-xs font-bold bg-yellow-500 text-white rounded">
                                    🟡 Á MORGUN {job.scheduled_time && `kl. ${job.scheduled_time}`}
                                  </span>
                                )}
                              </div>
                              <div className="font-bold text-slate-900 dark:text-slate-100 text-lg">
                                {job.plate}
                              </div>
                              {(job.car_make || job.car_model) && (
                                <div className="text-sm text-slate-600 dark:text-slate-400">
                                  {job.car_make} {job.car_model}
                                </div>
                              )}
                              <div className="text-sm text-slate-500">
                                {job.owner_name}
                              </div>
                            </div>
                          </div>
                          
                          <div className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
                            {/* Phone number - clickable to call */}
                            {(job.phone || job.owner_phone || job.renter_phone) && (
                              <a 
                                href={`tel:${job.phone || job.owner_phone || job.renter_phone}`}
                                className="flex items-center gap-2 font-bold text-lg text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/30 p-2 rounded hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors"
                              >
                                📞 {job.phone || job.owner_phone || job.renter_phone}
                                <span className="text-xs font-normal text-green-600">(smella til að hringja)</span>
                              </a>
                            )}
                            {job.pickup_from_dealership && (
                              <div>🏢 {job.pickup_from_dealership}</div>
                            )}
                            {/* Address - clickable to open maps */}
                            {job.pickup_address && (
                              <a 
                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(job.pickup_address)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 p-2 rounded hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                              >
                                📍 {job.pickup_address}
                                <span className="text-xs font-normal text-blue-600">(opna kort)</span>
                              </a>
                            )}
                            {job.pickup_delivery_note && (
                              <div className="text-xs bg-yellow-50 dark:bg-yellow-900/30 p-2 rounded mt-1 border border-yellow-200 dark:border-yellow-700">
                                💬 {job.pickup_delivery_note}
                              </div>
                            )}
                            {job.notes && (
                              <div className="text-xs bg-slate-100 dark:bg-slate-700 p-2 rounded mt-1">
                                📝 {job.notes}
                              </div>
                            )}
                          </div>
                          
                          {onMarkPickupComplete && (
                            <Button
                              size="sm"
                              className="w-full mt-3 bg-green-600 hover:bg-green-700 text-white font-bold"
                              onClick={() => onMarkPickupComplete(job.id)}
                            >
                              ✓ Sótt
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Pending Deliveries Notification - Cars that need to be delivered back */}
            {pendingDeliveries.length > 0 && setDeliveryDropdownOpen && (
              <div className="relative" ref={deliveryDropdownRef}>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setDeliveryDropdownOpen(!deliveryDropdownOpen)}
                  className="h-10 px-3 relative animate-bounce bg-gradient-to-r from-purple-400 to-indigo-500 border-0 hover:from-purple-500 hover:to-indigo-600 text-white shadow-lg"
                  title="Bílar til að skila"
                >
                  <span className="text-lg mr-1">🏠</span>
                  <span className="font-bold">{pendingDeliveries.length} SKILA</span>
                </Button>
                
                {/* Delivery Dropdown */}
                {deliveryDropdownOpen && (
                  <div className="fixed sm:absolute left-2 right-2 sm:right-0 sm:left-auto mt-2 sm:w-96 bg-white dark:bg-slate-800 rounded-lg shadow-xl border-2 border-purple-400 dark:border-purple-500 z-50 max-h-96 overflow-y-auto">
                    <div className="p-4 border-b border-purple-200 dark:border-purple-700 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/30 dark:to-indigo-900/30">
                      <h3 className="font-bold text-purple-900 dark:text-purple-100 text-lg">
                        🏠 Bílar til að skila!
                      </h3>
                      <p className="text-sm text-purple-700 dark:text-purple-300">
                        {pendingDeliveries.length} bíl{pendingDeliveries.length !== 1 ? 'ar' : 'l'} tilbúi{pendingDeliveries.length !== 1 ? 'nir' : 'nn'} til skila
                      </p>
                    </div>
                    
                    <div className="divide-y divide-slate-200 dark:divide-slate-700">
                      {pendingDeliveries.map((job) => (
                        <div key={job.id} className="p-4 hover:bg-purple-50 dark:hover:bg-slate-700/50">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1 min-w-0">
                              <div className="font-bold text-slate-900 dark:text-slate-100 text-lg">
                                {job.plate}
                              </div>
                              {(job.car_make || job.car_model) && (
                                <div className="text-sm text-slate-600 dark:text-slate-400">
                                  {job.car_make} {job.car_model}
                                </div>
                              )}
                              <div className="text-sm text-slate-500">
                                {job.owner_name}
                              </div>
                            </div>
                          </div>
                          
                          <div className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
                            {/* Phone number - clickable to call */}
                            {(job.phone || job.owner_phone || job.renter_phone) && (
                              <a 
                                href={`tel:${job.phone || job.owner_phone || job.renter_phone}`}
                                className="flex items-center gap-2 font-bold text-lg text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/30 p-2 rounded hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors"
                              >
                                📞 {job.phone || job.owner_phone || job.renter_phone}
                                <span className="text-xs font-normal text-green-600">(smella til að hringja)</span>
                              </a>
                            )}
                            {job.pickup_from_dealership && (
                              <div>🏢 {job.pickup_from_dealership}</div>
                            )}
                            {/* Address - clickable to open maps */}
                            {job.pickup_address && (
                              <a 
                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(job.pickup_address)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 p-2 rounded hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                              >
                                📍 {job.pickup_address}
                                <span className="text-xs font-normal text-blue-600">(opna kort)</span>
                              </a>
                            )}
                            {job.pickup_delivery_note && (
                              <div className="text-xs bg-yellow-50 dark:bg-yellow-900/30 p-2 rounded mt-1 border border-yellow-200 dark:border-yellow-700">
                                💬 {job.pickup_delivery_note}
                              </div>
                            )}
                            {job.notes && (
                              <div className="text-xs bg-slate-100 dark:bg-slate-700 p-2 rounded mt-1">
                                📝 {job.notes}
                              </div>
                            )}
                          </div>
                          
                          {onMarkDeliveryComplete && (
                            <Button
                              size="sm"
                              className="w-full mt-3 bg-purple-600 hover:bg-purple-700 text-white font-bold"
                              onClick={() => onMarkDeliveryComplete(job.id)}
                            >
                              ✓ Skilað
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Pending Approval Notifications - Visible for ALL users */}
            {setNotificationDropdownOpen && (
              <div className="relative" ref={dropdownRef}>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    // If there are undismissed notifications, show popup first
                    if (dealershipPopupNotifications.length > 0 && onShowDealershipPopup) {
                      onShowDealershipPopup();
                    } else {
                      setNotificationDropdownOpen(!notificationDropdownOpen);
                    }
                  }}
                  className={`w-10 h-10 p-0 relative ${dealershipPopupNotifications.length > 0 ? 'animate-pulse bg-orange-100 border-orange-400 hover:bg-orange-200' : ''}`}
                  title="Beiðnir frá bílasölum"
                >
                  <span className={`text-lg ${dealershipPopupNotifications.length > 0 ? 'animate-bounce' : ''}`}>
                    {dealershipPopupNotifications.length > 0 ? '🚗' : '🔔'}
                  </span>
                  {(pendingApprovalCount > 0 || dealershipPopupNotifications.length > 0) && (
                    <span className={`absolute -top-1 -right-1 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold ${dealershipPopupNotifications.length > 0 ? 'bg-orange-500 animate-pulse' : 'bg-red-500'}`}>
                      {dealershipPopupNotifications.length > 0 ? dealershipPopupNotifications.length : pendingApprovalCount}
                    </span>
                  )}
                </Button>
                
                {/* Notification Dropdown */}
                {notificationDropdownOpen && (
                  <div className="fixed sm:absolute left-2 right-2 sm:right-0 sm:left-auto mt-2 sm:w-96 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 z-50 max-h-96 overflow-y-auto">
                    <div className="p-4 border-b border-slate-200 dark:border-slate-700">
                      <h3 className="font-semibold text-slate-900 dark:text-slate-100 break-words">
                        📋 Beiðnir frá bílasölum
                      </h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {pendingApprovalCount} beiðn{pendingApprovalCount !== 1 ? 'ir' : ''} bíða samþykkis
                      </p>
                    </div>
                    
                    {pendingApprovalJobs.length === 0 ? (
                      <div className="p-8 text-center text-slate-500 dark:text-slate-400">
                        <div className="text-4xl mb-2">✅</div>
                        <div>Engar beiðnir í bið</div>
                      </div>
                    ) : (
                      <div className="divide-y divide-slate-200 dark:divide-slate-700">
                        {pendingApprovalJobs.map((job) => (
                          <div key={job.id} className="p-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 min-w-0">
                            <div className="flex justify-between items-start mb-2 gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-slate-900 dark:text-slate-100 break-words">
                                  {job.plate}
                                </div>
                                <div className="text-sm text-slate-600 dark:text-slate-400 break-words">
                                  {job.owner_name}
                                </div>
                                <div className="text-sm text-slate-500 dark:text-slate-500 break-words">
                                  🏢 {job.dealership}
                                </div>
                              </div>
                              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200 flex-shrink-0 whitespace-nowrap">
                                Í bið
                              </Badge>
                            </div>
                            
                            <div className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                              <div className="break-words">📅 {job.scheduled_date}</div>
                              {job.notes && (
                                <div className="mt-1 text-xs italic break-words">💬 {job.notes}</div>
                              )}
                            </div>
                            
                            <div className="flex flex-col sm:flex-row gap-2 mt-3">
                              <Button
                                size="sm"
                                onClick={() => onApproveJob && onApproveJob(job)}
                                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                              >
                                ✓ Samþykkja
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onRejectJob && onRejectJob(job)}
                                className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
                              >
                                ✕ Hafna
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Dark Mode Toggle */}
            <Button 
              variant="outline" 
              size="sm"
              onClick={onDarkModeToggle}
              className="w-10 h-10 p-0"
              title={darkMode ? "Light Mode" : "Dark Mode"}
            >
              {darkMode ? '☀️' : '🌙'}
            </Button>
            
            {/* Language Dropdown with Flags */}
            <Select value={language} onValueChange={onLanguageChange}>
              <SelectTrigger className="w-12 sm:w-16 h-8 border rounded-md dark:bg-slate-800 dark:border-slate-600">
                <SelectValue>
                  {language === 'is' && '🇮🇸'}
                  {language === 'en' && '🇬🇧'}
                  {language === 'pl' && '🇵🇱'}
                  {language === 'lt' && '🇱🇹'}
                  {language === 'ru' && '🇷🇺'}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="is" className="flex items-center">
                  <span className="mr-2">🇮🇸</span> Íslenska
                </SelectItem>
                <SelectItem value="en" className="flex items-center">
                  <span className="mr-2">🇬🇧</span> English
                </SelectItem>
                <SelectItem value="pl" className="flex items-center">
                  <span className="mr-2">🇵🇱</span> Polski
                </SelectItem>
                <SelectItem value="lt" className="flex items-center">
                  <span className="mr-2">🇱🇹</span> Lietuvių
                </SelectItem>
                <SelectItem value="ru" className="flex items-center">
                  <span className="mr-2">🇷🇺</span> Русский
                </SelectItem>
              </SelectContent>
            </Select>
            
            <span className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 hidden md:inline">
              {user.name} ({user.role})
            </span>
            <Button variant="outline" size="sm" onClick={onLogout} className="hidden sm:flex">
              <LogOut className="h-4 w-4 mr-2" />
              {t('logout', language)}
            </Button>
            <Button variant="outline" size="sm" onClick={onLogout} className="sm:hidden w-10 h-10 p-0">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Desktop Navigation - Hidden on Mobile */}
        <div className="hidden lg:flex space-x-0 border-t border-slate-200 dark:border-slate-700 overflow-x-auto">
          <button
            onClick={() => onNavigate('registration')}
            className={`px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
              currentView === 'registration'
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
            }`}
          >
            {t('registration', language)}
          </button>
          
          {user.role !== 'viewer' && (
            <>
              {(user.role === 'admin' || user.role === 'staff' || user.permissions?.jobs) && (
                <>
                  <button onClick={() => onNavigate('calendar')} className={`px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${currentView === 'calendar' ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'}`}>📅 {t('calendar', language)}</button>
                  <button onClick={() => onNavigate('jobs')} className={`px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${currentView === 'jobs' ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'}`}>{t('jobs', language)}</button>
                </>
              )}
              {(user.role === 'admin' || user.role === 'staff' || user.permissions?.payments) && (
                <button onClick={() => onNavigate('payments')} className={`px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${currentView === 'payments' ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'}`}>{t('payments', language)}</button>
              )}
              {user.role !== 'staff' && (user.role === 'admin' || user.permissions?.dealerships) && (
                <button onClick={() => onNavigate('dealerships')} className={`px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${currentView === 'dealerships' ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'}`}>{t('dealerships', language)}</button>
              )}
              {user.role !== 'staff' && (
                <>
                  {(user.role === 'admin' || user.permissions?.reports) && (
                    <button onClick={() => onNavigate('reports')} className={`px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${currentView === 'reports' ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'}`}>{t('reports', language)}</button>
                  )}
                  {(user.role === 'admin' || user.permissions?.customers) && (
                    <button onClick={() => onNavigate('customers')} className={`px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${currentView === 'customers' ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'}`}>👥 {t('customers', language)}</button>
                  )}
                  {(user.role === 'admin' || user.permissions?.customers) && (
                    <button onClick={() => onNavigate('store')} className={`px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${currentView === 'store' ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'}`}>🏪 {t('store', language)}</button>
                  )}
                </>
              )}
              {(user.role === 'admin' || (user.role === 'manager' && (user.permissions?.payday || user.permissions?.accounting))) && (
                <button
                  onClick={() => onNavigate('accounting')}
                  className={`px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                    currentView === 'accounting'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                >
                  📊 {t('accounting', language)}
                </button>
              )}
            </>
          )}
          
          {/* Price List - Available to all authenticated users */}
          <button 
            onClick={() => onNavigate('pricelist')} 
            className={`px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
              currentView === 'pricelist' 
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400' 
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
            }`}
          >
            📋 {t('pricelist', language)}
          </button>
          
          {/* QR Scanner - Available to staff and admin */}
          {(user.role === 'admin' || user.role === 'staff' || user.role === 'manager') && (
            <button 
              onClick={() => onNavigate('qrscanner')} 
              className={`px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                currentView === 'qrscanner' 
                  ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400' 
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
              }`}
            >
              📱 QR Skanni
            </button>
          )}
          
          {(user.role === 'admin' || (user.role === 'manager' && user.permissions?.settings)) && (
            <>
              <button onClick={() => onNavigate('products')} className={`px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${currentView === 'products' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100'}`}>📦 {t('products', language)}</button>
              <button onClick={() => onNavigate('inventory')} className={`px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${currentView === 'inventory' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-600 hover:text-slate-900'}`}>📦 {t('inventory', language)}</button>
              <button onClick={() => onNavigate('corporate')} className={`px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${currentView === 'corporate' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-600 hover:text-slate-900'}`}><Building2 className="h-4 w-4 mr-2 inline" />Fyrirtæki</button>
              <button onClick={() => onNavigate('orders')} className={`px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${currentView === 'orders' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-600 hover:text-slate-900'}`}><Package className="h-4 w-4 mr-2 inline" />Pantanir</button>
              <button onClick={() => onNavigate('settings')} className={`px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${currentView === 'settings' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-600 hover:text-slate-900'}`}><Settings className="h-4 w-4 mr-2 inline" />{t('settings', language)}</button>
            </>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

// Registration Form Component - Simple form
function CarwashRegistrationForm({ language = 'is' }) {
  const { user } = useAuth(); // Get current user
  
  const [formData, setFormData] = useState({
    plate: '',
    owner_name: '',
    owner_kt: '',
    owner_address: '', // Owner's registered address
    phone_number: '',
    car_make: '',
    car_model: '',
    car_color: '', // Vehicle color
    dealership: '',
    services: [],
    size: '',
    scheduled_date: localStorage.getItem('prefilledDate') || new Date().toISOString().split('T')[0],
    scheduled_time: '', // Optional time for future dates
    notes: '',
    key_location: '', // Where car keys are located
    images: [], // Array of base64 images for damage documentation
    ready_by_time: '',
    needs_loan_car: false,
    loan_car_id: null,
    needs_pickup_delivery: false,
    pickup_address: '',
    pickup_postal_code: '', // Postal code for pricing
    pickup_delivery_price: 0, // Price for pickup/delivery
    pickup_delivery_note: '', // Note about pickup/delivery
    pickup_delivery_discount: false, // Whether to allow discount on pickup/delivery
    use_custom_pickup_address: false, // Whether to use custom address
    shuttle_to_dealership: '', // Which dealership to shuttle to (for invoice customers)
    total: 0,
    payer_is_owner: true,
    payer_name: '',
    payer_kt: '',
    discount_percent: 0,
    send_completion_sms: false,
    send_payment_sms: false,
    rental_type: '' // "sale" or "long_term_rental" for car rental customers
  });
  
  // Clear prefilled date from localStorage after using it
  useEffect(() => {
    const prefilledDate = localStorage.getItem('prefilledDate');
    if (prefilledDate) {
      localStorage.removeItem('prefilledDate');
      toast.success(`📅 Dagsetning valin úr dagatali: ${prefilledDate}`);
    }
  }, []);

  const [manufacturers, setManufacturers] = useState([]);
  const [models, setModels] = useState([]);
  const [services, setServices] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [categories, setCategories] = useState([]);
  const sortedCategories = useMemo(() => categories?.sort((a, b) => (a.order || 0) - (b.order || 0)) || [], [categories]);
  const [dealerships, setDealerships] = useState([]);
  
  // Helper to get dealership name from string or object
  const getDealershipName = (d) => typeof d === 'string' ? d : d?.name || '';
  
  const [loanCars, setLoanCars] = useState([]);
  const [prices, setPrices] = useState({});
  const [pickupZones, setPickupZones] = useState([]);
  const [productServices, setProductServices] = useState([]); // Product-based services
  const [productQuantities, setProductQuantities] = useState({}); // Track quantities for each product
  const [showProducts, setShowProducts] = useState(false); // Toggle to show/hide products section
  const [blockedTimes, setBlockedTimes] = useState([]); // Blocked times
  const [openingHoursSettings, setOpeningHoursSettings] = useState(null); // Opening hours
  const [expandedCategories, setExpandedCategories] = useState({}); // Track which service categories are expanded
  
  // Auto-expand all service categories when they load so users can see services immediately
  useEffect(() => {
    if (categories.length > 0) {
      const expanded = {};
      categories.forEach(cat => { 
        expanded[cat.id] = true; 
      });
      setExpandedCategories(expanded);
    }
  }, [categories]);
  
  // QR Code Photo Upload states
  const [qrSessionId, setQrSessionId] = useState(null);
  const [qrUploadUrl, setQrUploadUrl] = useState('');
  const [showQrDialog, setShowQrDialog] = useState(false);
  const [pollingInterval, setPollingInterval] = useState(null);
  
  // Loan agreement dialog states
  const [loanAgreementDialogOpen, setLoanAgreementDialogOpen] = useState(false);
  const [selectedJobForAgreement, setSelectedJobForAgreement] = useState(null);
  const [loanAgreementData, setLoanAgreementData] = useState({
    km_out: '',
    delivery_date: new Date().toISOString().split('T')[0],
    comments: '',
    signature_company: '',
    signature_customer: '',
    date_company: new Date().toISOString().split('T')[0],
    date_customer: new Date().toISOString().split('T')[0]
  });
  
  // Renter info for print agreement
  const [printRenterDialogOpen, setPrintRenterDialogOpen] = useState(false);
  const [printRenterInfo, setPrintRenterInfo] = useState(null);
  const [tempPrintRenterInfo, setTempPrintRenterInfo] = useState({
    name: '',
    kennitala: '',
    phone: ''
  });
  
  // Signature link dialog states
  const [signatureLinkDialogOpen, setSignatureLinkDialogOpen] = useState(false);
  const [signatureLink, setSignatureLink] = useState('');
  const [saving, setSaving] = useState(false);
  
  // SMS sending for signature link
  const [sendSMS, setSendSMS] = useState(false);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState('');
  const [customPhoneNumber, setCustomPhoneNumber] = useState('');
  const [sendingSMS, setSendingSMS] = useState(false);
  const [smsResult, setSmsResult] = useState(null);
  
  // Ref to store latest car info for size override saving
  const carInfoRef = useRef({ car_make: '', car_model: '' });
  
  // Camera ref for plate scanning
  const cameraInputRef = React.useRef(null);
  const [scanningPlate, setScanningPlate] = useState(false);
  
  // Live camera states for plate scanning
  const [liveCameraOpen, setLiveCameraOpen] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [autoScanning, setAutoScanning] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const liveCameraVideoRef = useRef(null);
  const liveCameraCanvasRef = useRef(null);
  const scanIntervalRef = useRef(null);
  const isScanningRef = useRef(false);
  
  // Handle scan plate button click - open live camera on mobile
  const handleScanPlate = () => {
    // Check if on mobile
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth < 768;
    if (isMobile) {
      openLiveCamera();
    } else if (cameraInputRef.current) {
      cameraInputRef.current.click();
    }
  };
  
  // Toggle torch/flash
  const toggleTorch = async () => {
    if (!cameraStream) return;
    try {
      const track = cameraStream.getVideoTracks()[0];
      const newTorchState = !torchOn;
      await track.applyConstraints({
        advanced: [{ torch: newTorchState }]
      });
      setTorchOn(newTorchState);
    } catch (error) {
      console.error('Torch toggle error:', error);
      toast.error('Gat ekki kveikt/slökkt á ljósi');
    }
  };
  
  // Open live camera with auto-scanning
  const openLiveCamera = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error('Myndavél er ekki studd í þessum vafra');
        return;
      }
      const isSecure = window.location.protocol === 'https:' || window.location.hostname === 'localhost';
      if (!isSecure) {
        toast.error('Myndavél virkar aðeins á HTTPS tengingu');
        return;
      }
      setLiveCameraOpen(true);
      setAutoScanning(true);
      setTorchOn(false);
      
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: false
      });
      setCameraStream(mediaStream);
      
      // Check if torch is supported
      const track = mediaStream.getVideoTracks()[0];
      const capabilities = track.getCapabilities ? track.getCapabilities() : {};
      setTorchSupported(capabilities.torch === true);
      
      if (liveCameraVideoRef.current) {
        liveCameraVideoRef.current.srcObject = mediaStream;
        liveCameraVideoRef.current.onloadedmetadata = () => {
          startLiveAutoScanning();
        };
      }
    } catch (error) {
      console.error('Camera access error:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Leyfi fyrir myndavél var hafnað');
      } else if (error.name === 'NotFoundError') {
        toast.error('Engin myndavél fannst');
      } else {
        toast.error('Gat ekki opnað myndavél');
      }
      setLiveCameraOpen(false);
      setAutoScanning(false);
    }
  };
  
  // Start auto-scanning for plates - FAST continuous scanning
  const startLiveAutoScanning = () => {
    if (scanIntervalRef.current) clearInterval(scanIntervalRef.current);
    // Scan every 100ms for faster detection
    scanIntervalRef.current = setInterval(() => {
      if (liveCameraVideoRef.current && liveCameraCanvasRef.current && !isScanningRef.current) {
        scanLiveFrame();
      }
    }, 100);
  };
  
  // Scan a single frame - optimized for speed
  const scanLiveFrame = async () => {
    if (!liveCameraVideoRef.current || !liveCameraCanvasRef.current || isScanningRef.current) return;
    try {
      isScanningRef.current = true;
      const video = liveCameraVideoRef.current;
      const canvas = liveCameraCanvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      // Use higher quality (0.5) for better OCR accuracy
      const base64Image = canvas.toDataURL('image/jpeg', 0.5);
      const response = await axios.post(`${API}/vehicles/ocr-plate`, { image: base64Image }, { timeout: 3000 });
      if (response.data.success && response.data.plate_number) {
        if (scanIntervalRef.current) {
          clearInterval(scanIntervalRef.current);
          scanIntervalRef.current = null;
        }
        isScanningRef.current = false;
        const plateNumber = response.data.plate_number.toUpperCase();
        setFormData(prev => ({ ...prev, plate: plateNumber }));
        toast.success(`✅ Plata greind: ${plateNumber}`);
        
        // Auto-lookup vehicle info immediately after detection
        try {
          const lookupResponse = await axios.get(`${API}/vehicles/lookup/${plateNumber}`);
          if (lookupResponse.data) {
            const vehicleData = lookupResponse.data;
            setFormData(prev => ({
              ...prev,
              plate: plateNumber,
              car_make: vehicleData.manufacturer || vehicleData.make || '',
              car_model: vehicleData.model || '',
              owner_name: vehicleData.owner_name || '',
              owner_kt: vehicleData.owner_kt || '',
              phone_number: vehicleData.owner_phone || prev.phone_number || '',
            }));
            
            // Auto-detect size
            const detectedSize = vehicleData.size || '';
            if (detectedSize) {
              setFormData(prev => ({ ...prev, size: detectedSize }));
              toast.success(`🚗 ${vehicleData.manufacturer} ${vehicleData.model} - ${detectedSize}`);
            }
          }
        } catch (lookupError) {
          console.log('Vehicle lookup failed:', lookupError);
        }
        
        closeLiveCamera();
      } else {
        isScanningRef.current = false;
      }
    } catch (error) {
      isScanningRef.current = false;
    }
  };
  
  // Close live camera
  const closeLiveCamera = () => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setLiveCameraOpen(false);
    setAutoScanning(false);
    setTorchOn(false);
    setTorchSupported(false);
    isScanningRef.current = false;
  };
  
  // Manual capture from live camera
  const captureLivePhoto = async () => {
    if (!liveCameraVideoRef.current || !liveCameraCanvasRef.current) return;
    try {
      setScanningPlate(true);
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current);
        scanIntervalRef.current = null;
      }
      setAutoScanning(false);
      const video = liveCameraVideoRef.current;
      const canvas = liveCameraCanvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      const base64Image = canvas.toDataURL('image/jpeg', 0.7);
      toast.info('🔍 Greinir númeraplötu...');
      const response = await axios.post(`${API}/vehicles/ocr-plate`, { image: base64Image });
      if (response.data.success && response.data.plate_number) {
        const plateNumber = response.data.plate_number.toUpperCase();
        setFormData(prev => ({ ...prev, plate: plateNumber }));
        toast.success(`✅ Plata greind: ${plateNumber}`);
        closeLiveCamera();
      } else {
        toast.error('Gat ekki lesið plötu - reyndu aftur');
        startLiveAutoScanning();
        setAutoScanning(true);
      }
    } catch (error) {
      console.error('OCR error:', error);
      toast.error('Villa við að lesa plötu');
    } finally {
      setScanningPlate(false);
    }
  };
  
  // Handle camera capture (file input fallback for desktop)
  const handleCameraCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setScanningPlate(true);
    toast.info('🔍 Greinir númeraplötu...');
    
    try {
      // Compress and resize image before sending
      const compressedImage = await compressImage(file, 600, 0.6); // Max 600px width, 60% quality
      
      // Call backend OCR endpoint
      const response = await axios.post(`${API}/vehicles/ocr-plate`, {
        image: compressedImage
      });
      
      if (response.data.success && response.data.plate_number) {
        const plateNumber = response.data.plate_number;
        setFormData({ ...formData, plate: plateNumber });
        
        if (response.data.valid_format) {
          toast.success(`✅ Númeraplata greind: ${plateNumber}`);
        } else {
          toast.success(`⚠️ Plata greind: ${plateNumber} (staðfestu snið)`);
        }
      } else {
        toast.error(response.data.message || 'Tókst ekki að greina númeraplötu');
      }
    } catch (error) {
      console.error('OCR error:', error);
      toast.error('Villa við að greina númeraplötu. Reyndu aftur.');
    } finally {
      setScanningPlate(false);
      // Reset file input
      if (cameraInputRef.current) {
        cameraInputRef.current.value = '';
      }
    }
  };
  
  // Compress image helper function
  const compressImage = (file, maxWidth, quality) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Resize if wider than maxWidth
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to base64 with compression
          const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
          resolve(compressedBase64);
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  
  // Renter handlers for print agreement
  const handleChangePrintRenter = () => {
    setTempPrintRenterInfo({
      name: printRenterInfo?.name || '',
      kennitala: printRenterInfo?.kennitala || '',
      phone: printRenterInfo?.phone || ''
    });
    setPrintRenterDialogOpen(true);
  };

  const handlePrintKennitalaLookup = async () => {
    if (!tempPrintRenterInfo.kennitala || tempPrintRenterInfo.kennitala.length !== 10) {
      toast.error('Vinsamlegast sláðu inn gilda kennitölu (10 tölustafir)');
      return;
    }

    try {
      const response = await axios.get(`${API}/person/lookup/${tempPrintRenterInfo.kennitala}`);
      
      if (response.data.found) {
        setTempPrintRenterInfo(prev => ({
          ...prev,
          name: response.data.full_name || response.data.company_name || '',
          phone: response.data.phone || prev.phone
        }));
        toast.success('Upplýsingar fundnar í Þjóðskrá!');
      } else {
        toast.error('Kennitala fannst ekki í Þjóðskrá');
      }
    } catch (error) {
      console.error('Failed to lookup kennitala:', error);
      toast.error('Villa við að leita að kennitölu');
    }
  };

  const handleSavePrintRenter = () => {
    if (!tempPrintRenterInfo.name || !tempPrintRenterInfo.kennitala || !tempPrintRenterInfo.phone) {
      toast.error('Vinsamlegast fylltu út öll reitina');
      return;
    }
    setPrintRenterInfo(tempPrintRenterInfo);
    setPrintRenterDialogOpen(false);
    toast.success('Upplýsingar um leigjanda vistaðar!');
  };
  
  // Send signature link via SMS
  const handleSendSignatureSMS = async () => {
    if (!selectedPhoneNumber) {
      toast.error('Vinsamlegast veldu símanúmer');
      return;
    }
    
    if (selectedPhoneNumber === 'custom' && !customPhoneNumber) {
      toast.error('Vinsamlegast sláðu inn símanúmer');
      return;
    }
    
    try {
      setSendingSMS(true);
      const customerName = printRenterInfo?.name || formData.owner_name;
      const phoneToSend = selectedPhoneNumber === 'custom' ? customPhoneNumber : selectedPhoneNumber;
      
      const response = await axios.post(`${API}/loan-cars/send-signature-sms`, {
        phone_number: phoneToSend,
        signature_url: signatureLink,
        customer_name: customerName
      });
      
      if (response.data.success) {
        setSmsResult({success: true, message: response.data.message});
        toast.success(`✅ ${response.data.message}`);
      }
    } catch (error) {
      console.error('Failed to send SMS:', error);
      const errorMsg = error.response?.data?.detail || 'Villa við að senda SMS';
      setSmsResult({success: false, message: errorMsg});
      toast.error(`❌ ${errorMsg}`);
    } finally {
      setSendingSMS(false);
    }
  };
  
  // Debug: Monitor when formData.size changes
  useEffect(() => {
    if (formData.size) {
      console.log('🎯 formData.size changed to:', formData.size);
      const sizeObj = sizes.find(s => s.id === formData.size);
      console.log('📋 Size object found:', sizeObj);
      console.log('✅ Owner info filled:', !!formData.owner_name && !!formData.owner_kt);
    }
  }, [formData.size, sizes, formData.owner_name, formData.owner_kt]);
  const [loading, setLoading] = useState(false);
  const [manufacturerSearch, setManufacturerSearch] = useState('');
  
  // Vehicle lookup state
  const [vehicleLookupSuccess, setVehicleLookupSuccess] = useState(false);
  const [vehicleInfo, setVehicleInfo] = useState(null);
  
  // Pickup price editing state
  const [editingPickupPrice, setEditingPickupPrice] = useState(false);
  
  // Payer lookup state
  const [payerKennitalaLoading, setPayerKennitalaLoading] = useState(false);
  
  // Customer search state for payer selection
  const [payerCustomers, setPayerCustomers] = useState([]);
  const [payerCustomerSearch, setPayerCustomerSearch] = useState('');
  const [payerCustomerSortBy, setPayerCustomerSortBy] = useState('popularity'); // 'popularity' or 'name'
  const [showPayerCustomerDropdown, setShowPayerCustomerDropdown] = useState(false);
  const [loadingPayerCustomers, setLoadingPayerCustomers] = useState(false);
  const [updateOwnerWithPayer, setUpdateOwnerWithPayer] = useState(false);
  
  // Car rental customer tracking
  const [isCarRentalCustomer, setIsCarRentalCustomer] = useState(false);
  
  // Customer discount tracking
  const [customerDiscount, setCustomerDiscount] = useState(null);
  const [discountLocked, setDiscountLocked] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null); // Store full customer object for category discounts
  const [lookupAttempted, setLookupAttempted] = useState(false);
  
  // Custom prices for services with editable prices
  const [customPrices, setCustomPrices] = useState({});
  const [customServiceNames, setCustomServiceNames] = useState({}); // For "Vinna" service - custom names
  
  // Manual total override
  const [manualTotalEnabled, setManualTotalEnabled] = useState(false);
  const [manualTotal, setManualTotal] = useState(0);

  useEffect(() => {
    loadDropdownData();
  }, []);
  
  // Auto-lookup discount when payer kennitala changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.payer_kt && formData.payer_kt.length === 10 && !formData.payer_is_owner) {
        lookupPayerDiscount(formData.payer_kt);
      }
    }, 800); // Debounce for 800ms
    
    return () => clearTimeout(timer);
  }, [formData.payer_kt, formData.payer_is_owner]);
  
  const lookupPayerDiscount = async (kennitala) => {
    try {
      const response = await axios.get(`${API}/customers/${kennitala}`);
      if (response.data) {
        const customer = response.data;
        const discount = customer.discount_percentage || 0;
        
        // Auto-fill payer name if not already filled
        if (!formData.payer_name && customer.name) {
          setFormData(prev => ({
            ...prev,
            payer_name: customer.name,
            discount_percent: discount
          }));
          toast.success(`✅ Greiðandi: ${customer.name} - Afsláttur: ${discount}%`);
        } else if (discount > 0) {
          setFormData(prev => ({
            ...prev,
            discount_percent: discount
          }));
          toast.success(`✅ Afsláttur uppfærður: ${discount}%`);
        }
        
        // Store full customer for category discounts
        setSelectedCustomer(customer);
      }
    } catch (error) {
      console.log('Payer customer not found:', error);
      // Silently fail - not all kennitölur will be in system
    }
  };

  useEffect(() => {
    calculateTotal();
  }, [formData.services, formData.size, prices, formData.discount_percent, customPrices, manualTotalEnabled, manualTotal, formData.pickup_delivery_price, formData.needs_pickup_delivery, productQuantities]);

  // Vehicle registry lookup
  const lookupVehicleInfo = async (plateNumber) => {
    if (!plateNumber || plateNumber.length < 2) return;
    
    try {
      setLoading(true);
      setLookupAttempted(true);
      const response = await axios.get(`${API}/vehicles/lookup/${plateNumber}`);
      const vehicleData = response.data;
      
      console.log('Vehicle data received:', vehicleData);
      
      // Check if vehicle was actually found
      if (vehicleData.found) {
        // Store vehicle info and mark lookup as successful
        setVehicleInfo(vehicleData);
        setVehicleLookupSuccess(true);
        
        // Extract postal code from rogg.is data
        let extractedPostalCode = '';
        let pickupDeliveryPrice = 0;
        
        // First priority: Use postal code directly from rogg.is (owner_postal_code field)
        if (vehicleData.owner_postal_code) {
          extractedPostalCode = vehicleData.owner_postal_code;
          console.log(`📍 Póstnúmer sótt beint úr ökutækjaskrá: ${extractedPostalCode}`);
          
          // Find matching zone and price
          const zone = pickupZones.find(z => z.postal_code === extractedPostalCode && z.enabled);
          
          if (zone) {
            pickupDeliveryPrice = zone.price;
            console.log(`📍 Svæði fundið - ${zone.area_name}: ${zone.price} ISK`);
          } else {
            console.log(`📍 Póstnúmer ${extractedPostalCode} ekki í verðlista`);
          }
        } 
        // Fallback: Try to parse from address if postal code field is not available
        else if (vehicleData.owner_address) {
          const postalCodeMatch = vehicleData.owner_address.match(/\b(\d{3})\b/);
          
          if (postalCodeMatch) {
            extractedPostalCode = postalCodeMatch[1];
            
            const zone = pickupZones.find(z => z.postal_code === extractedPostalCode && z.enabled);
            
            if (zone) {
              pickupDeliveryPrice = zone.price;
              console.log(`📍 Póstnúmer fundið í heimilisfangi - ${zone.area_name}: ${zone.price} ISK`);
            }
          } else {
            console.log(`⚠️ Engin póstnúmer fundið í heimilisfangi: "${vehicleData.owner_address}"`);
          }
        }
        
        // Auto-fill basic fields first
        // Note: We ALWAYS fill address and postal code, but price ONLY if checkbox is checked
        const ownerName = vehicleData.owner_name || '';
        
        // Check if owner is a dealership and auto-select it
        let autoDealership = '';
        if (ownerName && dealerships && dealerships.length > 0) {
          console.log('🔍 Checking dealership match. Available dealerships:', dealerships);
          console.log('🔍 Owner name:', ownerName, 'Owner KT:', vehicleData.owner_kennitala);
          
          // Clean owner name for comparison
          const cleanOwnerName = ownerName.replace(/\s+(ehf\.|ehf|slf\.|slf|hf\.|hf)$/i, '').trim().toLowerCase();
          const ownerKt = vehicleData.owner_kennitala ? vehicleData.owner_kennitala.replace(/\D/g, '') : '';
          
          // Find matching dealership by checking name, company_name, or kennitala
          const matchedDealership = dealerships.find(d => {
            // Handle both string (old format) and object (new format with details)
            const dealerName = typeof d === 'string' ? d : d.name;
            const dealerCompanyName = typeof d === 'object' ? d.company_name : null;
            const dealerKt = typeof d === 'object' && d.kennitala ? d.kennitala.replace(/\D/g, '') : '';
            
            const cleanDealerName = dealerName.replace(/\s+(ehf\.|ehf|slf\.|slf|hf\.|hf)$/i, '').trim().toLowerCase();
            const cleanDealerCompanyName = dealerCompanyName ? dealerCompanyName.replace(/\s+(ehf\.|ehf|slf\.|slf|hf\.|hf)$/i, '').trim().toLowerCase() : '';
            
            console.log(`  Comparing owner "${cleanOwnerName}" (kt: ${ownerKt}) with dealer "${cleanDealerName}" / company "${cleanDealerCompanyName}" (kt: ${dealerKt})`);
            
            // Match by kennitala (most reliable)
            if (ownerKt && dealerKt && ownerKt === dealerKt) {
              console.log('    ✅ KENNITALA MATCH!');
              return true;
            }
            
            // Match by company_name (for cases like "2017 slf." matching company_name "2017 slf.")
            if (cleanDealerCompanyName && (
              cleanOwnerName.includes(cleanDealerCompanyName) || 
              cleanDealerCompanyName.includes(cleanOwnerName) ||
              cleanOwnerName === cleanDealerCompanyName
            )) {
              console.log('    ✅ COMPANY NAME MATCH!');
              return true;
            }
            
            // Match by display name
            if (cleanOwnerName.includes(cleanDealerName) || cleanDealerName.includes(cleanOwnerName)) {
              console.log('    ✅ DISPLAY NAME MATCH!');
              return true;
            }
            
            return false;
          });
          
          if (matchedDealership) {
            // Return the display name (for dropdown value)
            autoDealership = typeof matchedDealership === 'string' ? matchedDealership : matchedDealership.name;
            console.log(`✅ Auto-detected dealership: ${autoDealership} from owner: ${ownerName}`);
          } else {
            console.log(`❌ No dealership match found for: ${ownerName}`);
          }
        } else {
          console.log('⚠️ Dealerships array empty or not loaded:', dealerships);
        }
        
        setFormData(prev => ({
          ...prev,
          owner_name: ownerName,
          owner_kt: vehicleData.owner_kennitala || '',
          phone_number: vehicleData.phone_number || '',  // Phone from já.is Search API
          owner_address: vehicleData.owner_address || '',  // Store owner address for customer record
          owner_postal_code: vehicleData.owner_postal_code || extractedPostalCode || '',  // Store postal code for full address
          pickup_address: vehicleData.owner_address || '',  // Always fill address
          pickup_postal_code: extractedPostalCode,  // Always fill postal code
          dealership: autoDealership || prev.dealership, // Auto-fill MAIN dealership field
          shuttle_to_dealership: autoDealership || prev.shuttle_to_dealership, // Auto-fill shuttle destination
          car_color: vehicleData.color || '', // Vehicle color from lookup
          // Only fill price if checkbox is already checked
          pickup_delivery_price: prev.needs_pickup_delivery ? pickupDeliveryPrice : 0
        }));
        
        // Show toast if dealership was auto-detected
        if (autoDealership) {
          toast.success(`🚗 Bílasala greind sjálfkrafa: ${autoDealership}`, {duration: 3000});
        }
        
        // Show toast if phone was found from já.is
        if (vehicleData.phone_number && vehicleData.phone_source === 'ja.is_search_api') {
          toast.success(`📞 Símanúmer fundið á já.is: ${vehicleData.phone_number}`, {duration: 4000});
        }
        
        // Check for customer discount AND phone number when owner kennitala is set
        if (vehicleData.owner_kennitala) {
          // First check our local database for existing customer data
          const localCustomer = await checkLocalCustomer(vehicleData.owner_kennitala);
          
          if (localCustomer && localCustomer.found && localCustomer.phone) {
            // Update phone number from local customer data (overrides já.is if exists)
            setFormData(prev => ({
              ...prev,
              phone_number: localCustomer.phone
            }));
            toast.success(`📞 Símanúmer uppfært úr gagnagrunni: ${localCustomer.phone}`);
          }
          
          // Also check for discount
          await checkCustomerDiscount(vehicleData.owner_kennitala);
        }
        
        // Use the size from vehicle lookup if available, otherwise leave empty for auto-detection
        const detectedSize = vehicleData.size || '';
        
        console.log('🚗 Vehicle lookup - detected size:', detectedSize);
        console.log('📋 Available sizes in frontend:', sizes.map(s => ({id: s.id, name: s.name})));
        
        // Handle car make and model - set both together for immediate availability
        if (vehicleData.manufacturer) {
          const availableModels = models.filter(m => m.manufacturer === vehicleData.manufacturer);
          const exactMatch = availableModels.find(m => m.model === vehicleData.model);
          const caseInsensitiveMatch = availableModels.find(m => m.model.toLowerCase() === vehicleData.model?.toLowerCase());
          const finalModel = exactMatch ? exactMatch.model : (caseInsensitiveMatch ? caseInsensitiveMatch.model : vehicleData.model || '');
          
          console.log('Setting car make and model:', {
            manufacturer: vehicleData.manufacturer,
            model: finalModel
          });
          
          // Store in ref for immediate access when size is changed
          carInfoRef.current = { car_make: vehicleData.manufacturer, car_model: finalModel };
          
          // Set both make and model together so they're available when size is changed
          setFormData(prev => ({
            ...prev, 
            car_make: vehicleData.manufacturer, 
            car_model: finalModel
          }));
          
          // Auto-detect car size after model is set
          setTimeout(() => {
            if (detectedSize) {
              // Use size from vehicle lookup
              console.log('🎯 Setting size from vehicle lookup:', detectedSize);
              setFormData(prev => {
                console.log('🔄 Updating formData.size to:', detectedSize);
                return {...prev, size: detectedSize};
              });
              
              // Show toast message with delay to ensure it's visible
              setTimeout(() => {
                const sizeObject = sizes.find(s => s.id === detectedSize);
                const sizeName = sizeObject ? sizeObject.name : detectedSize;
                toast.success(`🎯 Stærð greind sjálfkrafa: ${sizeName}`);
              }, 100);
            } else {
              // Auto-detect from manufacturer/model
              autoDetectCarSize(vehicleData.manufacturer, finalModel);
            }
          }, 150);
        }
        
        toast.success(`✅ Bílupplýsingar sóttar: ${vehicleData.manufacturer} ${vehicleData.model}`);
        
        // Show toast for postal code if found
        if (extractedPostalCode) {
          const zone = pickupZones.find(z => z.postal_code === extractedPostalCode);
          if (zone && zone.price > 0) {
            toast.info(`📍 Póstnúmer ${extractedPostalCode} - ${zone.area_name}: Sækja/skila ${zone.price.toLocaleString()} ISK`);
          } else if (zone && zone.price === 0) {
            toast.success(`📍 Póstnúmer ${extractedPostalCode} - ${zone.area_name}: Ekkert sækja/skila gjald`);
          }
        }

        
        // Check if vehicle is deregistered or has special status
        if (vehicleData.vehicle_status) {
          const status = vehicleData.vehicle_status.toLowerCase();
          if (status.includes('afskráð')) {
            toast.warning('Athugið: Þetta ökutæki er afskráð');
          } else if (status.includes('stolinn')) {
            toast.error('Athugið: Þetta ökutæki er skráð stolið');
          } else if (status.includes('eytt')) {
            toast.warning('Athugið: Þetta ökutæki er eytt úr skrá');
          } else if (status !== 'virkt' && status !== '') {
            toast.info(`Athugið: Staða ökutækis - ${vehicleData.vehicle_status}`);
          }
        }
      } else {
        // Vehicle not found - handle as error
        console.log('Vehicle not found:', vehicleData.error || 'No data found');
        
        // Mark lookup as failed
        setVehicleLookupSuccess(false);
        setVehicleInfo(null);
        
        // Show appropriate error message
        if (vehicleData.error) {
          toast.warning(`${vehicleData.error} - færðu inn upplýsingar manually`);
        } else {
          toast.warning('Bíll fannst ekki í skrá - færðu inn upplýsingar manually');
        }
      }
      
    } catch (error) {
      console.error('Vehicle lookup error:', error);
      
      // Mark lookup as failed
      setVehicleLookupSuccess(false);
      setVehicleInfo(null);
      
      // Handle different types of errors with user-friendly messages
      if (error.response?.status === 404) {
        toast.error('Ökutæki fannst ekki í skrá - færðu inn upplýsingar manually');
      } else if (error.response?.status === 502) {
        toast.warning('Ekki tókst að ná sambandi við Samgöngustofu - færðu inn upplýsingar manually');
      } else if (error.response?.status === 429) {
        toast.error(error.response?.data?.detail || 'Þú hefur náð daglegu marki uppflettinga - reyndu aftur á morgun');
      } else if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Þú þarft að vera innskráð(ur) til að fletta upp bíl - skráðu þig inn aftur');
      } else if (error.response?.status === 422) {
        toast.warning('Ógilt plötunúmer - færðu inn manually');
      } else if (error.response?.data?.error) {
        // Use the error message from the backend if available
        toast.warning(`${error.response.data.error} - færðu inn upplýsingar manually`);
      } else {
        toast.error('Villa við að sækja bílupplýsingar - færðu inn manually');
      }
    } finally {
      setLoading(false);
    }
  };

  // Debounced vehicle lookup when plate changes
  useEffect(() => {
    // Reset lookup attempted flag when plate changes
    setLookupAttempted(false);
    
    const timer = setTimeout(() => {
      if (formData.plate && formData.plate.length >= 5) {
        lookupVehicleInfo(formData.plate);
      }
    }, 500); // Wait 500ms (0.5 seconds) after user stops typing - faster response

    return () => clearTimeout(timer);
  }, [formData.plate]);

  const filteredModels = useMemo(() => {
    // Filter models based on selected manufacturer
    if (formData.car_make) {
      const filtered = models.filter(model => model.manufacturer === formData.car_make);
      console.log('Models filtered for', formData.car_make, ':', filtered.map(m => m.model));
      return filtered;
    }
    return [];
  }, [formData.car_make, models]);

  // Separate useEffect to handle model updates after filtering
  useEffect(() => {
    if (formData.car_make && formData.car_model && filteredModels.length > 0) {
      const modelExists = filteredModels.some(m => m.model === formData.car_model);
      if (!modelExists) {
        // Try to find case-insensitive match
        const caseInsensitiveMatch = filteredModels.find(m => 
          m.model.toLowerCase() === formData.car_model.toLowerCase()
        );
        if (caseInsensitiveMatch) {
          console.log('Updating model to case-insensitive match:', caseInsensitiveMatch.model);
          setFormData(prev => ({...prev, car_model: caseInsensitiveMatch.model}));
        } else {
          console.log('Model not found in filtered list, clearing:', formData.car_model);
          setFormData(prev => ({...prev, car_model: ''}));
        }
      }
    }
  }, [formData.car_make, formData.car_model, filteredModels]);

  const loadDropdownData = async () => {
    try {
      const [manRes, dealRes, settingsRes, modelsRes, loanCarsRes, productServicesRes, blockedTimesRes] = await Promise.all([
        axios.get(`${API}/car-manufacturers`),
        axios.get(`${API}/dealerships?sort_by=popularity&include_details=true`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/car-models`),
        axios.get(`${API}/loan-cars`),
        axios.get(`${API}/product-services`),
        axios.get(`${API}/blocked-times`)
      ]);
      
      setManufacturers(manRes.data || []);
      setDealerships(dealRes.data || []); // Now includes company_name and kennitala for auto-detection
      setModels(modelsRes.data || []);
      setLoanCars(loanCarsRes.data || []);
      setProductServices(productServicesRes.data || []);
      setBlockedTimes(blockedTimesRes.data || []);
      
      if (settingsRes.data) {
        setServices(settingsRes.data.services || []);
        setSizes((settingsRes.data.sizes || []).sort((a, b) => (a.order || 0) - (b.order || 0)));
        setCategories(settingsRes.data.categories || [
          {id: "general", name: "Almenn þrif", icon: "🧼", order: 0},
          {id: "special", name: "Sérþjónusta", icon: "⭐", order: 1},
          {id: "keramix", name: "Keramik vörn", icon: "🛡️", order: 2}
        ]);
        setPickupZones(settingsRes.data.pickup_delivery_zones || []);
        setPrices(settingsRes.data.prices || {});
        setOpeningHoursSettings(settingsRes.data.opening_hours || null);
      }
    } catch (error) {
      toast.error('Tókst ekki að hlaða gögnum');
    }
  };


  
  const isWithinOpeningHours = (dateStr, timeStr) => {
    if (!openingHoursSettings || !openingHoursSettings.enabled) {
      return { within: true };
    }
    
    const date = moment(dateStr, 'YYYY-MM-DD');
    const weekday = date.weekday();
    
    const dayConfig = openingHoursSettings.hours?.[weekday];
    
    if (!dayConfig) {
      return { within: true };
    }
    
    if (dayConfig.closed) {
      const weekdayNames = ['Mánudagur', 'Þriðjudagur', 'Miðvikudagur', 'Fimmtudagur', 'Föstudagur', 'Laugardagur', 'Sunnudagur'];
      return {
        within: false,
        message: `Lokað á ${weekdayNames[weekday]}`
      };
    }
    
    if (timeStr < dayConfig.open || timeStr > dayConfig.close) {
      return {
        within: false,
        message: `Utan opnunartíma. Opið ${dayConfig.open}-${dayConfig.close}`,
        openTime: dayConfig.open,
        closeTime: dayConfig.close
      };
    }
    
    return { within: true };
  };

  
  const isTimeBlocked = (dateStr, timeStr) => {
    // Check if a specific date/time is blocked
    const date = moment(dateStr, 'YYYY-MM-DD');
    const weekday = date.weekday(); // 0=Monday in moment.js
    
    for (const bt of blockedTimes) {
      // Check full day blocks
      if (bt.type === 'full_day') {
        if (bt.date === dateStr) return { blocked: true, reason: bt.reason, isHoliday: bt.is_holiday || false };
        if (bt.weekday === weekday && !bt.date) return { blocked: true, reason: bt.reason, isHoliday: bt.is_holiday || false };
      }
      
      // Check time period blocks
      if (bt.type === 'time_period' && bt.start_time && bt.end_time) {
        if (timeStr >= bt.start_time && timeStr <= bt.end_time) {
          if (bt.date === dateStr) return { blocked: true, reason: bt.reason, timeRange: `${bt.start_time}-${bt.end_time}`, isHoliday: bt.is_holiday || false };
          if (bt.weekday === weekday && !bt.date) return { blocked: true, reason: bt.reason, timeRange: `${bt.start_time}-${bt.end_time}`, isHoliday: bt.is_holiday || false };
        }
      }
    }
    
    return { blocked: false };
  };

  const calculateTotal = () => {
    // If manual total is enabled, use that directly
    if (manualTotalEnabled && manualTotal !== undefined) {
      setFormData(prev => ({ ...prev, total: manualTotal }));
      return;
    }
    
    if (!formData.size || (!formData.services.length && Object.keys(productQuantities).length === 0) || !prices) return;

    // Get customer's category discounts if customer is selected
    const customerCategoryDiscounts = selectedCustomer?.category_discounts || {};
    const generalDiscount = formData.discount_percent || 0;
    
    // RULE: Custom pricing (sérverð) ONLY applies when the payer IS the dealership
    // If dealership is set but payer is someone else, apply discount to EVERYTHING (no custom pricing)
    const payerIsDealership = !formData.dealership || 
                              formData.payer_name === formData.dealership ||
                              selectedCustomer?.name === formData.dealership;
    
    // Check which services are included (free) based on selected services
    const includedServiceIds = new Set();
    formData.services.forEach(serviceId => {
      const service = services.find(s => s.id === serviceId);
      if (service && service.includes && Array.isArray(service.includes)) {
        service.includes.forEach(incId => {
          // Only mark as included if the included service is also selected
          if (formData.services.includes(incId)) {
            includedServiceIds.add(incId);
          }
        });
      }
    });
    
    let subtotal = 0;
    let totalDiscount = 0;
    
    // Calculate regular services
    formData.services.forEach(serviceId => {
      // Check if this service is included (free) in another selected service
      const isIncluded = includedServiceIds.has(serviceId);
      
      // Get service price - check for custom pricing ONLY if payer is dealership
      let servicePrice = 0;
      let hasCustomPrice = false;
      
      // Check for customer's custom pricing (sérverð) - ONLY if payer IS the dealership
      if (payerIsDealership && selectedCustomer?.custom_pricing_enabled && selectedCustomer?.custom_prices) {
        const customPrice = selectedCustomer.custom_prices?.[serviceId]?.[formData.size];
        if (customPrice !== undefined) {
          servicePrice = customPrice;
          hasCustomPrice = true;
        }
      }
      
      // If no custom price, check manual custom prices
      if (!hasCustomPrice && customPrices[serviceId] !== undefined) {
        servicePrice = customPrices[serviceId];
      }
      
      // If still no price, use default from price matrix
      if (servicePrice === 0 && prices[serviceId] && prices[serviceId][formData.size]) {
        servicePrice = prices[serviceId][formData.size];
      }
      
      // If service is included, set price to 0
      if (isIncluded) {
        servicePrice = 0;
        hasCustomPrice = false;
      }
      
      if (servicePrice > 0) {
        subtotal += servicePrice;
        
        // RULE: No discount on custom-priced items (sérverð)
        // Discount ONLY applies to items WITHOUT custom pricing
        if (!hasCustomPrice) {
          // Find service to get its category
          const service = services.find(s => s.id === serviceId);
          const serviceCategory = service?.category || 'general';
          
          // Determine which discount to apply for this service
          let applicableDiscount = generalDiscount;
          if (customerCategoryDiscounts[serviceCategory] !== undefined && customerCategoryDiscounts[serviceCategory] > 0) {
            // Use category-specific discount if available
            applicableDiscount = customerCategoryDiscounts[serviceCategory];
          }
          
          // Calculate discount for this service
          const serviceDiscount = servicePrice * (applicableDiscount / 100);
          totalDiscount += serviceDiscount;
        }
      }
    });
    
    // Calculate product services (rúðupiss, rammar, etc.)
    Object.keys(productQuantities).forEach(productId => {
      const quantity = productQuantities[productId] || 0;
      if (quantity > 0) {
        const productService = productServices.find(p => p.id === productId);
        if (productService) {
          const productSubtotal = quantity * productService.price_per_unit;
          subtotal += productSubtotal;
          
          // Apply discount if allowed
          if (productService.allow_discount && generalDiscount > 0) {
            // Cap discount at max_discount
            const effectiveDiscount = Math.min(generalDiscount, productService.max_discount || 100);
            const productDiscount = productSubtotal * (effectiveDiscount / 100);
            totalDiscount += productDiscount;
          }
        }
      }
    });
    
    // Add pickup/delivery price if applicable (only if checkbox is checked)
    const pickupDeliveryPrice = formData.needs_pickup_delivery ? (formData.pickup_delivery_price || 0) : 0;
    
    // Final total = subtotal - total discount + pickup/delivery
    const finalTotal = Math.round(subtotal - totalDiscount + pickupDeliveryPrice);
    
    setFormData(prev => ({ ...prev, total: finalTotal }));
  };

  const handleCarMakeChange = async (make) => {
    setFormData(prev => ({...prev, car_make: make, car_model: '', size: ''}));
  };

  const handleCarModelChange = async (model) => {
    setFormData(prev => ({...prev, car_model: model, size: ''}));
    
    if (formData.car_make && model) {
      await autoDetectCarSize(formData.car_make, model);
    }
  };

  const autoDetectCarSize = async (manufacturer, model) => {
    if (!manufacturer || !model) {
      console.log('⚠️ Cannot auto-detect size: missing manufacturer or model');
      return;
    }
    
    try {
      console.log(`🔍 Auto-detecting car size for: ${manufacturer} ${model}`);
      const response = await axios.get(`${API}/car-size?manufacturer=${manufacturer}&model=${model}`);
      
      if (response.data && response.data.size_id) {
        console.log('✅ Auto-detected car size:', response.data);
        console.log('📋 Setting size_id to formData:', response.data.size_id);
        
        // Verify the size_id exists in our sizes array
        const sizeExists = sizes.find(s => s.id === response.data.size_id);
        if (!sizeExists) {
          console.error('❌ Size ID not found in sizes array:', response.data.size_id);
          console.log('Available sizes:', sizes.map(s => s.id));
          return;
        }
        
        setFormData(prev => {
          console.log('🔄 Updating formData with size:', response.data.size_id);
          return {...prev, size: response.data.size_id};
        });
        
        // Show toast message with slight delay to ensure visibility
        setTimeout(() => {
          toast.success(`🎯 Stærð greind sjálfkrafa: ${response.data.size}`);
        }, 100);
      } else {
        console.log('⚠️ No size data returned from API');
      }
    } catch (error) {
      console.error('❌ Failed to detect car size:', error);
    }
  };

  // Enhanced function to check our local database first
  const checkLocalCustomer = async (kennitala) => {
    if (!kennitala || kennitala.replace('-', '').length !== 10) return null;
    
    try {
      const response = await axios.get(`${API}/customers`);
      // API returns { customers: [...], total: ... } format
      const customers = response.data.customers || response.data || [];
      const customer = customers.find(c => 
        (c.kennitala || '').replace('-', '') === kennitala.replace('-', '')
      );
      
      if (customer) {
        console.log('Local customer found:', customer);
        console.log('📞 Customer phone from database:', customer.phone);
        
        // Check if this is a car rental customer
        if (customer.is_car_rental) {
          setIsCarRentalCustomer(true);
          console.log('🚗 Car rental customer detected:', customer.name);
        } else {
          setIsCarRentalCustomer(false);
        }
        
        return {
          found: true,
          full_name: customer.name,
          company_name: customer.company,
          address: customer.address,
          phone: customer.phone,
          is_company: !!customer.company,
          source: "local_database",
          customer_id: customer.id,
          discount_percentage: customer.discount_percentage || 0,
          is_car_rental: customer.is_car_rental || false
        };
      }
      
      // Reset car rental flag if customer not found
      setIsCarRentalCustomer(false);
      return null;
    } catch (error) {
      console.error('Error checking local customer:', error);
      return null;
    }
  };

  // Load customers for payer selection dropdown
  const loadPayerCustomers = async () => {
    if (payerCustomers.length > 0) return; // Already loaded
    setLoadingPayerCustomers(true);
    try {
      // Use reports/customers endpoint which extracts customers from jobs
      const response = await axios.get(`${API}/reports/customers`);
      const customers = response.data || [];
      setPayerCustomers(customers);
    } catch (error) {
      console.error('Error loading payer customers:', error);
      toast.error('Villa við að sækja viðskiptavini');
    } finally {
      setLoadingPayerCustomers(false);
    }
  };

  // Filter and sort customers for payer dropdown
  const filteredPayerCustomers = useMemo(() => {
    let filtered = payerCustomers;
    
    // Filter by search term
    if (payerCustomerSearch) {
      const search = payerCustomerSearch.toLowerCase();
      filtered = filtered.filter(c => 
        (c.name && c.name.toLowerCase().includes(search)) ||
        (c.kennitala && c.kennitala.includes(search)) ||
        (c.company && c.company.toLowerCase().includes(search)) ||
        (c.phone && c.phone.includes(search))
      );
    }
    
    // Sort by selected option
    if (payerCustomerSortBy === 'popularity') {
      filtered = [...filtered].sort((a, b) => (b.job_count || 0) - (a.job_count || 0));
    } else {
      filtered = [...filtered].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'is'));
    }
    
    return filtered.slice(0, 50); // Limit to 50 results for performance
  }, [payerCustomers, payerCustomerSearch, payerCustomerSortBy]);

  // Select a customer as payer from dropdown
  const selectPayerFromCustomer = (customer) => {
    const payerName = customer.company || customer.name || '';
    setFormData(prev => {
      const updates = {
        ...prev,
        payer_kt: customer.kennitala || '',
        payer_name: payerName
      };
      if (updateOwnerWithPayer) {
        updates.owner_name = payerName;
        updates.owner_kt = customer.kennitala || '';
      }
      return updates;
    });
    setPayerCustomerSearch('');
    setShowPayerCustomerDropdown(false);
    toast.success(`✅ Greiðandi valinn: ${payerName}`);
    if (updateOwnerWithPayer) toast.info('👤 Eigandi uppfærður líka');
    
    // Store customer for discount calculation
    setSelectedCustomer(customer);
    if (customer.discount_percentage > 0) {
      setCustomerDiscount(customer.discount_percentage);
      toast.info(`💰 Afsláttur: ${customer.discount_percentage}%`);
    }
  };

  // Payer kennitala lookup function - enhanced with local check  
  const handlePayerKennitalaChange = async (kennitala) => {
    setFormData(prev => ({...prev, payer_kt: kennitala}));
    
    // Auto-fill payer if we have a complete 10-digit kennitala
    if (kennitala.replace('-', '').length === 10) {
      setPayerKennitalaLoading(true);
      
      try {
        // STEP 1: Check our local database first
        const localCustomer = await checkLocalCustomer(kennitala);
        
        if (localCustomer && localCustomer.found) {
          // Found in local database
          let payerName = localCustomer.full_name;
          
          // If it's a company, use company name
          if (localCustomer.is_company && localCustomer.company_name) {
            payerName = localCustomer.company_name;
          }
          
          setFormData(prev => {
            const updates = { ...prev, payer_kt: kennitala, payer_name: payerName };
            if (updateOwnerWithPayer) {
              updates.owner_name = payerName;
              updates.owner_kt = kennitala;
            }
            return updates;
          });
          
          toast.success(`💾 Greiðandi fundinn í gagnagrunni: ${payerName}`);
          if (updateOwnerWithPayer) toast.info('👤 Eigandi uppfærður líka');
          return;
        }
        
        // STEP 2: If not found locally, check external API
        const response = await axios.get(`${API}/person/lookup/${kennitala}`);
        if (response.data.found) {
          const personData = response.data;
          
          let payerName = personData.full_name;
          
          // If it's a company, use company name
          if (personData.is_company && personData.company_name) {
            payerName = personData.company_name;
          }
          
          setFormData(prev => {
            const updates = { ...prev, payer_kt: kennitala, payer_name: payerName };
            if (updateOwnerWithPayer) {
              updates.owner_name = payerName;
              updates.owner_kt = kennitala;
            }
            return updates;
          });
          
          toast.success(`🌐 Greiðandi fundinn í Þjóðskrá: ${payerName}`);
          if (updateOwnerWithPayer) toast.info('👤 Eigandi uppfærður líka');
        } else {
          // If not found, keep the kennitala but clear name for manual entry
          setFormData(prev => ({...prev, payer_kt: kennitala, payer_name: ''}));
          toast.warning('Greiðandi fannst ekki - færðu inn nafn handvirkt');
        }
      } catch (error) {
        console.error('Payer lookup failed:', error);
        setFormData(prev => ({...prev, payer_kt: kennitala}));
        toast.error('Villa við að sækja upplýsingar um greiðanda');
      } finally {
        setPayerKennitalaLoading(false);
      }
    }
  };

  // Handle manual owner kennitala changes (when not from vehicle lookup) - enhanced with local check
  const handleOwnerKennitalaChange = async (kennitala) => {
    setFormData(prev => ({...prev, owner_kt: kennitala}));
    
    // Check for local customer data and discount when kennitala is complete
    if (kennitala.replace('-', '').length === 10) {
      try {
        // First check our local database
        const localCustomer = await checkLocalCustomer(kennitala);
        
        if (localCustomer && localCustomer.found) {
          // Found in local database - update form with all available data
          setFormData(prev => ({
            ...prev,
            owner_kt: kennitala,
            owner_name: localCustomer.full_name || prev.owner_name,
            phone_number: localCustomer.phone || prev.phone_number  // Auto-fill phone!
          }));
          
          // Handle customer discount
          if (localCustomer.discount_percentage && localCustomer.discount_percentage > 0) {
            setCustomerDiscount(localCustomer.discount_percentage);
            setDiscountLocked(true);
            setFormData(prev => ({...prev, discount_percent: localCustomer.discount_percentage}));
            toast.success(`💾 Viðskiptavinur fundinn með ${localCustomer.discount_percentage}% afslætti og síma: ${localCustomer.phone || 'ekkert símanúmer'}`);
          } else {
            setCustomerDiscount(null);
            setDiscountLocked(false);
            toast.success(`💾 Viðskiptavinur fundinn${localCustomer.phone ? ` með síma: ${localCustomer.phone}` : ''}`);
          }
          
          return;
        }
        
        // If not found locally, just check for discount from API lookup
        await checkCustomerDiscount(kennitala);
        
      } catch (error) {
        console.error('Error in enhanced owner kennitala lookup:', error);
        await checkCustomerDiscount(kennitala);
      }
    } else {
      // Reset discount if kennitala is incomplete
      setCustomerDiscount(null);
      setDiscountLocked(false);
      setFormData(prev => ({...prev, discount_percent: 0}));
    }
  };
  const checkCustomerDiscount = async (kennitala) => {
    try {
      const response = await axios.get(`${API}/customers`);
      const data = response.data || {};
      const customers = Array.isArray(data) ? data : (data.customers || []);
      const customer = customers.find(c => 
        (c.kennitala || '').replace('-', '') === kennitala.replace('-', '')
      );
      
      if (customer) {
        // Store the full customer object for category discounts
        setSelectedCustomer(customer);
        
        // Check for custom pricing
        if (customer.custom_pricing_enabled && customer.custom_prices) {
          const customServiceCount = Object.keys(customer.custom_prices).length;
          toast.info(`💰 Sérverðskrá virk fyrir ${customer.name} (${customServiceCount} þjónustur)`, {duration: 4000});
        }
        
        // Check if customer has any discounts (general or category-based)
        const hasGeneralDiscount = customer.discount_percentage && customer.discount_percentage > 0;
        const hasCategoryDiscounts = customer.category_discounts && Object.keys(customer.category_discounts).length > 0;
        
        if (hasGeneralDiscount || hasCategoryDiscounts) {
          setCustomerDiscount(customer.discount_percentage || 0);
          setDiscountLocked(true);
          setFormData(prev => ({...prev, discount_percent: customer.discount_percentage || 0}));
          
          // Show appropriate toast message
          if (hasCategoryDiscounts) {
            const categoryCount = Object.keys(customer.category_discounts).length;
            toast.info(`💰 Afslættir skráðir fyrir ${customer.name}: Almennur ${customer.discount_percentage || 0}% + ${categoryCount} flokkur`);
          } else {
            toast.info(`Fastur afsláttur: ${customer.discount_percentage}% fyrir ${customer.name}`);
          }
          
          return customer.discount_percentage || 0;
        } else {
          setCustomerDiscount(null);
          setDiscountLocked(false);
          return 0;
        }
      } else {
        setSelectedCustomer(null);
        setCustomerDiscount(null);
        setDiscountLocked(false);
        return 0;
      }
    } catch (error) {
      console.error('Failed to check customer discount:', error);
      setSelectedCustomer(null);
      return 0;
    }
  };

  const createJobItems = () => {
    const items = [];

    // Finna innifaldar þjónustur út frá "includes" stillingum (eins og í verðútreikningi)
    const includedServiceIds = new Set();
    formData.services.forEach(serviceId => {
      const service = services.find(s => s.id === serviceId);
      if (service && Array.isArray(service.includes)) {
        service.includes.forEach(incId => {
          if (formData.services.includes(incId)) {
            includedServiceIds.add(incId);
          }
        });
      }
    });
    
    // Add regular services
    formData.services.forEach(serviceId => {
      const service = services.find(s => s.id === serviceId);

      // Er þessi þjónusta innifalin í öðrum pakka?
      const isIncluded = includedServiceIds.has(serviceId);
      
      // RULE: Custom pricing (sérverð) ONLY applies when the payer IS the dealership
      // If dealership is set but payer is someone else (their customer), NO custom pricing - just the dealership discount on everything
      let price = 0;
      let hasCustomPrice = false;
      
      // Check if payer is the same as dealership (or no dealership selected)
      const payerIsDealership = !formData.dealership || 
                                formData.payer_name === formData.dealership ||
                                selectedCustomer?.name === formData.dealership;
      
      // Only use custom pricing if payer IS the dealership
      const shouldUseCustomPricing = payerIsDealership && 
                                     selectedCustomer?.custom_pricing_enabled && 
                                     selectedCustomer?.custom_prices && 
                                     formData.size;
      
      if (!isIncluded && shouldUseCustomPricing) {
        const customPrice = selectedCustomer.custom_prices?.[serviceId]?.[formData.size];
        if (customPrice !== undefined) {
          price = customPrice;
          hasCustomPrice = true;
          console.log(`💰 Using custom price for ${serviceId}: ${price} ISK (payer is dealership)`);
        }
      }
      
      // If no custom pricing, check if user manually set a custom price for this service
      if (!isIncluded && price === 0 && customPrices[serviceId] !== undefined) {
        price = customPrices[serviceId];
      }
      
      // If still no price, use default from price matrix
      if (!isIncluded && price === 0 && formData.size) {
        const defaultPrice = prices[serviceId] && prices[serviceId][formData.size] ? prices[serviceId][formData.size] : 0;
        price = defaultPrice;
      }

      // Ef þjónustan er innifalin í öðrum pakka → halda verði 0 kr
      if (isIncluded) {
        price = 0;
        hasCustomPrice = false;
      }
      
      if (service) {
        // Use custom name if available (for "Vinna" service)
        const serviceName = customServiceNames[serviceId] || service.name;
        
        items.push({
          service: serviceName,
          service_id: serviceId,  // Add service_id for product consumption tracking
          description: service.description || serviceName,
          price: price,
          size: formData.size,
          quantity: 1,
          item_type: 'service',
          is_included: isIncluded,
          has_custom_price: hasCustomPrice  // Mark if this item has custom pricing (no discount should apply)
        });
      }
    });
    
    // Add product services (rúðupiss, rammar, etc.)
    Object.keys(productQuantities).forEach(productId => {
      const quantity = productQuantities[productId] || 0;
      if (quantity > 0) {
        const productService = productServices.find(p => p.id === productId);
        if (productService) {
          const productSubtotal = quantity * productService.price_per_unit;
          
          // Calculate discount if allowed
          let productDiscount = 0;
          let effectiveDiscountPercent = 0;
          if (productService.allow_discount && formData.discount_percent > 0) {
            effectiveDiscountPercent = Math.min(formData.discount_percent, productService.max_discount || 100);
            productDiscount = productSubtotal * (effectiveDiscountPercent / 100);
          }
          
          const productTotal = productSubtotal - productDiscount;
          
          items.push({
            service: productService.name,
            description: `${quantity} ${productService.unit_type === 'liters' ? 'lítrar' : productService.unit_type === 'sets' ? 'sett' : 'stk'} á ${productService.price_per_unit} ISK${effectiveDiscountPercent > 0 ? ` (${effectiveDiscountPercent}% afsláttur)` : ''}`,
            price: productTotal,
            size: 'N/A',
            quantity: quantity,
            item_type: 'product',
            product_service_id: productId,
            inventory_item_id: productService.inventory_item_id,
            unit_type: productService.unit_type,
            payday_product_id: productService.payday_product_id || ''
          });
        }
      }
    });
    
    // Add pickup/delivery as a separate line item if applicable
    if (formData.needs_pickup_delivery && formData.pickup_delivery_price > 0) {
      const zone = pickupZones.find(z => z.postal_code === formData.pickup_postal_code);
      const areaName = zone ? zone.area_name : formData.pickup_postal_code;
      
      items.push({
        service: `Sækja/skila - ${areaName}`,
        description: `Heimilt sótt og skilað í póstnúmeri ${formData.pickup_postal_code}`,
        price: formData.pickup_delivery_price,
        size: 'N/A',
        quantity: 1,
        item_type: 'service',
        allow_discount: formData.pickup_delivery_discount || false,
        is_pickup_delivery: true
      });
    }
    
    return items;
  };


  // QR Code Photo Upload Functions
  const createQrSession = async () => {
    try {
      const response = await axios.post(`${API}/photo-upload/create-session`);
      setQrSessionId(response.data.session_id);
      setQrUploadUrl(response.data.upload_url);
      setShowQrDialog(true);
      
      // Start polling for uploaded images
      startPollingForImages(response.data.session_id);
      
      toast.success('QR kóði tilbúinn! Skannaðu með símanum');
    } catch (error) {
      console.error('Failed to create QR session:', error);
      toast.error('Villa við að búa til QR kóða');
    }
  };

  const startPollingForImages = (sessionId) => {
    // Clear any existing interval
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }

    // Poll every 2 seconds
    const interval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/photo-upload/session/${sessionId}`);
        
        if (response.data.completed && response.data.images.length > 0) {
          // Append new images to existing ones
          const existingImages = formData.images || [];
          const newImages = response.data.images;
          const combinedImages = [...existingImages, ...newImages].slice(0, 5); // Max 5 images
          
          setFormData({...formData, images: combinedImages});
          toast.success(`✅ ${newImages.length} mynd${newImages.length > 1 ? 'ir' : ''} móttekin frá síma`);
          
          // Close dialog and stop polling
          setShowQrDialog(false);
          clearInterval(interval);
          setPollingInterval(null);
        }
      } catch (error) {
        if (error.response?.status === 410) {
          // Session expired
          toast.error('QR hlekkur útrunninn (15 mínútur)');
          setShowQrDialog(false);
          clearInterval(interval);
          setPollingInterval(null);
        }
      }
    }, 2000);

    setPollingInterval(interval);
  };

  const closeQrDialog = () => {
    setShowQrDialog(false);
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  };

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  // Detect device type
  const isMobileDevice = () => {
    return window.innerWidth < 768; // Less than tablet size
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Regular job registration - removed clip card logic
    // Validation: 1B and 2A + 3B + 4A
    const ktValid = (kt) => kt && kt.replace('-', '').trim().length === 10 && /^\d{10}$/.test(kt.replace('-', '').trim());

    if (!formData.plate || (!formData.services.length && Object.values(productQuantities).every(q => q === 0)) || !formData.size || !formData.owner_name || !ktValid(formData.owner_kt)) {
      toast.error('Númeraplata, eigandi (nafn og 10 stafa kennitala), þjónusta eða vörur og bílastærð eru nauðsynleg');
      return;
    }

    // Dealership is required
    if (!formData.dealership) {
      toast.error('Vinsamlegast veldu bílasölu eða "Tengist ekki bílasölu"');
      return;
    }

    // Payer block (2A): if payer different from owner
    if (!formData.payer_is_owner) {
      if (!formData.payer_name || !ktValid(formData.payer_kt)) {
        toast.error('Greiðandi þarf að hafa nafn og 10 stafa kennitölu');
        return;
      }
    }

    // Auto-format kennitala ######-#### on blur/save
    const fmtKt = (kt) => {
      const c = (kt || '').replace(/[^0-9]/g, '');
      return c.length === 10 ? `${c.slice(0,6)}-${c.slice(6)}` : kt;
    };
    formData.owner_kt = fmtKt(formData.owner_kt);
    if (!formData.payer_is_owner) {
      formData.payer_kt = fmtKt(formData.payer_kt);

    }
    
    // Check if time is blocked (warning only, allow booking)
    if (formData.scheduled_time) {
      const blockCheck = isTimeBlocked(formData.scheduled_date, formData.scheduled_time);
      if (blockCheck.blocked) {
        const timeInfo = blockCheck.timeRange ? ` (${blockCheck.timeRange})` : '';
        const confirmed = confirm(`⚠️ Athugið: Þessi tími er lokaður${timeInfo}\n\nÁstæða: ${blockCheck.reason}\n\nViltu samt halda áfram með bókun?`);
        if (!confirmed) {
          return;
        }
      }
      
      // Check opening hours (warning only, allow booking)
      const hoursCheck = isWithinOpeningHours(formData.scheduled_date, formData.scheduled_time);
      if (!hoursCheck.within) {
        const confirmed = confirm(`⚠️ Athugið: ${hoursCheck.message}\n\nViltu samt halda áfram með bókun?`);
        if (!confirmed) {
          return;
        }
      }
    }

    setLoading(true);
    try {
      const jobData = {
        ...formData,
        dealership: formData.dealership === 'Tengist ekki bílasölu' ? '' : formData.dealership,
        items: createJobItems(),
        total: formData.total,
        manual_total_override: manualTotalEnabled  // Flag to preserve manual total
      };
      
      const response = await axios.post(`${API}/jobs`, jobData);
      
      // Check if this is a clipcard job (only "Klippikort" service)
      const isClipcardJob = jobData.items.length === 1 && 
                           jobData.items[0].service.toLowerCase().includes('klippikort');
      
      if (isClipcardJob) {
        toast.success('🎫 Klippikort verk búið til - opna greiðslu');
        
        // TODO: Open payment dialog here
        // For now, just show message
        setTimeout(() => {
          toast.info('💳 Farðu í "Greiðslur" til að greiða og stofna klippikort');
        }, 1000);
      } else {
        toast.success('Verk búið til');
        // Jump to the Verk view so the user can immediately see the created job
        // (requested behaviour). Uses URL hash sync effect in AuthenticatedApp
        // which listens for `hashchange` and updates currentView accordingly.
        try {
          window.location.hash = 'jobs';
        } catch (_err) {
          // ignore
        }
      }
      
      // Reset form and states
      setFormData({
        plate: '',
        owner_name: '',
        owner_kt: '',
        phone_number: '',
        car_make: '',
        car_model: '',
        dealership: '',
        services: [],
        size: '',
        scheduled_date: new Date().toISOString().split('T')[0],
        scheduled_time: '',
        notes: '',
        key_location: '', // Reset key location
        car_color: '', // Reset car color
        images: [], // Reset images
        ready_by_time: '',
        needs_loan_car: false,
        loan_car_id: null,
        needs_pickup_delivery: false,
        pickup_address: '',
        pickup_postal_code: '',
        pickup_delivery_price: 0,
        pickup_delivery_note: '',
        shuttle_to_dealership: '',
        total: 0,
        payer_is_owner: true,
        payer_name: '',
        payer_kt: '',
        discount_percent: 0,
        send_completion_sms: false,
        send_payment_sms: false,
        rental_type: '' // Reset rental type
      });
      
      // Reset progressive form states
      setVehicleLookupSuccess(false);
      setVehicleInfo(null);
      setCustomerDiscount(null);
      setDiscountLocked(false);
      setLookupAttempted(false);
      setIsCarRentalCustomer(false); // Reset car rental flag
      setCustomPrices({});
      setManualTotalEnabled(false);
      setManualTotal(0);
      setProductQuantities({}); // Reset product quantities
      setShowProducts(false); // Hide products section
      
      // If clipcard job, navigate to payments
      if (isClipcardJob) {
        setTimeout(() => {
          // Navigate to payments view
          if (window.location.pathname.includes('app')) {
            // In main app, we need to trigger navigation
            // This will be handled by parent component
          }
        }, 1500);
      }
    } catch (error) {
      toast.error('Tókst ekki að búa til verk');
    } finally {
      setLoading(false);
    }
  };

  const filteredManufacturers = useMemo(() => manufacturerSearch
    ? manufacturers.filter(m => m.toLowerCase().includes(manufacturerSearch.toLowerCase()))
    : manufacturers, [manufacturers, manufacturerSearch]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 p-3 sm:p-6">
      {/* Modern Header */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
          {t('registration', language)}
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">Skráðu nýtt verk í kerfið</p>
      </div>

      {/* Main Form Card */}
      <Card className="shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-shadow duration-300 border-0 bg-white dark:bg-slate-800">
        <CardContent className="p-4 sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* Step 1: License Plate - Hero Input */}
            <div className="pb-6 border-b border-slate-100 dark:border-slate-700">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* License Plate Input - Special Styled */}
                <div className="lg:col-span-4">
                  <Label htmlFor="plate" className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2 block">
                    {t('license_plate', language)} *
                  </Label>
                  <div className="relative">
                    {/* Icelandic License Plate Style Input */}
                    <div className="flex items-stretch h-14 bg-white border-2 border-blue-800 dark:border-blue-600 rounded-lg overflow-hidden shadow-sm transition-all focus-within:ring-4 focus-within:ring-blue-500/20 focus-within:border-blue-600">
                      {/* IS Badge */}
                      <div className="bg-blue-800 dark:bg-blue-700 text-white w-12 flex flex-col items-center justify-center text-xs font-bold shrink-0">
                        <span className="text-[10px]">🇮🇸</span>
                        <span>IS</span>
                      </div>
                      {/* Input */}
                      <Input
                        id="plate"
                        value={formData.plate}
                        onChange={(e) => setFormData({...formData, plate: e.target.value.toUpperCase()})}
                        placeholder="AB123"
                        required
                        className="border-0 h-full text-center font-mono text-xl sm:text-2xl font-bold tracking-[0.15em] uppercase bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-slate-300 dark:placeholder:text-slate-600"
                      />
                      {loading && formData.plate && formData.plate.length >= 5 && (
                        <div className="absolute right-14 top-1/2 -translate-y-1/2">
                          <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                        </div>
                      )}
                      {/* Camera button - mobile */}
                      <button
                        type="button"
                        onClick={handleScanPlate}
                        disabled={scanningPlate}
                        className="md:hidden w-12 flex items-center justify-center bg-blue-500 hover:bg-blue-600 text-white transition-colors rounded-r-lg"
                        title="Skanna númeraplötu"
                      >
                        {scanningPlate ? '⏳' : '📷'}
                      </button>
                    </div>
                    {/* Hidden file input for camera */}
                    <input
                      ref={cameraInputRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleCameraCapture}
                      style={{ display: 'none' }}
                    />
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                    {formData.plate && formData.plate.length >= 5 && loading 
                      ? '🔍 Leita að bíl í skrá...' 
                      : 'Sláðu inn bílnúmer til að sækja upplýsingar úr ökutækjaskrá'
                    }
                  </p>
                  
                  {/* Vehicle Info - Compact display below plate */}
                  {vehicleLookupSuccess && vehicleInfo && (
                    <div className="mt-3 p-3 bg-gradient-to-r from-slate-800 to-slate-700 dark:from-slate-700 dark:to-slate-600 text-white rounded-lg shadow-md">
                      <div className="flex items-center gap-3">
                        {/* Car Logo */}
                        {vehicleInfo.manufacturer && (() => {
                          const logoUrl = getCarLogo(vehicleInfo.manufacturer);
                          return logoUrl ? (
                            <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center p-1.5 shrink-0">
                              <img 
                                src={logoUrl}
                                alt={`${vehicleInfo.manufacturer} logo`}
                                className="w-full h-full object-contain filter brightness-0 invert"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            </div>
                          ) : null;
                        })()}
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-base truncate">
                            {vehicleInfo.manufacturer} {vehicleInfo.model}
                          </div>
                          <div className="text-slate-300 text-xs truncate">
                            {vehicleInfo.owner_name}
                            {vehicleInfo.color && <span className="ml-2">• {vehicleInfo.color}</span>}
                          </div>
                        </div>
                        <Badge className="bg-green-500/20 text-green-300 border-green-500/30 text-xs shrink-0">
                          ✓ Fundinn
                        </Badge>
                      </div>
                    </div>
                  )}
                </div>

                {/* Date Picker */}
                <div className="lg:col-span-2">
                  <Label htmlFor="scheduled_date" className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2 block">
                    {t('expected_date', language)} *
                  </Label>
                  <Input
                    id="scheduled_date"
                    type="date"
                    value={formData.scheduled_date || ''}
                    onChange={(e) => setFormData({...formData, scheduled_date: e.target.value})}
                    required
                    className="h-14 text-base bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 focus:bg-white dark:focus:bg-slate-600"
                  />
                </div>

                {/* Time picker - only show if date is not today */}
                {formData.scheduled_date && formData.scheduled_date !== new Date().toISOString().split('T')[0] && (
                  <div className="lg:col-span-2">
                    <Label htmlFor="scheduled_time" className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2 block">
                      Tími
                    </Label>
                    <Input
                      id="scheduled_time"
                      type="text"
                      value={formData.scheduled_time || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        const filtered = value.replace(/[^0-9:]/g, '');
                        setFormData({...formData, scheduled_time: filtered});
                      }}
                      onBlur={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        if (value.length >= 3) {
                          const hours = value.substring(0, 2);
                          const minutes = value.substring(2, 4);
                          const h = Math.min(23, parseInt(hours) || 0);
                          const m = Math.min(59, parseInt(minutes) || 0);
                          const formatted = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                          setFormData({...formData, scheduled_time: formatted});
                        }
                      }}
                      placeholder="14:30"
                      pattern="[0-9]{2}:[0-9]{2}"
                      maxLength="5"
                      className="h-14 text-base font-mono bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600"
                    />
                  </div>
                )}

                {/* Phone Number */}
                <div className="lg:col-span-2">
                  <Label htmlFor="phone_number" className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2 block">
                    Símanúmer
                  </Label>
                  <Input
                    id="phone_number"
                    value={formData.phone_number}
                    onChange={(e) => setFormData({...formData, phone_number: e.target.value})}
                    placeholder="5812345"
                    className="h-14 text-base bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600"
                  />
                </div>

                {/* Dealership */}
                <div className="lg:col-span-2">
                  <Label htmlFor="dealership" className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2 block">
                    Bílasala *
                  </Label>
                  <Select onValueChange={async (value) => {
                    const cleanValue = value === 'Tengist ekki bílasölu' ? '' : value;
                    const updates = { dealership: value };
                    if (cleanValue && !formData.shuttle_to_dealership) {
                      updates.shuttle_to_dealership = cleanValue;
                    }
                    setFormData({...formData, ...updates});
                    if (cleanValue) {
                      try {
                        await axios.post(`${API}/dealerships/increment-usage?dealership_name=${encodeURIComponent(cleanValue)}`);
                      } catch (error) {
                        console.log('Failed to update dealership usage');
                      }
                    }
                  }} value={formData.dealership}>
                    <SelectTrigger className={`h-14 bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 ${!formData.dealership ? 'border-red-300' : ''}`}>
                      <SelectValue placeholder="Veldu bílasölu *" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Tengist ekki bílasölu">Tengist ekki bílasölu</SelectItem>
                      {dealerships.map(dealership => {
                        const name = getDealershipName(dealership);
                        const isFav = typeof dealership === 'object' && dealership.is_favorite;
                        return (
                          <SelectItem key={name} value={name}>
                            {isFav ? '⭐ ' : ''}{name}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  {!formData.dealership && (
                    <p className="text-xs text-red-500 mt-1">Skylduval</p>
                  )}
                </div>
              </div>
            </div>

            {/* Step 2: Manual Vehicle Fields - Only show when lookup failed */}
            {formData.plate && formData.plate.length >= 5 && !loading && lookupAttempted && !vehicleLookupSuccess && (
              <div className="border border-amber-200 rounded-lg p-4 bg-amber-50">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                  <h3 className="font-medium text-amber-800">Bíll fannst ekki í skrá - Fylltu út handvirkt</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label className="text-sm">Tegund (Bílaframleiðandi)</Label>
                    <Input
                      value={formData.car_make}
                      onChange={(e) => setFormData({...formData, car_make: e.target.value})}
                      placeholder="t.d. Toyota, BMW, Ford..."
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Undirtegund (Bílgerð)</Label>
                    <Input
                      value={formData.car_model}
                      onChange={(e) => setFormData({...formData, car_model: e.target.value})}
                      placeholder="t.d. Corolla, X5, Focus..."
                      className="text-sm"
                    />
                  </div>
                </div>

                {/* Owner information when vehicle not found */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-amber-300">
                  <div>
                    <Label htmlFor="owner_name_manual" className="text-sm">Nafn eiganda *</Label>
                    <Input
                      id="owner_name_manual"
                      value={formData.owner_name}
                      onChange={(e) => setFormData({...formData, owner_name: e.target.value})}
                      placeholder="Jón Jónsson"
                      className="text-sm"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="owner_kt_manual" className="text-sm">Kennitala eiganda *</Label>
                    <Input
                      id="owner_kt_manual"
                      value={formData.owner_kt}
                      onChange={(e) => handleOwnerKennitalaChange(e.target.value)}
                      placeholder="1234567890"
                      className="text-sm"
                      required
                    />
                    {customerDiscount && (
                      <div className="text-xs text-green-600 mt-1">
                        ✅ Viðskiptavinur með {customerDiscount}% afslátt
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Owner Information - HIDDEN (already shown in green vehicle info box) */}
            {/* {formData.plate && (
              <div className="border rounded-lg p-4 bg-blue-50">
                <h3 className="font-medium text-blue-900 mb-3">👤 Eigandaupplýsingar</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="owner_name">Nafn eiganda *</Label>
                    <Input
                      id="owner_name"
                      value={formData.owner_name}
                      onChange={(e) => setFormData({...formData, owner_name: e.target.value})}
                      placeholder="Jón Jónsson"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="owner_kt">Kennitala eiganda *</Label>
                    <Input
                      id="owner_kt"
                      value={formData.owner_kt}
                      onChange={(e) => handleOwnerKennitalaChange(e.target.value)}
                      placeholder="1234567890"
                      required
                    />
                    {customerDiscount && (
                      <div className="text-xs text-green-600 mt-1">
                        ✅ Viðskiptavinur með {customerDiscount}% afslátt
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )} */}

            {/* Step 4: Car Size Selection - Modern Card Style */}
            {formData.plate && (
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-5 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                    <span className="text-xl">📏</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100">Stærð bíls</h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Verðlagning byggir á stærð</p>
                  </div>
                </div>
                <div>
                  <Select 
                    value={formData.size}
                    onValueChange={(value) => {
                      console.log('🔄 Car size manually changed to:', value);
                      
                      // Get car info from ref (always up-to-date)
                      const carMake = carInfoRef.current.car_make?.trim();
                      const carModel = carInfoRef.current.car_model?.trim();
                      
                      console.log('📋 carInfoRef:', { car_make: carMake, car_model: carModel, size: value });
                      
                      // First update the local state
                      setFormData(prev => ({...prev, size: value}));
                      
                      // Save to database if we have car info
                      if (carMake && carModel) {
                        console.log(`📡 Sending API request to save size for ${carMake} ${carModel}: ${value}`);
                        axios.post(`${API}/vehicles/manual-size`, {
                          manufacturer: carMake,
                          model: carModel,
                          size: value,
                          plate: formData.plate_number || ''
                        }).then(() => {
                          console.log(`✅ Manual size saved for ${carMake} ${carModel}: ${value}`);
                          toast.success(`Stærð vistuð fyrir ${formData.plate_number || `alla ${carMake} ${carModel}`}`);
                        }).catch(error => {
                          console.error('Failed to save manual size:', error);
                          const errorMsg = error.response?.data?.detail || 'Villa við að vista stærð';
                          toast.error(errorMsg);
                        });
                      } else if (formData.plate_number) {
                        // Even without car make/model, save by plate number
                        axios.post(`${API}/vehicles/manual-size`, {
                          manufacturer: '',
                          model: '',
                          size: value,
                          plate: formData.plate_number
                        }).then(() => {
                          toast.success(`Stærð vistuð fyrir ${formData.plate_number}`);
                        }).catch(error => {
                          console.error('Failed to save manual size:', error);
                        });
                      } else {
                        console.log('⚠️ car_make or car_model missing:', { carMake, carModel });
                        toast.warning('Vinsamlegast bíðið þar til bílupplýsingar hafa verið sóttar');
                      }
                    }} 
                  >
                    <SelectTrigger data-testid="car-size-select" className="h-12 bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600">
                      <SelectValue placeholder="Veldu stærð bíls" />
                    </SelectTrigger>
                    <SelectContent>
                      {sizes.map(size => (
                        <SelectItem key={size.id} value={size.id} data-testid={`size-option-${size.id}`}>
                          {size.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.size && vehicleLookupSuccess && (
                    <p className="text-xs text-green-600 dark:text-green-400 mt-2 flex items-center gap-1">
                      <span>✓</span> Greint sjálfkrafa úr ökutækjaskrá
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Step 5: Services - Modern Accordion Style */}
            {formData.size && (
              <div className="space-y-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                    <span className="text-xl">🚿</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100">Þjónusta</h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Veldu þjónustur fyrir þennan bíl</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {/* Dynamic Categories with Modern Collapsible Sections */}
                  {sortedCategories.map(category => {
                    const categoryServices = services.filter(s => {
                      if (category.id === 'general') {
                        return (s.category === 'general' || !s.category || ['althrif', 'uthrif', 'innhrif'].includes(s.id)) && s.available !== false;
                      }
                      return s.category === category.id && s.available !== false;
                    }).sort((a, b) => (a.order || 0) - (b.order || 0));

                    if (categoryServices.length === 0) return null;
                    
                    const isExpanded = expandedCategories[category.id] ?? false;
                    const selectedCount = categoryServices.filter(s => formData.services.includes(s.id)).length;

                    return (
                      <div key={category.id} className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden bg-white dark:bg-slate-800 shadow-sm hover:shadow-md transition-shadow">
                        {/* Category Header */}
                        <button
                          type="button"
                          onClick={() => setExpandedCategories(prev => ({...prev, [category.id]: !prev[category.id]}))}
                          className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{category.icon}</span>
                            <div className="text-left">
                              <span className="font-semibold text-slate-900 dark:text-slate-100 block">{category.name}</span>
                              <span className="text-xs text-slate-500 dark:text-slate-400">
                                {categoryServices.length} þjónustur
                                {selectedCount > 0 && (
                                  <span className="ml-2 text-green-600 dark:text-green-400 font-medium">
                                    • {selectedCount} valið
                                  </span>
                                )}
                              </span>
                            </div>
                          </div>
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${isExpanded ? 'bg-blue-100 dark:bg-blue-900/50 rotate-90' : 'bg-slate-100 dark:bg-slate-700'}`}>
                            <span className="text-slate-600 dark:text-slate-300">▶</span>
                          </div>
                        </button>
                        
                        {/* Category Content - Animated */}
                        <div 
                          className={`overflow-hidden transition-all duration-300 ease-in-out ${
                            isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                          }`}
                        >
                          <div className="px-5 pb-5 pt-2 border-t border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                              {categoryServices.map(service => {
                                const defaultPrice = formData.size && prices[service.id] && prices[service.id][formData.size] 
                                  ? prices[service.id][formData.size] : 0;
                                const currentPrice = customPrices[service.id] !== undefined ? customPrices[service.id] : defaultPrice;
                                const isPriceEditable = service.price_editable === true;
                                
                                // Check if this service is included (free) in another selected service
                                let isIncluded = false;
                                let includedInServiceName = '';
                                formData.services.forEach(selectedServiceId => {
                                  const selectedService = services.find(s => s.id === selectedServiceId);
                                  if (selectedService && selectedService.includes && selectedService.includes.includes(service.id)) {
                                    isIncluded = true;
                                    includedInServiceName = selectedService.name;
                                  }
                                });
                                
                                return (
                                  <div key={service.id} className="p-2 border dark:border-slate-700 rounded hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
                                    <div className="flex items-center space-x-3">
                                      <Checkbox
                                        id={service.id}
                                        checked={formData.services.includes(service.id)}
                                        onCheckedChange={(checked) => {
                                          if (checked) {
                                            // Add the service
                                            let newServices = [...formData.services, service.id];
                                            
                                            // Auto-check included services
                                            if (service.includes && Array.isArray(service.includes)) {
                                              service.includes.forEach(includedServiceId => {
                                                if (!newServices.includes(includedServiceId)) {
                                                  newServices.push(includedServiceId);
                                                }
                                              });
                                            }
                                            
                                            // Auto-combine: If both "Alþrif" and "Djúphreinsun" are selected, replace with combo
                                            const hasAlthrif = newServices.includes('althrif');
                                            const hasDjuphreinsun = newServices.includes('djuphreinsun');
                                            
                                            if (hasAlthrif && hasDjuphreinsun) {
                                              // Remove both individual services and add combo
                                              newServices = newServices.filter(id => id !== 'althrif' && id !== 'djuphreinsun');
                                              if (!newServices.includes('althrif_djuphreinsun')) {
                                                newServices.push('althrif_djuphreinsun');
                                              }
                                              toast.success('💡 Skipt yfir í "Alþrif og djúphreinsun" combo');
                                            }
                                            
                                            setFormData({...formData, services: newServices});
                                            if (isPriceEditable && !customPrices[service.id]) {
                                              setCustomPrices(prev => ({...prev, [service.id]: defaultPrice}));
                                            }
                                          } else {
                                            // When unchecking, remove the service
                                            setFormData({...formData, services: formData.services.filter(s => s !== service.id)});
                                          }
                                        }}
                                      />
                                      <Label htmlFor={service.id} className="flex-1 cursor-pointer">
                                        <div className="font-medium text-sm">
                                          {service.name}{service.price_editable ? ' (verð frá)' : ''}
                                        </div>
                                        <div className="text-xs text-slate-600">{service.description}</div>
                                      </Label>
                                    </div>
                                    {/* Custom service (Vinna) - Show name and price inputs */}
                                    {service.is_custom && formData.services.includes(service.id) ? (
                                      <div className="mt-2 ml-7 space-y-2">
                                        <div>
                                          <Label className="text-xs text-slate-600">Nafn á vinnu:</Label>
                                          <Input
                                            type="text"
                                            value={customServiceNames[service.id] || service.name}
                                            onChange={(e) => setCustomServiceNames(prev => ({...prev, [service.id]: e.target.value}))}
                                            placeholder="T.d. Sérstök hreinvinna"
                                            className="mt-1"
                                          />
                                        </div>
                                        <div>
                                          <Label className="text-xs text-slate-600">Verð (kr):</Label>
                                          <Input
                                            type="number"
                                            value={customPrices[service.id] || ''}
                                            onChange={(e) => setCustomPrices(prev => ({...prev, [service.id]: parseInt(e.target.value) || 0}))}
                                            placeholder="Sláðu inn verð"
                                            className="mt-1"
                                          />
                                        </div>
                                      </div>
                                    ) : isPriceEditable && formData.services.includes(service.id) && !isIncluded && (user?.role !== 'staff' || user?.permissions?.can_see_prices) ? (
                                      <div className="mt-2 ml-7">
                                        <Label className="text-xs text-slate-600">Verð (kr):</Label>
                                        <Input
                                          type="number"
                                          value={currentPrice || ''}
                                          onChange={(e) => setCustomPrices(prev => ({...prev, [service.id]: parseInt(e.target.value) || 0}))}
                                          placeholder={`Frá ${defaultPrice.toLocaleString()} kr`}
                                          className="mt-1"
                                        />
                                      </div>
                                    ) : formData.services.includes(service.id) && isIncluded && (user?.role !== 'staff' || user?.permissions?.can_see_prices) ? (
                                      // Show included/free price
                                      <div className="ml-7 mt-1">
                                        <div className="text-xs text-slate-400 line-through">
                                          {currentPrice.toLocaleString()} ISK
                                        </div>
                                        <div className="text-xs font-semibold text-green-600">
                                          0 ISK (innifalið í {includedInServiceName})
                                        </div>
                                      </div>
                                    ) : currentPrice > 0 && (user?.role !== 'staff' || user?.permissions?.can_see_prices) ? (
                                      // Always show price for all services (selected or not)
                                      <div className={`ml-7 mt-1 ${
                                        formData.services.includes(service.id) 
                                          ? 'text-xs font-semibold text-blue-600' 
                                          : 'text-xs text-slate-500'
                                      }`}>
                                        {currentPrice.toLocaleString()} ISK
                                      </div>
                                    ) : null}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {/* Dynamic categories rendered above - old static code removed */}
                </div>
                
                {/* Toggle for Products */}
                {productServices.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-green-300">
                    <div className="flex items-center gap-2 mb-3">
                      <Checkbox
                        id="showProducts"
                        checked={showProducts}
                        onCheckedChange={setShowProducts}
                      />
                      <Label htmlFor="showProducts" className="cursor-pointer font-medium">
                        📦 Bæta við vörum (rúðupiss, rammar, osfrv.)
                      </Label>
                    </div>
                    
                    {showProducts && (
                      <div className="border rounded-lg p-4 bg-purple-50">
                        <h4 className="font-medium text-slate-900 mb-3">📦 Vörunotkun</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {productServices.filter(p => p.available).map(product => {
                            const quantity = productQuantities[product.id] || 0;
                            const productTotal = quantity * product.price_per_unit;
                            
                            return (
                              <div key={product.id} className="p-3 border rounded bg-white">
                                <div className="font-medium mb-2">{product.name}</div>
                                {product.description && (
                                  <div className="text-xs text-slate-600 mb-2">{product.description}</div>
                                )}
                                <div className="flex items-center gap-2 mb-2">
                                  <Label className="text-xs">
                                    {product.unit_type === 'liters' ? 'Lítrar:' : product.unit_type === 'sets' ? 'Sett:' : 'Stykki:'}
                                  </Label>
                                  <Input
                                    type="number"
                                    min="0"
                                    step={product.unit_type === 'liters' ? '0.5' : '1'}
                                    value={quantity || ''}
                                    onChange={(e) => {
                                      const val = parseFloat(e.target.value) || 0;
                                      setProductQuantities(prev => ({...prev, [product.id]: val}));
                                    }}
                                    className="w-20 text-sm"
                                    placeholder="0"
                                  />
                                  <span className="text-xs text-slate-600">
                                    × {product.price_per_unit} ISK
                                  </span>
                                </div>
                                {product.in_stock ? (
                                  product.unit_type === 'liters' && product.liters_in_stock ? (
                                    <Badge className="bg-green-100 text-green-800 text-xs">
                                      ✅ ≈{product.liters_in_stock}L á lager
                                    </Badge>
                                  ) : (
                                    <Badge className="bg-green-100 text-green-800 text-xs">
                                      ✅ {product.stock_quantity} á lager
                                    </Badge>
                                  )
                                ) : (
                                  <Badge className="bg-red-100 text-red-800 text-xs">
                                    ⚠️ Ekki á lager
                                  </Badge>
                                )}
                                {quantity > 0 && (
                                  <div className="text-sm font-medium text-blue-600 mt-2">
                                    = {productTotal.toLocaleString()} ISK
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Step 6: Payer Options - Show after services OR products are selected */}
            {(formData.services.length > 0 || Object.values(productQuantities).some(q => q > 0)) && (
              <div className="border rounded-lg p-4 bg-purple-50">
                <h3 className="font-medium text-purple-900 mb-3">💳 Greiðandi</h3>
                <div className="flex items-center gap-2 mb-3">
                  <Checkbox
                    id="payer_is_owner"
                    checked={!formData.payer_is_owner}
                    onCheckedChange={(checked) => {
                      setFormData({...formData, payer_is_owner: !checked});
                      if (checked) loadPayerCustomers(); // Load customers when enabling payer selection
                    }}
                  />
                  <Label htmlFor="payer_is_owner" className="font-medium">Velja annan greiðanda</Label>
                  <div className="text-xs text-slate-500">(ef ekki eigandinn)</div>
                </div>
                {!formData.payer_is_owner && (
                  <div className="space-y-4">
                    {/* Checkbox to also update owner */}
                    <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      <Checkbox
                        id="ny-update-owner-with-payer"
                        checked={updateOwnerWithPayer}
                        onCheckedChange={(checked) => {
                          setUpdateOwnerWithPayer(!!checked);
                          if (checked && formData.payer_kt && formData.payer_name) {
                            setFormData(prev => ({
                              ...prev,
                              owner_name: prev.payer_name,
                              owner_kt: prev.payer_kt
                            }));
                            toast.info('👤 Eigandi uppfærður');
                          }
                        }}
                      />
                      <Label htmlFor="ny-update-owner-with-payer" className="text-sm cursor-pointer text-amber-800">
                        Breyta eiganda líka
                      </Label>
                      <span className="text-xs text-amber-600">(uppfærir eiganda þegar greiðandi er valinn)</span>
                    </div>

                    {/* Customer search dropdown */}
                    <div className="bg-white rounded-lg p-3 border border-purple-200">
                      <Label className="text-purple-700 font-medium mb-2 block">👥 Leita í viðskiptavinum</Label>
                      <div className="flex gap-2 mb-2">
                        <div className="flex-1 relative">
                          <Input
                            value={payerCustomerSearch}
                            onChange={(e) => {
                              setPayerCustomerSearch(e.target.value);
                              setShowPayerCustomerDropdown(true);
                              if (payerCustomers.length === 0) loadPayerCustomers();
                            }}
                            onFocus={() => {
                              setShowPayerCustomerDropdown(true);
                              if (payerCustomers.length === 0) loadPayerCustomers();
                            }}
                            placeholder="Leita eftir nafni, kt, fyrirtæki..."
                            className="pr-8"
                          />
                          {loadingPayerCustomers && (
                            <div className="absolute right-2 top-2">
                              <div className="animate-spin h-5 w-5 border-2 border-purple-500 rounded-full border-t-transparent"></div>
                            </div>
                          )}
                        </div>
                        <Select value={payerCustomerSortBy} onValueChange={setPayerCustomerSortBy}>
                          <SelectTrigger className="w-[140px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="popularity">🔥 Vinsælast</SelectItem>
                            <SelectItem value="name">🔤 Stafrófsröð</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Customer dropdown list */}
                      {showPayerCustomerDropdown && filteredPayerCustomers.length > 0 && (
                        <div className="max-h-48 overflow-y-auto border rounded-md bg-white shadow-lg">
                          {filteredPayerCustomers.map((customer, idx) => (
                            <div
                              key={customer.id || idx}
                              className="px-3 py-2 hover:bg-purple-50 cursor-pointer border-b last:border-b-0 flex justify-between items-center"
                              onClick={() => selectPayerFromCustomer(customer)}
                            >
                              <div>
                                <div className="font-medium text-sm">{customer.company || customer.name}</div>
                                <div className="text-xs text-slate-500">
                                  {customer.kennitala && <span className="mr-2">kt: {customer.kennitala}</span>}
                                  {customer.phone && <span>📞 {customer.phone}</span>}
                                </div>
                              </div>
                              <div className="text-right">
                                {customer.job_count > 0 && (
                                  <Badge variant="outline" className="text-xs">{customer.job_count} verk</Badge>
                                )}
                                {customer.discount_percentage > 0 && (
                                  <Badge className="bg-green-100 text-green-700 text-xs ml-1">{customer.discount_percentage}%</Badge>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {showPayerCustomerDropdown && payerCustomerSearch && filteredPayerCustomers.length === 0 && !loadingPayerCustomers && (
                        <div className="text-sm text-slate-500 text-center py-2">Enginn viðskiptavinur fannst</div>
                      )}
                    </div>

                    {/* Divider */}
                    <div className="flex items-center gap-2 text-xs text-slate-400">
                      <div className="flex-1 border-t"></div>
                      <span>eða</span>
                      <div className="flex-1 border-t"></div>
                    </div>

                    {/* Kennitala lookup (original) */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Kennitala greiðanda *</Label>
                        <Input
                          value={formData.payer_kt}
                          onChange={(e) => handlePayerKennitalaChange(e.target.value)}
                          placeholder="######-#### (tengist Þjóðskrá)"
                          className={payerKennitalaLoading ? "animate-pulse" : ""}
                          required
                        />
                        {payerKennitalaLoading && (
                          <div className="text-xs text-blue-600 mt-1">🔍 Leita í Þjóðskrá...</div>
                        )}
                      </div>
                      <div>
                        <Label>Nafn greiðanda *</Label>
                        <Input
                          value={formData.payer_name}
                          onChange={(e) => setFormData({...formData, payer_name: e.target.value})}
                          placeholder="Nafn greiðanda (fyllist sjálfkrafa)"
                          className={payerKennitalaLoading ? "animate-pulse" : ""}
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Step 7: Pricing Summary and Notes - Show when services are selected and user can see prices */}
            {formData.services.length > 0 && formData.total >= 0 && (user?.role !== 'staff' || user?.permissions?.can_see_prices) && (
              <Card className="bg-blue-50 border-blue-200">
                <CardHeader>
                  <CardTitle className="text-blue-900 flex items-center gap-2">
                    💰 Verðyfirlit
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="space-y-2">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Samtals án VSK:</span>
                          <span>{Math.round((formData.total + (formData.total * formData.discount_percent/100)) / 1.24).toLocaleString()} ISK</span>
                        </div>
                        {formData.discount_percent > 0 && (
                          <div className="flex justify-between text-sm text-orange-600">
                            <span>Afsláttur {formData.discount_percent}%:</span>
                            <span>-{Math.round((formData.total + (formData.total * formData.discount_percent/100)) * formData.discount_percent/100).toLocaleString()} ISK</span>
                          </div>
                        )}
                        {formData.pickup_delivery_price > 0 && (
                          <div className="flex justify-between text-sm text-green-600">
                            <span>🚚 Sækja/skila (pnr. {formData.pickup_postal_code}):</span>
                            <span>+{formData.pickup_delivery_price.toLocaleString()} ISK</span>
                          </div>
                        )}
                        <div className="flex justify-between text-sm">
                          <span>VSK 24%:</span>
                          <span>{Math.round(formData.total - (formData.total / 1.24)).toLocaleString()} ISK</span>
                        </div>
                        <div className="flex justify-between text-lg font-bold border-t border-blue-300 pt-2">
                          <span>Heildarupphæð:</span>
                          <span className="text-blue-600">{formData.total.toLocaleString()} ISK</span>
                        </div>
                      </div>
                      <div>
                        {/* Manual Total Override */}
                        <div className="flex items-center space-x-2 mb-2">
                          <Checkbox
                            id="manual-total"
                            checked={manualTotalEnabled}
                            onCheckedChange={(checked) => {
                              setManualTotalEnabled(!!checked);
                              if (checked) {
                                setManualTotal(formData.total);
                              }
                            }}
                          />
                          <Label htmlFor="manual-total" className="cursor-pointer text-sm">
                            💰 Handbreyta heildarverði
                          </Label>
                        </div>
                        
                        {manualTotalEnabled ? (
                          <div>
                            <Label>Heildarverð (ISK)</Label>
                            <Input
                              type="number"
                              min="0"
                              step="100"
                              value={manualTotal}
                              onChange={(e) => {
                                const inputValue = e.target.value;
                                
                                // Handle empty input or convert to number
                                let value = 0;
                                if (inputValue === '') {
                                  value = 0;
                                } else {
                                  value = Math.max(0, Number(inputValue));
                                  if (isNaN(value)) {
                                    return; // Invalid input, don't update
                                  }
                                }
                                
                                // Update manual total state
                                setManualTotal(value);
                                
                                // Calculate discount percentage based on manual total
                                // Calculate original subtotal without discount
                                let subtotal = 0;
                                formData.services.forEach(serviceId => {
                                  if (customPrices[serviceId] !== undefined) {
                                    subtotal += customPrices[serviceId];
                                  } else if (prices[serviceId] && prices[serviceId][formData.size]) {
                                    subtotal += prices[serviceId][formData.size];
                                  }
                                });
                                
                                // Calculate discount percentage: discount% = (1 - manualTotal/subtotal) * 100
                                if (subtotal > 0) {
                                  const calculatedDiscount = Math.max(0, Math.min(100, Math.round((1 - value/subtotal) * 100)));
                                  setFormData(prev => ({ ...prev, discount_percent: calculatedDiscount }));
                                }
                              }}
                              placeholder="Sláðu inn heildarverð"
                            />
                            <div className="text-xs text-blue-600 mt-1">
                              💡 Afsláttur verður reiknaður sjálfkrafa út frá handvirku verði
                            </div>
                          </div>
                        ) : (
                          <div>
                            <Label>Afsláttur (%)</Label>
                            <div className="relative">
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                step="1"
                                value={formData.discount_percent}
                                onChange={(e) => {
                                  if (!discountLocked) {
                                    setFormData({ ...formData, discount_percent: Math.max(0, Math.min(100, Number(e.target.value) || 0)) });
                                  }
                                }}
                                disabled={discountLocked}
                                className={discountLocked ? "bg-gray-100 cursor-not-allowed" : ""}
                              />
                              {discountLocked && (
                                <div className="absolute right-3 top-2 text-blue-500">
                                  🔒
                                </div>
                              )}
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                              {discountLocked 
                                ? `Fastur afsláttur fyrir þennan viðskiptavin` 
                                : 'Beitt á heildarupphæð'
                              }
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        {formData.discount_percent > 0 && (
                          <>
                            <div className="text-sm text-slate-500 line-through">
                              Upphaflegt: {Math.round((formData.total + (formData.total * formData.discount_percent/100))).toLocaleString()} ISK
                            </div>
                            <div className="text-sm">Með {formData.discount_percent}% afslætti:</div>
                          </>
                        )}
                        <div className="text-2xl font-bold text-green-700">
                          {formData.total.toLocaleString()} ISK
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Additional Job Requirements */}
            {formData.services.length > 0 && formData.total >= 0 && (
              <div className="border rounded-lg p-4 bg-slate-50 dark:bg-slate-800 dark:border-slate-700">
                <h3 className="font-medium text-slate-900 dark:text-slate-100 mb-3">📋 Viðbótarupplýsingar</h3>
                <div className="space-y-4">
                  {/* Ready by time */}
                  <div>
                    <Label htmlFor="ready_by_time">⏰ Tímafrestur (hvenær þarf bíll að vera tilbúinn)</Label>
                    <Input
                      id="ready_by_time"
                      type="text"
                      value={formData.ready_by_time}
                      onChange={(e) => {
                        let value = e.target.value.replace(/[^0-9]/g, ''); // Only numbers
                        
                        if (value.length >= 2) {
                          // Auto-add colon after 2 digits
                          value = value.slice(0, 2) + ':' + value.slice(2, 4);
                        }
                        
                        setFormData({...formData, ready_by_time: value});
                      }}
                      placeholder="1330"
                      maxLength={5}
                    />
                    <div className="text-xs text-slate-500 mt-1">
                      Skrifa t.d. 1330 → 13:30 (kolon kemur sjálfkrafa)
                    </div>
                  </div>

                  {/* Checkboxes for special requirements */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="flex items-center space-x-2 p-3 border rounded hover:bg-slate-100">
                      <Checkbox
                        id="needs_loan_car"
                        checked={formData.needs_loan_car}
                        onCheckedChange={(checked) => setFormData({...formData, needs_loan_car: !!checked})}
                      />
                      <Label htmlFor="needs_loan_car" className="cursor-pointer">
                        🚗 Viðskiptavinur þarf lánsbíl
                      </Label>
                    </div>

                    <div className="flex items-center space-x-2 p-3 border rounded hover:bg-slate-100">
                      <input 
                        type="checkbox" 
                        id="needs_pickup_delivery"
                        checked={formData.needs_pickup_delivery}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          if (isChecked) {
                            // When checked, calculate price if postal code exists
                            let price = 0;
                            if (formData.pickup_postal_code) {
                              const zone = pickupZones.find(z => z.postal_code === formData.pickup_postal_code && z.enabled);
                              if (zone) {
                                price = zone.price;
                              }
                            }
                            setFormData({
                              ...formData, 
                              needs_pickup_delivery: true,
                              pickup_delivery_price: price
                            });
                          } else {
                            // Clear pickup/delivery data when unchecked (but keep address and postal code for reference)
                            setFormData({
                              ...formData, 
                              needs_pickup_delivery: false,
                              pickup_delivery_price: 0,
                              pickup_delivery_note: ''
                            });
                          }
                        }}
                      />
                      <Label htmlFor="needs_pickup_delivery" className="cursor-pointer">
                        🚚 Sækja/skila bíl úti bæ
                      </Label>
                    </div>
                    {formData.needs_pickup_delivery && formData.pickup_delivery_price > 0 && (
                      <div className="flex items-center space-x-2 p-2 ml-6 border rounded bg-blue-50">
                        <input 
                          type="checkbox" 
                          id="pickup_delivery_discount"
                          checked={formData.pickup_delivery_discount || false}
                          onChange={(e) => setFormData({...formData, pickup_delivery_discount: e.target.checked})}
                        />
                        <Label htmlFor="pickup_delivery_discount" className="cursor-pointer text-sm">
                          💰 Leyfa afslátt á sækja/skila
                        </Label>
                      </div>
                    )}
                  </div>

                  {/* Loan Car Selection - Show when checkbox is checked - MOVED HERE */}
                  {formData.needs_loan_car && (
                    <div>
                      <Label htmlFor="loan_car_id">Veldu lánsbíl *</Label>
                      <Select 
                        value={formData.loan_car_id || undefined} 
                        onValueChange={(value) => setFormData({...formData, loan_car_id: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Veldu lánsbíl" />
                        </SelectTrigger>
                        <SelectContent>
                          {!loanCars || loanCars.filter(car => car.is_active !== false).length === 0 ? (
                            <div className="p-4 text-center text-slate-500 text-sm">
                              Engir lánsbílar skráðir. Farðu í Stillingar → Lánsbílar til að bæta við.
                            </div>
                          ) : (
                            loanCars.filter(car => car.is_active !== false).map(car => (
                              <SelectItem key={car.id} value={car.id}>
                                {car.plate} - {car.manufacturer} {car.model} {car.color && `(${car.color})`}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      <div className="text-xs text-slate-500 mt-1">
                        Veldu hvaða lánsbíl viðskiptavinur fær til nota
                      </div>
                      
                      {/* Unified Loan Agreement Button */}
                      {formData.loan_car_id && (
                        <div className="mt-2">
                          <Button
                            type="button"
                            variant="default"
                            onClick={() => {
                              // Open loan agreement dialog with current form data
                              setSelectedJobForAgreement({
                                owner_name: formData.owner_name,
                                owner_kt: formData.owner_kt,
                                phone_number: formData.phone_number,
                                plate: formData.plate,
                                loan_car_id: formData.loan_car_id
                              });
                              setLoanAgreementDialogOpen(true);
                            }}
                            className="w-full bg-blue-600 hover:bg-blue-700"
                          >
                            📄 Lánsbílasamningur
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Shuttle to Dealership - Show ONLY when pickup/delivery is NOT checked */}
                  {!formData.needs_pickup_delivery && (
                    <div>
                      <Label htmlFor="shuttle_to_dealership" className="font-medium">
                        🚗 Hvert á að skutla ökutækinu?
                      </Label>
                      <Select 
                        value={formData.shuttle_to_dealership || "NONE"} 
                        onValueChange={(value) => setFormData({...formData, shuttle_to_dealership: value === "NONE" ? "" : value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Veldu bílasölu (valkvætt)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="NONE">Enginn</SelectItem>
                          {dealerships.map(dealership => {
                            const name = getDealershipName(dealership);
                            return (
                              <SelectItem key={name} value={name}>
                                {name}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      <div className="text-xs text-blue-600 mt-1">
                        📍 Hvert á að skutla bílnum þegar hann er tilbúinn? (valkvætt)
                      </div>
                    </div>
                  )}

                  {/* Pickup/Delivery Address - Show when checkbox is checked */}
                  {formData.needs_pickup_delivery && (
                    <div className="space-y-4">
                      {/* Address selection - registered vs custom */}
                      <div className="space-y-3">
                        <Label className="text-sm font-medium">📍 Staðsetning</Label>
                        
                        {/* Option 1: Use registered address */}
                        <div 
                          className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                            !formData.use_custom_pickup_address 
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                              : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                          }`}
                          onClick={() => setFormData({
                            ...formData, 
                            use_custom_pickup_address: false,
                            pickup_address: formData.owner_address || ''
                          })}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                              !formData.use_custom_pickup_address ? 'border-blue-500' : 'border-slate-300'
                            }`}>
                              {!formData.use_custom_pickup_address && (
                                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                              )}
                            </div>
                            <span className="font-medium text-sm">Skráð heimilisfang eiganda</span>
                          </div>
                          {formData.owner_address && (
                            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 ml-6">
                              {formData.owner_address}
                            </p>
                          )}
                        </div>
                        
                        {/* Option 2: Custom address */}
                        <div 
                          className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                            formData.use_custom_pickup_address 
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                              : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                          }`}
                          onClick={() => setFormData({
                            ...formData, 
                            use_custom_pickup_address: true,
                            pickup_address: '', // Clear address when switching to custom
                            pickup_postal_code: '',
                            pickup_delivery_price: 0
                          })}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                              formData.use_custom_pickup_address ? 'border-blue-500' : 'border-slate-300'
                            }`}>
                              {formData.use_custom_pickup_address && (
                                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                              )}
                            </div>
                            <span className="font-medium text-sm">Annað heimilisfang</span>
                          </div>
                        </div>
                        
                        {/* Custom address input - only show when selected */}
                        {formData.use_custom_pickup_address && (
                          <div className="ml-6 relative">
                            <Input
                              id="pickup_address"
                              value={formData.pickup_address}
                              onChange={async (e) => {
                                const value = e.target.value;
                                setFormData({...formData, pickup_address: value});
                                
                                // Try to extract postal code from address (e.g. "Bakkasmári 7, 201 Kópavogur")
                                const postalMatch = value.match(/\b(\d{3})\b/);
                                if (postalMatch) {
                                  const extractedPostal = postalMatch[1];
                                  const zone = pickupZones.find(z => z.postal_code === extractedPostal);
                                  if (zone) {
                                    setFormData(prev => ({
                                      ...prev,
                                      pickup_address: value,
                                      pickup_postal_code: extractedPostal,
                                      pickup_delivery_price: zone.price
                                    }));
                                  }
                                }
                                
                                // Search for address suggestions
                                if (value.length >= 2) {
                                  try {
                                    const res = await axios.get(`${API}/addresses/search?q=${encodeURIComponent(value)}`);
                                    setFormData(prev => ({...prev, _addressSuggestions: res.data.addresses || []}));
                                  } catch (e) {
                                    console.error('Address search error:', e);
                                  }
                                } else {
                                  setFormData(prev => ({...prev, _addressSuggestions: []}));
                                }
                              }}
                              onFocus={async () => {
                                if (formData.pickup_address?.length >= 2) {
                                  try {
                                    const res = await axios.get(`${API}/addresses/search?q=${encodeURIComponent(formData.pickup_address)}`);
                                    setFormData(prev => ({...prev, _addressSuggestions: res.data.addresses || []}));
                                  } catch (e) {}
                                }
                              }}
                              onBlur={() => {
                                setTimeout(() => {
                                  setFormData(prev => ({...prev, _addressSuggestions: []}));
                                }, 200);
                              }}
                              placeholder="Heimilisfang, póstnúmer..."
                              autoFocus
                            />
                            
                            {/* Address suggestions dropdown */}
                            {formData._addressSuggestions?.length > 0 && (
                              <div className="absolute top-full left-0 right-0 z-50 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-48 overflow-y-auto mt-1">
                                {formData._addressSuggestions.map((item, idx) => (
                                  <button
                                    key={idx}
                                    type="button"
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-blue-900/30 border-b border-slate-100 dark:border-slate-700 last:border-b-0 flex justify-between items-center"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      const addr = typeof item === 'string' ? item : item.address;
                                      let postal = typeof item === 'string' ? '' : (item.postal_code || '');
                                      
                                      // Also try to extract postal from address string if not provided
                                      if (!postal) {
                                        const postalMatch = addr.match(/\b(\d{3})\b/);
                                        if (postalMatch) postal = postalMatch[1];
                                      }
                                      
                                      // Auto-calculate price based on postal code
                                      let price = 0;
                                      if (postal) {
                                        const zone = pickupZones.find(z => z.postal_code === postal);
                                        if (zone) {
                                          price = zone.price;
                                          toast.success(`📍 ${zone.area_name}: ${price > 0 ? price.toLocaleString() + ' ISK' : 'Ekkert gjald'}`);
                                        }
                                      }
                                      
                                      setFormData(prev => ({
                                        ...prev, 
                                        pickup_address: addr,
                                        pickup_postal_code: postal,
                                        pickup_delivery_price: price || prev.pickup_delivery_price,
                                        _addressSuggestions: []
                                      }));
                                    }}
                                  >
                                    <span>📍 {typeof item === 'string' ? item : item.address}</span>
                                    {typeof item !== 'string' && item.postal_code && (
                                      <span className="text-xs text-slate-500 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded">
                                        {item.postal_code}
                                      </span>
                                    )}
                                  </button>
                                ))}
                              </div>
                            )}
                            
                            {/* Show price if calculated */}
                            {formData.pickup_delivery_price > 0 && (
                              <div className="text-sm text-blue-600 dark:text-blue-400 font-medium p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg mt-2">
                                💰 Sækja/skila: {formData.pickup_delivery_price.toLocaleString()} ISK
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Pickup/Delivery Note Field */}
                      <div>
                        <Label htmlFor="pickup_delivery_note">💬 Athugasemd (valfrjálst)</Label>
                        <Input
                          id="pickup_delivery_note"
                          value={formData.pickup_delivery_note}
                          onChange={(e) => setFormData({...formData, pickup_delivery_note: e.target.value})}
                          placeholder="t.d. lyklar hjá móttöku, nafn fyrirtækis..."
                        />
                      </div>
                      
                      {/* Postal Code Display */}
                      {formData.pickup_postal_code && (
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="text-sm font-medium text-blue-900">
                                  📍 Póstnúmer: {formData.pickup_postal_code}
                                  {pickupZones.find(z => z.postal_code === formData.pickup_postal_code) && 
                                    ` - ${pickupZones.find(z => z.postal_code === formData.pickup_postal_code).area_name}`
                                  }
                                </div>
                              </div>
                            </div>
                            
                            {/* Show price info and action buttons when NOT editing and user can see prices */}
                            {!editingPickupPrice && (user?.role !== 'staff' || user?.permissions?.can_see_prices) && (
                              <div>
                                <div className="text-sm text-slate-700 mb-2">
                                  Sækja/skila gjald: <span className="font-medium">{formData.pickup_delivery_price.toLocaleString()} ISK</span>
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setFormData(prev => ({
                                        ...prev,
                                        pickup_delivery_price: 0
                                      }));
                                      toast.success('✓ Gjaldi sleppt');
                                    }}
                                    className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 border-orange-300"
                                  >
                                    Sleppa gjaldi
                                  </Button>
                                  
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setEditingPickupPrice(true);
                                    }}
                                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-300"
                                  >
                                    Breyta verði
                                  </Button>
                                </div>
                              </div>
                            )}
                            
                            {/* Show editable price field when editing and user can see prices */}
                            {editingPickupPrice && (user?.role !== 'staff' || user?.permissions?.can_see_prices) && (
                              <div className="space-y-2">
                                <Label htmlFor="pickup_price_edit" className="text-sm text-slate-700">Sækja/skila gjald</Label>
                                <div className="flex gap-2 items-center">
                                  <Input
                                    id="pickup_price_edit"
                                    type="number"
                                    value={formData.pickup_delivery_price}
                                    onChange={(e) => {
                                      const newPrice = parseFloat(e.target.value) || 0;
                                      setFormData(prev => ({
                                        ...prev,
                                        pickup_delivery_price: newPrice
                                      }));
                                    }}
                                    className="flex-1"
                                    min="0"
                                    step="100"
                                    autoFocus
                                  />
                                  <span className="text-sm text-slate-600 whitespace-nowrap">ISK</span>
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setEditingPickupPrice(false);
                                      toast.success('✓ Verð vistað');
                                    }}
                                    className="text-green-600 hover:text-green-700 hover:bg-green-50 border-green-300"
                                  >
                                    Vista
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      // Reset to original zone price
                                      const zone = pickupZones.find(z => z.postal_code === formData.pickup_postal_code);
                                      if (zone) {
                                        setFormData(prev => ({
                                          ...prev,
                                          pickup_delivery_price: zone.price
                                        }));
                                      }
                                      setEditingPickupPrice(false);
                                      toast.info('Hætt við breytingar');
                                    }}
                                    className="text-slate-600 hover:text-slate-700 hover:bg-slate-50 border-slate-300"
                                  >
                                    Hætta við
                                  </Button>
                                </div>
                              </div>
                            )}
                            
                            {/* Status badge */}
                            {!editingPickupPrice && formData.pickup_delivery_price === 0 && (
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                                ✓ Ókeypis
                              </Badge>
                            )}
                            {!editingPickupPrice && formData.pickup_delivery_price > 0 && pickupZones.find(z => z.postal_code === formData.pickup_postal_code) && formData.pickup_delivery_price !== pickupZones.find(z => z.postal_code === formData.pickup_postal_code).price && (
                              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">
                                ⚠️ Sérsniðið verð (upphaflegt: {pickupZones.find(z => z.postal_code === formData.pickup_postal_code).price.toLocaleString()} ISK)
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Notes, Key Location and Images */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="key_location">🔑 Hvar er lykillinn?</Label>
                      <Select 
                        onValueChange={(value) => setFormData({...formData, key_location: value})}
                        value={formData.key_location}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Veldu staðsetningu" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="í bílnum">Í bílnum</SelectItem>
                          <SelectItem value="í móttöku">Í móttöku</SelectItem>
                          <SelectItem value="hjá viðskiptavini">Hjá viðskiptavini</SelectItem>
                          <SelectItem value="í lyklaskáp">Í lyklaskáp</SelectItem>
                          <SelectItem value="annað">Annað</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="notes">Athugasemdir (valfrjálsar)</Label>
                      <Input
                        id="notes"
                        value={formData.notes}
                        onChange={(e) => setFormData({...formData, notes: e.target.value})}
                        placeholder="Viðbótar upplýsingar..."
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="images">📷 Myndir (t.d. rispur, skemmdir)</Label>
                      
                      {/* Desktop: Show file input + QR button */}
                      <div className="flex gap-2">
                        <input
                          id="images"
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            if (files.length === 0) return;
                            
                            // Limit to 5 images total
                            const existingCount = (formData.images || []).length;
                            const totalCount = existingCount + files.length;
                            if (totalCount > 5) {
                              toast.error(`Hámark 5 myndir. Þú hefur ${existingCount}, reyndir að bæta við ${files.length}`);
                              return;
                            }
                            
                            // Convert to base64
                            Promise.all(
                              files.map(file => {
                                return new Promise((resolve, reject) => {
                                  const reader = new FileReader();
                                  reader.onload = () => resolve(reader.result);
                                  reader.onerror = reject;
                                  reader.readAsDataURL(file);
                                });
                              })
                            ).then(newImages => {
                              // Append to existing images
                              const existingImages = formData.images || [];
                              const combinedImages = [...existingImages, ...newImages];
                              setFormData({...formData, images: combinedImages});
                              toast.success(`${files.length} mynd${files.length > 1 ? 'ir' : ''} valið`);
                            }).catch(() => {
                              toast.error('Villa við að lesa myndir');
                            });
                          }}
                          className="block w-full text-sm text-slate-500
                            file:mr-4 file:py-2 file:px-4
                            file:rounded-md file:border-0
                            file:text-sm file:font-semibold
                            file:bg-blue-50 file:text-blue-700
                            hover:file:bg-blue-100
                            cursor-pointer"
                        />
                        
                        {/* QR Code Button - Only show on desktop/tablet */}
                        {!isMobileDevice() && (
                          <Button
                            type="button"
                            onClick={createQrSession}
                            variant="outline"
                            className="shrink-0"
                            title="Skannaðu QR kóða með símanum til að hlaða upp myndum"
                          >
                            📱 QR
                          </Button>
                        )}
                      </div>
                      
                      <div className="text-xs text-slate-500 mt-1">
                        Hámark 5 myndir af ríspum, skemmdum o.fl.
                        {!isMobileDevice() && <span> • Smelltu á "QR" til að hlaða upp frá síma</span>}
                      </div>
                      
                      {/* Image Preview */}
                      {formData.images && formData.images.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {formData.images.map((img, idx) => (
                            <div key={idx} className="relative group">
                              <img 
                                src={img} 
                                alt={`Mynd ${idx + 1}`}
                                className="w-20 h-20 object-cover rounded border-2 border-slate-200"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const newImages = formData.images.filter((_, i) => i !== idx);
                                  setFormData({...formData, images: newImages});
                                  toast.info('Mynd fjarlægð');
                                }}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* SMS Options - Show when phone number is entered */}
            {formData.phone_number && formData.services.length > 0 && (
              <div className="border rounded-lg p-4 bg-blue-50">
                <h4 className="font-medium text-blue-900 mb-3">📱 {t('sms_notifications', language)}</h4>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="send_completion_sms"
                      checked={formData.send_completion_sms}
                      onCheckedChange={(checked) => setFormData({...formData, send_completion_sms: !!checked})}
                    />
                    <Label htmlFor="send_completion_sms" className="text-sm">
                      {t('send_completion_sms', language)}
                    </Label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="send_payment_sms"
                      checked={formData.send_payment_sms}
                      onCheckedChange={(checked) => setFormData({...formData, send_payment_sms: !!checked})}
                    />
                    <Label htmlFor="send_payment_sms" className="text-sm">
                      {t('send_payment_sms', language)}
                    </Label>
                  </div>
                  
                  <p className="text-xs text-blue-600 mt-2">
                    {t('sms_will_be_sent', language)} {formData.phone_number} {language === 'is' ? 'ef valið' : 'if selected'}
                  </p>
                </div>
              </div>
            )}

            {/* Submit Button - Only show when form is complete */}
            {formData.services.length > 0 && formData.total >= 0 && (
              <div className="flex justify-end pt-4 border-t">
                <Button type="submit" disabled={loading} className="px-8 py-3 text-lg">
                  {loading ? '⏳ Býr til verk...' : '✅ Stofna verk'}
                </Button>
              </div>
            )}
          </form>
        </CardContent>
      </Card>
      
      {/* Loan Agreement Dialog - In Job Creation Form */}
      <Dialog open={loanAgreementDialogOpen} onOpenChange={setLoanAgreementDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>📄 Lánsbílasamningur</DialogTitle>
          </DialogHeader>
          {selectedJobForAgreement && (
            <div className="space-y-4">
              {/* Vehicle Owner Info */}
              <div className="p-4 bg-slate-100 rounded-lg">
                <h3 className="font-medium mb-2">Eigandi ökutækis</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="font-medium">Nafn:</span> {selectedJobForAgreement.owner_name}</div>
                  <div><span className="font-medium">Kennitala:</span> {selectedJobForAgreement.owner_kt}</div>
                  <div><span className="font-medium">Bílnúmer:</span> {selectedJobForAgreement.plate}</div>
                </div>
              </div>

              {/* Renter Info (if different from owner) */}
              <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-medium text-blue-900">Leigjandi</h3>
                  <Button
                    type="button"
                    onClick={handleChangePrintRenter}
                    variant="outline"
                    size="sm"
                    className="text-blue-700 border-blue-300 hover:bg-blue-100"
                  >
                    {printRenterInfo ? '✏️ Breyta leigjanda' : '➕ Bæta við leigjanda'}
                  </Button>
                </div>
                {printRenterInfo ? (
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="font-medium">Nafn:</span> {printRenterInfo.name}</div>
                    <div><span className="font-medium">Kennitala:</span> {printRenterInfo.kennitala}</div>
                    <div><span className="font-medium">Sími:</span> {printRenterInfo.phone}</div>
                  </div>
                ) : (
                  <div className="text-sm text-slate-600 italic">
                    Leigjandi er sami og eigandi ökutækis
                  </div>
                )}
              </div>

              {/* Loan Car Info */}
              {selectedJobForAgreement.loan_car_id && loanCars.length > 0 && (
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h3 className="font-medium mb-2">🚗 Lánsbíll</h3>
                  {(() => {
                    const loanCar = loanCars.find(c => c.id === selectedJobForAgreement.loan_car_id);
                    if (loanCar) {
                      return (
                        <div className="text-sm">
                          <div><span className="font-medium">Bílnúmer:</span> {loanCar.plate}</div>
                          <div><span className="font-medium">Tegund:</span> {loanCar.manufacturer} {loanCar.model}</div>
                          {loanCar.year && <div><span className="font-medium">Árgerð:</span> {loanCar.year}</div>}
                          {loanCar.color && <div><span className="font-medium">Litur:</span> {loanCar.color}</div>}
                        </div>
                      );
                    }
                    return <div className="text-sm text-slate-600">Lánsbíll ekki fundinn</div>;
                  })()}
                </div>
              )}

              {/* Agreement Form Fields */}
              <div className="space-y-3">
                <div>
                  <Label htmlFor="km_out_form">Km við afhendingu</Label>
                  <Input
                    id="km_out_form"
                    type="number"
                    value={loanAgreementData.km_out}
                    onChange={(e) => setLoanAgreementData(prev => ({...prev, km_out: e.target.value}))}
                    placeholder="t.d. 45230"
                  />
                </div>

                <div>
                  <Label htmlFor="delivery_date_form">Afhent þann</Label>
                  <Input
                    id="delivery_date_form"
                    type="date"
                    value={loanAgreementData.delivery_date}
                    onChange={(e) => setLoanAgreementData(prev => ({...prev, delivery_date: e.target.value}))}
                  />
                </div>

                <div>
                  <Label htmlFor="comments_form">Athugasemdir</Label>
                  <textarea
                    id="comments_form"
                    value={loanAgreementData.comments}
                    onChange={(e) => setLoanAgreementData(prev => ({...prev, comments: e.target.value}))}
                    placeholder="t.d. Lítil rispa hægra megin"
                    className="w-full p-2 border rounded-md min-h-[80px]"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Dagsetning Alvörubón</Label>
                    <Input
                      type="date"
                      value={loanAgreementData.date_company}
                      onChange={(e) => setLoanAgreementData(prev => ({...prev, date_company: e.target.value}))}
                    />
                  </div>
                  <div>
                    <Label>Dagsetning viðskiptavinur</Label>
                    <Input
                      type="date"
                      value={loanAgreementData.date_customer}
                      onChange={(e) => setLoanAgreementData(prev => ({...prev, date_customer: e.target.value}))}
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between space-x-2 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setLoanAgreementDialogOpen(false);
                    setPrintRenterInfo(null);
                    setLoanAgreementData({
                      km_out: '',
                      delivery_date: new Date().toISOString().split('T')[0],
                      comments: '',
                      signature_company: '',
                      signature_customer: '',
                      date_company: new Date().toISOString().split('T')[0],
                      date_customer: new Date().toISOString().split('T')[0]
                    });
                  }}
                >
                  Hætta við
                </Button>
                
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="border-blue-600 text-blue-600 hover:bg-blue-50"
                    onClick={async () => {
                      try {
                        console.log('Generating loan agreement (from form) for:', selectedJobForAgreement);
                        
                        // Create a temporary job structure for PDF generation
                        const tempJobData = {
                          owner_name: printRenterInfo?.name || selectedJobForAgreement.owner_name,
                          owner_kt: printRenterInfo?.kennitala || selectedJobForAgreement.owner_kt,
                          phone_number: printRenterInfo?.phone || selectedJobForAgreement.phone_number,
                          plate: selectedJobForAgreement.plate,
                          loan_car_id: selectedJobForAgreement.loan_car_id,
                          pickup_address: formData.pickup_address || '',
                          ...loanAgreementData
                        };
                        
                        const response = await axios.post(`${API}/loan-cars/generate-agreement-preview`, tempJobData, {
                          responseType: 'blob'
                        });
                        
                        console.log('Response received:', response);
                        
                        // Open PDF in new tab
                        const blob = new Blob([response.data], { type: 'application/pdf' });
                        const url = window.URL.createObjectURL(blob);
                        window.open(url, '_blank');
                        
                        setTimeout(() => {
                          window.URL.revokeObjectURL(url);
                        }, 1000);
                        
                        toast.success('PDF lánaskilmála opnað í nýjum tab!');
                      } catch (error) {
                        console.error('Failed to generate loan agreement:', error);
                        console.error('Error response:', error.response);
                        toast.error(`Villa við að útbúa PDF: ${error.response?.status || error.message}`);
                      }
                    }}
                  >
                    📄 Prenta
                  </Button>
                  
                  <Button
                    className="bg-blue-600 hover:bg-blue-700"
                    disabled={saving}
                    onClick={async () => {
                      try {
                        setSaving(true);
                        const response = await axios.post(`${API}/loan-cars/create-signature-request`, {
                          owner_name: selectedJobForAgreement.owner_name,
                          owner_kt: selectedJobForAgreement.owner_kt,
                          phone_number: selectedJobForAgreement.phone_number,
                          plate: selectedJobForAgreement.plate,
                          pickup_address: formData.pickup_address,
                          loan_car_id: selectedJobForAgreement.loan_car_id,
                          renter_name: printRenterInfo?.name || null,
                          renter_kennitala: printRenterInfo?.kennitala || null,
                          renter_phone: printRenterInfo?.phone || null,
                          ...loanAgreementData
                        });
                        
                        const signatureUrl = response.data.signature_url;
                        
                        // Close this dialog and show signature link dialog
                        setLoanAgreementDialogOpen(false);
                        setSignatureLink(signatureUrl);
                        setSignatureLinkDialogOpen(true);
                        
                        toast.success('Undirskriftar link búinn til!');
                      } catch (error) {
                        console.error('Failed to create signature request:', error);
                        toast.error('Villa við að búa til undirskriftar link');
                      } finally {
                        setSaving(false);
                      }
                    }}
                  >
                    ✍️ Senda rafrænt
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      
      {/* Signature Link Dialog */}
      <Dialog open={signatureLinkDialogOpen} onOpenChange={(open) => {
        setSignatureLinkDialogOpen(open);
        if (!open) {
          // Reset SMS states when dialog closes
          setSendSMS(false);
          setSelectedPhoneNumber('');
          setCustomPhoneNumber('');
          setSmsResult(null);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>✍️ Rafræn undirritun</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Undirskriftar linkur hefur verið búinn til. Deildu þessum link með viðskiptavini til að undirrita rafrænt.
            </p>
            
            <div className="p-3 bg-slate-100 rounded-lg">
              <Label className="text-xs">Undirskriftar link:</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={signatureLink}
                  readOnly
                  className="font-mono text-xs"
                />
                <Button
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(signatureLink);
                    toast.success('Link afritaður!');
                  }}
                  variant="outline"
                >
                  📋 Afrita
                </Button>
              </div>
            </div>
            
            {/* SMS Option */}
            <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-lg space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="send-sms-checkbox"
                  checked={sendSMS}
                  onChange={(e) => {
                    setSendSMS(e.target.checked);
                    if (e.target.checked) {
                      // Auto-select phone number if only one available
                      const renterPhone = printRenterInfo?.phone;
                      const ownerPhone = formData.phone_number;
                      if (renterPhone && !ownerPhone) {
                        setSelectedPhoneNumber(renterPhone);
                      } else if (ownerPhone && !renterPhone) {
                        setSelectedPhoneNumber(ownerPhone);
                      }
                    }
                  }}
                  className="w-4 h-4"
                />
                <Label htmlFor="send-sms-checkbox" className="text-sm font-medium cursor-pointer">
                  📱 Senda linkinn í SMS líka
                </Label>
              </div>
              
              {sendSMS && (
                <div className="space-y-3 pt-2">
                  <div>
                    <Label className="text-xs text-slate-600">Veldu símanúmer:</Label>
                    <div className="space-y-2 mt-2">
                      {/* Owner phone */}
                      {formData.phone_number && (
                        <div className="flex items-center gap-2">
                          <input
                            type="radio"
                            id="phone-owner"
                            name="phone-selection"
                            value={formData.phone_number}
                            checked={selectedPhoneNumber === formData.phone_number}
                            onChange={(e) => setSelectedPhoneNumber(e.target.value)}
                            className="w-4 h-4"
                          />
                          <Label htmlFor="phone-owner" className="text-sm cursor-pointer">
                            {formData.owner_name} (Eigandi): {formData.phone_number}
                          </Label>
                        </div>
                      )}
                      
                      {/* Renter phone */}
                      {printRenterInfo?.phone && (
                        <div className="flex items-center gap-2">
                          <input
                            type="radio"
                            id="phone-renter"
                            name="phone-selection"
                            value={printRenterInfo.phone}
                            checked={selectedPhoneNumber === printRenterInfo.phone}
                            onChange={(e) => setSelectedPhoneNumber(e.target.value)}
                            className="w-4 h-4"
                          />
                          <Label htmlFor="phone-renter" className="text-sm cursor-pointer">
                            {printRenterInfo.name} (Leigjandi): {printRenterInfo.phone}
                          </Label>
                        </div>
                      )}
                      
                      {/* Custom phone number */}
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          id="phone-custom"
                          name="phone-selection"
                          value="custom"
                          checked={selectedPhoneNumber === 'custom'}
                          onChange={(e) => {
                            setSelectedPhoneNumber('custom');
                            setCustomPhoneNumber('');
                          }}
                          className="w-4 h-4"
                        />
                        <Label htmlFor="phone-custom" className="text-sm cursor-pointer">
                          Annað símanúmer
                        </Label>
                      </div>
                      
                      {/* Custom phone input */}
                      {selectedPhoneNumber === 'custom' && (
                        <div className="ml-6">
                          <Input
                            type="tel"
                            value={customPhoneNumber}
                            onChange={(e) => setCustomPhoneNumber(e.target.value.replace(/[^0-9+]/g, ''))}
                            placeholder="t.d. 8885544 eða +3548885544"
                            className="text-sm"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <Button
                    onClick={handleSendSignatureSMS}
                    disabled={sendingSMS || !selectedPhoneNumber || (selectedPhoneNumber === 'custom' && !customPhoneNumber)}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    size="sm"
                  >
                    {sendingSMS ? '⏳ Sendir SMS...' : '📱 Senda SMS'}
                  </Button>
                  
                  {smsResult && (
                    <div className={`p-2 rounded text-sm ${smsResult.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {smsResult.success ? '✅' : '❌'} {smsResult.message}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={() => window.open(signatureLink, '_blank')}
                className="flex-1"
              >
                🔗 Opna link
              </Button>
              <Button
                onClick={() => setSignatureLinkDialogOpen(false)}
                variant="outline"
                className="flex-1"
              >
                Loka
              </Button>
            </div>
            
            <p className="text-xs text-slate-500">
              💡 Þessi linkur er gildur í 7 daga. Eftir undirritun getur þú sótt undirritaða samninginn.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Renter Dialog for Print */}
      <Dialog open={printRenterDialogOpen} onOpenChange={setPrintRenterDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Breyta leigjanda</DialogTitle>
            <p className="text-sm text-slate-600">
              Ef leigjandi er annar en skráður eigandi ökutækis, vinsamlegast fylltu út upplýsingar hér að neðan.
            </p>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="print_renter_kennitala">Kennitala *</Label>
              <div className="flex gap-2">
                <Input
                  id="print_renter_kennitala"
                  value={tempPrintRenterInfo.kennitala}
                  onChange={(e) => setTempPrintRenterInfo(prev => ({...prev, kennitala: e.target.value.replace(/[^0-9]/g, '')}))}
                  placeholder="t.d. 0101234567"
                  maxLength={10}
                />
                <Button
                  type="button"
                  onClick={handlePrintKennitalaLookup}
                  variant="outline"
                  disabled={tempPrintRenterInfo.kennitala.length !== 10}
                >
                  🔍 Leita
                </Button>
              </div>
            </div>
            <div>
              <Label htmlFor="print_renter_name">Nafn leigjanda *</Label>
              <Input
                id="print_renter_name"
                value={tempPrintRenterInfo.name}
                onChange={(e) => setTempPrintRenterInfo(prev => ({...prev, name: e.target.value}))}
                placeholder="t.d. Jón Jónsson"
              />
            </div>
            <div>
              <Label htmlFor="print_renter_phone">Símanúmer *</Label>
              <Input
                id="print_renter_phone"
                value={tempPrintRenterInfo.phone}
                onChange={(e) => setTempPrintRenterInfo(prev => ({...prev, phone: e.target.value}))}
                placeholder="t.d. 8885544"
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              onClick={() => setPrintRenterDialogOpen(false)}
            >
              Hætta við
            </Button>
            <Button
              onClick={handleSavePrintRenter}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Vista leigjanda
            </Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* QR Code Upload Dialog */}
      <Dialog open={showQrDialog} onOpenChange={(open) => {
        if (!open) closeQrDialog();
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>📱 Skannaðu QR kóða með símanum</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Skannaðu þennan kóða með myndavél símans til að hlaða upp myndum.
            </p>
            
            {/* QR Code */}
            {qrSessionId && (
              <div className="flex justify-center p-6 bg-white rounded-lg border-2 border-slate-200">
                <QRCodeSVG 
                  value={qrUploadUrl} 
                  size={256}
                  level="M"
                />
              </div>
            )}
            
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">1</div>
                <span>Opnaðu myndavél á símanum</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">2</div>
                <span>Skannaðu QR kóðann</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">3</div>
                <span>Veldu myndir og smelltu á "Senda"</span>
              </div>
            </div>
            
            <div className="flex items-center justify-center gap-2 p-3 bg-blue-50 rounded-lg">
              <div className="animate-pulse w-2 h-2 bg-blue-500 rounded-full"></div>
              <span className="text-sm text-blue-700 font-medium">Bíður eftir myndum frá síma...</span>
            </div>
            
            <div className="text-xs text-center text-slate-500">
              QR kóðinn rennur út eftir 15 mínútur
            </div>
            
            <Button
              onClick={closeQrDialog}
              variant="outline"
              className="w-full"
            >
              Hætta við
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Live Camera Modal for Plate Scanning */}
      {liveCameraOpen && (
        <div className="fixed inset-0 bg-black z-[9999] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 bg-gradient-to-b from-black/80 to-transparent absolute top-0 left-0 right-0 z-10">
            <div>
              <h2 className="text-white text-lg font-bold flex items-center gap-2">
                📷 Sjálfvirk skönnun
              </h2>
              {autoScanning && (
                <p className="text-green-400 text-sm animate-pulse">✨ Skannar sjálfkrafa - beindu að plötu!</p>
              )}
            </div>
            <button
              onClick={closeLiveCamera}
              className="bg-white/10 hover:bg-white/20 text-white rounded-full w-10 h-10 flex items-center justify-center"
            >
              ✕
            </button>
          </div>

          {/* Video Preview */}
          <div className="flex-1 relative flex items-center justify-center">
            <video ref={liveCameraVideoRef} autoPlay playsInline className="max-w-full max-h-full" />
            
            {/* Scanning overlay */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              {/* Corner brackets - larger for plates */}
              <div className="relative w-80 h-28">
                <div className={`absolute top-0 left-0 w-10 h-10 border-t-4 border-l-4 rounded-tl-lg transition-colors ${autoScanning ? 'border-green-400' : 'border-white/50'}`} />
                <div className={`absolute top-0 right-0 w-10 h-10 border-t-4 border-r-4 rounded-tr-lg transition-colors ${autoScanning ? 'border-green-400' : 'border-white/50'}`} />
                <div className={`absolute bottom-0 left-0 w-10 h-10 border-b-4 border-l-4 rounded-bl-lg transition-colors ${autoScanning ? 'border-green-400' : 'border-white/50'}`} />
                <div className={`absolute bottom-0 right-0 w-10 h-10 border-b-4 border-r-4 rounded-br-lg transition-colors ${autoScanning ? 'border-green-400' : 'border-white/50'}`} />
                
                {/* Scanning animation */}
                {autoScanning && (
                  <div className="absolute inset-0 border-2 border-green-400/50 rounded animate-pulse" />
                )}
              </div>
            </div>

            {/* Instructions */}
            <div className="absolute bottom-28 left-0 right-0 text-center">
              <p className="text-white bg-black/60 inline-block px-6 py-3 rounded-full text-base font-medium">
                🎯 Haltu plötunni innan rammans
              </p>
            </div>
          </div>

          {/* Bottom controls */}
          <div className="p-6 flex items-center justify-center gap-6 bg-gradient-to-t from-black/80 to-transparent absolute bottom-0 left-0 right-0">
            {/* Torch button */}
            {torchSupported && (
              <button
                onClick={toggleTorch}
                className={`w-14 h-14 rounded-full flex items-center justify-center text-2xl transition-colors ${
                  torchOn 
                    ? 'bg-yellow-400 text-yellow-900' 
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                {torchOn ? '🔦' : '💡'}
              </button>
            )}
            
            {/* Capture button */}
            <button
              onClick={captureLivePhoto}
              disabled={scanningPlate}
              className="w-16 h-16 rounded-full bg-white hover:bg-slate-100 text-slate-900 shadow-xl flex items-center justify-center text-2xl"
            >
              {scanningPlate ? '⏳' : '📸'}
            </button>
            
            {/* Placeholder for symmetry when torch not supported */}
            {torchSupported && <div className="w-14 h-14" />}
          </div>
          
          {/* Torch hint */}
          <div className="absolute bottom-2 left-0 right-0 text-center">
            <p className="text-white/50 text-xs">
              {torchSupported 
                ? (torchOn ? 'Ljós kveikt' : 'Ýttu á 💡 fyrir ljós') 
                : 'Sjálfvirk skönnun virk'}
            </p>
          </div>
          
          <canvas ref={liveCameraCanvasRef} style={{ display: 'none' }} />
        </div>
      )}

    </div>
  );
}

// Countdown Timer Component
const CountdownTimer = ({ readyByTime, scheduledDate }) => {
  const [timeRemaining, setTimeRemaining] = useState('');
  const [isOverdue, setIsOverdue] = useState(false);

  useEffect(() => {
    const calculateTimeRemaining = () => {
      try {
        // Get current time
        const now = new Date();
        
        // Parse scheduled date and ready_by_time
        const targetDate = scheduledDate ? new Date(scheduledDate + 'T' + readyByTime) : new Date();
        targetDate.setHours(parseInt(readyByTime.split(':')[0]), parseInt(readyByTime.split(':')[1]), 0, 0);
        
        // Calculate difference in milliseconds
        const diff = targetDate - now;
        
        if (diff < 0) {
          // Overdue
          setIsOverdue(true);
          const absDiff = Math.abs(diff);
          const hours = Math.floor(absDiff / (1000 * 60 * 60));
          const minutes = Math.floor((absDiff % (1000 * 60 * 60)) / (1000 * 60));
          setTimeRemaining(`${hours}t ${minutes}m of tíma`);
        } else {
          // Time remaining
          setIsOverdue(false);
          const hours = Math.floor(diff / (1000 * 60 * 60));
          const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          setTimeRemaining(`${hours}t ${minutes}m eftir`);
        }
      } catch (error) {
        console.error('Countdown error:', error);
        setTimeRemaining('');
      }
    };

    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [readyByTime, scheduledDate]);

  if (!timeRemaining) return null;

  return (
    <span className={`text-xs font-medium px-2 py-1 rounded ${
      isOverdue 
        ? 'bg-red-100 text-red-700' 
        : 'bg-green-100 text-green-700'
    }`}>
      ⏱️ {timeRemaining}
    </span>
  );
};

// Calendar View Component

// Accounting Management Component - Combines Payday and DK
function AccountingManagement() {
  const [activeTab, setActiveTab] = useState('payday');

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">📊 Bókhaldskerfi</h1>
          <p className="text-slate-600">Stjórna bókhaldi og reikningagerð</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="payday">💳 Payday</TabsTrigger>
          <TabsTrigger value="dk">📊 DK Plus ERP</TabsTrigger>
        </TabsList>

        <TabsContent value="payday" className="space-y-4">
          <PaydayManagement />
        </TabsContent>

        <TabsContent value="dk" className="space-y-4">
          <DKManagement />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Comprehensive Payday Management Component
function PaydayManagement() {
  const [paydayStatus, setPaydayStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [syncingCustomer, setSyncingCustomer] = useState(null);
  const [creatingInvoice, setCreatingInvoice] = useState(null);
  const [recordingPayment, setRecordingPayment] = useState(null);
  const [accountingSystemName, setAccountingSystemName] = useState('Bókhaldskerfið'); // Display name for accounting system
  const [invoiceSearch, setInvoiceSearch] = useState('');

  // Dialog state for sending invoice emails from Payday view
  const [paydayInvoiceEmailDialogOpen, setPaydayInvoiceEmailDialogOpen] = useState(false);
  const [paydayInvoiceEmailAddress, setPaydayInvoiceEmailAddress] = useState('');
  const [sendingPaydayInvoiceEmail, setSendingPaydayInvoiceEmail] = useState(false);
  const [selectedPaydayInvoiceJob, setSelectedPaydayInvoiceJob] = useState(null);

  // Helper til að sækja Payday reikning PDF með auth (axios sendir token)
  const downloadPaydayInvoicePdf = async (job) => {
    if (!job?.id) return;
    try {
      const response = await axios.get(`${API}/payday/invoices/${job.id}/pdf`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download Payday invoice PDF:', error);
      toast.error('Tókst ekki að sækja Payday reikning');
    }
  };

  useEffect(() => {
    loadPaydayData();
    loadAccountingSystem();
  }, []);

  const loadPaydayData = async () => {
    try {
      const [statusRes, customersRes, jobsRes] = await Promise.all([
        axios.get(`${API}/payday/status`),
        axios.get(`${API}/customers`),
        axios.get(`${API}/jobs`)
      ]);
      
      // Backend now returns paginated response
      const customersData = customersRes.data.customers || customersRes.data || [];
      const jobsData = jobsRes.data.jobs || jobsRes.data || [];
      
      setPaydayStatus(statusRes.data);
      setCustomers(customersData);
      setJobs(jobsData.filter(job => job.status === 'done' || job.status === 'paid') || []);
    } catch (error) {
      toast.error('Tókst ekki að hlaða Payday gögnum');
    } finally {
      setLoading(false);
    }
  };

  // Filtered jobs for invoice list with search support
  const filteredInvoiceJobs = useMemo(() => jobs.filter(job => {
    if (!invoiceSearch) return job.status === 'done';
    const q = invoiceSearch.toLowerCase();
    return (
      (job.plate && job.plate.toLowerCase().includes(q)) ||
      (job.payer_name && job.payer_name.toLowerCase().includes(q)) ||
      (job.owner_name && job.owner_name.toLowerCase().includes(q)) ||
      (job.invoice_number && String(job.invoice_number).toLowerCase().includes(q))
    );
  }), [jobs, invoiceSearch]);

  const handleSendInvoiceEmailFromPayday = async () => {
    if (!selectedPaydayInvoiceJob) return;

    const emailToSend = paydayInvoiceEmailAddress || selectedPaydayInvoiceJob.payer_email || selectedPaydayInvoiceJob.owner_email;

    if (!emailToSend || !emailToSend.includes('@')) {
      toast.error('Vinsamlegast sláðu inn gilt netfang');
      return;
    }

    try {
      setSendingPaydayInvoiceEmail(true);
      toast.loading('Sendi reikning á tölvupóst...');

      const response = await axios.post(`${API}/jobs/${selectedPaydayInvoiceJob.id}/send-invoice-email`, {
        email: emailToSend
      });

      toast.dismiss();

      if (response.data.success) {
        toast.success(`✅ Reikningur sendur á ${emailToSend}`);
        setPaydayInvoiceEmailDialogOpen(false);
        setPaydayInvoiceEmailAddress('');
        setSelectedPaydayInvoiceJob(null);
        await loadPaydayData();
      } else {
        toast.error('Tókst ekki að senda reikning');
      }
    } catch (error) {
      toast.dismiss();
      console.error('Payday invoice email error:', error);
      toast.error('Tókst ekki að senda reikning: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSendingPaydayInvoiceEmail(false);
    }
  };

  const loadAccountingSystem = async () => {
    try {
      const response = await axios.get(`${API}/accounting/status`);
      const system = response.data.accounting_system;
      const systems = response.data.systems || {};
      
      // Priority: Show the system that is NOT in mock mode, or the one that is connected
      let activeName = 'Bókhaldskerfið'; // Default
      
      if (system === 'DK') {
        activeName = 'DK';
      } else if (system === 'PAYDAY') {
        activeName = 'Payday';
      } else if (system === 'BOTH') {
        // Check which one is actually being used (not mock)
        const dkInMock = systems.dk?.message === 'Mock mode';
        const paydayInMock = systems.payday?.message === 'Mock mode';
        
        if (!dkInMock && systems.dk?.enabled) {
          activeName = 'DK'; // DK is real, use it
        } else if (!paydayInMock && systems.payday?.enabled) {
          activeName = 'Payday'; // Payday is real, use it
        } else if (systems.payday?.enabled) {
          activeName = 'Payday'; // Default to Payday if both are mock
        } else if (systems.dk?.enabled) {
          activeName = 'DK';
        }
      }
      
      setAccountingSystemName(activeName);
    } catch (error) {
      console.error('Error loading accounting system:', error);
      setAccountingSystemName('Bókhaldskerfið'); // Default
    }
  };

  const syncCustomerToPayday = async (customerId) => {
    setSyncingCustomer(customerId);
    try {
      const response = await axios.post(`${API}/payday/sync-customer`, { customer_id: customerId });
      toast.success('Viðskiptavinur samstilltur við Payday');
      loadPaydayData(); // Refresh data
    } catch (error) {
      toast.error('Tókst ekki að samstilla viðskiptavin');
    } finally {
      setSyncingCustomer(null);
    }
  };

  const createInvoiceInPayday = async (jobId) => {
    setCreatingInvoice(jobId);
    try {
      const response = await axios.post(`${API}/payday/create-invoice`, { job_id: jobId });
      toast.success('Reikningur búinn til í Payday');
      loadPaydayData(); // Refresh data
    } catch (error) {
      toast.error('Tókst ekki að búa til reikning');
    } finally {
      setCreatingInvoice(null);
    }
  };

  const recordPaymentInPayday = async (jobId, amount) => {
    setRecordingPayment(jobId);
    try {
      const response = await axios.post(`${API}/payday/record-payment`, { 
        job_id: jobId, 
        amount: amount,
        payment_method: 'card'
      });
      toast.success('Greiðsla skráð í Payday');
      loadPaydayData(); // Refresh data
    } catch (error) {
      toast.error('Tókst ekki að skrá greiðslu');
    } finally {
      setRecordingPayment(null);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-8">Hleður Payday samþættingu...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end items-center">
        <Button onClick={loadPaydayData} variant="outline" size="sm">
          🔄 Uppfæra Payday gögn
        </Button>
      </div>

      {/* Payday Status */}
      <Card>
        <CardHeader>
          <CardTitle>Payday Staða</CardTitle>
        </CardHeader>
        <CardContent>
          {paydayStatus ? (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className={`text-2xl font-bold ${paydayStatus.status === 'connected' ? 'text-green-600' : 'text-red-600'}`}>
                  {paydayStatus.status === 'connected' ? '✅' : '❌'}
                </div>
                <div className="text-sm text-slate-600">
                  {paydayStatus.status === 'connected' ? 'Tengt' : 'Ekki tengt'}
                </div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-blue-600">
                  {paydayStatus.mode?.toUpperCase()}
                </div>
                <div className="text-sm text-slate-600">Hamur</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-purple-600">
                  {paydayStatus.api_url ? '🌐' : '❌'}
                </div>
                <div className="text-sm text-slate-600">API Tengill</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-green-600">
                  {paydayStatus.last_sync ? '🕒' : '⏳'}
                </div>
                <div className="text-sm text-slate-600">Síðasta samstilling</div>
              </div>
            </div>
          ) : (
            <div className="text-center text-red-600">Tókst ekki að hlaða Payday stöðu</div>
          )}
          
          {paydayStatus?.message && (
            <div className="mt-4 p-3 bg-blue-50 text-blue-700 rounded-lg text-sm">
              {paydayStatus.message}
            </div>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="customers">
        <TabsList>
          <TabsTrigger value="customers">Viðskiptavinir</TabsTrigger>
          <TabsTrigger value="invoices">Reikningar</TabsTrigger>
          <TabsTrigger value="payments">Greiðslur</TabsTrigger>
        </TabsList>

        {/* Customers Tab */}
        <TabsContent value="customers" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Viðskiptavinir - Payday Samstilling</CardTitle>
              <p className="text-sm text-slate-600">
                Samstilltu viðskiptavini við Payday til að búa til reikninga
              </p>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nafn</TableHead>
                    <TableHead>Kennitala</TableHead>
                    <TableHead>Netfang</TableHead>
                    <TableHead>Payday ID</TableHead>
                    <TableHead>Samstillt</TableHead>
                    <TableHead>Aðgerðir</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customers.map(customer => (
                    <TableRow key={customer.id}>
                      <TableCell className="font-medium">{customer.name}</TableCell>
                      <TableCell>{customer.kennitala}</TableCell>
                      <TableCell>{customer.email || '—'}</TableCell>
                      <TableCell>
                        {customer.payday_customer_id ? (
                          <Badge variant="success">
                            {customer.payday_customer_id}
                          </Badge>
                        ) : (
                          <Badge variant="secondary">Ekki samstillt</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {customer.payday_synced_at ? (
                          <span className="text-sm text-slate-600">
                            {new Date(customer.payday_synced_at).toLocaleDateString('is')}
                          </span>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          onClick={() => syncCustomerToPayday(customer.id)}
                          disabled={syncingCustomer === customer.id}
                        >
                          {syncingCustomer === customer.id ? 'Samstillir...' : 'Samstilla'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Reikningar - Payday Samstilling</CardTitle>
              <p className="text-sm text-slate-600">
                Búðu til reikninga í Payday fyrir lokið verk og sjáðu stöðu þeirra
              </p>
              <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <p className="text-xs text-slate-500">
                  Leitaðu eftir númeraplötu, greiðanda/eiganda, verk ID eða Payday reikningsnúmeri
                </p>
                <div className="flex items-center gap-2 w-full sm:w-64">
                  <Input
                    placeholder="Leita... t.d. Z1001 eða 12345"
                    value={invoiceSearch}
                    onChange={(e) => setInvoiceSearch(e.target.value)}
                    className="text-sm"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Verk ID</TableHead>
                    <TableHead>Númeraplata</TableHead>
                    <TableHead>Greiðandi</TableHead>
                    <TableHead>Heildarupphæð</TableHead>
                    <TableHead>{accountingSystemName}</TableHead>
                    <TableHead>Aðgerðir</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInvoiceJobs.map(job => (
                    <TableRow key={job.id}>
                      <TableCell className="font-mono text-sm">{job.invoice_number || job.id.slice(-8)}</TableCell>
                      <TableCell className="font-medium">
                        <div>
                          <div>{job.plate}</div>
                          {(job.car_make || job.car_model) && (
                            <div className="text-xs text-slate-500">
                              {[job.car_make, job.car_model].filter(Boolean).join(' ')}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{job.payer_name || job.owner_name}</TableCell>
                      <TableCell className="font-medium">{job.total?.toLocaleString()} ISK</TableCell>
                      <TableCell>
                        {job.payday_invoice_id ? (
                          <Badge variant="success">
                            {job.payday_invoice_id}
      {/* Payday Invoice Email Dialog */}
      <Dialog open={paydayInvoiceEmailDialogOpen} onOpenChange={setPaydayInvoiceEmailDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Senda reikning á tölvupóst</DialogTitle>
            {selectedPaydayInvoiceJob && (
              <p className="text-sm text-slate-600 mt-1">
                {selectedPaydayInvoiceJob.plate || '—'} – {selectedPaydayInvoiceJob.payer_name || selectedPaydayInvoiceJob.owner_name || 'Viðskiptavinur'}
              </p>
            )}
          </DialogHeader>
          <div className="space-y-3 mt-3">
            <div>
              <Label>Netfang viðskiptavinar</Label>
              <Input
                type="email"
                placeholder="email@example.is"
                value={paydayInvoiceEmailAddress}
                onChange={(e) => setPaydayInvoiceEmailAddress(e.target.value)}
              />
              <p className="text-xs text-slate-500 mt-1">
                Ef ekkert er fyllt inn er notað netfang greiðanda/eiganda ef til er.
              </p>
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPaydayInvoiceEmailDialogOpen(false)}
              disabled={sendingPaydayInvoiceEmail}
            >
              Hætta við
            </Button>
            <Button
              size="sm"
              onClick={handleSendInvoiceEmailFromPayday}
              disabled={sendingPaydayInvoiceEmail}
            >
              {sendingPaydayInvoiceEmail ? 'Sendi...' : 'Senda reikning'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

                          </Badge>
                        ) : (
                          <Badge variant="secondary">Ekki búinn til</Badge>
                        )}
                      </TableCell>
                      <TableCell className="space-x-2 whitespace-nowrap">
                        <Button
                          size="sm"
                          onClick={() => createInvoiceInPayday(job.id)}
                          disabled={creatingInvoice === job.id || !!job.payday_invoice_id}
                        >
                          {creatingInvoice === job.id ? 'Býr til...' : 
                           job.payday_invoice_id ? 'Búinn til' : 'Búa til reikning'}
                        </Button>
                        {job.payday_payment_url && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => window.open(job.payday_payment_url, '_blank')}
                          >
                            🔗 Opna í Payday
                          </Button>
                        )}
                        {job.payday_invoice_id && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => downloadPaydayInvoicePdf(job)}
                            >
                              ⬇️ Sækja reikning
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedPaydayInvoiceJob(job);
                                setPaydayInvoiceEmailAddress(job.payer_email || job.owner_email || '');
                                setPaydayInvoiceEmailDialogOpen(true);
                              }}
                              title="Senda reikning á tölvupóst"
                              className="text-blue-600 border-blue-300 hover:bg-blue-50"
                            >
                              📧 Senda
                            </Button>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredInvoiceJobs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-6 text-sm text-slate-500">
                        Engir reikningar fundust með þessu svari.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Greiðslur - Payday Skráning</CardTitle>
              <p className="text-sm text-slate-600">
                Skráðu greiðslur í Payday fyrir greidd verk
              </p>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Verk ID</TableHead>
                    <TableHead>Númeraplata</TableHead>
                    <TableHead>Greiðandi</TableHead>
                    <TableHead>Upphæð</TableHead>
                    <TableHead>Payday Greiðsla</TableHead>
                    <TableHead>Aðgerðir</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {jobs.filter(job => job.status === 'paid').map(job => (
                    <TableRow key={job.id}>
                      <TableCell className="font-mono text-sm">{job.invoice_number || job.id.slice(-8)}</TableCell>
                      <TableCell className="font-medium">
                        <div>
                          <div>{job.plate}</div>
                          {(job.car_make || job.car_model) && (
                            <div className="text-xs text-slate-500">
                              {[job.car_make, job.car_model].filter(Boolean).join(' ')}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{job.payer_name || job.owner_name}</TableCell>
                      <TableCell className="font-medium">{job.total?.toLocaleString()} ISK</TableCell>
                      <TableCell>
                        {job.payday_payment_id ? (
                          <Badge variant="success">
                            {job.payday_payment_id}
                          </Badge>
                        ) : (
                          <Badge variant="secondary">Ekki skráð</Badge>
                        )}
                      </TableCell>
                      <TableCell className="space-x-2 whitespace-nowrap">
                        <Button
                          size="sm"
                          onClick={() => recordPaymentInPayday(job.id, job.total)}
                          disabled={recordingPayment === job.id || !!job.payday_payment_id}
                        >
                          {recordingPayment === job.id ? 'Skráir...' : 
                           job.payday_payment_id ? 'Skráð' : 'Skrá greiðslu'}
                        </Button>
                        {job.payday_invoice_id && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => downloadPaydayInvoicePdf(job)}
                          >
                            ⬇️ Sækja reikning
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Comprehensive User Management Component
function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPermissions, setShowPermissions] = useState(false);
  const [availablePermissions, setAvailablePermissions] = useState({});
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(true);
  
  // 2FA setup state
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [setup2FAUser, setSetup2FAUser] = useState(null);
  const [setup2FAData, setSetup2FAData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying2FA, setVerifying2FA] = useState(false);
  
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'staff'
  });
  
  const [sendInvitation, setSendInvitation] = useState(true); // Default to sending invitation
  
  const [permissions, setPermissions] = useState({});

  useEffect(() => {
    loadUsers();
    loadAvailablePermissions();
    loadAuditLogs();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data || []);
    } catch (error) {
      toast.error('Tókst ekki að hlaða notendum');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailablePermissions = async () => {
    try {
      const response = await axios.get(`${API}/permissions/list`);
      setAvailablePermissions(response.data.permissions || {});
    } catch (error) {
      console.error('Failed to load permissions:', error);
    }
  };

  const loadAuditLogs = async () => {
    setAuditLoading(true);
    try {
      const response = await axios.get(`${API}/audit-logs?limit=50`);
      setAuditLogs(response.data || []);
    } catch (error) {
      console.error('Failed to load audit logs:', error);
    } finally {
      setAuditLoading(false);
    }
  };

  const loadUserPermissions = async (userId) => {
    setPermissionsLoading(true);
    try {
      const response = await axios.get(`${API}/users/${userId}/permissions`);
      setPermissions(response.data.custom_permissions || {});
    } catch (error) {
      toast.error('Tókst ekki að hlaða heimildum');
    } finally {
      setPermissionsLoading(false);
    }
  };

  const validatePassword = (password) => {
    const minLength = password.length >= 10;
    const hasUpper = /[A-Z]/.test(password);
    const hasDigit = /\d/.test(password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const noSpaces = !/\s/.test(password);
    
    return minLength && hasUpper && hasDigit && hasSpecial && noSpaces;
  };

  const createUser = async (e) => {
    e.preventDefault();
    
    // If sending invitation, use the /users/invite endpoint
    if (sendInvitation) {
      try {
        await axios.post(`${API}/users/invite`, {
          name: newUser.name,
          email: newUser.email,
          role: newUser.role
        });
        toast.success(`Boð sent til ${newUser.email}`);
        setNewUser({ name: '', email: '', password: '', role: 'staff' });
        setShowAddForm(false);
        loadUsers();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Tókst ekki að senda boð');
      }
      return;
    }
    
    // Otherwise, create user with password
    if (!validatePassword(newUser.password)) {
      toast.error('Lykilorð þarf að vera a.m.k. 10 stafir með stórum staf, tölu, sérstafir og engin bil');
      return;
    }

    try {
      await axios.post(`${API}/users`, newUser);
      toast.success('Notandi búinn til');
      setNewUser({ name: '', email: '', password: '', role: 'staff' });
      setShowAddForm(false);
      loadUsers();
    } catch (error) {
      toast.error('Tókst ekki að búa til notanda');
    }
  };

  const updatePermissions = async () => {
    if (!selectedUser) return;

    try {
      await axios.put(`${API}/users/${selectedUser.id}/permissions`, { permissions });
      toast.success('Heimildir uppfærðar');
      setShowPermissions(false);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Tókst ekki að uppfæra heimildir');
    }
  };

  const deactivateUser = async (userId) => {
    if (!confirm('Ertu viss um að þú viljir afvirkja þennan notanda?')) return;

    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success('Notandi afvirkjaður');
      loadUsers();
    } catch (error) {
      toast.error('Tókst ekki að afvirkja notanda');
    }
  };

  const disable2FAForUser = async (userId, userName) => {
    if (!confirm(`Ertu viss um að þú viljir slökkva á tvíþættri auðkenningu (2FA) fyrir ${userName}?`)) return;

    try {
      await axios.post(`${API}/auth/2fa/admin-disable/${userId}`);
      toast.success(`2FA slökkt fyrir ${userName}`);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Tókst ekki að slökkva á 2FA');
    }
  };

  const startSetup2FA = async (user) => {
    try {
      const response = await axios.post(`${API}/auth/2fa/admin-setup/${user.id}`);
      setSetup2FAData(response.data);
      setSetup2FAUser(user);
      setShow2FASetup(true);
      setVerificationCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Tókst ekki að setja upp 2FA');
    }
  };

  const verify2FAForUser = async () => {
    if (verificationCode.length !== 6) {
      toast.error('Kóði verður að vera 6 tölustafir');
      return;
    }

    setVerifying2FA(true);
    try {
      await axios.post(`${API}/auth/2fa/admin-verify/${setup2FAUser.id}`, { code: verificationCode });
      toast.success(`✅ 2FA virkjað fyrir ${setup2FAUser.name}`);
      setShow2FASetup(false);
      setSetup2FAData(null);
      setSetup2FAUser(null);
      setVerificationCode('');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Rangur kóði');
    } finally {
      setVerifying2FA(false);
    }
  };

  const getRoleName = (role) => {
    const roles = {
      admin: 'Stjórnandi',
      manager: 'Yfirmaður',
      staff: 'Starfsmaður',
      viewer: 'Skoðandi'
    };
    return roles[role] || role;
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: 'bg-red-100 text-red-800',
      manager: 'bg-blue-100 text-blue-800',
      staff: 'bg-green-100 text-green-800',
      viewer: 'bg-gray-100 text-gray-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Notendastjórnun</h1>
        <Button onClick={() => setShowAddForm(true)}>
          <UserPlus className="h-4 w-4 mr-2" />
          Bæta við notanda
        </Button>
      </div>

      {/* Add User Form */}
      {showAddForm && (
        <Card>
          <CardHeader>
            <CardTitle>Bæta við nýjum notanda</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={createUser} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Nafn</Label>
                  <Input
                    id="name"
                    value={newUser.name}
                    onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email">Netfang</Label>
                  <Input
                    id="email"
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="role">Hlutverk</Label>
                  <Select onValueChange={(value) => setNewUser({...newUser, role: value})} value={newUser.role}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="staff">Starfsmaður</SelectItem>
                      <SelectItem value="manager">Yfirmaður</SelectItem>
                      <SelectItem value="admin">Stjórnandi</SelectItem>
                      <SelectItem value="viewer">Skoðandi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Invitation vs Manual Password Toggle */}
              <div className="border rounded-lg p-4 bg-blue-50">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="sendInvitation" 
                    checked={sendInvitation}
                    onCheckedChange={setSendInvitation}
                  />
                  <Label htmlFor="sendInvitation" className="text-sm font-medium cursor-pointer">
                    📧 Senda boð í tölvupósti (mælt með)
                  </Label>
                </div>
                <p className="text-xs text-slate-600 mt-2 ml-6">
                  {sendInvitation 
                    ? 'Notandi fær tölvupóst með hlekk til að búa til sitt eigið lykilorð'
                    : 'Þú býrð til lykilorð fyrir notandann (minna öruggt)'
                  }
                </p>
              </div>
              
              {/* Manual Password Fields - Only shown if not sending invitation */}
              {!sendInvitation && (
                <div>
                  <Label htmlFor="password">Lykilorð</Label>
                  <Input
                    id="password"
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    required={!sendInvitation}
                  />
                  <div className="text-xs text-slate-600 mt-1">
                    A.m.k. 10 stafir, stór stafur, tala, sérstafur, engin bil
                  </div>
                </div>
              )}
              
              <div className="flex space-x-2">
                <Button 
                  type="submit" 
                  disabled={!sendInvitation && !validatePassword(newUser.password)}
                >
                  {sendInvitation ? '📧 Senda boð' : 'Búa til notanda'}
                </Button>
                <Button type="button" variant="outline" onClick={() => {
                  setShowAddForm(false);
                  setSendInvitation(true); // Reset to default
                }}>
                  Hætta við
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Users List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-8">Hleður notendum...</div>
          ) : users.length === 0 ? (
            <div className="text-center py-8 text-slate-500">Engir notendur fundust</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nafn</TableHead>
                  <TableHead>Netfang</TableHead>
                  <TableHead>Hlutverk</TableHead>
                  <TableHead>2FA</TableHead>
                  <TableHead>Stöða</TableHead>
                  <TableHead>Síðasta innskráning</TableHead>
                  <TableHead>Aðgerðir</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map(user => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Badge className={getRoleColor(user.role)}>
                        {getRoleName(user.role)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {user.two_factor_enabled ? (
                        <Badge className="bg-green-100 text-green-800">🔐 Virkt</Badge>
                      ) : (
                        <Badge variant="secondary" className="text-slate-500">Óvirkt</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={user.is_active ? 'success' : 'destructive'}>
                        {user.is_active ? 'Virkur' : 'Óvirkur'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {user.last_login ? new Date(user.last_login).toLocaleDateString('is') : 'Aldrei'}
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        {(user.role === 'manager' || user.role === 'staff') && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedUser(user);
                              loadUserPermissions(user.id);
                              setShowPermissions(true);
                            }}
                          >
                            ⚙️ Heimildir
                          </Button>
                        )}
                        {user.two_factor_enabled ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-amber-600 border-amber-300 hover:bg-amber-50"
                            onClick={() => disable2FAForUser(user.id, user.name)}
                          >
                            🔓 Slökkva 2FA
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-600 border-green-300 hover:bg-green-50"
                            onClick={() => startSetup2FA(user)}
                          >
                            🔐 Virkja 2FA
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => deactivateUser(user.id)}
                        >
                          Afvirkja
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* 2FA Setup Dialog */}
      {show2FASetup && setup2FAUser && (
        <Dialog open={show2FASetup} onOpenChange={setShow2FASetup}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                🔐 Setja upp 2FA fyrir {setup2FAUser.name}
              </DialogTitle>
              <p className="text-sm text-slate-500">
                Láttu notandann skanna QR kóðann með Authenticator appi
              </p>
            </DialogHeader>

            {setup2FAData && (
              <div className="space-y-6 py-4">
                {/* QR Code */}
                <div className="flex justify-center">
                  <div className="p-4 bg-white rounded-xl shadow-inner border">
                    <img 
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(setup2FAData.qr_uri)}`}
                      alt="2FA QR Code"
                      className="w-48 h-48"
                    />
                  </div>
                </div>

                {/* Secret key for manual entry */}
                <div className="space-y-2">
                  <p className="text-xs text-slate-500 text-center">
                    Eða sláðu inn leynilykil handvirkt:
                  </p>
                  <div className="bg-slate-100 p-2 rounded text-center font-mono text-sm break-all">
                    {setup2FAData.secret}
                  </div>
                </div>

                {/* Verification code input */}
                <div className="space-y-2">
                  <Label>Staðfestingarkóði</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="000000"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                    className="text-center text-xl tracking-[0.5em] font-mono"
                  />
                  <p className="text-xs text-slate-500 text-center">
                    Sláðu inn 6 stafa kóðann úr appinu til að staðfesta
                  </p>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShow2FASetup(false)}>
                Hætta við
              </Button>
              <Button 
                onClick={verify2FAForUser} 
                disabled={verifying2FA || verificationCode.length !== 6}
              >
                {verifying2FA ? 'Staðfesti...' : '✓ Staðfesta og virkja'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Audit Log / Login History */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <span className="text-xl">🔐</span>
            </div>
            <div>
              <CardTitle className="text-lg">Innskráningarsaga</CardTitle>
              <p className="text-sm text-slate-500">Nýlegar innskráningar og misheppnaðar tilraunir</p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {auditLoading ? (
            <div className="text-center py-8">Hleður innskráningarsögu...</div>
          ) : auditLogs.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">Engin innskráningarsaga</p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {auditLogs.map((log, index) => (
                <div 
                  key={index}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    log.event_type === 'login_success' 
                      ? 'bg-green-50/50 border-green-100' 
                      : 'bg-red-50/50 border-red-100'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-lg">
                      {log.event_type === 'login_success' ? '✅' : '❌'}
                    </span>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{log.email || 'Óþekktur'}</span>
                        <span className="text-xs text-slate-500">
                          {log.event_type === 'login_success' ? 'Innskráning' : 'Misheppnuð tilraun'}
                        </span>
                      </div>
                      <div className="text-xs text-slate-400">
                        IP: {log.ip_address || 'Óþekkt'}
                      </div>
                    </div>
                  </div>
                  <div className="text-sm text-slate-500">
                    {log.created_at ? new Date(log.created_at).toLocaleDateString('is-IS', { 
                      day: 'numeric', 
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Permissions Dialog */}
      {showPermissions && selectedUser && (
        <Dialog open={showPermissions} onOpenChange={setShowPermissions}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Heimildir fyrir {selectedUser.name} ({getRoleName(selectedUser.role)})</DialogTitle>
              <p className="text-sm text-slate-500 mt-2">
                Hér getur þú stjórnað nákvæmum heimildum fyrir þennan notanda. 
                Sjálfgefnar heimildir eru settar miðað við hlutverk.
              </p>
            </DialogHeader>
            
            {permissionsLoading ? (
              <div className="text-center py-8">Hleður heimildum...</div>
            ) : (
              <div className="space-y-6">
                {/* Jobs Permissions */}
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold mb-3 flex items-center">
                    <span className="text-xl mr-2">📋</span> Verk
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {['view_jobs', 'create_jobs', 'edit_jobs', 'delete_jobs'].map(key => (
                      availablePermissions[key] && (
                        <div key={key} className="flex items-center space-x-2">
                          <Checkbox
                            id={key}
                            checked={permissions[key] === true}
                            onCheckedChange={(checked) => setPermissions({...permissions, [key]: checked})}
                          />
                          <Label htmlFor={key} className="text-sm">
                            {availablePermissions[key]}
                          </Label>
                        </div>
                      )
                    ))}
                  </div>
                </div>

                {/* Payments & Invoices Permissions */}
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold mb-3 flex items-center">
                    <span className="text-xl mr-2">💰</span> Greiðslur & Reikningar
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {['view_payments', 'process_payments', 'create_invoices', 'send_invoices', 'refund_payments'].map(key => (
                      availablePermissions[key] && (
                        <div key={key} className="flex items-center space-x-2">
                          <Checkbox
                            id={key}
                            checked={permissions[key] === true}
                            onCheckedChange={(checked) => setPermissions({...permissions, [key]: checked})}
                          />
                          <Label htmlFor={key} className="text-sm">
                            {availablePermissions[key]}
                          </Label>
                        </div>
                      )
                    ))}
                  </div>
                </div>

                {/* Customers Permissions */}
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold mb-3 flex items-center">
                    <span className="text-xl mr-2">👥</span> Viðskiptavinir
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {['view_customers', 'create_customers', 'edit_customers', 'delete_customers'].map(key => (
                      availablePermissions[key] && (
                        <div key={key} className="flex items-center space-x-2">
                          <Checkbox
                            id={key}
                            checked={permissions[key] === true}
                            onCheckedChange={(checked) => setPermissions({...permissions, [key]: checked})}
                          />
                          <Label htmlFor={key} className="text-sm">
                            {availablePermissions[key]}
                          </Label>
                        </div>
                      )
                    ))}
                  </div>
                </div>

                {/* Dealerships Permissions */}
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold mb-3 flex items-center">
                    <span className="text-xl mr-2">🚗</span> Bílasölur
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {['view_dealerships', 'manage_dealerships', 'send_confirmations'].map(key => (
                      availablePermissions[key] && (
                        <div key={key} className="flex items-center space-x-2">
                          <Checkbox
                            id={key}
                            checked={permissions[key] === true}
                            onCheckedChange={(checked) => setPermissions({...permissions, [key]: checked})}
                          />
                          <Label htmlFor={key} className="text-sm">
                            {availablePermissions[key]}
                          </Label>
                        </div>
                      )
                    ))}
                  </div>
                </div>

                {/* Settings Permissions */}
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold mb-3 flex items-center">
                    <span className="text-xl mr-2">⚙️</span> Stillingar
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {['view_settings', 'edit_settings', 'manage_users'].map(key => (
                      availablePermissions[key] && (
                        <div key={key} className="flex items-center space-x-2">
                          <Checkbox
                            id={key}
                            checked={permissions[key] === true}
                            onCheckedChange={(checked) => setPermissions({...permissions, [key]: checked})}
                          />
                          <Label htmlFor={key} className="text-sm">
                            {availablePermissions[key]}
                          </Label>
                        </div>
                      )
                    ))}
                  </div>
                </div>

                {/* Reports Permissions */}
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold mb-3 flex items-center">
                    <span className="text-xl mr-2">📊</span> Skýrslur
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {['view_reports', 'view_kennitala'].map(key => (
                      availablePermissions[key] && (
                        <div key={key} className="flex items-center space-x-2">
                          <Checkbox
                            id={key}
                            checked={permissions[key] === true}
                            onCheckedChange={(checked) => setPermissions({...permissions, [key]: checked})}
                          />
                          <Label htmlFor={key} className="text-sm">
                            {availablePermissions[key]}
                          </Label>
                        </div>
                      )
                    ))}
                  </div>
                </div>

                {/* Pricing Visibility for Staff */}
                {selectedUser.role === 'staff' && (
                  <div className="border rounded-lg p-4 bg-yellow-50 border-yellow-300">
                    <h3 className="font-semibold mb-3 flex items-center">
                      <span className="text-xl mr-2">💰</span> Verðsýnileiki (Staff)
                    </h3>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="can_see_prices"
                        checked={permissions['can_see_prices'] === true}
                        onCheckedChange={(checked) => setPermissions({...permissions, can_see_prices: checked})}
                      />
                      <Label htmlFor="can_see_prices" className="text-sm">
                        Getur séð verð í "Ný skráning"
                      </Label>
                    </div>
                    <p className="text-xs text-slate-600 mt-2">
                      💡 Ef þetta er virkt, sér þessi starfsmaður verð þegar hann skráir ný verk. Sjálfgefið er að staff sjái ekki verð.
                    </p>
                  </div>
                )}
              </div>
            )}
            
            <div className="flex justify-end space-x-2 mt-6">
              <Button onClick={updatePermissions} disabled={permissionsLoading}>
                💾 Vista heimildir
              </Button>
              <Button variant="outline" onClick={() => setShowPermissions(false)}>
                Hætta við
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ========================================================================================
// PRODUCTS MANAGEMENT (Vörunotkun - Supplies for Internal Use)
// ========================================================================================

function ProductsManagement() {
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [stockDialogOpen, setStockDialogOpen] = useState(false);
  const [stockUpdateProduct, setStockUpdateProduct] = useState(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyProduct, setHistoryProduct] = useState(null);
  const [productHistory, setProductHistory] = useState([]);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [inventoryActionDialog, setInventoryActionDialog] = useState(false);
  const [scannedProduct, setScannedProduct] = useState(null);
  const [inventoryAction, setInventoryAction] = useState(null); // 'refill' or 'update'
  const [inventoryAmount, setInventoryAmount] = useState('');
  const [inventoryContainerCount, setInventoryContainerCount] = useState(''); // Number of containers for refill
  const [inventoryPrice, setInventoryPrice] = useState('');
  const [priceChanged, setPriceChanged] = useState(false);
  const [useLiveScanner, setUseLiveScanner] = useState(false);
  const cameraInputRef = React.useRef(null);
  
  // Handle barcode scan button click - show option dialog
  const handleBarcodeScanClick = () => {
    setUseLiveScanner(true);
  };

  // Handle native camera capture
  const handleCameraCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setScanning(true);
    toast.info('⏳ Les strikamerki...');
    
    try {
      // Convert image to base64
      const reader = new FileReader();
      reader.onload = async (readerEvent) => {
        try {
          const base64Image = readerEvent.target.result.split(',')[1];
          
          // Send to GPT-5 Vision for barcode reading
          const response = await axios.post(`${API}/products/scan-barcode`, { 
            image: base64Image 
          });
          
          if (response.data.found) {
            // Product exists - show inventory action dialog
            const productData = response.data.product;
            console.log('📦 Product found:', productData);
            console.log('🔄 Opening inventory action dialog...');
            
            // Make sure edit dialog is closed
            setDialogOpen(false);
            setEditingProduct(null);
            
            // Open inventory action dialog
            setScannedProduct(productData);
            setInventoryActionDialog(true);
            setInventoryAction(null); // Reset action state
            setInventoryAmount('');
            setInventoryPrice('');
            setPriceChanged(false);
            
            console.log('✅ Dialog state set to true');
            toast.success(`✅ Vara fundin: ${productData.name}`);
          } else if (response.data.barcode) {
            // Barcode read but not in database
            setEditingProduct({ barcode: response.data.barcode });
            setDialogOpen(true);
            toast.info(`🆕 Nýtt strikamerki: ${response.data.barcode}`);
          } else {
            // Could not read barcode
            toast.error('⚠️ Gat ekki lesið strikamerki. Prófaðu aftur með skýrari mynd.');
          }
        } catch (error) {
          console.error('Failed to scan barcode:', error);
          toast.error('Villa við að lesa strikamerki');
        } finally {
          setScanning(false);
          // Reset file input
          if (cameraInputRef.current) {
            cameraInputRef.current.value = '';
          }
        }
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Failed to process image:', error);
      toast.error('Villa við að vinna úr mynd');
      setScanning(false);
    }
  };

  // Handle inventory action selection (refill or update)
  const handleInventoryAction = (action) => {
    setInventoryAction(action);
    setInventoryAmount('');
    setInventoryContainerCount('');
    setInventoryPrice(scannedProduct?.cost_price || '');
    setPriceChanged(false);
  };

  // Handle inventory update submission
  const handleInventorySubmit = async () => {
    if (!scannedProduct || !inventoryAction) {
      toast.error('Vinsamlegast fylltu út magn');
      return;
    }

    try {
      let amount = 0;
      
      if (inventoryAction === 'refill') {
        // For refill, calculate liters from container count
        const containerCount = parseFloat(inventoryContainerCount);
        if (isNaN(containerCount) || containerCount <= 0) {
          toast.error('Vinsamlegast sláðu inn fjölda íláta');
          return;
        }
        const containerSize = scannedProduct.container_size || 0;
        if (containerSize <= 0) {
          toast.error('Vara hefur enga ílátastærð skilgreinda');
          return;
        }
        amount = containerCount * containerSize; // Total liters to add
      } else {
        // For update, use the amount directly as total liters
        amount = parseFloat(inventoryAmount);
        if (isNaN(amount) || amount <= 0) {
          toast.error('Vinsamlegast sláðu inn magn');
          return;
        }
      }

      // Update price if changed (only for refill)
      if (inventoryAction === 'refill' && priceChanged && inventoryPrice) {
        const newPrice = parseFloat(inventoryPrice);
        if (!isNaN(newPrice) && newPrice > 0) {
          await axios.put(`${API}/products/${scannedProduct.id}`, {
            ...scannedProduct,
            cost_price: newPrice
          });
          toast.success(`✅ Verð uppfært: ${newPrice.toLocaleString()} kr`);
        }
      }

      if (inventoryAction === 'refill') {
        // Áfylling - Add to existing stock
        const currentAmount = scannedProduct.quantity_liters || 0;
        const newQuantity = currentAmount + amount;
        
        const response = await axios.post(`${API}/products/${scannedProduct.id}/update-stock`, {
          new_quantity: newQuantity,
          notes: `Áfylling: ${inventoryContainerCount} ${scannedProduct.unit_type || 'ílát'}`
        });
        toast.success(`✅ ${inventoryContainerCount} ${scannedProduct.unit_type || 'ílát'} bætt við (${amount}L)`);
      } else if (inventoryAction === 'update') {
        // Uppfæra lagerstöðu - Set inventory to specified amount
        const response = await axios.post(`${API}/products/${scannedProduct.id}/update-stock`, {
          new_quantity: amount,
          notes: `Lagerstaða uppfærð í ${amount}L`
        });
        toast.success(`✅ Lagerstaða uppfærð: ${amount}L`);
      }

      // Close dialog and refresh products
      setInventoryActionDialog(false);
      setScannedProduct(null);
      setInventoryAction(null);
      setInventoryAmount('');
      setInventoryContainerCount('');
      setInventoryPrice('');
      setPriceChanged(false);
      loadProducts();
    } catch (error) {
      console.error('Failed to update inventory:', error);
      toast.error('Villa við að uppfæra lagerstaðu');
    }
  };
  
  useEffect(() => {
    loadProducts();
    loadServices();
  }, []);
  
  const loadServices = async () => {
    try {
      const response = await axios.get(`${API}/settings/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Failed to load services:', error);
    }
  };
  
  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to load products:', error);
      toast.error('Mistókst að sækja vörur');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSaveProduct = async (productData) => {
    try {
      // Clean the data: convert empty strings to null for numeric fields
      const cleanedData = {
        ...productData,
        container_size: productData.container_size === '' ? null : parseFloat(productData.container_size) || null,
        quantity_containers: productData.quantity_containers === '' ? null : parseFloat(productData.quantity_containers) || null,
        quantity_liters: parseFloat(productData.quantity_liters) || 0,
        cost_price: productData.cost_price === '' ? null : parseFloat(productData.cost_price) || null,
        minimum_stock: productData.minimum_stock === '' ? null : parseFloat(productData.minimum_stock) || null
      };
      
      if (editingProduct && editingProduct.id) {
        // Update existing product
        await axios.put(`${API}/products/${editingProduct.id}`, cleanedData);
        toast.success('Vara uppfærð');
      } else {
        // Create new product
        await axios.post(`${API}/products`, cleanedData);
        toast.success('Vara búin til');
      }
      setDialogOpen(false);
      setEditingProduct(null);
      loadProducts();
    } catch (error) {
      console.error('Failed to save product:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      console.error('Error details:', errorMessage);
      toast.error(`Mistókst að vista vöru: ${errorMessage}`);
    }
  };
  
  const handleDeleteProduct = async (productId) => {
    if (!confirm('Ertu viss um að þú viljir eyða þessari vöru?')) return;
    
    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success('Vöru eytt');
      loadProducts();
    } catch (error) {
      console.error('Failed to delete product:', error);
      toast.error('Mistókst að eyða vöru');
    }
  };
  
  
  const handleUpdateStock = async (stockData) => {
    try {
      await axios.post(`${API}/products/${stockUpdateProduct.id}/update-stock`, stockData);
      toast.success('Birgðastaða uppfærð');
      setStockDialogOpen(false);
      setStockUpdateProduct(null);
      loadProducts();
    } catch (error) {
      console.error('Failed to update stock:', error);
      toast.error('Mistókst að uppfæra birgðastöðu');
    }
  };
  
  const loadProductHistory = async (product) => {
    try {
      const response = await axios.get(`${API}/products/${product.id}/history`);
      setProductHistory(response.data);
      setHistoryProduct(product);
      setHistoryDialogOpen(true);
    } catch (error) {
      console.error('Failed to load history:', error);
      toast.error('Mistókst að sækja sögu');
    }
  };
  
  const handleDownloadTemplate = async () => {
    try {
      console.log('📥 Downloading template from:', `${API}/products/excel-template`);
      
      // Try backend first
      try {
        const response = await axios.get(`${API}/products/excel-template`, {
          responseType: 'blob',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        
        console.log('✅ Response received from backend:', response);
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'vorur_template.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        toast.success('✅ Sniðmát niðurhalað');
        return;
      } catch (backendError) {
        console.warn('⚠️ Backend download failed, using fallback:', backendError);
        
        // Fallback: Create CSV instead (Excel alternative)
        const headers = [
          'Strikamerki',
          'Nafn vöru *',
          'Magn (lítrar)',
          'Eining',
          'Fjöldi íláta',
          'Stærð íláts (L)',
          'Lágmarksstig (L)',
          'Kostnaðarverð',
          'Birgir',
          'Þjónustur',
          'Athugasemdir'
        ];
        
        const exampleRow = [
          '5901234123457',
          'Tjöruhreinsi Premium',
          '100',
          'tunna',
          '0.5',
          '200',
          '50',
          '15000',
          'Skeljungur',
          '',
          'Dæmi um vöru'
        ];
        
        // Create CSV content with SEMICOLON separator (Icelandic Excel standard)
        const csvContent = [
          headers.join(';'),  // Use semicolon instead of comma
          exampleRow.join(';')
        ].join('\n');
        
        // Add BOM for proper Excel UTF-8 encoding
        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'vorur_template.csv');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        toast.success('✅ Sniðmát niðurhalað (CSV)');
      }
    } catch (error) {
      console.error('❌ All download methods failed:', error);
      toast.error(`Mistókst að sækja sniðmát: ${error.message}`);
    }
  };
  
  const handleImportExcel = async () => {
    if (!importFile) {
      toast.error('Vinsamlegast veldu Excel skrá');
      return;
    }
    
    try {
      setImporting(true);
      
      const formData = new FormData();
      formData.append('file', importFile);
      
      const response = await axios.post(`${API}/products/import-excel`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setImportResults(response.data);
      setImportFile(null);
      
      // Reload products to show newly imported items
      loadProducts();
      
      toast.success(`✅ Innflutningur lokið: ${response.data.success_count} vörur bættust við`);
    } catch (error) {
      console.error('Failed to import Excel:', error);
      toast.error('Mistókst að flytja inn vörur');
      setImportResults(null);
    } finally {
      setImporting(false);
    }
  };
  
  const getStockStatus = (product) => {
    if (!product.minimum_stock) return 'ok';
    if (product.quantity_liters <= 0) return 'empty';
    if (product.quantity_liters <= product.minimum_stock) return 'low';
    return 'ok';
  };
  
  return (
    <div className="p-3 sm:p-6 space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center bg-gradient-to-r from-blue-50 to-slate-50 dark:from-blue-900/20 dark:to-slate-800 p-4 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-400 dark:to-blue-600 bg-clip-text text-transparent">📦 Vörunotkun</h1>
          <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1 font-medium">Birgðastjórnun fyrir efni í notkun</p>
        </div>
        
        <div className="flex gap-2">
          <Button 
            type="button"
            onClick={handleBarcodeScanClick}
            disabled={scanning}
          >
            {scanning ? '⏳ Skannar...' : '📷 Skanna strikamerki'}
          </Button>
          <Button 
            type="button"
            variant="outline"
            onClick={() => setImportDialogOpen(true)}
          >
            📤 Flytja inn Excel
          </Button>
          <Button 
            type="button"
            onClick={() => {setEditingProduct(null); setDialogOpen(true)}}
          >
            ➕ Ný vara
          </Button>
        </div>
      </div>
      
      {/* Products List */}
      {loading ? (
        <div className="text-center py-8 text-slate-500">Hleður vörum...</div>
      ) : products.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-slate-500">
            <div className="text-4xl mb-2">📦</div>
            <div>Engar vörur skráðar ennþá</div>
            <Button onClick={() => setDialogOpen(true)} className="mt-4">
              Bæta við fyrstu vöru
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map(product => {
            const status = getStockStatus(product);
            return (
              <Card key={product.id} className={`${
                status === 'empty' ? 'border-red-500 dark:border-red-700' :
                status === 'low' ? 'border-yellow-500 dark:border-yellow-700' :
                'border-slate-200 dark:border-slate-700'
              }`}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg dark:text-slate-100">{product.name}</CardTitle>
                      {product.barcode && (
                        <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                          🏷️ {product.barcode}
                        </div>
                      )}
                    </div>
                    <div className={`px-2 py-1 rounded text-xs font-medium ${
                      status === 'empty' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' :
                      status === 'low' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' :
                      'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                    }`}>
                      {status === 'empty' ? '⚠️ Tómt' :
                       status === 'low' ? '⚠️ Lágmark' :
                       '✅ Í lagi'}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {/* Show container info if using containers */}
                    {product.unit_type && product.unit_type !== 'liters' && product.container_size && (
                      <div className="text-sm bg-blue-50 dark:bg-blue-900/20 p-2 rounded border border-blue-200 dark:border-blue-700">
                        <div className="font-medium text-blue-900 dark:text-blue-200">
                          {product.quantity_containers || 0} {product.unit_type} × {product.container_size}L = {product.quantity_liters}L
                        </div>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-400">Magn:</span>
                      <span className="font-bold text-slate-900 dark:text-slate-100">{product.quantity_liters} L</span>
                    </div>
                    {product.minimum_stock && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600 dark:text-slate-400">Lágmark:</span>
                        <span className="text-slate-700 dark:text-slate-300">{product.minimum_stock} L</span>
                      </div>
                    )}
                    {product.average_usage_per_job && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600 dark:text-slate-400">Meðalnotkun:</span>
                        <span className="text-slate-700 dark:text-slate-300">
                          {product.average_usage_per_job.toFixed(2)} L/bíl
                          {product.average_usage_per_job_containers && product.unit_type !== 'liters' && (
                            <span className="text-xs ml-1">({product.average_usage_per_job_containers.toFixed(3)} {product.unit_type})</span>
                          )}
                        </span>
                      </div>
                    )}
                    {/* Prediction: Cars remaining */}
                    {product.average_usage_per_job && product.quantity_liters > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600 dark:text-slate-400">📊 Endist í:</span>
                        <span className="font-medium text-blue-600 dark:text-blue-400">
                          ~{Math.floor(product.quantity_liters / product.average_usage_per_job)} bíla
                        </span>
                      </div>
                    )}
                    {product.supplier && (
                      <div className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                        Birgir: {product.supplier}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-2 mt-4">
                    <Button 
                      onClick={() => {setStockUpdateProduct(product); setStockDialogOpen(true)}}
                      variant="outline"
                      size="sm"
                      className="flex-1"
                    >
                      📊 Uppfæra
                    </Button>
                    <Button 
                      onClick={() => loadProductHistory(product)}
                      variant="outline"
                      size="sm"
                    >
                      📜
                    </Button>
                    <Button 
                      onClick={() => {setEditingProduct(product); setDialogOpen(true)}}
                      variant="outline"
                      size="sm"
                    >
                      ✏️
                    </Button>
                    <Button 
                      onClick={() => handleDeleteProduct(product.id)}
                      variant="outline"
                      size="sm"
                      className="text-red-600 dark:text-red-400"
                    >
                      🗑️
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
      
      {/* Add/Edit Product Dialog */}
      <ProductDialog
        open={dialogOpen}
        onClose={() => {setDialogOpen(false); setEditingProduct(null)}}
        product={editingProduct}
        services={services}
        onSave={handleSaveProduct}
      />
      
      {/* Update Stock Dialog */}
      <StockUpdateDialog
        open={stockDialogOpen}
        onClose={() => {setStockDialogOpen(false); setStockUpdateProduct(null)}}
        product={stockUpdateProduct}
        onSave={handleUpdateStock}
      />
      
      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Birgðasaga - {historyProduct?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {productHistory.map(trans => (
              <div key={trans.id} className="p-3 border dark:border-slate-700 rounded">
                <div className="flex justify-between text-sm">
                  <span className="font-medium dark:text-slate-200">
                    {trans.change_amount > 0 ? '📈' : '📉'} {trans.change_amount > 0 ? '+' : ''}{trans.change_amount.toFixed(2)} L
                  </span>
                  <span className="text-slate-500 dark:text-slate-400">
                    {new Date(trans.created_at).toLocaleString('is-IS')}
                  </span>
                </div>
                <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                  Ný staða: {trans.new_quantity} L
                  {trans.jobs_count && ` • ${trans.jobs_count} verk`}
                </div>
                {trans.notes && (
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">{trans.notes}</div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Import Excel Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>📤 Flytja inn vörur úr Excel</DialogTitle>
          </DialogHeader>
          
          {!importResults ? (
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded">
                <div className="text-sm text-blue-900 dark:text-blue-200 space-y-2">
                  <p className="font-medium">📋 Leiðbeiningar:</p>
                  <ol className="list-decimal list-inside space-y-1 text-xs">
                    <li>Sæktu Excel sniðmát með dæmum</li>
                    <li>Fylltu út vöruupplýsingar í sniðmátið</li>
                    <li>Vistaðu skrána sem .xlsx</li>
                    <li>Hlaðið upp skránni hér</li>
                  </ol>
                  <p className="text-xs mt-2 pt-2 border-t border-blue-300 dark:border-blue-700">
                    <strong>Athugið:</strong> Aðeins nýjar vörur verða bættar við. Vörur með sama strikamerki eða nafni verða slepptar.
                  </p>
                </div>
              </div>
              
              <div className="flex justify-center">
                <Button 
                  variant="outline"
                  onClick={handleDownloadTemplate}
                >
                  📥 Sækja Excel sniðmát
                </Button>
              </div>
              
              <div className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-6">
                <div className="text-center">
                  <div className="text-4xl mb-2">📂</div>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => setImportFile(e.target.files[0])}
                    className="hidden"
                    id="excel-upload"
                  />
                  <label 
                    htmlFor="excel-upload"
                    className="cursor-pointer inline-block px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Velja Excel skrá
                  </label>
                  {importFile && (
                    <div className="mt-3 text-sm text-slate-600 dark:text-slate-400">
                      ✓ {importFile.name}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => {setImportDialogOpen(false); setImportFile(null)}}>
                  Hætta við
                </Button>
                <Button 
                  onClick={handleImportExcel} 
                  disabled={!importFile || importing}
                >
                  {importing ? '⏳ Flyt inn...' : '📤 Flytja inn'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded">
                <div className="text-lg font-bold text-green-900 dark:text-green-200 mb-2">
                  ✅ Innflutningur lokið
                </div>
                <div className="text-sm text-green-800 dark:text-green-300 space-y-1">
                  <p>📊 Heildarfjöldi lína: {importResults.total_rows}</p>
                  <p>✓ Vörur bættust við: {importResults.success_count}</p>
                  <p>⏭️ Vörur slepptar: {importResults.skipped_count}</p>
                  <p>❌ Villur: {importResults.error_count}</p>
                </div>
              </div>
              
              {importResults.success_products.length > 0 && (
                <div className="p-3 bg-white dark:bg-slate-800 border dark:border-slate-700 rounded">
                  <div className="font-medium text-sm mb-2">✅ Nýjar vörur ({importResults.success_count}):</div>
                  <div className="space-y-1 max-h-32 overflow-y-auto text-xs">
                    {importResults.success_products.map((p, idx) => (
                      <div key={idx} className="text-slate-600 dark:text-slate-400">
                        Lína {p.row}: {p.name} {p.barcode && `(${p.barcode})`}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {importResults.skipped_products.length > 0 && (
                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded">
                  <div className="font-medium text-sm mb-2">⏭️ Slepptar vörur ({importResults.skipped_count}):</div>
                  <div className="space-y-1 max-h-32 overflow-y-auto text-xs">
                    {importResults.skipped_products.map((p, idx) => (
                      <div key={idx} className="text-yellow-800 dark:text-yellow-300">
                        Lína {p.row}: {p.reason}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {importResults.errors.length > 0 && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded">
                  <div className="font-medium text-sm mb-2">❌ Villur ({importResults.error_count}):</div>
                  <div className="space-y-1 max-h-32 overflow-y-auto text-xs">
                    {importResults.errors.map((err, idx) => (
                      <div key={idx} className="text-red-800 dark:text-red-300">
                        Lína {err.row}: {err.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex justify-end">
                <Button onClick={() => {setImportDialogOpen(false); setImportResults(null); setImportFile(null)}}>
                  Loka
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Native camera input for barcode scanning */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleCameraCapture}
        style={{ display: 'none' }}
      />

      {/* Live Quagga Scanner Dialog */}
      <Dialog open={useLiveScanner} onOpenChange={setUseLiveScanner}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>📷 Hraðskönnun</DialogTitle>
          </DialogHeader>
          <QuaggaScanner
            onDetected={async (barcode) => {
              setUseLiveScanner(false);
              setScanning(true);
              toast.info('⏳ Athuga vöru...');
              
              try {
                // Quick lookup by barcode string (no Vision needed!)
                const response = await axios.post(`${API}/products/scan-barcode`, { 
                  barcode: barcode 
                });
                
                if (response.data.found) {
                  const productData = response.data.product;
                  setScannedProduct(productData);
                  setInventoryActionDialog(true);
                  setInventoryAction(null);
                  toast.success(`✅ ${productData.name}`);
                } else {
                  setEditingProduct({ barcode: barcode });
                  setDialogOpen(true);
                  toast.info(`🆕 Nýtt: ${barcode}`);
                }
              } catch (error) {
                console.error('Lookup error:', error);
                toast.error('Villa við uppflettingu');
              } finally {
                setScanning(false);
              }
            }}
            onClose={() => setUseLiveScanner(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Inventory Action Dialog - When scanning existing product */}
      <Dialog open={inventoryActionDialog} onOpenChange={setInventoryActionDialog} key={scannedProduct?.id}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>📦 {scannedProduct?.name}</DialogTitle>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">
              Vara fundin í kerfinu. Hvað viltu gera?
            </p>
          </DialogHeader>

          {!inventoryAction ? (
            // Step 1: Choose action
            <div className="space-y-3 mt-4">
              <Button
                className="w-full justify-start h-auto py-4"
                variant="outline"
                onClick={() => handleInventoryAction('refill')}
              >
                <div className="text-left">
                  <div className="font-bold text-base">📥 Áfylling</div>
                  <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                    Bæta við magni (t.d. ný sending komin)
                  </div>
                </div>
              </Button>

              <Button
                className="w-full justify-start h-auto py-4"
                variant="outline"
                onClick={() => handleInventoryAction('update')}
              >
                <div className="text-left">
                  <div className="font-bold text-base">🔄 Uppfæra lagerstöðu</div>
                  <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                    Setja núverandi lítrafjölda sem er eftir
                  </div>
                </div>
              </Button>

              <div className="pt-3 border-t mt-4">
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  <strong>Núverandi staða:</strong> {scannedProduct?.quantity_liters || 0} L
                </div>
              </div>

              <div className="flex justify-end mt-4">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setInventoryActionDialog(false);
                    setScannedProduct(null);
                  }}
                >
                  Hætta við
                </Button>
              </div>
            </div>
          ) : (
            // Step 2: Enter amount
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded">
                <div className="text-sm font-medium text-blue-900 dark:text-blue-200">
                  {inventoryAction === 'refill' ? (
                    <>
                      📥 <strong>Áfylling</strong>
                      <p className="text-xs mt-1 text-blue-800 dark:text-blue-300">
                        Hversu mörg ílát ertu að bæta við? ({scannedProduct?.container_size || 0}L per {scannedProduct?.unit_type || 'ílát'})
                      </p>
                    </>
                  ) : (
                    <>
                      🔄 <strong>Uppfæra lagerstöðu</strong>
                      <p className="text-xs mt-1 text-blue-800 dark:text-blue-300">
                        Sláðu inn núverandi lítrafjölda sem er eftir á vörunni
                      </p>
                    </>
                  )}
                </div>
              </div>

              <div>
                {inventoryAction === 'refill' ? (
                  // Refill: Ask for container count
                  <>
                    <Label htmlFor="inventory-container-count">
                      Fjöldi íláta ({scannedProduct?.unit_type || 'ílát'})
                    </Label>
                    <input
                      id="inventory-container-count"
                      type="number"
                      step="1"
                      min="1"
                      value={inventoryContainerCount}
                      onChange={(e) => setInventoryContainerCount(e.target.value)}
                      placeholder="1"
                      className="w-full px-3 py-2 border rounded-md mt-1 text-lg font-bold"
                      autoFocus
                    />
                    {inventoryContainerCount && scannedProduct?.container_size && (
                      <p className="text-sm text-blue-600 dark:text-blue-400 mt-2">
                        = {(parseFloat(inventoryContainerCount) * scannedProduct.container_size).toFixed(1)} L
                      </p>
                    )}
                  </>
                ) : (
                  // Update: Ask for total liters
                  <>
                    <Label htmlFor="inventory-amount">
                      Núverandi magn (L)
                    </Label>
                    <input
                      id="inventory-amount"
                      type="number"
                      step="0.1"
                      min="0"
                      value={inventoryAmount}
                      onChange={(e) => setInventoryAmount(e.target.value)}
                      placeholder="0.0"
                      className="w-full px-3 py-2 border rounded-md mt-1"
                      autoFocus
                    />
                  </>
                )}
              </div>

              {/* Price Section - ONLY for refill, per container */}
              {inventoryAction === 'refill' && (
                <div className="border-t pt-3">
                  <div className="mb-3">
                    <div className="text-sm font-medium mb-2">💰 Verð per {scannedProduct?.unit_type || 'ílát'}</div>
                    <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded text-sm space-y-1">
                      <div className="text-slate-600 dark:text-slate-400">
                        <strong>Núverandi verð per {scannedProduct?.unit_type || 'ílát'}:</strong>
                      </div>
                      <div className="text-slate-800 dark:text-slate-200 ml-2">
                        • Með VSK (24%): <strong>{(scannedProduct?.cost_price || 0).toLocaleString()} kr</strong>
                      </div>
                      <div className="text-slate-600 dark:text-slate-400 ml-2">
                        • Án VSK: {((scannedProduct?.cost_price || 0) / 1.24).toFixed(0).toLocaleString()} kr
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="inventory-price" className="text-sm">
                      Nýtt verð (með VSK) - valfrjálst
                    </Label>
                    <input
                      id="inventory-price"
                      type="number"
                      step="1"
                      min="0"
                      value={inventoryPrice}
                      onChange={(e) => {
                        setInventoryPrice(e.target.value);
                        setPriceChanged(e.target.value !== '' && parseFloat(e.target.value) !== scannedProduct?.cost_price);
                      }}
                      placeholder={scannedProduct?.cost_price || '0'}
                      className="w-full px-3 py-2 border rounded-md mt-1"
                    />
                    {priceChanged && inventoryPrice && (
                      <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded text-xs">
                        <div className="text-yellow-800 dark:text-yellow-300">
                          ⚠️ Verð mun breytast:
                        </div>
                        <div className="text-yellow-900 dark:text-yellow-200 mt-1 ml-2">
                          • Með VSK: <strong>{parseFloat(inventoryPrice).toLocaleString()} kr</strong>
                        </div>
                        <div className="text-yellow-700 dark:text-yellow-400 ml-2">
                          • Án VSK: {(parseFloat(inventoryPrice) / 1.24).toFixed(0).toLocaleString()} kr
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded text-sm">
                <div className="text-slate-600 dark:text-slate-400">
                  <strong>Núverandi staða:</strong> {scannedProduct?.quantity_liters || 0} L
                </div>
                {inventoryAction === 'refill' && inventoryContainerCount && scannedProduct?.container_size && (
                  <div className="text-green-600 dark:text-green-400 mt-1">
                    <strong>Eftir áfyllingu:</strong> {(parseFloat(scannedProduct?.quantity_liters || 0) + (parseFloat(inventoryContainerCount) * scannedProduct.container_size)).toFixed(1)} L
                  </div>
                )}
                {inventoryAction === 'update' && inventoryAmount && (
                  <div className="text-blue-600 dark:text-blue-400 mt-1">
                    <strong>Ný staða:</strong> {parseFloat(inventoryAmount || 0).toFixed(1)} L
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setInventoryAction(null);
                    setInventoryAmount('');
                    setInventoryContainerCount('');
                    setInventoryPrice('');
                    setPriceChanged(false);
                  }}
                >
                  ← Til baka
                </Button>
                <Button 
                  onClick={handleInventorySubmit}
                  disabled={
                    (inventoryAction === 'refill' && (!inventoryContainerCount || parseFloat(inventoryContainerCount) <= 0)) ||
                    (inventoryAction === 'update' && (!inventoryAmount || parseFloat(inventoryAmount) <= 0))
                  }
                >
                  ✓ Vista
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Quagga Live Barcode Scanner Component
function QuaggaScanner({ onDetected, onClose }) {
  const scannerRef = React.useRef(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let isActive = true;

    const initScanner = async () => {
      try {
        const Quagga = (await import('@ericblade/quagga2')).default;

        Quagga.init({
          inputStream: {
            type: 'LiveStream',
            target: scannerRef.current,
            constraints: {
              facingMode: 'environment',
              aspectRatio: { min: 1, max: 2 }
            }
          },
          decoder: {
            readers: ['ean_reader', 'ean_8_reader', 'code_128_reader', 'upc_reader', 'upc_e_reader'],
            multiple: false
          },
          locate: true,
          locator: {
            patchSize: 'medium',
            halfSample: true
          },
          numOfWorkers: 2,
          frequency: 10
        }, (err) => {
          if (err) {
            console.error('Quagga init error:', err);
            setError('Ekki tókst að opna myndavél');
            return;
          }
          if (isActive) {
            Quagga.start();
            console.log('✅ Quagga scanner started');
          }
        });

        Quagga.onDetected((result) => {
          if (result && result.codeResult && result.codeResult.code) {
            console.log('✅ Barcode detected:', result.codeResult.code);
            Quagga.stop();
            if (isActive) {
              onDetected(result.codeResult.code);
            }
          }
        });

      } catch (err) {
        console.error('Quagga error:', err);
        setError('Villa við að ræsa skanna');
      }
    };

    initScanner();

    return () => {
      isActive = false;
      const Quagga = require('@ericblade/quagga2').default;
      if (Quagga) {
        Quagga.stop();
        Quagga.offDetected();
      }
    };
  }, [onDetected]);

  const handleClose = async () => {
    try {
      const Quagga = (await import('@ericblade/quagga2')).default;
      Quagga.stop();
    } catch (err) {
      console.error('Error stopping scanner:', err);
    }
    onClose();
  };

  return (
    <div className="space-y-4">
      <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded">
        <p className="text-sm text-green-900 dark:text-green-200 font-bold">
          🚀 Haltu símanum yfir strikamerkinu
        </p>
        <p className="text-xs text-green-800 dark:text-green-300 mt-1">
          Hraðskönnun - finnur sjálfkrafa og lokar strax!
        </p>
      </div>

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded">
          <p className="text-sm text-red-900 dark:text-red-200">{error}</p>
        </div>
      )}

      <div 
        ref={scannerRef}
        className="w-full rounded-lg overflow-hidden bg-black relative"
        style={{ minHeight: '400px', maxHeight: '500px' }}
      />

      <div className="flex justify-center">
        <Button variant="outline" onClick={handleClose} className="w-full">
          ❌ Loka
        </Button>
      </div>
    </div>
  );
}

// Product Dialog Component
function ProductDialog({ open, onClose, product, services, onSave }) {
  const [formData, setFormData] = useState({
    barcode: '',
    name: '',
    unit_type: 'liters',
    container_size: '',
    quantity_containers: '',
    quantity_liters: 0,
    cost_price: '',
    minimum_stock: '',
    supplier: '',
    services_used: [],
    notes: '',
    payday_product_id: ''
  });
  const [hasMinimumStock, setHasMinimumStock] = useState(false);
  
  useEffect(() => {
    if (product) {
      const hasMin = product.minimum_stock && product.minimum_stock > 0;
      setHasMinimumStock(hasMin);
      setFormData({
        barcode: product.barcode || '',
        name: product.name || '',
        unit_type: product.unit_type || 'liters',
        container_size: product.container_size || '',
        quantity_containers: product.quantity_containers || '',
        quantity_liters: product.quantity_liters || 0,
        cost_price: product.cost_price || '',
        minimum_stock: product.minimum_stock || '',
        supplier: product.supplier || '',
        services_used: product.services_used || [],
        notes: product.notes || '',
        payday_product_id: product.payday_product_id || ''
      });
    } else {
      setHasMinimumStock(false);
      setFormData({
        barcode: '',
        name: '',
        unit_type: 'liters',
        container_size: '',
        quantity_containers: '',
        quantity_liters: 0,
        cost_price: '',
        minimum_stock: '',
        supplier: '',
        services_used: [],
        notes: '',
        payday_product_id: ''
      });
    }
  }, [product, open]);
  
  // Auto-detect unit type and suggest breakdown based on total liters
  const autoDetectUnit = (totalLiters) => {
    const liters = parseFloat(totalLiters);
    if (!liters || liters <= 0) return [];
    
    const suggestions = [];
    
    // Bambi is always 1000L
    if (liters === 1000) {
      suggestions.push({
        unit_type: 'bambi',
        container_size: 1000,
        quantity_containers: 1,
        suggestion: '1 bambi × 1000L'
      });
      return suggestions;
    }
    
    // Tunna: ALWAYS 200L per container
    if (liters >= 200 && liters < 1000) {
      if (liters % 200 === 0) {
        suggestions.push({ unit_type: 'tunna', container_size: 200, quantity_containers: liters / 200, suggestion: `${liters/200} ${liters/200 > 1 ? 'tunnur' : 'tunna'} × 200L` });
      }
    }
    
    // Brúsi: For 5-199L (containers are 5, 10, 20, or 25L each)
    if (liters >= 5 && liters < 200) {
      // Try 25L containers
      if (liters % 25 === 0) {
        suggestions.push({ unit_type: 'brusi', container_size: 25, quantity_containers: liters / 25, suggestion: `${liters/25} ${liters/25 > 1 ? 'brúsar' : 'brúsi'} × 25L` });
      }
      // Try 20L containers
      if (liters % 20 === 0) {
        suggestions.push({ unit_type: 'brusi', container_size: 20, quantity_containers: liters / 20, suggestion: `${liters/20} ${liters/20 > 1 ? 'brúsar' : 'brúsi'} × 20L` });
      }
      // Try 10L containers
      if (liters % 10 === 0) {
        suggestions.push({ unit_type: 'brusi', container_size: 10, quantity_containers: liters / 10, suggestion: `${liters/10} ${liters/10 > 1 ? 'brúsar' : 'brúsi'} × 10L` });
      }
      // Try 5L containers (only if it divides evenly)
      if (liters % 5 === 0) {
        suggestions.push({ unit_type: 'brusi', container_size: 5, quantity_containers: liters / 5, suggestion: `${liters/5} ${liters/5 > 1 ? 'brúsar' : 'brúsi'} × 5L` });
      }
    }
    
    // Flaska: ONLY for very small amounts (< 5L)
    if (liters < 5 && liters > 0) {
      // Try 1L bottles
      if (liters % 1 === 0 && liters <= 5) {
        suggestions.push({ unit_type: 'flaska', container_size: 1, quantity_containers: liters, suggestion: `${liters} ${liters > 1 ? 'flöskur' : 'flaska'} × 1L` });
      }
      // Try 0.5L bottles
      if (liters % 0.5 === 0 && liters <= 2) {
        suggestions.push({ unit_type: 'flaska', container_size: 0.5, quantity_containers: liters / 0.5, suggestion: `${liters/0.5} ${liters/0.5 > 1 ? 'flöskur' : 'flaska'} × 0.5L` });
      }
    }
    
    return suggestions;
  };
  
  const suggestions = autoDetectUnit(formData.quantity_liters);
  
  const handleAcceptSuggestion = (suggestion) => {
    setFormData({
      ...formData,
      unit_type: suggestion.unit_type,
      container_size: suggestion.container_size,
      quantity_containers: suggestion.quantity_containers
    });
  };
  
  const handleSubmit = () => {
    if (!formData.name) {
      toast.error('Nafn vöru er skyldubundið');
      return;
    }
    onSave(formData);
  };
  
  const unitLabels = {
    'liters': 'Lítrar',
    'flaska': 'Flaska',
    'brusi': 'Brúsi', 
    'tunna': 'Tunna',
    'bambi': 'Bambi'
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{product ? 'Breyta vöru' : 'Ný vara'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Strikamerki</Label>
              <Input
                value={formData.barcode}
                onChange={(e) => setFormData({...formData, barcode: e.target.value})}
                placeholder="5901234123457"
              />
            </div>
            <div>
              <Label>Nafn vöru *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="t.d. Tjöruhreinsi Premium"
              />
            </div>
          </div>
          
          {/* Main input: Total Liters */}
          <div>
            <Label>Samtals magn (lítrar) *</Label>
            <Input
              type="number"
              step="0.1"
              value={formData.quantity_liters}
              onChange={(e) => setFormData({...formData, quantity_liters: parseFloat(e.target.value) || 0})}
              placeholder="t.d. 100"
            />
          </div>
          
          {/* Auto-detection suggestions - show multiple options */}
          {suggestions.length > 0 && formData.unit_type === 'liters' && (
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded">
              <div className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-2">
                💡 Veldu skiptingu:
              </div>
              <div className="space-y-2">
                {suggestions.map((sug, idx) => (
                  <Button
                    key={idx}
                    size="sm"
                    variant="outline"
                    onClick={() => handleAcceptSuggestion(sug)}
                    className="w-full justify-start bg-white dark:bg-slate-700 hover:bg-blue-100 dark:hover:bg-blue-900/40"
                  >
                    <span className="mr-2">📦</span>
                    {sug.suggestion}
                  </Button>
                ))}
                <div className="text-xs text-blue-700 dark:text-blue-300 pt-1 border-t dark:border-blue-700">
                  Eða halda sem {formData.quantity_liters}L í lítrum
                </div>
              </div>
            </div>
          )}
          
          {/* Show breakdown if unit type is set */}
          {formData.unit_type !== 'liters' && (
            <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded">
              <div className="text-sm font-medium text-green-900 dark:text-green-200 mb-2">
                📦 {unitLabels[formData.unit_type]}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Fjöldi {unitLabels[formData.unit_type]}</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.quantity_containers}
                    onChange={(e) => {
                      const newCount = parseFloat(e.target.value) || 0;
                      const newTotal = newCount * (formData.container_size || 0);
                      setFormData({
                        ...formData, 
                        quantity_containers: e.target.value,
                        quantity_liters: newTotal
                      });
                    }}
                    className="h-8"
                  />
                </div>
                <div>
                  <Label className="text-xs">Stærð per {formData.unit_type}</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.container_size}
                    onChange={(e) => {
                      const newSize = parseFloat(e.target.value) || 0;
                      const newTotal = newSize * (formData.quantity_containers || 0);
                      setFormData({
                        ...formData, 
                        container_size: e.target.value,
                        quantity_liters: newTotal
                      });
                    }}
                    disabled={formData.unit_type === 'bambi'}
                    className="h-8"
                  />
                </div>
              </div>
              <div className="text-xs text-green-700 dark:text-green-300 mt-2">
                = {formData.quantity_liters}L samtals
              </div>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => setFormData({...formData, unit_type: 'liters', container_size: '', quantity_containers: ''})}
                className="mt-2 text-xs h-7"
              >
                ↩️ Breyta aftur í lítrar
              </Button>
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-3">
              <Label>Lágmarksmagn</Label>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="has-minimum"
                  checked={hasMinimumStock}
                  onChange={(e) => {
                    setHasMinimumStock(e.target.checked);
                    if (!e.target.checked) {
                      setFormData({...formData, minimum_stock: ''});
                    }
                  }}
                  className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="has-minimum" className="text-sm font-medium">
                  Fylgjast með lágmarksmagni
                </label>
              </div>
              {hasMinimumStock && (
                <div className="ml-6">
                  <Label className="text-xs">Lágmarksmagn (L)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.minimum_stock}
                    onChange={(e) => setFormData({...formData, minimum_stock: parseFloat(e.target.value) || ''})}
                    placeholder="100"
                    className="mt-1"
                  />
                </div>
              )}
            </div>
            <div>
              <Label>Kostnaðarverð</Label>
              <Input
                type="number"
                value={formData.cost_price}
                onChange={(e) => setFormData({...formData, cost_price: parseFloat(e.target.value) || ''})}
                placeholder="15000"
              />
            </div>
          </div>
          
          <div>
            <Label>Birgir</Label>
            <Select 
              value={formData.supplier} 
              onValueChange={(value) => {
                if (value === 'new') {
                  const newSupplier = prompt('Nafn á nýjum birgi:');
                  if (newSupplier) {
                    setFormData({...formData, supplier: newSupplier});
                  }
                } else {
                  setFormData({...formData, supplier: value});
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Veldu birgi" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Skeljungur">Skeljungur</SelectItem>
                <SelectItem value="Málingarvörur">Málingarvörur</SelectItem>
                <SelectItem value="Orka">Orka</SelectItem>
                <SelectItem value="Takk hreinlæti">Takk hreinlæti</SelectItem>
                <SelectItem value="new" className="text-blue-600 dark:text-blue-400 font-medium">
                  ➕ Bæta við nýjum birgi
                </SelectItem>
              </SelectContent>
            </Select>
            {formData.supplier && !['Skeljungur', 'Málingarvörur', 'Orka', 'Takk hreinlæti'].includes(formData.supplier) && (
              <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                ✓ Sérsniðinn birgir: {formData.supplier}
              </div>
            )}
          </div>
          
          <div>
            <Label>Payday vörunúmer (SKU)</Label>
            <Input
              value={formData.payday_product_id || ''}
              onChange={(e) => setFormData({...formData, payday_product_id: e.target.value})}
              placeholder="t.d. 1002"
            />
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Sláðu inn vörunúmer (SKU) úr Payday til að tengja við reikninga.
            </div>
          </div>

          {/* Services Used - Link product to services */}
          {services && services.length > 0 && (
            <div className="border dark:border-slate-700 rounded-lg p-4 bg-slate-50 dark:bg-slate-800">
              <Label className="mb-3 block dark:text-slate-200">🔧 Notuð í þjónustum</Label>
              <div className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                Veldu þær þjónustur sem nota þessa vöru
              </div>
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                {services.map(service => (
                  <div key={service.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`service-${service.id}`}
                      checked={formData.services_used?.includes(service.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setFormData({
                            ...formData,
                            services_used: [...(formData.services_used || []), service.id]
                          });
                        } else {
                          setFormData({
                            ...formData,
                            services_used: (formData.services_used || []).filter(id => id !== service.id)
                          });
                        }
                      }}
                    />
                    <Label 
                      htmlFor={`service-${service.id}`} 
                      className="text-sm cursor-pointer dark:text-slate-300"
                    >
                      {service.name}
                    </Label>
                  </div>
                ))}
              </div>
              {formData.services_used?.length > 0 && (
                <div className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                  ✓ {formData.services_used.length} þjónusta{formData.services_used.length > 1 ? 'r' : ''} valin
                </div>
              )}
            </div>
          )}
          
          <div>
            <Label>Athugasemdir</Label>
            <Input
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Valkvætt"
            />
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Hætta við</Button>
            <Button onClick={handleSubmit}>Vista</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Stock Update Dialog Component
function StockUpdateDialog({ open, onClose, product, onSave }) {
  const [newQuantityLiters, setNewQuantityLiters] = useState('');
  const [newQuantityContainers, setNewQuantityContainers] = useState('');
  const [notes, setNotes] = useState('');
  const [inputMode, setInputMode] = useState('containers'); // 'containers' or 'liters'
  
  useEffect(() => {
    if (product) {
      setNewQuantityLiters(product.quantity_liters || 0);
      setNewQuantityContainers(product.quantity_containers || 0);
      setNotes('');
      // Default to containers mode if product has container type
      setInputMode(product.unit_type !== 'liters' ? 'containers' : 'liters');
    }
  }, [product, open]);
  
  // Calculate total liters from containers
  const calculatedLiters = product?.container_size && newQuantityContainers 
    ? parseFloat(newQuantityContainers) * parseFloat(product.container_size)
    : parseFloat(newQuantityLiters) || 0;
  
  const handleSubmit = () => {
    const finalQuantity = inputMode === 'liters' ? parseFloat(newQuantityLiters) : calculatedLiters;
    
    onSave({
      new_quantity: finalQuantity || 0,
      notes
    });
  };
  
  if (!product) return null;
  
  const change = (inputMode === 'liters' ? parseFloat(newQuantityLiters) : calculatedLiters) - product.quantity_liters;
  
  // Calculate predictions if average usage is known
  const finalQuantity = inputMode === 'liters' ? parseFloat(newQuantityLiters) : calculatedLiters;
  const carsRemaining = product.average_usage_per_job ? Math.floor(finalQuantity / product.average_usage_per_job) : null;
  
  const unitLabels = {
    'liters': 'Lítrar',
    'flaska': 'Flöskur',
    'brusi': 'Brúsar', 
    'tunna': 'Tunnur',
    'bambi': 'Bambar'
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Uppfæra birgðastöðu - {product.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded">
            <div className="text-sm text-slate-600 dark:text-slate-400">Núverandi magn:</div>
            {product.unit_type !== 'liters' && product.container_size ? (
              <div>
                <div className="text-2xl font-bold dark:text-slate-100">
                  {product.quantity_containers || 0} {unitLabels[product.unit_type] || product.unit_type}
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  ({product.quantity_liters} L samtals)
                </div>
              </div>
            ) : (
              <div className="text-2xl font-bold dark:text-slate-100">{product.quantity_liters} L</div>
            )}
            {product.average_usage_per_job && (
              <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Meðalnotkun: {product.average_usage_per_job.toFixed(2)} L/bíl
                {product.average_usage_per_job_containers && product.unit_type !== 'liters' && (
                  <span> ({product.average_usage_per_job_containers.toFixed(3)} {product.unit_type})</span>
                )}
              </div>
            )}
          </div>
          
          {/* Toggle between container and liter input for products with containers */}
          {product.unit_type !== 'liters' && product.container_size && (
            <div className="flex gap-2 justify-center">
              <Button
                type="button"
                size="sm"
                variant={inputMode === 'containers' ? 'default' : 'outline'}
                onClick={() => setInputMode('containers')}
                className="flex-1"
              >
                📦 {unitLabels[product.unit_type] || product.unit_type}
              </Button>
              <Button
                type="button"
                size="sm"
                variant={inputMode === 'liters' ? 'default' : 'outline'}
                onClick={() => setInputMode('liters')}
                className="flex-1"
              >
                💧 Lítrar
              </Button>
            </div>
          )}
          
          {/* Input based on selected mode */}
          {inputMode === 'containers' && product.unit_type !== 'liters' && product.container_size ? (
            <div>
              <Label>Nýr fjöldi {unitLabels[product.unit_type] || product.unit_type} *</Label>
              <Input
                type="number"
                step="0.1"
                value={newQuantityContainers}
                onChange={(e) => setNewQuantityContainers(e.target.value)}
              />
              {newQuantityContainers && (
                <div className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                  = {calculatedLiters.toFixed(1)} L samtals
                </div>
              )}
              {change !== 0 && (
                <div className={`text-sm mt-1 ${change > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  Breyting: {change > 0 ? '+' : ''}{change.toFixed(2)} L
                </div>
              )}
            </div>
          ) : (
            <div>
              <Label>Nýtt magn (lítrar) *</Label>
              <Input
                type="number"
                step="0.1"
                value={newQuantityLiters}
                onChange={(e) => setNewQuantityLiters(e.target.value)}
                placeholder="t.d. 4.5"
              />
              {change !== 0 && (
                <div className={`text-sm mt-1 ${change > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  Breyting: {change > 0 ? '+' : ''}{change.toFixed(2)} L
                </div>
              )}
            </div>
          )}
          
          {/* Automatic prediction display */}
          {change < 0 && (
            <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded border border-blue-200 dark:border-blue-700">
              <div className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-1">
                🤖 Sjálfvirk læring
              </div>
              <div className="text-xs text-blue-700 dark:text-blue-300">
                • Kerfið telur sjálfkrafa fjölda þrifaðra bíla<br/>
                • Reiknar meðalnotkun per bíl<br/>
                • Lærir og bætir spár með tímanum
              </div>
            </div>
          )}
          
          {/* Show prediction for new quantity */}
          {carsRemaining !== null && finalQuantity > 0 && (
            <div className="p-3 bg-green-50 dark:bg-green-900/30 rounded border border-green-200 dark:border-green-700">
              <div className="text-sm font-medium text-green-900 dark:text-green-200 mb-1">
                📊 Áætlun með nýju magni
              </div>
              <div className="text-sm text-green-700 dark:text-green-300">
                Endist í ca. <strong>{carsRemaining} bíla</strong>
              </div>
            </div>
          )}
          
          <div>
            <Label>Athugasemdir</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="t.d. Móttók nýja sendingu"
            />
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Hætta við</Button>
            <Button onClick={handleSubmit}>Uppfæra</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Inventory Management Page

// NEW INVENTORY PAGE WITH SIDEBAR LAYOUT
function InventoryPage() {
  const [categories, setCategories] = useState([]);
  const sortedCategories = useMemo(() => categories?.sort((a, b) => (a.order || 0) - (b.order || 0)) || [], [categories]);
  const [inventory, setInventory] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [zoomDialogOpen, setZoomDialogOpen] = useState(false);
  const [zoomPaintItem, setZoomPaintItem] = useState(null);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [savingCategory, setSavingCategory] = useState(false);
  
  // Order dialog states
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [orderingItem, setOrderingItem] = useState(null);
  const [orderData, setOrderData] = useState({
    supplier_email: '',
    quantity_ml: 300,
    paint_code: '',
    message: ''
  });
  
  // Paint code search states for inventory
  const [inventoryPaintCodeSearch, setInventoryPaintCodeSearch] = useState('');
  const [inventoryPaintCodeResults, setInventoryPaintCodeResults] = useState([]);
  const [inventoryPaintCodeSearching, setInventoryPaintCodeSearching] = useState(false);
  const [showInventoryPaintCodeResults, setShowInventoryPaintCodeResults] = useState(false);
  
  const [formData, setFormData] = useState({
    category_id: '',
    product_name: '',
    quantity: 0,
    location: '',
    size: '',
    supplier: 'Orka',
    supplier_email: '',
    purchase_price: 0,
    purchase_price_with_vat: 0,
    selling_price: 0,
    selling_price_with_vat: 0,
    product_number: '',
    notes: '',
    // Paint specific
    paint_code: '',
    paint_name: '',
    paint_hex: '',
    manufacturer: '',
    sequence_number: '',
    min_quantity: null,
    // Custom fields
    custom_fields: {}
  });
  
  useEffect(() => {
    initializeData();
  }, []);
  
  useEffect(() => {
    if (selectedCategory) {
      loadInventory(selectedCategory.id);
    }
  }, [selectedCategory]);

  // Search paint codes for inventory with debouncing
  useEffect(() => {
    const searchInventoryPaintCodes = async () => {
      if (!inventoryPaintCodeSearch || inventoryPaintCodeSearch.trim().length < 2) {
        setInventoryPaintCodeResults([]);
        setShowInventoryPaintCodeResults(false);
        return;
      }
      
      try {
        setInventoryPaintCodeSearching(true);
        const response = await axios.post(`${API}/paint-codes/search`, {
          query: inventoryPaintCodeSearch
        });
        
        if (response.data.results) {
          setInventoryPaintCodeResults(response.data.results);
          setShowInventoryPaintCodeResults(true);
        }
      } catch (error) {
        console.error('Failed to search paint codes for inventory:', error);
        toast.error('Tókst ekki að leita að litakóðum');
      } finally {
        setInventoryPaintCodeSearching(false);
      }
    };
    
    // Debounce search by 500ms
    const debounceTimer = setTimeout(() => {
      searchInventoryPaintCodes();
    }, 500);
    
    return () => clearTimeout(debounceTimer);
  }, [inventoryPaintCodeSearch, API]);

  
  const initializeData = async () => {
    try {
      // Try to initialize categories first
      await axios.post(`${API}/inventory-categories/initialize`);
    } catch (error) {
      // Categories might already exist, that's okay
    }
    
    await loadCategories();
  };
  
  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/inventory-categories`);
      const cats = response.data || [];
      // Deduplicate categories by ID to prevent duplicates in UI
      const uniqueCats = Array.from(new Map(cats.map(c => [c.id, c])).values());
      setCategories(uniqueCats);
      if (uniqueCats.length > 0 && !selectedCategory) {
        setSelectedCategory(uniqueCats[0]);
      }
    } catch (error) {
      toast.error('Tókst ekki að hlaða flokkum');
    } finally {
      setLoading(false);
    }
  };
  
  const loadInventory = async (categoryId) => {
    try {
      const response = await axios.get(`${API}/inventory?category_id=${categoryId}`);
      setInventory(response.data || []);
    } catch (error) {
      toast.error('Tókst ekki að hlaða birgðum');
    }
  };
  
  const handleSaveCategory = async () => {
    if (savingCategory) return; // Prevent double submission
    setSavingCategory(true);
    try {
      if (editingCategory?.id) {
        await axios.put(`${API}/inventory-categories/${editingCategory.id}`, editingCategory);
        toast.success('Flokkur uppfærður');
      } else {
        await axios.post(`${API}/inventory-categories`, editingCategory);
        toast.success('Flokkur búinn til');
      }
      await loadCategories();
      setCategoryDialogOpen(false);
      setEditingCategory(null);
    } catch (error) {
      toast.error('Villa við að vista flokk');
    } finally {
      setSavingCategory(false);
    }
  };

  const handleSelectInventoryPaintCode = (paintCode) => {
    setFormData({
      ...formData,
      paint_code: paintCode.code,
      paint_name: paintCode.name,
      paint_hex: paintCode.hex,
      manufacturer: paintCode.make,
      product_name: paintCode.name
    });
    setInventoryPaintCodeSearch('');
    setShowInventoryPaintCodeResults(false);
    toast.success(`✅ Litakóði valinn: ${paintCode.code}`);
  };

  
  const handleSaveItem = async () => {
    try {
      const dataToSave = {
        ...formData,
        category_id: selectedCategory.id,
        item_type: selectedCategory.id, // For backward compatibility
      };
      
      // Clean up empty strings for optional fields
      if (dataToSave.sequence_number === '') {
        dataToSave.sequence_number = null;
      }
      if (dataToSave.min_quantity === '') {
        dataToSave.min_quantity = null;
      }
      
      if (editingItem) {
        await axios.put(`${API}/inventory/${editingItem.id}`, dataToSave);
        toast.success('Vara uppfærð');
      } else {
        await axios.post(`${API}/inventory`, dataToSave);
        toast.success('Vara búin til');
      }
      
      await loadInventory(selectedCategory.id);
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving item:', error);
      toast.error(error.response?.data?.detail || 'Villa við að vista vöru');
    }
  };
  
  const handleDelete = async (itemId) => {
    if (!confirm('Ertu viss um að þú viljir eyða þessari vöru?')) return;
    
    try {
      await axios.delete(`${API}/inventory/${itemId}`);
      toast.success('Vöru eytt');
      await loadInventory(selectedCategory.id);
    } catch (error) {
      toast.error('Tókst ekki að eyða vöru');
    }
  };

  const handlePrintLabel = async (item) => {
    console.log('🖨️ handlePrintLabel called with item:', item);
    
    // Validate that we have an item
    if (!item) {
      toast.error('Engin vara valin til að prenta');
      return;
    }
    
    // Validate that we have at least a paint code
    if (!item.paint_code) {
      toast.error('Lakkkóði vantar - ekki hægt að prenta límmiða');
      return;
    }
    
    // Show loading toast
    toast.info('Undirbý prentun...');
    
    // Build QR URL for paint image - use backend generated QR
    const qrImageUrl = `${API}/qr-code/${item.id}`;
    
    // Fetch label settings
    let settings = {
      width: 62,
      height: 29,
      font_size_code: 24,
      font_size_name: 14,
      font_size_details: 10,
      show_color_box: false,  // Changed to false - use QR instead
      show_qr_code: true,      // New setting
      show_manufacturer: true,
      show_size: true,
      show_location: true,
      show_sequence: true
    };
    
    try {
      const response = await axios.get(`${API}/admin/label-settings`);
      if (response.data) {
        settings = { ...settings, ...response.data };
        console.log('✅ Label settings loaded:', settings);
      }
    } catch (error) {
      console.log('⚠️ Using default label settings:', error.message);
    }
    
    const labelHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Límmiði - ${item.paint_code || 'Lakk'}</title>
        <meta charset="UTF-8">
        <style>
          @page {
            size: ${settings.width}mm ${settings.height}mm;
            margin: 0;
          }
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: Arial, sans-serif;
            padding: 0;
            margin: 0;
            width: ${settings.width}mm;
            height: ${settings.height}mm;
            overflow: hidden;
          }
          .label {
            border: 1px solid #000;
            padding: 2mm;
            width: 100%;
            height: 100%;
            box-sizing: border-box;
            background: white;
            overflow: hidden;
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 2mm;
          }
          .text-content {
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: center;
            gap: 1mm;
            text-align: left;
            overflow: hidden;
          }
          .color-box {
            width: ${Math.min(20, settings.width * 0.3)}mm;
            height: ${Math.min(25, settings.height * 0.7)}mm;
            border: 1px solid #333;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            color-adjust: exact;
            flex-shrink: 0;
          }
          .qr-code {
            width: ${Math.min(18, settings.height * 0.6)}mm;
            height: ${Math.min(18, settings.height * 0.6)}mm;
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
            border: 1px solid #ddd;
            background: white;
          }
          .qr-code img, .qr-code canvas {
            width: 100% !important;
            height: 100% !important;
            object-fit: contain;
            display: block;
          }
          .line {
            line-height: 1.2;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 100%;
          }
          .line-sequence-location {
            font-size: ${settings.font_size_details || 10}px;
            font-weight: bold;
            color: #000;
          }
          .line-code-manufacturer {
            font-size: ${settings.font_size_code || 24}px;
            font-weight: bold;
            color: #000;
          }
          .line-name {
            font-size: ${settings.font_size_name || 14}px;
            color: #000;
          }
          .line-size {
            font-size: ${settings.font_size_details || 10}px;
            color: #333;
          }
          @media print {
            body {
              padding: 0;
              margin: 0;
            }
            .label {
              border: 1px solid #000;
              page-break-inside: avoid;
            }
            .color-box {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              color-adjust: exact;
            }
          }
        </style>
      </head>
      <body>
        <div class="label">
          <div class="text-content">
            ${settings.show_sequence || settings.show_location ? `
              <div class="line line-sequence-location">
                ${settings.show_sequence && item.sequence_number ? `#${item.sequence_number}` : ''}
                ${settings.show_sequence && item.sequence_number && settings.show_location && (item.location || item.shelf_number) ? ' - ' : ''}
                ${settings.show_location && (item.location || item.shelf_number) ? `Hilla: ${item.location || item.shelf_number}` : ''}
              </div>
            ` : ''}
            <div class="line line-code-manufacturer">
              ${item.paint_code || ''} ${settings.show_manufacturer && item.manufacturer ? item.manufacturer : ''}
            </div>
            <div class="line line-name">${item.paint_name || item.product_name || ''}</div>
            ${settings.show_size && item.size ? `<div class="line line-size">${item.size}</div>` : ''}
          </div>
          ${settings.show_qr_code && item.id ? `
            <div class="qr-code">
              <img src="${qrImageUrl}" alt="QR Code" style="width:100%;height:100%;" />
            </div>
          ` : settings.show_color_box && item.paint_hex ? `
            <div class="color-box" style="background-color: ${item.paint_hex}; -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact;"></div>
          ` : ''}
        </div>
        <script>
          try {
            console.log('Print label loaded for:', '${(item.paint_code || 'unknown').replace(/'/g, "\\'")}');
            
            window.onload = function() {
              console.log('Window loaded, starting print in 1000ms to allow QR to load...');
              setTimeout(function() {
                console.log('Calling window.print()');
                window.print();
              }, 1000);
            }
          } catch(e) {
            console.error('Print error:', e);
            alert('Villa við prentun: ' + e.message);
          }
        </script>
      </body>
      </html>
    `;
    
    // Try to open print window
    try {
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      
      if (!printWindow) {
        // Fallback: Create blob and download
        console.warn('⚠️ Popup blocked, using blob fallback');
        const blob = new Blob([labelHtml], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `limmiði-${item.paint_code || 'lakk'}.html`;
        link.click();
        URL.revokeObjectURL(url);
        toast.warning('Popup lokað - límmiði vistaður sem HTML skrá. Opnaðu skrána og prentaðu.');
        return;
      }
      
      printWindow.document.write(labelHtml);
      printWindow.document.close();
      
      console.log('✅ Print window opened successfully');
      toast.success('Prentvinnugluggað opnaður!');
      
    } catch (error) {
      console.error('❌ Error opening print window:', error);
      toast.error('Villa við að opna prentvinnu: ' + error.message);
    }
  };

  
  const resetForm = () => {
    setFormData({
      category_id: selectedCategory?.id || '',
      product_name: '',
      quantity: 0,
      location: '',
      size: '',
      supplier: 'Orka',
      supplier_email: '',
      purchase_price: 0,
      purchase_price_with_vat: 0,
      selling_price: 0,
      selling_price_with_vat: 0,
      product_number: '',
      notes: '',
      paint_code: '',
      paint_name: '',
      paint_hex: '',
      manufacturer: '',
      sequence_number: '',
      min_quantity: null,
      custom_fields: {}
    });
    setEditingItem(null);
    // Clear paint code search
    setInventoryPaintCodeSearch('');
    setInventoryPaintCodeResults([]);
    setShowInventoryPaintCodeResults(false);
  };
  
  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData({
      category_id: item.category_id,
      product_name: item.product_name,
      quantity: item.quantity,
      location: item.location || item.shelf_number || '',
      size: item.size || '',
      supplier: item.supplier,
      supplier_email: item.supplier_email || '',
      purchase_price: item.purchase_price || item.purchase_price_no_vat || 0,
      purchase_price_with_vat: item.purchase_price_with_vat || 0,
      selling_price: item.selling_price || 0,
      selling_price_with_vat: item.selling_price_with_vat || 0,
      product_number: item.product_number || '',
      notes: item.notes || '',
      paint_code: item.paint_code || '',
      paint_name: item.paint_name || '',
      paint_hex: item.paint_hex || '',
      manufacturer: item.manufacturer || '',
      sequence_number: item.sequence_number || '',
      min_quantity: item.min_quantity,
      custom_fields: item.custom_fields || {}
    });
    setDialogOpen(true);
  };
  
  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center">Hleður lager...</div>
      </div>
    );
  }
  
  return (
    <div className="flex h-screen bg-slate-50">
      {/* Sidebar */}
      <div className="w-64 bg-white border-r border-slate-200 overflow-y-auto">
        <div className="p-4 border-b">
          <h2 className="text-lg font-bold">📦 Lager</h2>
        </div>
        
        {/* Category List */}
        <div className="p-2">
          {categories.map(category => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category)}
              className={`w-full text-left px-4 py-3 rounded-lg mb-1 transition-colors ${
                selectedCategory?.id === category.id
                  ? 'bg-blue-100 text-blue-900 font-medium'
                  : 'hover:bg-slate-100 text-slate-700'
              }`}
            >
              <span className="mr-2">{category.icon}</span>
              {category.name}
            </button>
          ))}
          
          <button
            onClick={() => {
              setEditingCategory({ name: '', icon: '📦', order: categories.length });
              setCategoryDialogOpen(true);
            }}
            className="w-full text-left px-4 py-3 rounded-lg mb-1 text-slate-500 hover:bg-slate-100 mt-2 border-t pt-4"
          >
            ➕ Nýr flokkur
          </button>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {selectedCategory && (
          <>
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold">
                  {selectedCategory.icon} {selectedCategory.name}
                </h1>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEditingCategory(selectedCategory);
                    setCategoryDialogOpen(true);
                  }}
                >
                  ✏️ Breyta nafni
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    if (window.confirm(`Ertu viss um að þú viljir eyða flokknum "${selectedCategory.name}"? Allar vörur í þessum flokki verða eyddar.`)) {
                      try {
                        await axios.delete(`${API}/inventory-categories/${selectedCategory.id}`);
                        toast.success(`Flokkur "${selectedCategory.name}" eytt`);
                        await loadCategories();
                      } catch (error) {
                        toast.error('Tókst ekki að eyða flokki');
                      }
                    }
                  }}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  🗑️ Eyða flokki
                </Button>
              </div>
              <Button onClick={() => {
                resetForm();
                setDialogOpen(true);
              }}>
                ➕ Ný vara
              </Button>
            </div>
            
            {/* Inventory Table */}
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {selectedCategory.id === 'paint' && <TableHead>Nr.</TableHead>}
                      {selectedCategory.id === 'paint' && <TableHead>Litur</TableHead>}
                      {selectedCategory.id === 'paint' && <TableHead>Kóði</TableHead>}
                      {selectedCategory.id !== 'paint' && <TableHead>Vörunúmer</TableHead>}
                      <TableHead>Nafn</TableHead>
                      {selectedCategory.id !== 'washer_fluid' && <TableHead>Staðsetning</TableHead>}
                      <TableHead>Stærð</TableHead>
                      <TableHead>Magn</TableHead>
                      <TableHead>Verð</TableHead>
                      <TableHead className="text-right">Aðgerðir</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inventory.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                          Engar vörur í þessum flokki. Bættu við!
                        </TableCell>
                      </TableRow>
                    ) : (
                      inventory.map(item => (
                        <TableRow key={item.id}>
                          {selectedCategory.id === 'paint' && (
                            <TableCell>
                              {item.sequence_number && (
                                <Badge className="bg-blue-100 text-blue-900">
                                  #{item.sequence_number}
                                </Badge>
                              )}
                            </TableCell>
                          )}
                          {selectedCategory.id !== 'paint' && (
                            <TableCell>
                              {item.product_number ? (
                                <Badge variant="outline">{item.product_number}</Badge>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </TableCell>
                          )}
                          {selectedCategory.id === 'paint' && (
                            <TableCell>
                              {item.paint_hex && (
                                <div
                                  className="w-12 h-12 rounded border-2 cursor-zoom-in hover:ring-2 hover:ring-blue-400"
                                  style={{backgroundColor: item.paint_hex}}
                                  onClick={() => {
                                    setZoomPaintItem({
                                      code: item.paint_code,
                                      name: item.paint_name,
                                      hex: item.paint_hex
                                    });
                                    setZoomDialogOpen(true);
                                  }}
                                />
                              )}
                            </TableCell>
                          )}
                          {selectedCategory.id === 'paint' && (
                            <TableCell>
                              <div className="font-bold">{item.paint_code}</div>
                            </TableCell>
                          )}
                          <TableCell>
                            <div className="font-medium">{item.product_name || item.paint_name}</div>
                            {item.manufacturer && (
                              <div className="text-xs text-slate-500">{item.manufacturer}</div>
                            )}
                          </TableCell>
                          {selectedCategory.id !== 'washer_fluid' && (
                            <TableCell>
                              <Badge variant="outline">{item.location || item.shelf_number || '-'}</Badge>
                            </TableCell>
                          )}
                          <TableCell>{item.size || '-'}</TableCell>
                          <TableCell>
                            <span className="font-bold text-green-600">{item.quantity}</span>
                            {item.min_quantity && item.quantity <= item.min_quantity && (
                              <span className="ml-2 text-red-600">⚠️</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {selectedCategory.id === 'paint' ? (
                              <div className="text-sm">
                                {item.purchase_price || item.purchase_price_with_vat ? 
                                  `${(item.purchase_price || item.purchase_price_with_vat).toLocaleString()} kr` : 
                                  '-'}
                              </div>
                            ) : (
                              <div className="text-xs space-y-1">
                                {item.purchase_price > 0 && (
                                  <div>
                                    <span className="text-slate-500">Inn án VSK:</span> {item.purchase_price.toLocaleString()} kr
                                    {selectedCategory.id === 'washer_fluid' && parseFloat(item.size) > 0 && (
                                      <span className="text-orange-700 font-medium ml-2">
                                        (≈{Math.round(item.purchase_price / parseFloat(item.size))} kr/L)
                                      </span>
                                    )}
                                  </div>
                                )}
                                {item.purchase_price_with_vat > 0 && (
                                  <div>
                                    <span className="text-slate-500">Inn m/VSK:</span> {item.purchase_price_with_vat.toLocaleString()} kr
                                    {selectedCategory.id === 'washer_fluid' && parseFloat(item.size) > 0 && (
                                      <span className="text-orange-700 font-medium ml-2">
                                        (≈{Math.round(item.purchase_price_with_vat / parseFloat(item.size))} kr/L)
                                      </span>
                                    )}
                                  </div>
                                )}
                                {item.selling_price > 0 && (
                                  <div className="text-blue-700 font-medium">
                                    <span className="text-slate-500">Sala án VSK:</span> {item.selling_price.toLocaleString()} kr
                                    {selectedCategory.id === 'washer_fluid' && parseFloat(item.size) > 0 && (
                                      <span className="text-green-700 font-medium ml-2">
                                        (≈{Math.round(item.selling_price / parseFloat(item.size))} kr/L)
                                      </span>
                                    )}
                                  </div>
                                )}
                                {item.selling_price_with_vat > 0 && (
                                  <div className="text-blue-700 font-medium">
                                    <span className="text-slate-500">Sala m/VSK:</span> {item.selling_price_with_vat.toLocaleString()} kr
                                    {selectedCategory.id === 'washer_fluid' && parseFloat(item.size) > 0 && (
                                      <span className="text-green-700 font-medium ml-2">
                                        (≈{Math.round(item.selling_price_with_vat / parseFloat(item.size))} kr/L)
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1 justify-end">
                              {selectedCategory.id === 'paint' && (
                                <Button size="sm" variant="outline" onClick={() => handlePrintLabel(item)} title="Prenta límiða">
                                  🖨️
                                </Button>
                              )}
                              {selectedCategory.id === 'paint' && (
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  onClick={() => {
                                    // Initialize order data with item info
                                    setOrderData({
                                      supplier_email: item.supplier_email || 'orka@orka.is',
                                      quantity_ml: 300,
                                      paint_code: item.paint_code,
                                      message: `Góðan dag,\n\nVið óskum eftir að panta:\n\nLitarkóði: ${item.paint_code}\nNafn: ${item.paint_name}\nMagn: 300ml\n\nKveðja,\nAlvörubón ehf.`
                                    });
                                    setOrderingItem(item);
                                    setOrderDialogOpen(true);
                                  }} 
                                  title="Panta meira"
                                  className="text-blue-600"
                                >
                                  📦
                                </Button>
                              )}
                              <Button size="sm" variant="outline" onClick={() => openEditDialog(item)}>
                                ✏️
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleDelete(item.id)} className="text-red-600">
                                🗑️
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        )}
      </div>
      
      {/* Category Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCategory?.id ? 'Breyta flokki' : 'Nýr flokkur'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nafn flokks</Label>
              <Input
                value={editingCategory?.name || ''}
                onChange={(e) => setEditingCategory({...editingCategory, name: e.target.value})}
                placeholder="t.d. Tuskur og handklæði"
              />
            </div>
            <div>
              <Label>Emoji</Label>
              <Input
                value={editingCategory?.icon || ''}
                onChange={(e) => setEditingCategory({...editingCategory, icon: e.target.value})}
                placeholder="🧽"
                maxLength={2}
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSaveCategory} className="flex-1" disabled={savingCategory}>
                {savingCategory ? 'Vistar...' : 'Vistaa'}
              </Button>
              <Button variant="outline" onClick={() => setCategoryDialogOpen(false)} className="flex-1" disabled={savingCategory}>
                Hætta við
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Item Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Breyta vöru' : 'Ný vara'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Paint specific fields */}
            {selectedCategory?.id === 'paint' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Raðnúmer</Label>
                    <Input
                      type="number"
                      value={formData.sequence_number}
                      onChange={(e) => setFormData({...formData, sequence_number: e.target.value})}
                    />
                  </div>
                  <div className="relative">
                    <Label>Lakkkóði * 🔍</Label>
                    <Input
                      value={formData.paint_code}
                      onChange={(e) => {
                        setFormData({...formData, paint_code: e.target.value});
                        setInventoryPaintCodeSearch(e.target.value);
                      }}
                      placeholder="C1M, 218, A7W, 41V..."
                      onFocus={() => {
                        if (inventoryPaintCodeSearch && inventoryPaintCodeResults.length > 0) {
                          setShowInventoryPaintCodeResults(true);
                        }
                      }}
                    />
                    {inventoryPaintCodeSearching && (
                      <div className="absolute right-3 top-8 text-xs text-slate-500">⏳</div>
                    )}
                    
                    {/* Search Results Dropdown */}
                    {showInventoryPaintCodeResults && inventoryPaintCodeResults.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg max-h-80 overflow-y-auto">
                        <div className="p-2 bg-blue-50 border-b border-blue-200 font-medium text-sm text-blue-900">
                          🔍 {inventoryPaintCodeResults.length} niðurstöður
                        </div>
                        {inventoryPaintCodeResults.map((result, idx) => (
                          <div
                            key={idx}
                            onClick={() => handleSelectInventoryPaintCode(result)}
                            className="p-3 hover:bg-blue-50 cursor-pointer border-b border-slate-100 last:border-b-0"
                          >
                            <div className="flex items-center gap-3">
                              {result.hex && (
                                <div
                                  className="w-8 h-8 rounded border-2 flex-shrink-0"
                                  style={{backgroundColor: result.hex}}
                                />
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="font-bold text-slate-900">{result.code}</div>
                                <div className="text-sm text-slate-600 truncate">{result.name}</div>
                                <div className="text-xs text-slate-500">{result.make} {result.model} {result.year ? `(${result.year})` : ''}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {inventoryPaintCodeSearch && inventoryPaintCodeSearch.length >= 2 && !inventoryPaintCodeSearching && inventoryPaintCodeResults.length === 0 && showInventoryPaintCodeResults && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg p-4 text-center text-slate-500">
                        <p>Engar niðurstöður</p>
                        <p className="text-xs mt-1">Reyndu aðra leitarorð</p>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <Label>Nafn lakks *</Label>
                  <Input
                    value={formData.paint_name}
                    onChange={(e) => setFormData({...formData, paint_name: e.target.value, product_name: e.target.value})}
                    placeholder="Phytonic Blue Metallic"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Framleiðandi</Label>
                    <Input
                      value={formData.manufacturer}
                      onChange={(e) => setFormData({...formData, manufacturer: e.target.value})}
                      placeholder="BMW, Toyota..."
                    />
                  </div>
                  <div>
                    <Label>HEX litur</Label>
                    <div className="flex gap-2">
                      <Input
                        value={formData.paint_hex}
                        onChange={(e) => setFormData({...formData, paint_hex: e.target.value})}
                        placeholder="#37568F"
                      />
                      {formData.paint_hex && (
                        <div className="w-10 h-10 rounded border-2" style={{backgroundColor: formData.paint_hex}} />
                      )}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Staðsetning/Hilla</Label>
                    <Input
                      value={formData.location}
                      onChange={(e) => setFormData({...formData, location: e.target.value})}
                      placeholder="A1, B2..."
                    />
                  </div>
                  <div>
                    <Label>Stærð/Eining</Label>
                    <Input
                      value={formData.size}
                      onChange={(e) => setFormData({...formData, size: e.target.value})}
                      placeholder="250ml, 500ml, 1L..."
                    />
                  </div>
                </div>
              </>
            )}
            
            {/* Common fields */}
            {selectedCategory?.id !== 'paint' && (
              <div>
                <Label>Nafn vöru *</Label>
                <Input
                  value={formData.product_name}
                  onChange={(e) => setFormData({...formData, product_name: e.target.value})}
                />
              </div>
            )}
            
            {selectedCategory?.id !== 'washer_fluid' && selectedCategory?.id !== 'paint' && (
              <div>
                <Label>Staðsetning</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  placeholder="Hilla A1, B2..."
                />
              </div>
            )}
            
            {selectedCategory?.id === 'paint' ? null : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Stærð/Eining</Label>
                  <Input
                    type={selectedCategory?.id === 'washer_fluid' ? 'number' : 'text'}
                    value={formData.size}
                    onChange={(e) => setFormData({...formData, size: e.target.value})}
                    placeholder={selectedCategory?.id === 'washer_fluid' || selectedCategory?.id === 'wax' ? 'Lítrar' : '250ml, Staðlað...'}
                  />
                  {selectedCategory?.id === 'washer_fluid' && (
                    <div className="text-xs text-slate-500 mt-1">
                      Lítrar í hverri einingu (t.d. 200 fyrir 200L tunnu)
                    </div>
                  )}
                </div>
                <div>
                  <Label>Magn á lager *</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.quantity}
                    onChange={(e) => setFormData({...formData, quantity: parseFloat(e.target.value) || 0})}
                  />
                  {selectedCategory?.id === 'washer_fluid' && (
                    <div className="text-xs text-slate-500 mt-1">
                      Fjöldi eininga (t.d. 1 tunna, 5 tunnur)
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {selectedCategory?.id === 'paint' && (
              <div>
                <Label>Magn á lager *</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.quantity}
                  onChange={(e) => setFormData({...formData, quantity: parseFloat(e.target.value) || 0})}
                />
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Birgir</Label>
                <Input
                  value={formData.supplier}
                  onChange={(e) => setFormData({...formData, supplier: e.target.value})}
                />
              </div>
              <div>
                <Label>Birgja netfang</Label>
                <Input
                  type="email"
                  value={formData.supplier_email}
                  onChange={(e) => setFormData({...formData, supplier_email: e.target.value})}
                />
              </div>
            </div>
            
            {/* Product number for non-paint items */}
            {selectedCategory?.id !== 'paint' && (
              <div>
                <Label>Vörunúmer</Label>
                <Input
                  value={formData.product_number}
                  onChange={(e) => setFormData({...formData, product_number: e.target.value})}
                  placeholder="T.d. 12345"
                />
              </div>
            )}
            
            {/* Price fields - show for non-paint categories */}
            {selectedCategory?.id !== 'paint' && (
              <>
                <div className="border-t pt-4">
                  <h3 className="font-medium mb-3">💰 Innkaupsverð</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Án VSK</Label>
                      <Input
                        type="number"
                        value={formData.purchase_price}
                        onChange={(e) => {
                          const priceNoVat = parseFloat(e.target.value) || 0;
                          setFormData({
                            ...formData, 
                            purchase_price: priceNoVat,
                            purchase_price_with_vat: Math.round(priceNoVat * 1.24)
                          });
                        }}
                        placeholder="0"
                      />
                      {selectedCategory?.id === 'washer_fluid' && parseFloat(formData.size) > 0 && formData.purchase_price > 0 && (
                        <div className="text-xs text-orange-600 font-medium mt-1">
                          ≈{Math.round(formData.purchase_price / parseFloat(formData.size))} kr/L
                        </div>
                      )}
                    </div>
                    <div>
                      <Label>Með VSK (24%)</Label>
                      <Input
                        type="number"
                        value={formData.purchase_price_with_vat}
                        onChange={(e) => {
                          const priceWithVat = parseFloat(e.target.value) || 0;
                          setFormData({
                            ...formData, 
                            purchase_price_with_vat: priceWithVat,
                            purchase_price: Math.round(priceWithVat / 1.24)
                          });
                        }}
                        placeholder="0"
                      />
                      {selectedCategory?.id === 'washer_fluid' && parseFloat(formData.size) > 0 && formData.purchase_price_with_vat > 0 && (
                        <div className="text-xs text-orange-600 font-medium mt-1">
                          ≈{Math.round(formData.purchase_price_with_vat / parseFloat(formData.size))} kr/L
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="border-t pt-4">
                  <h3 className="font-medium mb-3">💵 Söluverð</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Án VSK</Label>
                      <Input
                        type="number"
                        value={formData.selling_price}
                        onChange={(e) => {
                          const priceNoVat = parseFloat(e.target.value) || 0;
                          setFormData({
                            ...formData, 
                            selling_price: priceNoVat,
                            selling_price_with_vat: Math.round(priceNoVat * 1.24)
                          });
                        }}
                        placeholder="0"
                      />
                      {selectedCategory?.id === 'washer_fluid' && parseFloat(formData.size) > 0 && formData.selling_price > 0 && (
                        <div className="text-xs text-green-600 font-medium mt-1">
                          ≈{Math.round(formData.selling_price / parseFloat(formData.size))} kr/L
                        </div>
                      )}
                    </div>
                    <div>
                      <Label>Með VSK (24%)</Label>
                      <Input
                        type="number"
                        value={formData.selling_price_with_vat}
                        onChange={(e) => {
                          const priceWithVat = parseFloat(e.target.value) || 0;
                          setFormData({
                            ...formData, 
                            selling_price_with_vat: priceWithVat,
                            selling_price: Math.round(priceWithVat / 1.24)
                          });
                        }}
                        placeholder="0"
                      />
                      {selectedCategory?.id === 'washer_fluid' && parseFloat(formData.size) > 0 && formData.selling_price_with_vat > 0 && (
                        <div className="text-xs text-green-600 font-medium mt-1">
                          ≈{Math.round(formData.selling_price_with_vat / parseFloat(formData.size))} kr/L
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
            
            {/* Only purchase price for paint */}
            {selectedCategory?.id === 'paint' && (
              <div>
                <Label>Innkaupsverð (án VSK)</Label>
                <Input
                  type="number"
                  value={formData.purchase_price}
                  onChange={(e) => setFormData({...formData, purchase_price: parseFloat(e.target.value) || 0})}
                />
              </div>
            )}
            
            <div>
              <Label>Athugasemdir</Label>
              <textarea
                className="w-full border rounded p-2"
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
              />
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button onClick={handleSaveItem} className="flex-1">💾 Vista</Button>
              {selectedCategory?.id === 'paint' && formData.paint_code && (
                <Button 
                  onClick={() => handlePrintLabel({
                    ...editingItem,
                    ...formData
                  })} 
                  variant="outline"
                  className="flex-1"
                >
                  🖨️ Prenta límiða
                </Button>
              )}
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                Hætta við
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Paint Color Zoom Dialog */}
      {zoomPaintItem && (
        <Dialog open={zoomDialogOpen} onOpenChange={setZoomDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-2xl">🎨 Litasýni - {zoomPaintItem.code}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex justify-center">
                <div
                  className="w-64 h-64 rounded-lg border-4 border-slate-300 shadow-lg"
                  style={{backgroundColor: zoomPaintItem.hex}}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50 dark:bg-slate-800 p-4 rounded-lg">
                <div>
                  <div className="text-sm text-slate-600 dark:text-slate-400">Lakkkóði</div>
                  <div className="font-bold text-2xl dark:text-slate-100">{zoomPaintItem.code}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-600 dark:text-slate-400">Heiti</div>
                  <div className="font-medium text-lg dark:text-slate-100">{zoomPaintItem.name}</div>
                </div>
                <div className="md:col-span-2">
                  <div className="text-sm text-slate-600">HEX litur</div>
                  <div className="font-mono text-lg">{zoomPaintItem.hex}</div>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Order Dialog */}
      <Dialog open={orderDialogOpen} onOpenChange={(open) => {
        setOrderDialogOpen(open);
        if (!open) {
          setOrderingItem(null);
          setOrderData({
            supplier_email: '',
            quantity_ml: 300,
            paint_code: '',
            message: ''
          });
        }
      }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>📦 Panta lakk</DialogTitle>
          </DialogHeader>
          {orderingItem && (
            <div className="space-y-4">
              {/* Item Info */}
              <div className="bg-slate-50 p-4 rounded-lg border">
                <div className="font-bold text-lg">{orderingItem.paint_code}</div>
                <div className="text-sm text-slate-600">{orderingItem.paint_name}</div>
                {orderingItem.manufacturer && (
                  <div className="text-xs text-slate-500 mt-1">{orderingItem.manufacturer}</div>
                )}
              </div>
              
              {/* Supplier Email */}
              <div>
                <Label>Email birgis *</Label>
                <Input
                  type="email"
                  value={orderData.supplier_email || orderingItem.supplier_email || 'orka@orka.is'}
                  onChange={(e) => setOrderData({...orderData, supplier_email: e.target.value})}
                  placeholder="orka@orka.is"
                />
              </div>
              
              {/* Paint Code (editable) */}
              <div>
                <Label>Litarkóði *</Label>
                <Input
                  value={orderData.paint_code || orderingItem.paint_code}
                  onChange={(e) => setOrderData({...orderData, paint_code: e.target.value})}
                  placeholder="T.d. C1M"
                />
              </div>
              
              {/* Quantity */}
              <div>
                <Label>Magn (ml) *</Label>
                <Input
                  type="number"
                  min="250"
                  max="1000"
                  step="50"
                  value={orderData.quantity_ml}
                  onChange={(e) => setOrderData({...orderData, quantity_ml: parseInt(e.target.value) || 300})}
                />
                <div className="text-xs text-slate-500 mt-1">
                  Lágmark: 250ml • Hámark: 1,000ml
                </div>
              </div>
              
              {/* Message */}
              <div>
                <Label>Skilaboð til birgis</Label>
                <textarea
                  value={orderData.message || `Góðan dag,\n\nVið óskum eftir að panta:\n\nLitarkóði: ${orderingItem.paint_code}\nNafn: ${orderingItem.paint_name}\nMagn: ${orderData.quantity_ml}ml\n\nKveðja,\nAlvörubón ehf.`}
                  onChange={(e) => setOrderData({...orderData, message: e.target.value})}
                  className="w-full p-3 border rounded-lg h-32 resize-none"
                  placeholder="Skilaboð..."
                />
              </div>
              
              {/* Actions */}
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setOrderDialogOpen(false)} className="flex-1">
                  Hætta við
                </Button>
                <Button 
                  onClick={async () => {
                    try {
                      const emailToSend = orderData.supplier_email || orderingItem.supplier_email || 'orka@orka.is';
                      const paintCode = orderData.paint_code || orderingItem.paint_code;
                      const message = orderData.message || `Góðan dag,\n\nVið óskum eftir að panta:\n\nLitarkóði: ${paintCode}\nNafn: ${orderingItem.paint_name}\nMagn: ${orderData.quantity_ml}ml\n\nKveðja,\nAlvörubón ehf.`;
                      
                      await axios.post(`${API}/inventory/${orderingItem.id}/order-email`, {
                        to_email: emailToSend,
                        paint_code: paintCode,
                        quantity_ml: orderData.quantity_ml,
                        message: message
                      });
                      
                      toast.success(`✅ Pöntun send til ${emailToSend}`);
                      setOrderDialogOpen(false);
                    } catch (error) {
                      toast.error('Villa við að senda pöntun');
                    }
                  }}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  📧 Senda pöntun
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


// Product Services Manager Component
function ProductServicesManager() {
  const [productServices, setProductServices] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    inventory_item_id: '',
    unit_type: 'liters',
    price_per_unit: 0,
    available: true,
    payday_product_id: ''
  });
  
  const loadData = async () => {
    try {
      const [servicesRes, inventoryRes] = await Promise.all([
        axios.get(`${API}/product-services`),
        axios.get(`${API}/inventory`)
      ]);
      
      setProductServices(servicesRes.data || []);
      // Filter for non-paint items (frames, washer fluid, wax)
      const nonPaintItems = (inventoryRes.data || []).filter(item => item.item_type !== 'paint');
      setInventoryItems(nonPaintItems);
    } catch (error) {
      console.error('Failed to load product services:', error);
      toast.error('Tókst ekki að hlaða vörum');
    }
  };
  
  useEffect(() => {
    loadData();
  }, []);
  
  const handleSave = async () => {
    try {
      if (!formData.name) {
        toast.error('Nafn vantar');
        return;
      }
      
      if (editingService) {
        await axios.put(`${API}/product-services/${editingService.id}`, formData);
        toast.success('Vara uppfærð');
      } else {
        await axios.post(`${API}/product-services`, formData);
        toast.success('Vara búin til');
      }
      
      setDialogOpen(false);
      setEditingService(null);
      resetForm();
      await loadData();
    } catch (error) {
      console.error('Failed to save product service:', error);
      toast.error('Tókst ekki að vista vöru');
    }
  };
  
  const handleDelete = async (serviceId) => {
    if (!confirm('Ertu viss um að þú viljir eyða þessari vöru?')) return;
    
    try {
      await axios.delete(`${API}/product-services/${serviceId}`);
      toast.success('Vöru eytt');
      await loadData();
    } catch (error) {
      toast.error('Tókst ekki að eyða vöru');
    }
  };
  
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      inventory_item_id: '',
      unit_type: 'liters',
      price_per_unit: 0,
      allow_discount: false,
      max_discount: 0,
      available: true,
      payday_product_id: ''
    });
  };
  
  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>📦 Vörunotkun (frá lager)</CardTitle>
            <p className="text-sm text-slate-600 mt-1">
              Vörur sem draga úr birgðum (rúðupiss, númerrammar, osfrv.)
            </p>
          </div>
          <Button onClick={() => {
            setEditingService(null);
            resetForm();
            setDialogOpen(true);
          }}>
            ➕ Bæta við vöru
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nafn</TableHead>
              <TableHead>Lýsing</TableHead>
              <TableHead>Mælieining</TableHead>
              <TableHead>Verð/Eining</TableHead>
              <TableHead>Á lager</TableHead>
              <TableHead>Staða</TableHead>
              <TableHead>Aðgerðir</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {productServices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                  Engar vörur skráðar. Bættu við!
                </TableCell>
              </TableRow>
            ) : (
              productServices.map(service => (
                <TableRow key={service.id}>
                  <TableCell>
                    <div className="font-medium">{service.name}</div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-slate-600">{service.description || '-'}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {service.unit_type === 'liters' ? '📏 Lítrar' : service.unit_type === 'sets' ? '📦 Sett' : '🔢 Stykki'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{service.price_per_unit?.toLocaleString()} ISK</div>
                  </TableCell>
                  <TableCell>
                    {service.in_stock ? (
                      service.unit_type === 'liters' && service.liters_in_stock ? (
                        <Badge className="bg-green-100 text-green-800">
                          ✅ ≈{service.liters_in_stock}L á lager
                        </Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-800">
                          ✅ {service.stock_quantity} á lager
                        </Badge>
                      )
                    ) : (
                      <Badge className="bg-red-100 text-red-800">
                        ⚠️ Ekki á lager
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {service.available ? (
                      <Badge className="bg-blue-100 text-blue-800">Virkt</Badge>
                    ) : (
                      <Badge variant="outline">Óvirkt</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingService(service);
                          setFormData({
                            name: service.name,
                            description: service.description || '',
                            inventory_item_id: service.inventory_item_id,
                            unit_type: service.unit_type,
                            price_per_unit: service.price_per_unit,
                            allow_discount: service.allow_discount || false,
                            max_discount: service.max_discount || 0,
                            available: service.available,
                            payday_product_id: service.payday_product_id || ''
                          });
                          setDialogOpen(true);
                        }}
                      >
                        ✏️ Breyta
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(service.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        🗑️
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
      
      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingService ? '✏️ Breyta vöru' : '➕ Bæta við vöru'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nafn vöru *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Rúðupiss, Númerrammi..."
              />
            </div>
            
            <div>
              <Label>Lýsing</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Nánar um vöruna..."
              />
            </div>
            
            <div>
              <Label>Tengja við lager vöru *</Label>
              <Select 
                value={formData.inventory_item_id} 
                onValueChange={(v) => setFormData({...formData, inventory_item_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Veldu vöru úr lager" />
                </SelectTrigger>
                <SelectContent>
                  {inventoryItems.map(item => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.product_name} - {item.size} ({item.quantity} á lager)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-xs text-slate-500 mt-1">
                Þessi vara verður dregin frá birgðum þegar hún er seld
              </div>
            </div>

            <div>
              <Label>Payday vörunúmer (SKU)</Label>
              <Input
                value={formData.payday_product_id || ''}
                onChange={(e) => setFormData({...formData, payday_product_id: e.target.value})}
                placeholder="t.d. 1002"
              />
              <div className="text-xs text-slate-500 mt-1">
                Sláðu inn vörunúmer (SKU) úr Payday til að tengja við reikninga.
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Mælieining *</Label>
                <Select 
                  value={formData.unit_type} 
                  onValueChange={(v) => setFormData({...formData, unit_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="liters">📏 Lítrar</SelectItem>
                    <SelectItem value="pieces">🔢 Stykki</SelectItem>
                    <SelectItem value="sets">📦 Sett</SelectItem>
                  </SelectContent>
                </Select>
                {formData.unit_type === 'liters' && (
                  <div className="text-xs text-blue-600 mt-1">
                    💡 Dæmi: Rúðupiss 200L tunna - selja 5L í einu
                  </div>
                )}
              </div>
              
              <div>
                <Label>Verð per {formData.unit_type === 'liters' ? 'lítra' : formData.unit_type === 'sets' ? 'sett' : 'stykki'} *</Label>
                <Input
                  type="number"
                  value={formData.price_per_unit}
                  onChange={(e) => setFormData({...formData, price_per_unit: parseFloat(e.target.value) || 0})}
                  placeholder="500"
                />
                {formData.unit_type === 'liters' && (
                  <div className="text-xs text-slate-500 mt-1">
                    Verð per 1 lítra (t.d. 500 ISK/L)
                  </div>
                )}
              </div>
            </div>
            
            <div className="border rounded-lg p-3 bg-slate-50 space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="allow_discount"
                  checked={formData.allow_discount}
                  onCheckedChange={(checked) => setFormData({...formData, allow_discount: checked})}
                />
                <Label htmlFor="allow_discount" className="cursor-pointer">
                  Leyfa afslátt á þessa vöru
                </Label>
              </div>
              
              {formData.allow_discount && (
                <div>
                  <Label>Hámarks afsláttur (%) *</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.max_discount}
                    onChange={(e) => setFormData({...formData, max_discount: parseFloat(e.target.value) || 0})}
                    placeholder="10"
                  />
                  <div className="text-xs text-slate-500 mt-1">
                    T.d. ef hámark er 10%, þá fær viðskiptavinur max 10% afslátt þó hann hafi 15%
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              <Checkbox
                id="available"
                checked={formData.available}
                onCheckedChange={(checked) => setFormData({...formData, available: checked})}
              />
              <Label htmlFor="available" className="cursor-pointer">
                Virk vara (sýnist í skráningu)
              </Label>
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={() => {
                setDialogOpen(false);
                setEditingService(null);
                resetForm();
              }} className="flex-1">
                Hætta við
              </Button>
              <Button onClick={handleSave} className="flex-1 bg-blue-600 hover:bg-blue-700">
                💾 Vista
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


// Price List Page Component - For Staff to quickly view prices
function PriceListPage() {
  // API constant definition
  const API_BASE = API_BASE_URL || 'http://localhost:8001';
  const API = `${API_BASE}/api`;
  
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [discount, setDiscount] = useState(0);
  const [showWithoutVAT, setShowWithoutVAT] = useState(false);
  
  // Vehicle lookup state for highlighting prices
  const [plateInput, setPlateInput] = useState('');
  const [lookingUpPlate, setLookingUpPlate] = useState(false);
  const [highlightedSizeId, setHighlightedSizeId] = useState(null);
  const [vehicleInfo, setVehicleInfo] = useState(null);
  
  // Price list email state
  const [pricelistDialogOpen, setPricelistDialogOpen] = useState(false);
  const [pricelistForm, setPricelistForm] = useState({
    recipient_email: '',
    recipient_name: '',
    company_name: '',
    custom_message: '',
    selected_services: [],
    selected_sizes: [],
    show_discount: false,
    discount_percentage: 0,
    without_vat: false
  });
  const [sendingPricelist, setSendingPricelist] = useState(false);
  
  useEffect(() => {
    loadSettings();
  }, []);
  
  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      toast.error('Tókst ekki að hlaða verðskrá');
    } finally {
      setLoading(false);
    }
  };
  
  const calculatePrice = (basePrice) => {
    if (!basePrice) return '-';
    let price = basePrice;
    
    // Remove VAT if enabled (24% VAT in Iceland)
    if (showWithoutVAT) {
      price = price / 1.24;
    }
    
    // Apply discount
    const discountedPrice = price * (1 - discount / 100);
    return Math.round(discountedPrice).toLocaleString('is-IS');
  };
  
  const handlePrint = () => {
    window.print();
  };
  
  // Lookup vehicle by plate to find its size and highlight the column
  const lookupPlateForPricing = async (plate) => {
    const cleanPlate = (plate || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (cleanPlate.length < 3) {
      setHighlightedSizeId(null);
      setVehicleInfo(null);
      return;
    }
    
    setLookingUpPlate(true);
    try {
      const response = await axios.get(`${API}/vehicles/lookup/${cleanPlate}`);
      if (response.data && response.data.found) {
        const vehicle = response.data;
        const make = vehicle.manufacturer || vehicle.make || '';
        setVehicleInfo({
          make: make,
          model: vehicle.model,
          size: vehicle.size
        });
        
        // Find matching size ID
        if (settings?.sizes && vehicle.size) {
          const matchingSize = settings.sizes.find(s => 
            s.id === vehicle.size || 
            s.name?.toLowerCase() === vehicle.size?.toLowerCase()
          );
          if (matchingSize) {
            setHighlightedSizeId(matchingSize.id);
            toast.success(`🚗 ${make} ${vehicle.model} - ${matchingSize.name}`);
          } else {
            setHighlightedSizeId(null);
            toast.info(`🚗 ${make} ${vehicle.model} - Stærð ekki skilgreind`);
          }
        }
      } else {
        setHighlightedSizeId(null);
        setVehicleInfo(null);
      }
    } catch (error) {
      console.log('Plate lookup failed:', error);
      setHighlightedSizeId(null);
      setVehicleInfo(null);
    } finally {
      setLookingUpPlate(false);
    }
  };
  
  // Debounced plate lookup
  useEffect(() => {
    const timer = setTimeout(() => {
      if (plateInput.length >= 3) {
        lookupPlateForPricing(plateInput);
      } else {
        setHighlightedSizeId(null);
        setVehicleInfo(null);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [plateInput]);
  
  // Send price list email
  const sendPricelist = async () => {
    if (!pricelistForm.recipient_email) {
      toast.error('Netfang vantar');
      return;
    }
    
    try {
      setSendingPricelist(true);
      
      const response = await axios.post(`${API}/send-pricelist`, pricelistForm);
      
      if (response.data.success) {
        toast.success(`✅ Verðskrá send á ${pricelistForm.recipient_email}`);
        setPricelistDialogOpen(false);
        // Reset form
        setPricelistForm({
          recipient_email: '',
          recipient_name: '',
          company_name: '',
          custom_message: '',
          selected_services: [],
          selected_sizes: [],
          show_discount: false,
          discount_percentage: 0,
          without_vat: false
        });
      } else {
        toast.error('Tókst ekki að senda verðskrá');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Villa við að senda verðskrá');
    } finally {
      setSendingPricelist(false);
    }
  };
  
  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="text-lg">⏳ Hleð verðskrá...</div>
      </div>
    );
  }
  
  if (!settings) {
    return (
      <div className="p-8">
        <div className="text-red-600">❌ Tókst ekki að hlaða verðskrá</div>
      </div>
    );
  }
  
  // Get all services and sizes
  // Filter services: only show those with display_in_pricelist !== false
  // Sort by order field (ascending) - use ?? instead of || to handle order: 0 correctly
  const services = (settings.services || [])
    .filter(service => service.display_in_pricelist !== false)
    .sort((a, b) => {
      const orderA = a.order !== undefined && a.order !== null ? a.order : 999;
      const orderB = b.order !== undefined && b.order !== null ? b.order : 999;
      return orderA - orderB;
    });
  const sizes = settings.sizes || [];
  const prices = settings.prices || {};
  
  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto">
      {/* Header Section - Hidden on Print */}
      <div className="print:hidden mb-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">📋 Verðskrá</h1>
            <p className="text-sm text-slate-600 mt-1">
              Fljótleg yfirlitsskoðun fyrir starfsfólk
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Vehicle Plate Lookup */}
            <div className="flex items-center gap-2">
              <div className="relative">
                <Input
                  value={plateInput}
                  onChange={(e) => setPlateInput(e.target.value.toUpperCase())}
                  placeholder="🚗 Bílnúmer..."
                  className={`w-36 font-mono uppercase ${lookingUpPlate ? 'animate-pulse' : ''} ${highlightedSizeId ? 'border-green-500 bg-green-50' : ''}`}
                  data-testid="pricelist-plate-input"
                />
                {lookingUpPlate && (
                  <div className="absolute right-2 top-2">
                    <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                  </div>
                )}
              </div>
              {vehicleInfo && (
                <div className="text-sm bg-green-100 text-green-800 px-2 py-1 rounded-lg font-medium">
                  {vehicleInfo.make} {vehicleInfo.model}
                </div>
              )}
              {highlightedSizeId && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setPlateInput('');
                    setHighlightedSizeId(null);
                    setVehicleInfo(null);
                  }}
                  className="text-red-600 hover:text-red-700 h-8 px-2"
                >
                  ✕
                </Button>
              )}
            </div>
            <Button 
              onClick={() => setPricelistDialogOpen(true)} 
              variant="outline"
              className="flex items-center gap-2 bg-green-50 hover:bg-green-100 text-green-700 border-green-300"
            >
              📧 Senda á tölvupóst
            </Button>
            <Button onClick={handlePrint} className="flex items-center gap-2">
              🖨️ Prenta verðskrá
            </Button>
          </div>
        </div>
        
        {/* Discount Calculator */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <Label htmlFor="discount" className="font-semibold min-w-fit">
                🧮 Reiknivél fyrir afslátt:
              </Label>
              <div className="flex items-center gap-3 flex-1">
                <Input
                  id="discount"
                  type="number"
                  min="0"
                  max="100"
                  value={discount}
                  onChange={(e) => setDiscount(Math.min(100, Math.max(0, Number(e.target.value))))}
                  className="w-24"
                />
                <span className="font-medium">%</span>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={discount}
                  onChange={(e) => setDiscount(Number(e.target.value))}
                  className="flex-1"
                />
                {discount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDiscount(0)}
                    className="text-red-600 hover:text-red-700"
                  >
                    ✕ Núllstilla
                  </Button>
                )}
                {/* VSK Toggle - Small button */}
                <Button
                  variant={showWithoutVAT ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setShowWithoutVAT(!showWithoutVAT)}
                  className={showWithoutVAT ? 'bg-green-600 hover:bg-green-700' : ''}
                >
                  {showWithoutVAT ? '✓ Án VSK' : 'Án VSK'}
                </Button>
              </div>
            </div>
            {discount > 0 && (
              <div className="mt-2 text-sm text-blue-700 font-medium">
                Verð með {discount}% afslætti eru sýnd í töflunni
              </div>
            )}
            {showWithoutVAT && (
              <div className="mt-2 text-sm text-green-700 font-medium">
                Verð án 24% VSK eru sýnd í töflunni
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Print Header - Only visible on print */}
      <div className="hidden print:block mb-4 text-center">
        <h1 className="text-2xl font-bold mb-1">Alvörubón ehf. - Verðskrá</h1>
        <p className="text-xs text-slate-600">
          Prentað: {new Date().toLocaleDateString('is-IS')} kl. {new Date().toLocaleTimeString('is-IS', { hour: '2-digit', minute: '2-digit' })}
        </p>
        {discount > 0 && (
          <p className="text-xs font-semibold text-blue-700 mt-1">
            Verð með {discount}% afslætti
          </p>
        )}
        {showWithoutVAT && (
          <p className="text-xs font-semibold text-green-700 mt-1">
            Verð án VSK (24%)
          </p>
        )}
      </div>
      
      {/* Price Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-slate-100 print:bg-slate-200">
                  <th className="border border-slate-300 p-2 print:p-1 print:text-xs text-left font-bold sticky left-0 bg-slate-100 print:bg-slate-200 z-10 min-w-[180px]">
                    Þjónusta
                  </th>
                  {sizes.map((size) => (
                    <th 
                      key={size.id} 
                      className={`border border-slate-300 px-1.5 py-2 print:p-1 print:text-xs text-center font-bold whitespace-nowrap text-xs transition-all duration-300 ${
                        highlightedSizeId === size.id 
                          ? 'bg-green-200 border-green-400 ring-2 ring-green-400 ring-inset' 
                          : ''
                      }`}
                    >
                      {size.name}
                      {highlightedSizeId === size.id && (
                        <span className="block text-green-700 text-[10px] font-normal">▼</span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {services
                  .sort((a, b) => {
                    const orderA = a.order !== undefined && a.order !== null ? a.order : 999;
                    const orderB = b.order !== undefined && b.order !== null ? b.order : 999;
                    return orderA - orderB;
                  })
                  .map((service, idx) => {
                  const isAuxService = service.category === 'auka';
                  return (
                    <tr 
                      key={service.id}
                      className={`${idx % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-slate-50 dark:bg-slate-700'} ${isAuxService ? 'bg-amber-50 dark:bg-amber-900/30 print:bg-amber-100' : ''} hover:bg-blue-50 dark:hover:bg-blue-900/30 print:hover:bg-white transition-colors`}
                    >
                      <td className="border border-slate-300 dark:border-slate-600 p-2 print:p-1 sticky left-0 bg-inherit z-10 min-w-[180px]">
                        <div className="font-semibold text-slate-900 dark:text-slate-100 text-sm print:text-xs">
                          {service.name}{service.price_editable ? ' (verð frá)' : ''}
                        </div>
                        {service.description && (
                          <div className="text-xs text-slate-600 dark:text-slate-400 mt-0.5 print:hidden">{service.description}</div>
                        )}
                        {isAuxService && (
                          <span className="inline-block text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded mt-1 print:hidden">
                            Auka
                          </span>
                        )}
                      </td>
                      {sizes.map((size) => {
                        // Prices are stored as: prices[service.id][size.id]
                        const servicePrice = prices[service.id];
                        const basePrice = servicePrice ? servicePrice[size.id] : null;
                        const displayPrice = calculatePrice(basePrice);
                        const isHighlighted = highlightedSizeId === size.id;
                        
                        return (
                          <td 
                            key={size.id} 
                            className={`border border-slate-300 px-1.5 py-2 print:p-1 text-center transition-all duration-300 ${
                              isHighlighted 
                                ? 'bg-green-100 border-green-400 font-bold' 
                                : ''
                            }`}
                          >
                            {basePrice ? (
                              <div>
                                <span className="font-semibold text-base print:text-xs">
                                  {displayPrice}
                                </span>
                                <span className="text-xs text-slate-600 ml-0.5 print:hidden">kr.</span>
                                {discount > 0 && basePrice && (
                                  <div className="text-xs text-slate-500 line-through mt-0.5 print:hidden">
                                    {Math.round(basePrice).toLocaleString('is-IS')} kr.
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-400 print:text-xs">-</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      
      {/* Footer Notes - Print only */}
      <div className="hidden print:block mt-6 text-sm text-slate-600">
        <p>• Öll verð eru í íslenskum krónum (ISK)</p>
        <p>• Verð geta breyst án fyrirvara</p>
        <p>• Hafið samband fyrir sértilboð</p>
      </div>
      
      {/* Print CSS */}
      <style>{`
        @media print {
          @page {
            size: A4 landscape;
            margin: 0.5cm;
          }
          
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
            font-size: 8pt;
          }
          
          /* Hide navigation header */
          .no-print {
            display: none !important;
          }
          
          /* Hide elements with print:hidden class */
          .print\\:hidden {
            display: none !important;
          }
          
          /* Show elements with print:block class */
          .print\\:block {
            display: block !important;
          }
          
          /* Compact table for print - fit on one page */
          table {
            font-size: 7pt !important;
            width: 100%;
          }
          
          th, td {
            padding: 2px 4px !important;
            page-break-inside: avoid;
            font-size: 7pt !important;
            line-height: 1.2;
          }
          
          th {
            font-weight: 600;
          }
          
          /* Make print header compact */
          .print\\:block h1 {
            font-size: 16pt;
            margin-bottom: 2px;
          }
          
          .print\\:block p {
            font-size: 8pt;
            margin: 1px 0;
          }
          
          /* Remove padding from containers for print */
          body > div,
          main {
            padding: 0 !important;
            margin: 0 !important;
          }
          
          /* Compact borders */
          .border {
            border-width: 0.5px !important;
          }
        }
      `}</style>
      
      {/* Send Pricelist Dialog */}
      <Dialog open={pricelistDialogOpen} onOpenChange={setPricelistDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>📧 Senda verðskrá á tölvupóst</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Recipient info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Netfang *</Label>
                <Input
                  type="email"
                  placeholder="fyrirtaeki@example.is"
                  value={pricelistForm.recipient_email}
                  onChange={(e) => setPricelistForm(prev => ({...prev, recipient_email: e.target.value}))}
                />
              </div>
              <div>
                <Label>Nafn tengiliðs</Label>
                <Input
                  placeholder="Jón Jónsson"
                  value={pricelistForm.recipient_name}
                  onChange={(e) => setPricelistForm(prev => ({...prev, recipient_name: e.target.value}))}
                />
              </div>
            </div>
            
            <div>
              <Label>Fyrirtækjanafn</Label>
              <Input
                placeholder="Fyrirtækið ehf."
                value={pricelistForm.company_name}
                onChange={(e) => setPricelistForm(prev => ({...prev, company_name: e.target.value}))}
              />
            </div>
            
            <div>
              <Label>Sérsniðin skilaboð (valfrjálst)</Label>
              <textarea
                className="w-full p-2 border rounded-md min-h-[80px]"
                placeholder="Bæta við sérstökum skilaboðum..."
                value={pricelistForm.custom_message}
                onChange={(e) => setPricelistForm(prev => ({...prev, custom_message: e.target.value}))}
              />
            </div>
            
            {/* Price options */}
            <div className="grid grid-cols-2 gap-4">
              {/* Discount option */}
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <div className="flex items-center gap-3 mb-3">
                  <input
                    type="checkbox"
                    id="pricelist_show_discount"
                    checked={pricelistForm.show_discount}
                    onChange={(e) => setPricelistForm(prev => ({...prev, show_discount: e.target.checked}))}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="pricelist_show_discount" className="mb-0 cursor-pointer">Sýna fyrirtækjaafslátt</Label>
                </div>
                {pricelistForm.show_discount && (
                  <div className="flex items-center gap-2">
                    <Label className="mb-0">Afsláttur:</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      className="w-24"
                      value={pricelistForm.discount_percentage}
                      onChange={(e) => setPricelistForm(prev => ({...prev, discount_percentage: parseInt(e.target.value) || 0}))}
                    />
                    <span>%</span>
                  </div>
                )}
              </div>
              
              {/* VAT option */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="pricelist_without_vat"
                    checked={pricelistForm.without_vat}
                    onChange={(e) => setPricelistForm(prev => ({...prev, without_vat: e.target.checked}))}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="pricelist_without_vat" className="mb-0 cursor-pointer">Sýna verð án VSK</Label>
                </div>
                <p className="text-xs text-blue-600 mt-2">Verð verða sýnd án 24% VSK</p>
              </div>
            </div>
            
            {/* Select sizes */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="mb-0">Veldu bílastærðir</Label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setPricelistForm(prev => ({...prev, selected_sizes: sizes.map(s => s.id)}))}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    Velja allar
                  </button>
                  <span className="text-slate-300">|</span>
                  <button
                    type="button"
                    onClick={() => setPricelistForm(prev => ({...prev, selected_sizes: []}))}
                    className="text-xs text-slate-600 hover:text-slate-800"
                  >
                    Afvelja allar
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {sizes.map(size => (
                  <button
                    key={size.id}
                    type="button"
                    onClick={() => {
                      const isSelected = pricelistForm.selected_sizes.includes(size.id);
                      setPricelistForm(prev => ({
                        ...prev,
                        selected_sizes: isSelected 
                          ? prev.selected_sizes.filter(id => id !== size.id)
                          : [...prev.selected_sizes, size.id]
                      }));
                    }}
                    className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                      pricelistForm.selected_sizes.includes(size.id)
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'bg-white text-slate-700 border-slate-300 hover:border-blue-300'
                    }`}
                  >
                    {size.name}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Select services */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="mb-0">Veldu þjónustur</Label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const visibleServices = services.filter(s => s.display_in_pricelist !== false);
                      setPricelistForm(prev => ({...prev, selected_services: visibleServices.map(s => s.id)}));
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    Velja allar
                  </button>
                  <span className="text-slate-300">|</span>
                  <button
                    type="button"
                    onClick={() => setPricelistForm(prev => ({...prev, selected_services: []}))}
                    className="text-xs text-slate-600 hover:text-slate-800"
                  >
                    Afvelja allar
                  </button>
                </div>
              </div>
              <div className="max-h-48 overflow-y-auto border rounded-md p-2 space-y-1">
                {services.filter(s => s.display_in_pricelist !== false).map(service => (
                  <label
                    key={service.id}
                    className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={pricelistForm.selected_services.includes(service.id)}
                      onChange={() => {
                        const isSelected = pricelistForm.selected_services.includes(service.id);
                        setPricelistForm(prev => ({
                          ...prev,
                          selected_services: isSelected 
                            ? prev.selected_services.filter(id => id !== service.id)
                            : [...prev.selected_services, service.id]
                        }));
                      }}
                      className="h-4 w-4"
                    />
                    <span className="text-sm">{service.name}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {services.filter(s => s.display_in_pricelist !== false).length} þjónustur í verðskrá
                {pricelistForm.selected_services.length > 0 && ` • ${pricelistForm.selected_services.length} valdar`}
              </p>
            </div>
          </div>
          
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setPricelistDialogOpen(false)}>
              Hætta við
            </Button>
            <Button 
              onClick={sendPricelist}
              disabled={sendingPricelist || !pricelistForm.recipient_email}
              className="bg-green-600 hover:bg-green-700"
            >
              {sendingPricelist ? '⏳ Sendir...' : '📧 Senda verðskrá'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ============================================================
// DISCOUNT CODES MANAGER COMPONENT
// ============================================================
function DiscountCodesManager() {
  const [discountCodes, setDiscountCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    code: '',
    discount_percent: 10,
    description: '',
    valid_from: '',
    valid_to: '',
    max_uses: '',
    active: true
  });

  const API = API_BASE_URL;

  // Load discount codes on mount
  useEffect(() => {
    loadDiscountCodes();
  }, []);

  const loadDiscountCodes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/api/discount-codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDiscountCodes(response.data?.codes || response.data || []);
    } catch (error) {
      console.error('Failed to load discount codes:', error);
      toast.error('Villa við að sækja afsláttarkóða');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      discount_percent: 10,
      description: '',
      valid_from: '',
      valid_to: '',
      max_uses: '',
      active: true
    });
    setEditingCode(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.code.trim()) {
      toast.error('Vinsamlega sláðu inn afsláttarkóða');
      return;
    }
    
    if (!formData.discount_percent || formData.discount_percent <= 0 || formData.discount_percent > 100) {
      toast.error('Afsláttur verður að vera á milli 1-100%');
      return;
    }

    try {
      setSaving(true);
      
      const payload = {
        ...formData,
        code: formData.code.toUpperCase().trim(),
        discount_percent: parseInt(formData.discount_percent),
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        valid_from: formData.valid_from || null,
        valid_to: formData.valid_to || null
      };

      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };

      if (editingCode) {
        await axios.put(`${API}/api/discount-codes/${editingCode.id}`, payload, { headers });
        toast.success('Afsláttarkóði uppfærður');
      } else {
        await axios.post(`${API}/api/discount-codes`, payload, { headers });
        toast.success('Afsláttarkóði búinn til');
      }

      setShowAddDialog(false);
      resetForm();
      loadDiscountCodes();
    } catch (error) {
      console.error('Failed to save discount code:', error);
      toast.error(error.response?.data?.detail || 'Villa við að vista afsláttarkóða');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (code) => {
    setFormData({
      code: code.code,
      discount_percent: code.discount_percent,
      description: code.description || '',
      valid_from: code.valid_from || '',
      valid_to: code.valid_to || '',
      max_uses: code.max_uses || '',
      active: code.active
    });
    setEditingCode(code);
    setShowAddDialog(true);
  };

  const handleToggleActive = async (code) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.put(`${API}/api/discount-codes/${code.id}`, { active: !code.active }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(code.active ? 'Kóði óvirkjaður' : 'Kóði virkjaður');
      loadDiscountCodes();
    } catch (error) {
      toast.error('Villa við að breyta stöðu');
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Ertu viss um að þú viljir eyða kóðanum "${code.code}"?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API}/api/discount-codes/${code.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Kóði eytt');
      loadDiscountCodes();
    } catch (error) {
      toast.error('Villa við að eyða kóða');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-slate-500">Hleð afsláttarkóðum...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                🏷️ Afsláttarkóðar
              </CardTitle>
              <CardDescription>
                Búðu til og stjórnaðu afsláttarkóðum fyrir bókunarsíðuna
              </CardDescription>
            </div>
            <Button onClick={() => { resetForm(); setShowAddDialog(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              Nýr kóði
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {discountCodes.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <p>Engir afsláttarkóðar skráðir</p>
              <Button variant="outline" className="mt-4" onClick={() => setShowAddDialog(true)}>
                Búa til fyrsta kóðann
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Kóði</TableHead>
                  <TableHead>Afsláttur</TableHead>
                  <TableHead>Lýsing</TableHead>
                  <TableHead>Gildistími</TableHead>
                  <TableHead>Notkun</TableHead>
                  <TableHead>Staða</TableHead>
                  <TableHead className="text-right">Aðgerðir</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {discountCodes.map((code) => (
                  <TableRow key={code.id} className={!code.active ? 'opacity-50' : ''}>
                    <TableCell className="font-mono font-bold">{code.code}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="bg-green-100 text-green-800">
                        {code.discount_percent}% afsláttur
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-600">{code.description || '-'}</TableCell>
                    <TableCell className="text-sm">
                      {code.valid_from || code.valid_to ? (
                        <span>
                          {code.valid_from && <span>{code.valid_from}</span>}
                          {code.valid_from && code.valid_to && ' → '}
                          {code.valid_to && <span>{code.valid_to}</span>}
                        </span>
                      ) : (
                        <span className="text-slate-400">Engin takmörk</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {code.max_uses ? (
                        <span>{code.current_uses || 0} / {code.max_uses}</span>
                      ) : (
                        <span>{code.current_uses || 0} <span className="text-slate-400">(ótakmarkað)</span></span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={code.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                        {code.active ? '✅ Virkur' : '❌ Óvirkur'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(code)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleToggleActive(code)}>
                          {code.active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(code)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCode ? 'Breyta afsláttarkóða' : 'Nýr afsláttarkóði'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="code">Afsláttarkóði *</Label>
              <Input
                id="code"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="T.d. SUMAR25"
                className="font-mono"
                disabled={!!editingCode}
              />
            </div>
            
            <div>
              <Label htmlFor="discount_percent">Afsláttur (%) *</Label>
              <Input
                id="discount_percent"
                type="number"
                min="1"
                max="100"
                value={formData.discount_percent}
                onChange={(e) => setFormData({ ...formData, discount_percent: e.target.value })}
              />
            </div>
            
            <div>
              <Label htmlFor="description">Lýsing</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="T.d. Sumarafsláttur 2025"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="valid_from">Gildir frá</Label>
                <Input
                  id="valid_from"
                  type="date"
                  value={formData.valid_from}
                  onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="valid_to">Gildir til</Label>
                <Input
                  id="valid_to"
                  type="date"
                  value={formData.valid_to}
                  onChange={(e) => setFormData({ ...formData, valid_to: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="max_uses">Hámarksfjöldi notkunar</Label>
              <Input
                id="max_uses"
                type="number"
                min="1"
                value={formData.max_uses}
                onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                placeholder="Skildu eftir autt fyrir ótakmarkað"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="active"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="active">Virkur kóði</Label>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => { setShowAddDialog(false); resetForm(); }}>
                Hætta við
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Vista...' : (editingCode ? 'Uppfæra' : 'Búa til')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ============================================================
// ONLINE BOOKING SETTINGS COMPONENT
// ============================================================
function OnlineBookingSettings() {
  const [activeSubTab, setActiveSubTab] = useState('settings');
  const [settings, setSettings] = useState({
    max_online_per_day: 6,
    max_total_per_day: 12,
    available_times: ["08:30", "10:00", "11:30", "13:00", "14:30", "16:00"],
    capacity_mode: 'manual'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newTime, setNewTime] = useState('');
  const [autoCapacity, setAutoCapacity] = useState(null);

  const API = API_BASE_URL;

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const [settingsRes, capacityRes] = await Promise.all([
        axios.get(`${API}/api/admin/booking-settings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/admin/booking-capacity`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      const data = settingsRes.data;
      // Normalize: backend may use time_slots or available_times
      if (!data.available_times && data.time_slots) {
        data.available_times = data.time_slots;
      }
      if (!data.capacity_mode) data.capacity_mode = 'manual';
      setSettings(data);
      setAutoCapacity(capacityRes.data);
    } catch (error) {
      console.error('Failed to load booking settings:', error);
      toast.error('Villa við að sækja stillingar');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('access_token');
      await axios.put(`${API}/api/admin/booking-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Stillingar vistaðar');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(error.response?.data?.detail || 'Villa við að vista stillingar');
    } finally {
      setSaving(false);
    }
  };

  const addTime = () => {
    if (!newTime) return;
    const times = settings.available_times || [];
    if (!times.includes(newTime)) {
      const updated = [...times, newTime].sort();
      setSettings({ ...settings, available_times: updated });
      setNewTime('');
    }
  };

  const removeTime = (timeToRemove) => {
    const times = settings.available_times || [];
    setSettings({ ...settings, available_times: times.filter(t => t !== timeToRemove) });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-slate-500">Hleð stillingum...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Sub-tabs for Online Booking section */}
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList>
          <TabsTrigger value="settings">⚙️ Stillingar</TabsTrigger>
          <TabsTrigger value="discounts">🏷️ Afsláttarkóðar</TabsTrigger>
        </TabsList>

        {/* Settings Sub-Tab */}
        <TabsContent value="settings" className="space-y-6 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                📅 Online Bókana Stillingar
              </CardTitle>
              <CardDescription>
                Stilltu hámarksfjölda bókana og tímasetningar fyrir online bókunarsíðuna
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Capacity Mode Selection */}
              <div className="p-4 bg-slate-50 rounded-lg border">
                <Label className="font-medium mb-3 block">Aðferð afkastastillingar</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button type="button" onClick={() => setSettings({...settings, capacity_mode: 'manual'})} className={`p-4 rounded-lg border-2 text-left transition-all ${settings.capacity_mode !== 'auto' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-testid="capacity-mode-manual">
                    <div className="font-medium text-sm">Handvirkt</div>
                    <p className="text-xs text-slate-500 mt-1">Stilltu fastar tölur fyrir hámark bókana</p>
                  </button>
                  <button type="button" onClick={() => setSettings({...settings, capacity_mode: 'auto'})} className={`p-4 rounded-lg border-2 text-left transition-all ${settings.capacity_mode === 'auto' ? 'border-green-500 bg-green-50' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-testid="capacity-mode-auto">
                    <div className="font-medium text-sm">Sjálfvirkt</div>
                    <p className="text-xs text-slate-500 mt-1">Kerfið reiknar út frá sögulegum gögnum</p>
                  </button>
                </div>
              </div>

              {/* Auto Capacity Stats */}
              {autoCapacity && (
                <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg border border-emerald-200">
                  <h4 className="font-medium text-emerald-800 mb-3">Gögn úr eldri verkum</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                    <div className="bg-white p-3 rounded-lg text-center" data-testid="cap-avg-day">
                      <div className="text-xl font-bold text-emerald-700">{autoCapacity.avg_per_day}</div>
                      <div className="text-xs text-slate-500">Meðaltal/dag</div>
                    </div>
                    <div className="bg-white p-3 rounded-lg text-center" data-testid="cap-avg-weekday">
                      <div className="text-xl font-bold text-emerald-700">{autoCapacity.avg_per_weekday}</div>
                      <div className="text-xs text-slate-500">Meðaltal virka daga</div>
                    </div>
                    <div className="bg-white p-3 rounded-lg text-center" data-testid="cap-max-day">
                      <div className="text-xl font-bold text-blue-700">{autoCapacity.max_day}</div>
                      <div className="text-xs text-slate-500">Hámark á degi</div>
                    </div>
                    <div className="bg-white p-3 rounded-lg text-center" data-testid="cap-sample-days">
                      <div className="text-xl font-bold text-slate-600">{autoCapacity.sample_days}</div>
                      <div className="text-xs text-slate-500">Dagar greindur</div>
                    </div>
                  </div>
                  {settings.capacity_mode === 'auto' && (
                    <div className="space-y-2">
                      <div className="flex gap-4 p-3 bg-white rounded-lg text-sm">
                        <div>Stungið upp á online: <strong className="text-emerald-700">{autoCapacity.recommended_online} bílar</strong></div>
                        <div>Stungið upp á heildar: <strong className="text-emerald-700">{autoCapacity.recommended_total} bílar</strong></div>
                      </div>
                      {autoCapacity.recommended_times?.length > 0 && (
                        <div className="p-3 bg-white rounded-lg text-sm">
                          <span>Stungið upp á tímar: </span>
                          {autoCapacity.recommended_times.map((t, i) => (
                            <span key={i} className="font-semibold text-emerald-700">{t}{i < autoCapacity.recommended_times.length - 1 ? ', ' : ''}</span>
                          ))}
                        </div>
                      )}
                      <button type="button" onClick={() => {
                        setSettings({
                          ...settings,
                          max_online_per_day: autoCapacity.recommended_online,
                          max_total_per_day: autoCapacity.recommended_total,
                          available_times: autoCapacity.recommended_times || settings.available_times
                        });
                        toast.success('Ráðlagðar stillingar settar inn - mundu að vista!');
                      }} className="w-full py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors" data-testid="apply-recommendations-btn">
                        Nota ráðlagðar stillingar
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Booking Limits - Manual mode or override */}
              <div className={settings.capacity_mode === 'auto' ? 'opacity-50' : ''}>
                <Label className="font-medium mb-2 block">{settings.capacity_mode === 'auto' ? 'Varagildi (ef engin gögn)' : 'Hámarksfjöldi bókana'}</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="max_online">Hámark online bókanir á dag</Label>
                    <Input id="max_online" type="number" min="1" max="50" value={settings.max_online_per_day} onChange={(e) => setSettings({ ...settings, max_online_per_day: parseInt(e.target.value) || 6 })} data-testid="max-online-input" />
                    <p className="text-xs text-slate-500">Þegar þessu hámarki er náð lokast á online bókanir en samt hægt að hringja</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="max_total">Loka online ef heildarfjöldi yfir</Label>
                    <Input id="max_total" type="number" min="1" max="100" value={settings.max_total_per_day} onChange={(e) => setSettings({ ...settings, max_total_per_day: parseInt(e.target.value) || 12 })} data-testid="max-total-input" />
                    <p className="text-xs text-slate-500">Ef heildarfjöldi bókana (online + símtal + walk-in) fer yfir þetta lokast á online</p>
                  </div>
                </div>
              </div>

              {/* Available Times */}
              <div className="space-y-3">
                <Label>Tímasetningar fyrir bókanir</Label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {(settings.available_times || []).map((time) => (
                    <Badge key={time} variant="secondary" className="px-3 py-1 text-sm">
                      {time}
                      <button
                        type="button"
                        className="ml-2 text-red-500 hover:text-red-700"
                        onClick={() => removeTime(time)}
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    type="time"
                    value={newTime}
                    onChange={(e) => setNewTime(e.target.value)}
                    className="w-40"
                  />
                  <Button type="button" variant="outline" onClick={addTime}>
                    <Plus className="h-4 w-4 mr-1" /> Bæta við
                  </Button>
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end pt-4 border-t">
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? 'Vista...' : 'Vista stillingar'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Info Card */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <h4 className="font-medium text-blue-900 mb-2">ℹ️ Hvernig virkar þetta?</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Ef <strong>online bókanir</strong> ná hámarki → Online lokað, en hægt að hringja</li>
                <li>• Ef <strong>heildarfjöldi</strong> (allir bókunarflokkar) ná hámarki → Online lokað</li>
                <li>• <strong>Lokaðir dagar</strong> eru sóttir úr Opnunartímum (Stillingar → Opnunartímar)</li>
              </ul>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Discount Codes Sub-Tab */}
        <TabsContent value="discounts" className="mt-4">
          <DiscountCodesManager />
        </TabsContent>
      </Tabs>
    </div>
  );
}


// Comprehensive System Settings Component
function SystemSettings() {
  const { user } = useAuth(); // Get user for password verification
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('pricing');
  const [activeInvoiceTab, setActiveInvoiceTab] = useState('dk'); // Track invoice sub-tab
  const [editingService, setEditingService] = useState(null);
  const [editingSize, setEditingSize] = useState(null);
  const [editingProductNumbers, setEditingProductNumbers] = useState({});
  const [editingCategory, setEditingCategory] = useState(null);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategory, setNewCategory] = useState({ id: '', name: '', icon: '📦' });
  
  // Email settings state
  const [emailSettings, setEmailSettings] = useState({
    tenant_id: '',
    client_id: '',
    client_secret: '',
    sender_email: '',
    sender_name: 'Alvörubón ehf.',
    enabled: false,
    calendar_sync_enabled: true,
    email_subject: '',
    email_greeting: '',
    email_body: '',
    email_footer: ''
  });
  const [testEmailAddress, setTestEmailAddress] = useState('');
  
  // Integration settings state (Payday vs DK)
  const [integrationSettings, setIntegrationSettings] = useState({
    active_integration: 'dk',
    available_integrations: ['payday', 'dk']
  });
  const [savingIntegration, setSavingIntegration] = useState(false);
  const [showIntegrationPasswordDialog, setShowIntegrationPasswordDialog] = useState(false);
  const [integrationPassword, setIntegrationPassword] = useState('');
  const [pendingIntegration, setPendingIntegration] = useState(null);
  
  // DK Plus settings state
  const [dkSettings, setDkSettings] = useState({
    api_url: 'https://api.dkplus.is/api/v1',
    api_key: '',
    use_mock: false,
    enabled: false,
    salesperson: 'Rafrænn'
  });
  const [savingDkSettings, setSavingDkSettings] = useState(false);
  
  // Payday settings state
  const [paydaySettings, setPaydaySettings] = useState({
    api_url: 'https://api.test.payday.is',
    client_id: '',
    client_secret: '',
    use_mock: false,
    enabled: false,
    sandbox_mode: true,
    due_days: 2,
    final_due_days: 5
  });
  const [savingPaydaySettings, setSavingPaydaySettings] = useState(false);
  
  // Rapyd settings
  const [rapydSettings, setRapydSettings] = useState({
    base_url: 'https://api.rapyd.net',
    access_key: '',
    secret_key: '',
    webhook_secret: '',
    use_mock: false,
    enabled: false,
    sandbox_mode: false
  });
  const [savingRapydSettings, setSavingRapydSettings] = useState(false);
  
  // Invoice default settings (due dates)
  const [invoiceSettings, setInvoiceSettings] = useState({
    invoice_due_days: 0,  // Gjaldagi - default same day
    invoice_final_due_days: 7  // Eindagi - default 7 days
  });
  const [savingInvoiceSettings, setSavingInvoiceSettings] = useState(false);
  
  // Price list email state
  const [pricelistDialogOpen, setPricelistDialogOpen] = useState(false);
  const [pricelistForm, setPricelistForm] = useState({
    recipient_email: '',
    recipient_name: '',
    company_name: '',
    custom_message: '',
    selected_services: [],
    selected_sizes: [],
    show_discount: false,
    discount_percentage: 0,
    without_vat: false
  });
  const [sendingPricelist, setSendingPricelist] = useState(false);
  
  // Label print settings
  const [labelSettings, setLabelSettings] = useState({
    width: 100,  // mm
    height: 50,  // mm
    font_size_code: 32,  // px
    font_size_name: 16,  // px
    font_size_details: 12,  // px
    show_color_box: true,
    show_manufacturer: true,
    show_size: true,
    show_location: true,
    show_sequence: true
  });
  const [savingLabelSettings, setSavingLabelSettings] = useState(false);
  
  // Cache settings
  const [cacheSettings, setCacheSettings] = useState({
    vehicle_cache_days: 0,  // 0 = indefinite
    person_cache_days: 0,
    vehicle_lookup_price: 41,  // ISK with VAT
    person_lookup_price: 23    // ISK with VAT
  });
  const [savingCacheSettings, setSavingCacheSettings] = useState(false);
  
  // Key location settings
  const [keyLocations, setKeyLocations] = useState([]);
  const [loadingKeyLocations, setLoadingKeyLocations] = useState(false);
  const [newKeyLocation, setNewKeyLocation] = useState({ label: '', icon: '🔑' });
  const [editingKeyLocation, setEditingKeyLocation] = useState(null);
  const [savingKeyLocation, setSavingKeyLocation] = useState(false);
  
  // Pause status options state
  const [pauseStatuses, setPauseStatuses] = useState([]);
  const [newPauseStatus, setNewPauseStatus] = useState({ label: '', icon: '⏸️' });
  const [editingPauseStatus, setEditingPauseStatus] = useState(null);
  const [savingPauseStatus, setSavingPauseStatus] = useState(false);
  
  // Já.is phone lookup settings
  const [jaLookupSettings, setJaLookupSettings] = useState({
    enabled: true,
    price_per_lookup: 11,
    has_api_key: false,
    api_key_preview: null,
    api_key: ''
  });
  const [savingJaSettings, setSavingJaSettings] = useState(false);

  // QR Security Settings state
  const [qrSecuritySettings, setQrSecuritySettings] = useState({
    enabled: true,
    pin_code: '',
    require_pin_outside_hours: false,
    has_pin: false
  });
  const [savingQrSecurity, setSavingQrSecurity] = useState(false);

  
  // Auto-fill product numbers dialog
  const [showAutoFillDialog, setShowAutoFillDialog] = useState(false);
  const [autoFillStartNumber, setAutoFillStartNumber] = useState(101);
  const [autoFillPassword, setAutoFillPassword] = useState('');
  const [autoFillOverwrite, setAutoFillOverwrite] = useState(false);
  const [autoFilling, setAutoFilling] = useState(false);
  
  // MyPOS settings
  const [myposSettings, setMyposSettings] = useState({
    enabled: false,
    sid: '',
    wallet_number: '',
    key_index: 2,
    environment: 'production',
    has_private_key: false
  });
  const [savingMyposSettings, setSavingMyposSettings] = useState(false);
  
  // Add/Edit states for services and sizes
  const [showAddService, setShowAddService] = useState(false);
  const [showAddSize, setShowAddSize] = useState(false);
  const [newService, setNewService] = useState({ 
    name: '', 
    description: '', 
    id: '',
    available: true,
    price_editable: false,
    allow_discount: true,
    display_in_pricelist: true
  });
  const [newSize, setNewSize] = useState({ name: '', id: '', order: undefined });
  
  // Dealerships management state
  const [dealerships, setDealerships] = useState([]);
  const [showAddDealership, setShowAddDealership] = useState(false);
  const [editingDealership, setEditingDealership] = useState(null);
  const [newDealership, setNewDealership] = useState({ name: '', company_name: '', kennitala: '', email: '', phone: '', send_sms: false });
  const [deletePassword, setDeletePassword] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [dealershipToDelete, setDealershipToDelete] = useState(null);
  const [dealershipSearchTerm, setDealershipSearchTerm] = useState(''); // Search filter for dealerships
  
  // Loan cars management state
  const [loanCars, setLoanCars] = useState([]);
  const [showAddLoanCar, setShowAddLoanCar] = useState(false);
  const [editingLoanCar, setEditingLoanCar] = useState(null);
  const [newLoanCar, setNewLoanCar] = useState({ plate: '', manufacturer: '', model: '', year: '', color: '' });
  const [lookingUpLoanCar, setLookingUpLoanCar] = useState(false);
  
  // PDF template state - separate for print and electronic
  const [printTemplateExists, setPrintTemplateExists] = useState(false);
  const [electronicTemplateExists, setElectronicTemplateExists] = useState(false);
  
  // Signature requests state
  const [signatureRequests, setSignatureRequests] = useState([]);
  const [signatureRequestsLoading, setSignatureRequestsLoading] = useState(false);
  const [signatureRequestsFilter, setSignatureRequestsFilter] = useState('all'); // all, pending, signed, expired
  
  // SMS settings state
  const [smsSettings, setSmsSettings] = useState({
    sender_name: 'Alvorubon',
    booking_confirmation_enabled: true,
    booking_reminder_enabled: false,
    reminder_hours_before: 24,
    completion_notification_enabled: false,
    payment_reminder_enabled: false,
    templates: {
      booking_confirmation: '',
      booking_reminder: '',
      job_completed: '',
      payment_link: '',
      payment_reminder: ''
    }
  });
  const [savingSms, setSavingSms] = useState(false);
  const [testSmsPhone, setTestSmsPhone] = useState('');
  const [testSmsMessage, setTestSmsMessage] = useState('');
  const [sendingTestSms, setSendingTestSms] = useState(false);

  useEffect(() => {
    loadSettings();
    loadSignatureRequests();
  }, []);
  
  useEffect(() => {
    loadSignatureRequests();
  }, [signatureRequestsFilter]);

  const loadSettings = async () => {
    try {
      const [settingsRes, emailRes, dealershipsRes, loanCarsRes, integrationRes, dkRes, paydayRes, rapydRes, smsRes, labelRes, cacheRes, myposRes, invoiceRes, jaRes, qrSecurityRes, keyLocationsRes, pauseStatusesRes] = await Promise.all([
        axios.get(`${API}/settings`),
        axios.get(`${API}/admin/email-settings`),
        axios.get(`${API}/dealerships/all`),
        axios.get(`${API}/loan-cars?include_inactive=true`),
        axios.get(`${API}/settings/integration`),
        axios.get(`${API}/admin/dk-settings`),
        axios.get(`${API}/admin/payday-settings`),
        axios.get(`${API}/admin/rapyd-settings`),
        axios.get(`${API}/admin/sms-settings`),
        axios.get(`${API}/admin/label-settings`),
        axios.get(`${API}/admin/cache-settings`),
        axios.get(`${API}/admin/mypos-settings`),
        axios.get(`${API}/admin/invoice-settings`),
        axios.get(`${API}/settings/ja-lookup`),
        axios.get(`${API}/settings/qr-security`),
        axios.get(`${API}/settings/key-locations`),
        axios.get(`${API}/settings/pause-statuses`)
      ]);
      
      // Create new settings object with sorted sizes to trigger React re-render
      const settingsData = settingsRes.data;
      if (settingsData && settingsData.sizes) {
        settingsData.sizes = [...settingsData.sizes].sort((a, b) => (a.order || 0) - (b.order || 0));
      }
      // Deep copy services array to force React re-render
      if (settingsData && settingsData.services) {
        settingsData.services = [...settingsData.services];
      }
      setSettings({...settingsData});
      
      setEmailSettings(emailRes.data || {
        tenant_id: '',
        client_id: '',
        client_secret: '',
        sender_email: '',
        sender_name: 'Alvörubón ehf.',
        enabled: false,
        email_subject: '',
        email_greeting: '',
        email_body: '',
        email_footer: ''
      });
      setDealerships(dealershipsRes.data || []);
      setLoanCars(loanCarsRes.data || []);
      setIntegrationSettings(integrationRes.data || {
        active_integration: 'dk',
        available_integrations: ['payday', 'dk']
      });
      
      // Set active invoice tab to the active integration
      const activeIntegration = integrationRes.data?.active_integration || 'dk';
      setActiveInvoiceTab(activeIntegration);
      setDkSettings(dkRes.data || {
        api_url: 'https://api.dkplus.is/api/v1',
        api_key: '',
        use_mock: false,
        enabled: false,
        salesperson: 'Rafrænn'
      });
      setPaydaySettings(paydayRes.data || {
        api_url: 'https://api.test.payday.is',
        client_id: '',
        client_secret: '',
        use_mock: false,
        enabled: false,
        sandbox_mode: true,
        due_days: 2,
        final_due_days: 5
      });
      setRapydSettings(rapydRes.data || {
        base_url: 'https://api.rapyd.net',
        access_key: '',
        secret_key: '',
        webhook_secret: '',
        use_mock: false,
        enabled: false,
        sandbox_mode: false
      });
      setSmsSettings(smsRes.data || {
        sender_name: 'Alvorubon',
        booking_confirmation_enabled: true,
        booking_reminder_enabled: false,
        reminder_hours_before: 24,
        completion_notification_enabled: false,
        payment_reminder_enabled: false,
        templates: {
          booking_confirmation: '',
          booking_reminder: '',
          job_completed: '',
          payment_link: '',
          payment_reminder: ''
        }
      });
      setLabelSettings(labelRes.data || {
        width: 100,
        height: 50,
        font_size_code: 32,
        font_size_name: 16,
        font_size_details: 12,
        show_color_box: true,
        show_manufacturer: true,
        show_size: true,
        show_location: true,
        show_sequence: true
      });
      setCacheSettings(cacheRes.data || {
        vehicle_cache_days: 0,
        person_cache_days: 0,
        vehicle_lookup_price: 41,
        person_lookup_price: 23
      });
      setJaLookupSettings({
        enabled: jaRes.data?.enabled ?? true,
        price_per_lookup: jaRes.data?.price_per_lookup ?? 11,
        has_api_key: jaRes.data?.has_api_key ?? false,
        api_key_preview: jaRes.data?.api_key_preview ?? null,
        api_key: ''
      });

      setQrSecuritySettings(qrSecurityRes.data || {
        enabled: true,
        pin_code: '',
        require_pin_outside_hours: false,
        has_pin: false
      });

      // Load key locations
      setKeyLocations(keyLocationsRes.data?.locations || []);
      
      // Load pause statuses
      setPauseStatuses(pauseStatusesRes.data?.statuses || []);

      setMyposSettings(myposRes.data || {
        enabled: false,
        sid: '',
        wallet_number: '',
        key_index: 2,
        environment: 'production',
        has_private_key: false
      });
      
      // Load invoice settings from dedicated endpoint
      setInvoiceSettings(invoiceRes.data || {
        invoice_due_days: 0,
        invoice_final_due_days: 7
      });
      
      // Check if PDF templates exist (both print and electronic)
      try {
        const templateStatus = await axios.get(`${API}/loan-cars/template-status`);
        setPrintTemplateExists(templateStatus.data.print?.exists || false);
        setElectronicTemplateExists(templateStatus.data.electronic?.exists || false);
      } catch (error) {
        setPrintTemplateExists(false);
        setElectronicTemplateExists(false);
      }
    } catch (error) {
      toast.error('Tókst ekki að hlaða stillingum');
    } finally {
      setLoading(false);
    }
  };
  
  const saveSmsSettings = async () => {
    try {
      setSavingSms(true);
      await axios.put(`${API}/admin/sms-settings`, smsSettings);
      toast.success('SMS stillingar vistaðar');
    } catch (error) {
      toast.error('Tókst ekki að vista SMS stillingar');
    } finally {
      setSavingSms(false);
    }
  };
  
  const loadSignatureRequests = async () => {
    try {
      setSignatureRequestsLoading(true);
      const statusFilter = signatureRequestsFilter === 'all' ? '' : signatureRequestsFilter;
      const params = statusFilter ? { status: statusFilter } : {};
      
      const response = await axios.get(`${API}/signature-requests`, { params });
      setSignatureRequests(response.data || []);
    } catch (error) {
      console.error('Failed to load signature requests:', error);
      toast.error('Tókst ekki að hlaða undirritaða samninga');
    } finally {
      setSignatureRequestsLoading(false);
    }
  };

  const saveEmailSettings = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/admin/email-settings`, emailSettings);
      toast.success('Tölvupóststillingar vistaðar');
    } catch (error) {
      toast.error('Tókst ekki að vista tölvupóststillingar');
    } finally {
      setSaving(false);
    }
  };
  
  const testEmailConnection = async () => {
    if (!testEmailAddress) {
      toast.error('Sláðu inn netfang fyrir prófun');
      return;
    }
    
    try {
      setSaving(true);
      const response = await axios.post(`${API}/admin/email-test`, {
        to_email: testEmailAddress,
        subject: 'Prófun á tölvupóst - Alvörubón',
        body: 'Þetta er prófunar email til að staðfesta að tölvupóststillingar virki rétt.'
      });
      
      if (response.data.success) {
        toast.success('Prófunar tölvupóstur sendur!');
      } else {
        toast.error('Villa við sending: ' + (response.data.error || 'Óþekkt villa'));
      }
    } catch (error) {
      toast.error('Villa við prófun á tölvupósti');
    } finally {
      setSaving(false);
    }
  };

  // Send price list email
  const sendPricelist = async () => {
    if (!pricelistForm.recipient_email) {
      toast.error('Netfang vantar');
      return;
    }
    
    try {
      setSendingPricelist(true);
      
      const response = await axios.post(`${API}/send-pricelist`, pricelistForm);
      
      if (response.data.success) {
        toast.success(`✅ Verðskrá send á ${pricelistForm.recipient_email}`);
        setPricelistDialogOpen(false);
        // Reset form
        setPricelistForm({
          recipient_email: '',
          recipient_name: '',
          company_name: '',
          custom_message: '',
          selected_services: [],
          selected_sizes: [],
          show_discount: false,
          discount_percentage: 0,
          without_vat: false
        });
      } else {
        toast.error('Tókst ekki að senda verðskrá');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Villa við að senda verðskrá');
    } finally {
      setSendingPricelist(false);
    }
  };

  const saveIntegrationSettings = async (integration) => {
    console.log('=== saveIntegrationSettings called ===');
    console.log('Integration to switch to:', integration);
    // Open password dialog instead of switching directly
    setPendingIntegration(integration);
    setShowIntegrationPasswordDialog(true);
    console.log('Password dialog should now be open');
  };

  const verifyIntegrationPassword = async () => {
    console.log('=== verifyIntegrationPassword called ===');
    console.log('integrationPassword:', integrationPassword ? 'PROVIDED' : 'EMPTY');
    console.log('pendingIntegration:', pendingIntegration);
    
    if (!integrationPassword.trim()) {
      toast.error('Sláðu inn lykilorð');
      return;
    }

    try {
      setSavingIntegration(true);
      
      console.log('Attempting password verification for:', user?.email);
      
      // Verify admin password by attempting login
      const loginResponse = await axios.post(`${API}/auth/login`, {
        email: user?.email || 'admin@carwash.is',
        password: integrationPassword
      });

      console.log('Login response:', loginResponse.data);

      if (loginResponse.data.access_token) {
        console.log('Password verified, switching integration to:', pendingIntegration);
        
        // Password is correct, proceed with integration switch
        const response = await axios.put(`${API}/settings/integration`, {
          active_integration: pendingIntegration
        });
        
        console.log('Integration switch response:', response.data);
        
        if (response.data.success) {
          toast.success(`✅ Skipt yfir í ${pendingIntegration === 'payday' ? 'Payday' : 'DK Plus'}`);
          setIntegrationSettings(prev => ({
            ...prev,
            active_integration: pendingIntegration
          }));
          
          // Reset and close dialog
          setShowIntegrationPasswordDialog(false);
          setIntegrationPassword('');
          setPendingIntegration(null);
          
          console.log('✅ Integration switched successfully');
        } else {
          console.error('❌ Integration switch failed - no success flag');
          toast.error('Villa: Tókst ekki að skipta um kerfi');
        }
      } else {
        console.error('❌ No access token in login response');
        toast.error('Villa: Óvænt svar frá þjóni');
      }
    } catch (error) {
      console.error('❌ Password verification error:', error);
      console.error('Error response:', error.response?.data);
      
      if (error.response?.status === 401) {
        toast.error('Rangt lykilorð');
      } else {
        toast.error('Villa við að skipta um bókhaldskerfi: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setSavingIntegration(false);
      console.log('=== verifyIntegrationPassword finished ===');
    }
  };

  const handleAutoFillProductNumbers = async () => {
    if (!autoFillPassword.trim()) {
      toast.error('Sláðu inn admin lykilorð');
      return;
    }

    if (!autoFillStartNumber || autoFillStartNumber < 1) {
      toast.error('Byrjunarnúmer verður að vera 1 eða hærra');
      return;
    }

    try {
      setAutoFilling(true);
      
      // Verify admin password by attempting login
      const loginResponse = await axios.post(`${API}/auth/login`, {
        email: user?.email || 'admin@carwash.is',
        password: autoFillPassword
      });

      if (loginResponse.data.access_token) {
        // Password is correct, proceed with auto-fill
        const response = await axios.post(`${API}/settings/auto-fill-product-numbers`, {
          start_number: autoFillStartNumber,
          overwrite: autoFillOverwrite
        });
        
        if (response.data.success) {
          toast.success(`✅ ${response.data.message}`);
          await loadSettings();
          
          // Reset and close dialog
          setShowAutoFillDialog(false);
          setAutoFillPassword('');
          setAutoFillStartNumber(101);
          setAutoFillOverwrite(false);
        }
      }
    } catch (error) {
      console.error('Auto-fill error:', error);
      
      if (error.response?.status === 401) {
        toast.error('Rangt lykilorð');
      } else {
        toast.error('Villa við að útfylla vörunúmer: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setAutoFilling(false);
    }
  };

  const saveDkSettings = async () => {
    try {
      setSavingDkSettings(true);
      const response = await axios.put(`${API}/admin/dk-settings`, dkSettings);
      
      if (response.data.success) {
        toast.success('✅ DK stillingar vistaðar');
        await loadSettings(); // Reload to get masked key
      }
    } catch (error) {
      toast.error('Villa við að vista DK stillingar: ' + (error.response?.data?.detail || error.message));
      console.error('DK settings save error:', error);
    } finally {
      setSavingDkSettings(false);
    }
  };

  const savePaydaySettings = async () => {
    try {
      setSavingPaydaySettings(true);
      const response = await axios.put(`${API}/admin/payday-settings`, paydaySettings);
      
      if (response.data.success) {
        toast.success('✅ Payday stillingar vistaðar');
        await loadSettings(); // Reload to get masked credentials
      }
    } catch (error) {
      toast.error('Villa við að vista Payday stillingar: ' + (error.response?.data?.detail || error.message));
      console.error('Payday settings save error:', error);
    } finally {
      setSavingPaydaySettings(false);
    }
  };

  const saveRapydSettings = async () => {
    try {
      setSavingRapydSettings(true);
      const response = await axios.put(`${API}/admin/rapyd-settings`, rapydSettings);
      
      if (response.data.success) {
        toast.success('✅ Rapyd stillingar vistaðar');
        await loadSettings(); // Reload to get masked credentials
      }
    } catch (error) {
      toast.error('Villa við að vista Rapyd stillingar: ' + (error.response?.data?.detail || error.message));
      console.error('Rapyd settings save error:', error);
    } finally {
      setSavingRapydSettings(false);
    }
  };


  const saveInvoiceSettings = async () => {
    try {
      setSavingInvoiceSettings(true);
      const response = await axios.put(`${API}/admin/invoice-settings`, invoiceSettings);
      
      if (response.data.success) {
        toast.success('✅ Reikningsstillingar vistaðar');
        await loadSettings(); // Reload settings
      }
    } catch (error) {
      toast.error('Villa við að vista reikningsstillingar: ' + (error.response?.data?.detail || error.message));
      console.error('Invoice settings save error:', error);
    } finally {
      setSavingInvoiceSettings(false);
    }
  };

  const saveLabelSettings = async () => {
    try {
      setSavingLabelSettings(true);
      const response = await axios.put(`${API}/admin/label-settings`, labelSettings);
      
      if (response.data.success) {
        toast.success('✅ Miðastillingar vistaðar');
        await loadSettings(); // Reload settings
      }
    } catch (error) {
      toast.error('Villa við að vista miðastillingar: ' + (error.response?.data?.detail || error.message));
      console.error('Label settings save error:', error);
    } finally {
      setSavingLabelSettings(false);
    }
  };


  const testDkConnection = async () => {
    try {
      setSavingDkSettings(true);
      const response = await axios.get(`${API}/admin/dk-status`);
      
      if (response.data.status === 'connected') {
        toast.success('✅ DK tenging virkar!');
      } else if (response.data.status === 'mock') {
        toast.info('🎭 DK er í mock mode');
      } else if (response.data.status === 'disabled') {
        toast.warning('⚠️ DK integration er óvirkt');
      } else {
        toast.error('❌ DK tenging virkar ekki: ' + response.data.message);
      }
    } catch (error) {
      toast.error('Villa við að prófa DK tengingu');
      console.error('DK connection test error:', error);
    } finally {
      setSavingDkSettings(false);
    }
  };

  const saveCacheSettings = async () => {
    try {
      setSavingCacheSettings(true);
      const response = await axios.put(`${API}/admin/cache-settings`, cacheSettings);
      
      if (response.data.success) {
        toast.success('✅ Cache stillingar vistaðar');
        await loadSettings();
      }
    } catch (error) {
      toast.error('Villa við að vista cache stillingar: ' + (error.response?.data?.detail || error.message));
      console.error('Cache settings save error:', error);
    } finally {
      setSavingCacheSettings(false);
    }
  };


  const saveJaLookupSettings = async () => {
    try {
      setSavingJaSettings(true);
      const payload = {
        enabled: jaLookupSettings.enabled,
        price_per_lookup: jaLookupSettings.price_per_lookup
      };
      // Ef notandi hefur slegið inn nýjan lykil, sendum hann með
      if (jaLookupSettings.api_key && jaLookupSettings.api_key.trim()) {
        payload.api_key = jaLookupSettings.api_key.trim();
      }

      const response = await axios.put(`${API}/settings/ja-lookup`, payload);
      
      if (response.data.success) {
        toast.success('✅ Já.is stillingar vistaðar');
        await loadSettings();
      }
    } catch (error) {
      toast.error('Villa við að vista já.is stillingar: ' + (error.response?.data?.detail || error.message));
      console.error('Já.is settings save error:', error);
    } finally {
      setSavingJaSettings(false);
    }
  };

  const saveQrSecuritySettings = async () => {
    try {
      setSavingQrSecurity(true);
      
      // Prepare data to send
      const dataToSend = {
        enabled: qrSecuritySettings.enabled,
        require_pin_outside_hours: qrSecuritySettings.require_pin_outside_hours
      };
      
      // Only send PIN if it was changed (not empty)
      if (qrSecuritySettings.pin_code && qrSecuritySettings.pin_code.length === 4) {
        dataToSend.pin_code = qrSecuritySettings.pin_code;
      } else if (qrSecuritySettings.has_pin) {
        // If there's an existing PIN and input is empty, mark it
        dataToSend.existing_pin = true;
      }
      
      const response = await axios.put(`${API}/settings/qr-security`, dataToSend);
      
      if (response.data.success) {
        toast.success('✅ QR öryggis stillingar vistaðar');
        await loadSettings();
        // Clear PIN input after successful save
        setQrSecuritySettings(prev => ({ ...prev, pin_code: '' }));
      }
    } catch (error) {
      toast.error('Villa við að vista QR öryggis stillingar: ' + (error.response?.data?.detail || error.message));
      console.error('QR security settings save error:', error);
    } finally {
      setSavingQrSecurity(false);
    }
  };




  const saveMyposSettings = async () => {
    try {
      setSavingMyposSettings(true);
      const response = await axios.put(`${API}/admin/mypos-settings`, myposSettings);
      
      if (response.data.success) {
        toast.success('✅ MyPOS stillingar vistaðar');
        await loadSettings();
      }
    } catch (error) {
      toast.error('Villa við að vista MyPOS stillingar: ' + (error.response?.data?.detail || error.message));
      console.error('MyPOS settings save error:', error);
    } finally {
      setSavingMyposSettings(false);
    }
  };


  const updatePrice = async (serviceId, sizeId, price) => {
    // Update local state immediately for UI responsiveness
    setSettings(prev => ({
      ...prev,
      prices: {
        ...prev.prices,
        [serviceId]: {
          ...prev.prices[serviceId],
          [sizeId]: parseInt(price) || 0
        }
      }
    }));
    
    // Save to backend automatically
    try {
      // Update the entire settings with new price
      const updatedPrices = {
        ...settings.prices,
        [serviceId]: {
          ...settings.prices[serviceId],
          [sizeId]: parseInt(price) || 0
        }
      };
      
      await axios.put(`${API}/settings`, {
        prices: updatedPrices
      });
      
      toast.success('Verð vistað');
    } catch (error) {
      console.error('Error updating price:', error);
      toast.error('Villa við að vista verð');
      // Reload to revert to saved state
      await loadSettings();
    }
  };

  // Service management functions
  const addService = async () => {
    if (!newService.name || !newService.id) {
      toast.error('Nafn og ID þjónustu eru nauðsynleg');
      return;
    }
    
    try {
      setSaving(true);
      const response = await axios.post(`${API}/settings/services`, newService);
      
      if (response.data.success) {
        toast.success('Þjónusta bætt við');
        setShowAddService(false);
        setNewService({ 
          name: '', 
          description: '', 
          id: '',
          available: true,
          price_editable: false,
          allow_discount: true,
          display_in_pricelist: true
        });
        loadSettings(); // Reload to see new service
      }
    } catch (error) {
      toast.error('Tókst ekki að bæta við þjónustu');
    } finally {
      setSaving(false);
    }
  };

  const updateService = async (serviceId, serviceData) => {
    try {
      setSaving(true);
      await axios.put(`${API}/settings/services/${serviceId}`, serviceData);
      toast.success('Þjónusta uppfærð');
      await loadSettings();
      setEditingService(null);
    } catch (error) {
      toast.error('Villa við að uppfæra þjónustu');
    } finally {
      setSaving(false);
    }
  };

  const reorderServices = async (serviceIds) => {
    try {
      await axios.put(`${API}/settings/services/reorder`, { service_ids: serviceIds });
      await loadSettings();
      toast.success('Röð uppfærð');
    } catch (error) {
      toast.error('Villa við að uppfæra röð');
    }
  };

  const updateCategoryOrder = async (updatedCategories) => {
    try {
      await axios.put(`${API}/settings/categories/reorder`, { categories: updatedCategories });
      await loadSettings();
      toast.success('Flokkur færður');
    } catch (error) {
      toast.error('Villa við að færa flokk');
      console.error('Category reorder error:', error);
    }
  };


  const updateProductNumber = async (serviceId, sizeId, productNumber) => {
    try {
      // Get current manual product numbers for this service
      const service = settings.services?.find(s => s.id === serviceId);
      const currentManual = service?.manual_product_numbers || {};
      
      // Update the manual product numbers
      const updatedManual = {
        ...currentManual,
        [sizeId]: productNumber ? parseInt(productNumber) : null
      };
      
      await axios.put(`${API}/settings/services/${serviceId}/product-numbers`, {
        manual_product_numbers: updatedManual
      });
      
      await loadSettings();
      toast.success('Vörunúmer uppfært');
    } catch (error) {
      toast.error('Villa við að uppfæra vörunúmer');
    }
  };

  const addCategory = async () => {
    if (!newCategory.id || !newCategory.name) {
      toast.error('ID og nafn eru nauðsynleg');
      return;
    }
    
    try {
      setSaving(true);
      const response = await axios.post(`${API}/settings/categories`, newCategory);
      
      if (response.data.success) {
        toast.success('Flokkur bætt við');
        setShowAddCategory(false);
        setNewCategory({ id: '', name: '', icon: '📦' });
        loadSettings();
      }
    } catch (error) {
      toast.error('Tókst ekki að bæta við flokki');
    } finally {
      setSaving(false);
    }
  };

  const updateCategory = async (categoryId, categoryData) => {
    try {
      setSaving(true);
      await axios.put(`${API}/settings/categories/${categoryId}`, categoryData);
      toast.success('Flokkur uppfærður');
      await loadSettings();
      setEditingCategory(null);
    } catch (error) {
      toast.error('Villa við að uppfæra flokk');
    } finally {
      setSaving(false);
    }
  };

  const deleteCategory = async (categoryId) => {
    // Prevent deleting default categories
    if (categoryId === 'general' || categoryId === 'special') {
      toast.error('Ekki hægt að eyða sjálfgefnum flokkum');
      return;
    }

    if (!confirm('Ertu viss um að þú viljir eyða þessum flokki? Allar þjónustur í þessum flokki verða færðar í "Almenn þrif".')) {
      return;
    }
    
    try {
      setSaving(true);
      const response = await axios.delete(`${API}/settings/categories/${categoryId}`);
      
      if (response.data.success) {
        toast.success('Flokkur eytt');
        loadSettings();
      }
    } catch (error) {
      toast.error('Tókst ekki að eyða flokki');
    } finally {
      setSaving(false);
    }
  };

  const deleteService = async (serviceId) => {
    if (!confirm('Ertu viss um að þú viljir eyða þessari þjónustu?')) {
      return;
    }
    
    try {
      setSaving(true);
      const response = await axios.delete(`${API}/settings/services/${serviceId}`);
      
      if (response.data.success) {
        toast.success('Þjónustu eytt');
        loadSettings();
      }
    } catch (error) {
      toast.error('Tókst ekki að eyða þjónustu');
    } finally {
      setSaving(false);
    }
  };

  // Size management functions
  const addSize = async () => {
    if (!newSize.name || !newSize.id) {
      toast.error('Nafn og ID bílastærðar eru nauðsynleg');
      return;
    }
    
    try {
      setSaving(true);
      const response = await axios.post(`${API}/settings/sizes`, newSize);
      
      if (response.data.success) {
        toast.success('Bílastærð bætt við');
        setShowAddSize(false);
        setNewSize({ name: '', id: '', order: undefined });
        loadSettings();
      }
    } catch (error) {
      toast.error('Tókst ekki að bæta við bílastærð');
    } finally {
      setSaving(false);
    }
  };

  const updateSize = async (sizeId, sizeData) => {
    try {
      setSaving(true);
      const response = await axios.put(`${API}/settings/sizes/${sizeId}`, sizeData);
      
      if (response.data.success) {
        toast.success('Bílastærð uppfærð');
        setEditingSize(null);
        loadSettings();
      }
    } catch (error) {
      toast.error('Tókst ekki að uppfæra bílastærð');
    } finally {
      setSaving(false);
    }
  };

  const deleteSize = async (sizeId) => {
    if (!confirm('Ertu viss um að þú viljir eyða þessari bílastærð?')) {
      return;
    }
    
    try {
      setSaving(true);
      const response = await axios.delete(`${API}/settings/sizes/${sizeId}`);
      
      if (response.data.success) {
        toast.success('Bílastærð eytt');
        loadSettings();
      }
    } catch (error) {
      toast.error('Tókst ekki að eyða bílastærð');
    } finally {
      setSaving(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      // Only send prices and version - opening_hours/duration/estimation saved by their own component
      const payload = {};
      if (settings?.prices) payload.prices = settings.prices;
      if (settings?.version) payload.version = settings.version;
      if (Object.keys(payload).length > 0) {
        await axios.put(`${API}/settings`, payload);
      }
      toast.success('Stillingar vistaðar');
    } catch (error) {
      toast.error('Tókst ekki að vista stillingar');
    } finally {
      setSaving(false);
    }
  };

  const resetSettings = async () => {
    if (!confirm('Ertu viss um að þú viljir endurstilla allar stillingar á upphafleg gildi?')) return;

    try {
      await axios.post(`${API}/settings/reset`);
      toast.success('Stillingar endurstilltar');
      loadSettings();
    } catch (error) {
      toast.error('Tókst ekki að endurstilla stillingar');
    }
  };

  // Pickup Zones Manager is defined as a standalone component below SystemSettings
  // to prevent re-mounting on every parent re-render




  function OpeningHoursManager({ settings, onSave }) {
    const fmtMin = (mins) => {
      if (mins == null || mins === 0) return '0 mín';
      if (mins < 60) return `${mins} mín`;
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      return `${h}:${String(m).padStart(2, '0')} klst`;
    };
    const defaultHours = {
      enabled: true,
      hours: {
        "0": {open: "08:30", close: "18:00", closed: false},
        "1": {open: "08:30", close: "18:00", closed: false},
        "2": {open: "08:30", close: "18:00", closed: false},
        "3": {open: "08:30", close: "18:00", closed: false},
        "4": {open: "08:30", close: "18:00", closed: false},
        "5": {open: "09:00", close: "14:00", closed: true},
        "6": {open: "09:00", close: "14:00", closed: true}
      }
    };
    const initHours = (() => {
      const dbHours = settings?.opening_hours;
      if (dbHours && typeof dbHours === 'object' && !Array.isArray(dbHours) && dbHours.hours) {
        // Merge DB hours with defaults to ensure all 7 days exist
        const merged = { ...defaultHours.hours };
        for (const day of Object.keys(dbHours.hours)) {
          merged[day] = dbHours.hours[day];
        }
        return { enabled: dbHours.enabled !== undefined ? dbHours.enabled : true, hours: merged };
      }
      return defaultHours;
    })();
    const [openingHours, setOpeningHours] = useState(initHours);
    
    const [durationTracking, setDurationTracking] = useState(
      settings?.duration_tracking || {enabled: false, default_duration: 30}
    );
    const [estimationMode, setEstimationMode] = useState(settings?.estimation_mode || 'manual');
    const [learnedStats, setLearnedStats] = useState(null);
    const [loadingStats, setLoadingStats] = useState(false);
    
    const [services, setServices] = useState(() => {
      const svcList = settings?.services || [];
      const savedDurations = settings?.service_durations || {};
      return svcList.map(s => ({
        ...s,
        duration: savedDurations[s.id] !== undefined ? savedDurations[s.id] : (s.duration || '')
      }));
    });
    const [saving, setSaving] = useState(false);
    
    const loadLearnedStats = async () => {
      setLoadingStats(true);
      try {
        const res = await axios.get(`${API}/analytics/job-statistics?days=90`);
        setLearnedStats(res.data);
      } catch (err) {
        console.error('Failed to load statistics:', err);
      } finally {
        setLoadingStats(false);
      }
    };
    
    useEffect(() => {
      if (durationTracking.enabled) {
        loadLearnedStats();
      }
    }, [durationTracking.enabled]);
    
    const weekdayNames = ['Mánudagur', 'Þriðjudagur', 'Miðvikudagur', 'Fimmtudagur', 'Föstudagur', 'Laugardagur', 'Sunnudagur'];
    
    const updateDayHours = (day, field, value) => {
      setOpeningHours(prev => ({
        ...prev,
        hours: {
          ...prev.hours,
          [day]: {
            ...prev.hours[day],
            [field]: value
          }
        }
      }));
    };
    
    const updateServiceDuration = (serviceId, duration) => {
      setServices(prev => prev.map(s => 
        s.id === serviceId ? {...s, duration: duration === '' ? '' : (parseInt(duration) || 0)} : s
      ));
    };
    
    const handleSave = async () => {
      try {
        setSaving(true);

        // Ensure empty duration values default to default_duration before saving
        const cleanDuration = {
          ...durationTracking,
          default_duration: durationTracking.default_duration === '' ? 30 : durationTracking.default_duration
        };

        // Save opening hours and duration tracking
        await axios.put(`${API}/settings`, {
          opening_hours: openingHours,
          duration_tracking: cleanDuration,
          estimation_mode: estimationMode
        });
        
        // Save service durations separately (update each service's duration)
        const serviceDurations = {};
        services.forEach(s => {
          if (s.duration !== undefined && s.duration !== '') {
            serviceDurations[s.id] = parseInt(s.duration) || cleanDuration.default_duration;
          }
        });
        
        if (Object.keys(serviceDurations).length > 0) {
          await axios.put(`${API}/settings`, {
            service_durations: serviceDurations
          });
        }

        toast.success('Stillingar vistaðar');
      } catch (error) {
        toast.error('Villa við að vista stillingar');
      } finally {
        setSaving(false);
      }
    };
    
    return (
      <div className="space-y-6">
        {/* Opening Hours Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium">Opnunartímar</h3>
            <div className="flex items-center gap-2">
              <Label htmlFor="enable-hours">Virkja</Label>
              <Checkbox
                id="enable-hours"
                checked={openingHours.enabled}
                onCheckedChange={(checked) => setOpeningHours({...openingHours, enabled: checked})}
              />
            </div>
          </div>
          
          {openingHours.enabled && (
            <div className="space-y-3">
              {Object.keys(openingHours.hours).map((day, index) => (
                <div key={day} className="grid grid-cols-12 gap-3 items-center p-3 bg-slate-50 rounded-lg">
                  <div className="col-span-3 font-medium">{weekdayNames[index]}</div>
                  
                  <div className="col-span-2">
                    <Checkbox
                      checked={openingHours.hours[day].closed}
                      onCheckedChange={(checked) => updateDayHours(day, 'closed', checked)}
                    />
                    <span className="ml-2 text-sm">Lokað</span>
                  </div>
                  
                  {!openingHours.hours[day].closed && (
                    <>
                      <div className="col-span-3">
                        <Label className="text-xs">Opnar</Label>
                        <Input
                          type="time"
                          value={openingHours.hours[day].open}
                          onChange={(e) => updateDayHours(day, 'open', e.target.value)}
                          className="text-sm"
                        />
                      </div>
                      <div className="col-span-3">
                        <Label className="text-xs">Lokar</Label>
                        <Input
                          type="time"
                          value={openingHours.hours[day].close}
                          onChange={(e) => updateDayHours(day, 'close', e.target.value)}
                          className="text-sm"
                        />
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Duration Tracking Section */}
        <div className="border-t pt-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-medium">Tímaáætlun</h3>
              <p className="text-sm text-slate-600">Stilltu lengd þjónustu og áætlun verka á dag</p>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="enable-duration">Virkja</Label>
              <Checkbox
                id="enable-duration"
                checked={durationTracking.enabled}
                onCheckedChange={(checked) => setDurationTracking({...durationTracking, enabled: checked})}
              />
            </div>
          </div>
          
          {durationTracking.enabled && (
            <>
              {/* Mode selection */}
              <div className="mb-6 p-4 bg-slate-50 rounded-lg border">
                <Label className="font-medium mb-3 block">Aðferð tímaáætlunar</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button type="button" onClick={() => setEstimationMode('manual')} className={`p-4 rounded-lg border-2 text-left transition-all ${estimationMode === 'manual' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-testid="estimation-mode-manual">
                    <div className="font-medium text-sm">Handvirkt</div>
                    <p className="text-xs text-slate-500 mt-1">Stilltu fastan tíma fyrir hverja þjónustu</p>
                  </button>
                  <button type="button" onClick={() => setEstimationMode('learned')} className={`p-4 rounded-lg border-2 text-left transition-all ${estimationMode === 'learned' ? 'border-green-500 bg-green-50' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-testid="estimation-mode-learned">
                    <div className="font-medium text-sm">Snjöll áætlun</div>
                    <p className="text-xs text-slate-500 mt-1">Lærir af raunverulegum vinnutímum eldri verka</p>
                  </button>
                </div>
              </div>

              {/* Learned statistics */}
              {learnedStats && (
                <div className="mb-6 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg border border-emerald-200">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-emerald-800">Tölfræði úr eldri verkum</h4>
                    <button type="button" onClick={loadLearnedStats} disabled={loadingStats} className="text-xs text-emerald-600 hover:text-emerald-800 underline" data-testid="refresh-stats-btn">{loadingStats ? 'Hleður...' : 'Endurhlaða'}</button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                    <div className="bg-white p-3 rounded-lg text-center" data-testid="stat-total-jobs">
                      <div className="text-2xl font-bold text-emerald-700">{learnedStats.total_jobs_done}</div>
                      <div className="text-xs text-slate-500">Lokið verk</div>
                    </div>
                    <div className="bg-white p-3 rounded-lg text-center" data-testid="stat-days-analyzed">
                      <div className="text-2xl font-bold text-emerald-700">{learnedStats.days_with_jobs}</div>
                      <div className="text-xs text-slate-500">Dagar með verk</div>
                    </div>
                    <div className="bg-white p-3 rounded-lg text-center" data-testid="stat-avg-per-day">
                      <div className="text-2xl font-bold text-blue-700">{learnedStats.avg_jobs_per_day}</div>
                      <div className="text-xs text-slate-500">Meðaltal á dag</div>
                    </div>
                    <div className="bg-white p-3 rounded-lg text-center" data-testid="stat-avg-weekday">
                      <div className="text-2xl font-bold text-blue-700">{learnedStats.avg_jobs_per_weekday}</div>
                      <div className="text-xs text-slate-500">Meðaltal virka daga</div>
                    </div>
                  </div>
                  {learnedStats.learned_durations && Object.keys(learnedStats.learned_durations).length > 0 && (
                    <div>
                      <h5 className="text-sm font-medium text-emerald-800 mb-2">Raunveruleg lengd þjónustu</h5>
                      <div className="space-y-1.5">
                        {Object.entries(learnedStats.learned_durations).map(([sid, data]) => (
                          <div key={sid} className="flex items-center justify-between p-2 bg-white rounded text-sm" data-testid={`learned-duration-${sid}`}>
                            <span className="font-medium">{data.name}</span>
                            <div className="flex items-center gap-3 text-xs text-slate-600">
                              <span>{data.sample_count} verk</span>
                              <span className="font-semibold text-emerald-700">{fmtMin(data.avg_minutes)} avg</span>
                              <span className="text-slate-400">{fmtMin(data.median_minutes)} miðgildi</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {learnedStats.total_jobs_analyzed === 0 && (
                    <p className="text-sm text-amber-600 mt-2">Engin verk með tímagögn fundust. Kerfið byrjar að safna gögnum eftir að verk eru kláruð.</p>
                  )}
                </div>
              )}
              {loadingStats && !learnedStats && (
                <div className="mb-6 p-4 bg-slate-50 rounded-lg text-center text-sm text-slate-500">Hleður tölfræði...</div>
              )}

              {/* Manual duration settings */}
              <div className={estimationMode === 'learned' ? 'opacity-60' : ''}>
                <Label className="font-medium mb-2 block">{estimationMode === 'manual' ? 'Handvirk lengd þjónustu' : 'Varalengd (ef engin gögn)'}</Label>
                <div className="mb-4">
                  <Label className="text-sm">Sjálfgefin lengd (mínútur)</Label>
                  <Input type="number" value={durationTracking.default_duration} onChange={(e) => setDurationTracking({...durationTracking, default_duration: e.target.value === '' ? '' : (parseInt(e.target.value) || 30)})} min="5" step="5" className="w-32" data-testid="default-duration-input" />
                </div>
                <div className="space-y-2">
                  {services.map(service => {
                    const learned = learnedStats?.learned_durations?.[service.id];
                    return (
                      <div key={service.id} className="grid grid-cols-12 gap-3 p-3 bg-slate-50 rounded-lg items-center">
                        <div className="col-span-4 font-medium text-sm">{service.name}</div>
                        <div className="col-span-4">
                          <Input type="number" value={service.duration !== '' && service.duration !== undefined ? service.duration : durationTracking.default_duration} onChange={(e) => updateServiceDuration(service.id, e.target.value)} min="5" step="5" className="text-sm" placeholder={`${durationTracking.default_duration} mín`} data-testid={`service-duration-${service.id}`} />
                        </div>
                        <div className="col-span-4 text-xs text-slate-500">
                          {learned ? (<span className="text-emerald-600">Raunv: {fmtMin(learned.avg_minutes)} ({learned.sample_count} verk)</span>) : (<span className="text-slate-400">Engin gögn</span>)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
        
        {/* Save Button */}
        <div className="flex justify-end pt-4 border-t">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Vistar...' : '💾 Vista stillingar'}
          </Button>
        </div>
      </div>
    );
  }



  function RoggIntegrationSettings() {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
      username: '',
      password: ''
    });

    useEffect(() => {
      loadSettings();
    }, []);

    const loadSettings = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/admin/rogg-settings`);
        setSettings(response.data);
        setFormData({
          username: response.data.username || '',
          password: response.data.password || ''
        });
      } catch (error) {
        toast.error('Tókst ekki að hlaða Rogg.is stillingum');
      } finally {
        setLoading(false);
      }
    };

    const handleSave = async () => {
      try {
        if (!formData.username.trim()) {
          toast.error('Notendanafn má ekki vera tómt');
          return;
        }
        if (!formData.password.trim() || formData.password === '****') {
          toast.error('Lykilorð má ekki vera tómt');
          return;
        }

        setSaving(true);
        await axios.put(`${API}/admin/rogg-settings`, formData);
        toast.success('✅ Rogg.is stillingar vistaðar!');
        await loadSettings();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Tókst ekki að vista stillingar');
      } finally {
        setSaving(false);
      }
    };

    if (loading) {
      return (
        <Card>
          <CardContent className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            🚗 Rogg.is Bílaskrár Tenging
          </CardTitle>
          <p className="text-sm text-slate-600">
            Stillingar fyrir tengingu við Rogg.is bílaskrá API. Notað fyrir sjálfvirka uppflettingu á bílum.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Configuration Status */}
          <div className={`p-4 rounded-lg border ${
            settings?.configured 
              ? 'bg-green-50 border-green-200' 
              : 'bg-yellow-50 border-yellow-200'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              {settings?.configured ? (
                <>
                  <span className="text-green-600">✅</span>
                  <span className="font-medium text-green-900">Tenging virk</span>
                </>
              ) : (
                <>
                  <span className="text-yellow-600">⚠️</span>
                  <span className="font-medium text-yellow-900">Tenging ekki stillt</span>
                </>
              )}
            </div>
            <p className="text-sm text-slate-600">
              {settings?.source === 'database' && 'Stillingar eru vistaðar í gagnagrunni (hægt að uppfæra hér)'}
              {settings?.source === 'env' && 'Stillingar eru frá .env skrá (hægt að yfirskrifa hér)'}
              {settings?.source === 'none' && 'Engar stillingar fundust - vinsamlegast settu inn credentials'}
            </p>
          </div>

          {/* Username Field */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">
              Notendanafn
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={formData.username}
              onChange={(e) => setFormData({...formData, username: e.target.value})}
              placeholder="t.d. abon"
            />
          </div>

          {/* Password Field */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">
              Lykilorð
            </label>
            <input
              type="password"
              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              placeholder="Sláðu inn lykilorð"
            />
            <p className="text-xs text-slate-500">
              {formData.password === '****' 
                ? 'Lykilorð er vistað - sláðu inn nýtt lykilorð til að breyta' 
                : 'Lykilorðið verður vistað dulkóðað'}
            </p>
          </div>

          {/* Save Button */}
          <div className="flex items-center gap-3 pt-4">
            <button
              onClick={handleSave}
              disabled={saving}
              className={`px-4 py-2 rounded-md font-medium ${
                saving 
                  ? 'bg-slate-300 cursor-not-allowed' 
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              }`}
            >
              {saving ? '⏳ Vistar...' : '💾 Vista stillingar'}
            </button>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
            <h4 className="font-medium text-blue-900 mb-2">ℹ️ Um Rogg.is tenginguna</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Notað fyrir sjálfvirka uppflettingu á bílum með kennitölu</li>
              <li>• Sækir upplýsingar um bíl, eiganda og símanúmer</li>
              <li>• Breytingar taka gildi strax án þess að þurfa að deploya aftur</li>
              <li>• Credentials eru vistaðar örugglega í gagnagrunninum</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    );
  }

  function BlockedTimesManager() {
    const [blockedTimes, setBlockedTimes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({
      type: 'full_day',
      recurrence: 'specific',  // 'specific' or 'recurring'
      date: '',
      weekday: '',
      start_time: '09:00',
      end_time: '17:00',
      reason: ''
    });
    
    const weekdayNames = ['Mánudagur', 'Þriðjudagur', 'Miðvikudagur', 'Fimmtudagur', 'Föstudagur', 'Laugardagur', 'Sunnudagur'];
    
    useEffect(() => {
      loadBlockedTimes();
    }, []);
    
    const loadBlockedTimes = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/blocked-times`);
        setBlockedTimes(response.data || []);
      } catch (error) {
        toast.error('Tókst ekki að hlaða lokuðum tímum');
      } finally {
        setLoading(false);
      }
    };
    
    const resetForm = () => {
      setFormData({
        type: 'full_day',
        recurrence: 'specific',
        date: '',
        weekday: '',
        start_time: '09:00',
        end_time: '17:00',
        reason: ''
      });
      setEditingItem(null);
    };
    
    const handleSave = async () => {
      try {
        // Validate
        if (!formData.reason.trim()) {
          toast.error('Vinsamlegast sláðu inn ástæðu');
          return;
        }
        
        if (formData.recurrence === 'specific' && !formData.date) {
          toast.error('Vinsamlegast veldu dagsetningu');
          return;
        }
        
        if (formData.recurrence === 'recurring' && !formData.weekday) {
          toast.error('Vinsamlegast veldu vikudag');
          return;
        }
        
        // Build payload
        const payload = {
          type: formData.type,
          reason: formData.reason,
          date: formData.recurrence === 'specific' ? formData.date : null,
          weekday: formData.recurrence === 'recurring' ? parseInt(formData.weekday) : null,
          start_time: formData.type === 'time_period' ? formData.start_time : null,
          end_time: formData.type === 'time_period' ? formData.end_time : null
        };
        
        if (editingItem) {
          // Update
          payload.id = editingItem.id;
          await axios.put(`${API}/blocked-times/${editingItem.id}`, payload);
          toast.success('Lokaður tími uppfærður');
        } else {
          // Create
          await axios.post(`${API}/blocked-times`, payload);
          toast.success('Lokaður tími skráður');
        }
        
        setDialogOpen(false);
        resetForm();
        loadBlockedTimes();
      } catch (error) {
        toast.error('Villa við að vista lokaðan tíma');
      }
    };
    
    const handleEdit = (item) => {
      setEditingItem(item);
      setFormData({
        type: item.type,
        recurrence: item.date ? 'specific' : 'recurring',
        date: item.date || '',
        weekday: item.weekday !== null ? item.weekday.toString() : '',
        start_time: item.start_time || '09:00',
        end_time: item.end_time || '17:00',
        reason: item.reason
      });
      setDialogOpen(true);
    };
    
    const handleDelete = async (id) => {
      if (!confirm('Ertu viss um að þú viljir eyða þessum lokuða tíma?')) return;
      
      try {
        await axios.delete(`${API}/blocked-times/${id}`);
        toast.success('Lokaður tími eytt');
        loadBlockedTimes();
      } catch (error) {
        toast.error('Tókst ekki að eyða lokuðum tíma');
      }
    };
    
    const getDisplayText = (item) => {
      let text = '';
      
      // Type
      if (item.type === 'full_day') {
        text += '🔒 Heill dagur: ';
      } else {
        text += `⏰ Tímabil ${item.start_time}-${item.end_time}: `;
      }
      
      // Date or weekday
      if (item.date) {
        text += moment(item.date).format('DD. MMM YYYY');
      } else if (item.weekday !== null) {
        text += `Allir ${weekdayNames[item.weekday]}ar`;
      }
      
      return text;
    };
    
    if (loading) {
      return <div className="text-center py-8">Hleður...</div>;
    }
    
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <p className="text-sm text-slate-600">
            {blockedTimes.length} lokaðir tímar skráðir
          </p>
          <Button onClick={() => {
            resetForm();
            setDialogOpen(true);
          }}>
            ➕ Bæta við lokuðum tíma
          </Button>
        </div>
        
        {blockedTimes.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            Engir lokaðir tímar skráðir
          </div>
        ) : (
          <div className="space-y-2">
            {blockedTimes.map(item => (
              <Card key={item.id}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-medium">{getDisplayText(item)}</div>
                      <div className="text-sm text-slate-600 mt-1">
                        Ástæða: {item.reason}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => handleEdit(item)}>
                        ✏️ Breyta
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDelete(item.id)} className="text-red-600">
                        🗑️ Eyða
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        
        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Breyta lokuðum tíma' : 'Nýr lokaður tími'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Type */}
              <div>
                <Label>Tegund</Label>
                <select
                  className="w-full p-2 border rounded-md"
                  value={formData.type}
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                >
                  <option value="full_day">Heill dagur</option>
                  <option value="time_period">Tímabil</option>
                </select>
              </div>
              
              {/* Recurrence */}
              <div>
                <Label>Endurtekning</Label>
                <select
                  className="w-full p-2 border rounded-md"
                  value={formData.recurrence}
                  onChange={(e) => setFormData({...formData, recurrence: e.target.value})}
                >
                  <option value="specific">Ákveðin dagsetning</option>
                  <option value="recurring">Endurtekinn vikudagur</option>
                </select>
              </div>
              
              {/* Date or Weekday */}
              {formData.recurrence === 'specific' ? (
                <div>
                  <Label>Dagsetning</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                  />
                </div>
              ) : (
                <div>
                  <Label>Vikudagur</Label>
                  <select
                    className="w-full p-2 border rounded-md"
                    value={formData.weekday}
                    onChange={(e) => setFormData({...formData, weekday: e.target.value})}
                  >
                    <option value="">Veldu vikudag</option>
                    {weekdayNames.map((name, index) => (
                      <option key={index} value={index}>{name}</option>
                    ))}
                  </select>
                </div>
              )}
              
              {/* Time Period */}
              {formData.type === 'time_period' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Frá</Label>
                    <Input
                      type="time"
                      value={formData.start_time}
                      onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Til</Label>
                    <Input
                      type="time"
                      value={formData.end_time}
                      onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                    />
                  </div>
                </div>
              )}
              
              {/* Reason */}
              <div>
                <Label>Ástæða</Label>
                <Input
                  value={formData.reason}
                  onChange={(e) => setFormData({...formData, reason: e.target.value})}
                  placeholder="t.d. Jól, Hádegishlé, Viðhald"
                />
              </div>
              
              {/* Actions */}
              <div className="flex gap-2 pt-4">
                <Button onClick={handleSave} className="flex-1">
                  {editingItem ? 'Vista breytingar' : 'Skrá'}
                </Button>
                <Button variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                  Hætta við
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }


  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-8">Hleður stillingum...</div>
      </div>
    );
  }
  
  if (!settings) {
    return (
      <div className="p-6">
        <div className="text-center py-8">Engar stillingar fundust</div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="p-6">
        <div className="text-center py-8 text-red-600">Villa við að hlaða stillingum</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Stillingar</h1>
        <div className="space-x-2">
          <Button variant="outline" onClick={resetSettings}>
            Endurstilla
          </Button>
          <Button onClick={saveSettings} disabled={saving}>
            {saving ? 'Vistar...' : 'Vista breytingar'}
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="pricing">💰 Verðskrá</TabsTrigger>
          <TabsTrigger value="dealerships">🏢 Bílasölur</TabsTrigger>
          <TabsTrigger value="online-booking">📅 Online bókanir</TabsTrigger>
          <TabsTrigger value="system">⚙️ Kerfisstillingar</TabsTrigger>
          <TabsTrigger value="hours">🕐 Opnunartímar</TabsTrigger>
          <TabsTrigger value="invoices">💰 Reikningsstillingar</TabsTrigger>
          <TabsTrigger value="integrations">🔌 Tengingar</TabsTrigger>
          <TabsTrigger value="other">🔧 Aðrar stillingar</TabsTrigger>
        </TabsList>

        {/* Pricing Tab - Combined all pricing-related settings */}
        <TabsContent value="pricing" className="space-y-6">
          <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-4">
            <h3 className="font-medium text-blue-900 dark:text-blue-200 mb-2">💰 Verðskrá og tengdar stillingar</h3>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Hér eru allar stillingar sem tengjast verðskrá: verðtafla, þjónustur, bílastærðir, flokkar og sækja/skila verð.
            </p>
          </div>

          {/* Sub-tabs for pricing sections */}
          <Tabs defaultValue="prices">
            <TabsList>
              <TabsTrigger value="prices">Verðtafla</TabsTrigger>
              <TabsTrigger value="services">Þjónustur</TabsTrigger>
              <TabsTrigger value="products">📦 Vörunotkun</TabsTrigger>
              <TabsTrigger value="sizes">Bílastærðir</TabsTrigger>
              <TabsTrigger value="categories">Flokkar</TabsTrigger>
              <TabsTrigger value="pickup-zones">Sækja/skila</TabsTrigger>
              <TabsTrigger value="import-export">📥 Import/Export</TabsTrigger>
            </TabsList>
            
            {/* Prices Matrix Sub-tab */}
            <TabsContent value="prices" className="space-y-4" key={`prices-${settings?.services?.length || 0}-${settings?.services?.[0]?.order || 0}`}>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Verðskrá - Þjónusta x Bílastærðir</CardTitle>
                  <p className="text-sm text-slate-600">
                    Allar upphæðir eru í ISK með VSK (24%). Vörunúmer raðast frá 101 og upp.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => setPricelistDialogOpen(true)}
                    variant="outline"
                    className="flex items-center gap-2 bg-green-50 hover:bg-green-100 text-green-700 border-green-300"
                  >
                    📧 Senda verðskrá
                  </Button>
                  <Button 
                    onClick={() => setShowAutoFillDialog(true)}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    🔢 Útfylla vörunúmer sjálfkrafa
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2 font-medium">Þjónusta</th>
                      {settings.sizes?.sort((a, b) => (a.order || 0) - (b.order || 0)).map(size => (
                        <th key={size.id} className="text-center p-2 font-medium min-w-24">
                          {size.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {settings.services
                      ?.filter(service => service.display_in_pricelist !== false)
                      .sort((a, b) => {
                        const orderA = a.order !== undefined && a.order !== null ? a.order : 999;
                        const orderB = b.order !== undefined && b.order !== null ? b.order : 999;
                        return orderA - orderB;
                      })
                      .map(service => (
                      <tr key={service.id} className="border-b hover:bg-slate-50">
                        <td className="p-2">
                          <div className="flex items-center space-x-2">
                            <div>
                              <div className="font-medium">{service.name}</div>
                              <div className="text-xs text-slate-600">{service.description}</div>
                              {service.product_code && (
                                <div className="text-xs text-slate-500 mt-1">
                                  Vörunr: {service.product_code}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        {settings.sizes?.sort((a, b) => (a.order || 0) - (b.order || 0)).map(size => (
                          <td key={size.id} className="p-2 text-center">
                            <div className="space-y-1">
                              <div className="text-xs">
                                <Input
                                  type="number"
                                  value={editingProductNumbers[`${service.id}_${size.id}`] ?? service.manual_product_numbers?.[size.id] ?? ''}
                                  onChange={(e) => {
                                    const key = `${service.id}_${size.id}`;
                                    setEditingProductNumbers({
                                      ...editingProductNumbers,
                                      [key]: e.target.value
                                    });
                                  }}
                                  onBlur={(e) => {
                                    const key = `${service.id}_${size.id}`;
                                    if (editingProductNumbers[key] !== undefined) {
                                      updateProductNumber(service.id, size.id, e.target.value);
                                      // Clear the editing state
                                      const newEditing = { ...editingProductNumbers };
                                      delete newEditing[key];
                                      setEditingProductNumbers(newEditing);
                                    }
                                  }}
                                  placeholder="---"
                                  className="w-20 text-center text-xs h-7"
                                  min="1"
                                  title="Vörunúmer - smelltu til að breyta"
                                />
                              </div>
                              <Input
                                type="number"
                                value={settings.prices?.[service.id]?.[size.id] || 0}
                                onChange={(e) => updatePrice(service.id, size.id, e.target.value)}
                                className="w-24 text-center text-sm"
                                min="0"
                                step="100"
                              />
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

            {/* Services Sub-tab */}
            <TabsContent value="services" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Þjónustuflokkur</CardTitle>
                  <p className="text-sm text-slate-600">
                    Stjórna öllum þjónustum, bæta við nýjum og breyta núverandi.
                  </p>
                </div>
                <Button onClick={() => setShowAddService(true)} className="bg-green-600 hover:bg-green-700">
                  ➕ Bæta við þjónustu
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Add Service Form */}
              {showAddService && (
                <Card className="mb-4 border-green-200">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                      <div>
                        <Label>ID þjónustu *</Label>
                        <Input
                          value={newService.id}
                          onChange={(e) => setNewService(prev => ({...prev, id: e.target.value}))}
                          placeholder="t.d. vaxing"
                        />
                      </div>
                      <div>
                        <Label>Nafn þjónustu *</Label>
                        <Input
                          value={newService.name}
                          onChange={(e) => setNewService(prev => ({...prev, name: e.target.value}))}
                          placeholder="t.d. Vaxning"
                        />
                      </div>
                      <div className="md:col-span-3">
                        <Label>📝 Lýsing fyrir viðskiptavini</Label>
                        <textarea
                          value={newService.description}
                          onChange={(e) => setNewService(prev => ({...prev, description: e.target.value}))}
                          placeholder="t.d. Alþrif felur í sér ytra og innra þrif á öllu bílnum, ryksuga, þrif á gluggum og mælaborði"
                          className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px]"
                          rows="3"
                        />
                        <div className="text-xs text-slate-500 mt-1">
                          💡 Þessi lýsing birtist viðskiptavinum í "Ný skráning" undir verði
                        </div>
                      </div>
                      <div className="md:col-span-3">
                        <Label>👨‍🔧 Lýsing fyrir starfsfólk</Label>
                        <textarea
                          value={newService.staff_description || ''}
                          onChange={(e) => setNewService(prev => ({...prev, staff_description: e.target.value}))}
                          placeholder="t.d. Munum að athuga öll horn, mælaborð þarf að vera ósprautað, gúmmlögn á hjólbarða"
                          className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px]"
                          rows="3"
                        />
                        <div className="text-xs text-slate-500 mt-1">
                          💡 Þessi lýsing birtist starfsfólki í "Verk" listanum þegar smellt er á þjónustunafn
                        </div>
                      </div>
                      <div>
                        <Label>Vörunúmer</Label>
                        <Input
                          value={newService.product_code || ''}
                          onChange={(e) => setNewService(prev => ({...prev, product_code: e.target.value}))}
                          placeholder="T.d. 'VNR-001'"
                        />
                        <div className="text-xs text-slate-500 mt-1">
                          Valkvætt: Vörunúmer eða auðkenni fyrir þessa þjónustu
                        </div>
                      </div>
                      <div>
                        <Label>Flokkur þjónustu</Label>
                        <select
                          value={newService.category || 'general'}
                          onChange={(e) => setNewService(prev => ({...prev, category: e.target.value}))}
                          className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {settings.categories?.sort((a, b) => (a.order || 0) - (b.order || 0)).map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                          ))}
                        </select>
                        <div className="text-xs text-slate-500 mt-1">
                          Veldu flokk fyrir þessa þjónustu
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2 mb-3">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="new-service-available"
                          checked={newService.available !== false}
                          onChange={(e) => setNewService(prev => ({...prev, available: e.target.checked}))}
                          className="w-4 h-4"
                        />
                        <Label htmlFor="new-service-available" className="cursor-pointer">
                          ✅ Þjónusta í boði (viðskiptavinir geta valið þessa þjónustu)
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="new-service-price-editable"
                          checked={newService.price_editable === true}
                          onChange={(e) => setNewService(prev => ({...prev, price_editable: e.target.checked}))}
                          className="w-4 h-4"
                        />
                        <Label htmlFor="new-service-price-editable" className="cursor-pointer">
                          💰 Verð breytanlegt (t.d. "frá 30.000 kr" - hægt að breyta verði þegar verk er skráð)
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="new-service-allow-discount"
                          checked={newService.allow_discount !== false}
                          onChange={(e) => setNewService(prev => ({...prev, allow_discount: e.target.checked}))}
                          className="w-4 h-4"
                        />
                        <Label htmlFor="new-service-allow-discount" className="cursor-pointer">
                          💸 Leyfa afslátt á þessa þjónustu
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="new-service-display-in-pricelist"
                          checked={newService.display_in_pricelist !== false}
                          onChange={(e) => setNewService(prev => ({...prev, display_in_pricelist: e.target.checked}))}
                          className="w-4 h-4"
                        />
                        <Label htmlFor="new-service-display-in-pricelist" className="cursor-pointer">
                          📋 Birta í verðskrá (sýnist í verðskrá og þjónustuvali)
                        </Label>
                      </div>
                      {newService.allow_discount !== false && (
                        <div>
                          <Label htmlFor="new-service-max-discount">Hámarks afsláttur % (valfrjálst)</Label>
                          <Input
                            id="new-service-max-discount"
                            type="number"
                            min="0"
                            max="100"
                            value={newService.max_discount_percent || ''}
                            onChange={(e) => setNewService(prev => ({
                              ...prev, 
                              max_discount_percent: e.target.value ? parseFloat(e.target.value) : null
                            }))}
                            placeholder="t.d. 20 fyrir 20% hámark"
                            className="w-full"
                          />
                          <div className="text-xs text-slate-500 mt-1">
                            Ef autt, þá er enginn hámarksafsláttur
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <Button onClick={addService} disabled={saving} className="bg-green-600 hover:bg-green-700">
                        {saving ? 'Bætir við...' : '✅ Bæta við'}
                      </Button>
                      <Button onClick={() => setShowAddService(false)} variant="outline">
                        Hætta við
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Existing Services - Grouped by Category */}
              <div className="space-y-6">
                {settings.categories?.sort((a, b) => {
                  const orderA = a.order !== undefined && a.order !== null ? a.order : 999;
                  const orderB = b.order !== undefined && b.order !== null ? b.order : 999;
                  return orderA - orderB;
                }).map((category, categoryIndex) => {
                  const categoryServices = settings.services?.filter(service => {
                    // Match by category or legacy IDs for backwards compatibility
                    if (category.id === 'general') {
                      return (service.category === 'general' || !service.category || ['althrif', 'uthrif', 'innhrif'].includes(service.id));
                    }
                    return service.category === category.id;
                  })?.sort((a, b) => {
                    const orderA = a.order !== undefined && a.order !== null ? a.order : 999;
                    const orderB = b.order !== undefined && b.order !== null ? b.order : 999;
                    return orderA - orderB;
                  }) || [];

                  if (categoryServices.length === 0) return null;
                  
                  const sortedCategories = settings.categories?.sort((a, b) => {
                    const orderA = a.order !== undefined && a.order !== null ? a.order : 999;
                    const orderB = b.order !== undefined && b.order !== null ? b.order : 999;
                    return orderA - orderB;
                  }) || [];

                  return (
                    <div key={category.id} className="border rounded-lg overflow-hidden">
                      {/* Category Header */}
                      <div className="px-4 py-3 bg-slate-50 border-b">
                        <div className="flex items-center gap-2">
                          {/* Category Up/Down Buttons */}
                          <div className="flex flex-col gap-0">
                            <button
                              onClick={async () => {
                                if (categoryIndex === 0) return;
                                const allCategories = [...sortedCategories];
                                const prevCategory = allCategories[categoryIndex - 1];
                                
                                // Swap order values
                                const tempOrder = category.order;
                                const updatedCategories = allCategories.map(cat => {
                                  if (cat.id === category.id) return { ...cat, order: prevCategory.order };
                                  if (cat.id === prevCategory.id) return { ...cat, order: tempOrder };
                                  return cat;
                                });
                                
                                // Update backend
                                await updateCategoryOrder(updatedCategories);
                              }}
                              disabled={categoryIndex === 0}
                              className={`text-xs px-1 ${categoryIndex === 0 ? 'text-slate-300' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200 rounded'}`}
                              title="Færa flokk upp"
                            >
                              ▲
                            </button>
                            <button
                              onClick={async () => {
                                if (categoryIndex === sortedCategories.length - 1) return;
                                const allCategories = [...sortedCategories];
                                const nextCategory = allCategories[categoryIndex + 1];
                                
                                // Swap order values
                                const tempOrder = category.order;
                                const updatedCategories = allCategories.map(cat => {
                                  if (cat.id === category.id) return { ...cat, order: nextCategory.order };
                                  if (cat.id === nextCategory.id) return { ...cat, order: tempOrder };
                                  return cat;
                                });
                                
                                // Update backend
                                await updateCategoryOrder(updatedCategories);
                              }}
                              disabled={categoryIndex === sortedCategories.length - 1}
                              className={`text-xs px-1 ${categoryIndex === sortedCategories.length - 1 ? 'text-slate-300' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200 rounded'}`}
                              title="Færa flokk niður"
                            >
                              ▼
                            </button>
                          </div>
                          <span className="text-xl">{category.icon}</span>
                          <span className="font-semibold text-slate-900">{category.name}</span>
                          <span className="text-sm text-slate-500">({categoryServices.length} þjónustur)</span>
                        </div>
                      </div>
                      
                      {/* Services in this category */}
                      <div className="space-y-0">
                        {categoryServices.map((service, index) => (
                  <div key={service.id} className="p-3 border rounded-lg hover:bg-slate-50 flex items-start gap-2">
                    {/* Drag Handle */}
                    <div className="flex flex-col gap-1 cursor-move pt-1">
                      <button
                        onClick={async () => {
                          if (index === 0) return;
                          // Reorder within the category - swap with previous
                          const allServices = [...(settings.services || [])];
                          const serviceIndex = allServices.findIndex(s => s.id === service.id);
                          const prevServiceInCategory = categoryServices[index - 1];
                          const prevServiceIndex = allServices.findIndex(s => s.id === prevServiceInCategory.id);
                          
                          // Swap order values instead of array positions
                          const tempOrder = allServices[serviceIndex].order;
                          allServices[serviceIndex].order = allServices[prevServiceIndex].order;
                          allServices[prevServiceIndex].order = tempOrder;
                          
                          // Sort by order before sending to backend
                          const sortedServices = allServices.sort((a, b) => {
                            const orderA = a.order !== undefined && a.order !== null ? a.order : 999;
                            const orderB = b.order !== undefined && b.order !== null ? b.order : 999;
                            return orderA - orderB;
                          });
                          
                          await reorderServices(sortedServices.map(s => s.id));
                        }}
                        disabled={index === 0}
                        className={`text-xs ${index === 0 ? 'text-slate-300' : 'text-slate-600 hover:text-slate-900'}`}
                      >
                        ▲
                      </button>
                      <button
                        onClick={async () => {
                          if (index === categoryServices.length - 1) return;
                          // Reorder within the category - swap with next
                          const allServices = [...(settings.services || [])];
                          const serviceIndex = allServices.findIndex(s => s.id === service.id);
                          const nextServiceInCategory = categoryServices[index + 1];
                          const nextServiceIndex = allServices.findIndex(s => s.id === nextServiceInCategory.id);
                          
                          // Swap order values instead of array positions
                          const tempOrder = allServices[serviceIndex].order;
                          allServices[serviceIndex].order = allServices[nextServiceIndex].order;
                          allServices[nextServiceIndex].order = tempOrder;
                          
                          // Sort by order before sending to backend
                          const sortedServices = allServices.sort((a, b) => {
                            const orderA = a.order !== undefined && a.order !== null ? a.order : 999;
                            const orderB = b.order !== undefined && b.order !== null ? b.order : 999;
                            return orderA - orderB;
                          });
                          
                          await reorderServices(sortedServices.map(s => s.id));
                        }}
                        disabled={index === categoryServices.length - 1}
                        className={`text-xs ${index === categoryServices.length - 1 ? 'text-slate-300' : 'text-slate-600 hover:text-slate-900'}`}
                      >
                        ▼
                      </button>
                    </div>
                    <div className="flex-1">
                      {editingService === service.id ? (
                      <div className="space-y-2">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <Label>Nafn þjónustu</Label>
                            <Input
                              defaultValue={service.name}
                              onChange={(e) => {
                                service.tempName = e.target.value;
                              }}
                              placeholder="t.d. Alþrif"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <Label>📝 Lýsing fyrir viðskiptavini</Label>
                            <textarea
                              defaultValue={service.description}
                              onChange={(e) => {
                                service.tempDescription = e.target.value;
                              }}
                              placeholder="t.d. Alþrif felur í sér ytra og innra þrif á öllu bílnum, ryksuga, þrif á gluggum"
                              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px]"
                              rows="3"
                            />
                            <div className="text-xs text-slate-500 mt-1">
                              💡 Birtist viðskiptavinum í "Ný skráning" undir verði
                            </div>
                          </div>
                          <div className="md:col-span-2">
                            <Label>👨‍🔧 Lýsing fyrir starfsfólk</Label>
                            <textarea
                              defaultValue={service.staff_description || ''}
                              onChange={(e) => {
                                service.tempStaffDescription = e.target.value;
                              }}
                              placeholder="t.d. Munum að athuga öll horn, mælaborð þarf að vera ósprautað, gúmmlögn á hjólbarða"
                              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px]"
                              rows="3"
                            />
                            <div className="text-xs text-slate-500 mt-1">
                              💡 Birtist í "Verk" listanum þegar smellt er á þjónustunafn
                            </div>
                          </div>
                          <div>
                            <Label>Vörunúmer</Label>
                            <Input
                              defaultValue={service.product_code || ''}
                              onChange={(e) => {
                                service.tempProductCode = e.target.value;
                              }}
                              placeholder="T.d. 'VNR-001'"
                            />
                          </div>
                          <div>
                            <Label>Flokkur</Label>
                            <select
                              defaultValue={service.category || 'general'}
                              onChange={(e) => {
                                service.tempCategory = e.target.value;
                              }}
                              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              {settings.categories?.sort((a, b) => (a.order || 0) - (b.order || 0)).map(cat => (
                                <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`service-available-${service.id}`}
                              defaultChecked={service.available !== false}
                              onChange={(e) => {
                                service.tempAvailable = e.target.checked;
                              }}
                              className="w-4 h-4"
                            />
                            <Label htmlFor={`service-available-${service.id}`} className="cursor-pointer">
                              ✅ Þjónusta í boði
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`service-price-editable-${service.id}`}
                              defaultChecked={service.price_editable === true}
                              onChange={(e) => {
                                service.tempPriceEditable = e.target.checked;
                              }}
                              className="w-4 h-4"
                            />
                            <Label htmlFor={`service-price-editable-${service.id}`} className="cursor-pointer">
                              💰 Verð breytanlegt
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`service-allow-discount-${service.id}`}
                              defaultChecked={service.allow_discount !== false}
                              onChange={(e) => {
                                service.tempAllowDiscount = e.target.checked;
                              }}
                              className="w-4 h-4"
                            />
                            <Label htmlFor={`service-allow-discount-${service.id}`} className="cursor-pointer">
                              💸 Leyfa afslátt
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`service-display-in-pricelist-${service.id}`}
                              defaultChecked={service.display_in_pricelist !== false}
                              onChange={(e) => {
                                service.tempDisplayInPricelist = e.target.checked;
                              }}
                              className="w-4 h-4"
                            />
                            <Label htmlFor={`service-display-in-pricelist-${service.id}`} className="cursor-pointer">
                              📋 Birta í verðskrá
                            </Label>
                          </div>
                          {(service.tempAllowDiscount !== false && service.allow_discount !== false) && (
                            <div>
                              <Label htmlFor={`service-max-discount-${service.id}`}>Hámarks afsláttur %</Label>
                              <Input
                                id={`service-max-discount-${service.id}`}
                                type="number"
                                min="0"
                                max="100"
                                defaultValue={service.max_discount_percent || ''}
                                onChange={(e) => {
                                  service.tempMaxDiscountPercent = e.target.value ? parseFloat(e.target.value) : null;
                                }}
                                placeholder="t.d. 20"
                                className="w-full"
                              />
                            </div>
                          )}
                        </div>
                        
                        {/* Included Services Section */}
                        <div className="border-t pt-4">
                          <Label className="flex items-center gap-2 mb-2">
                            <span className="text-base font-semibold">🎁 Innfaldar þjónustur</span>
                          </Label>
                          <div className="text-sm text-slate-600 mb-3">
                            💡 Ef þessi þjónusta er valin, verða eftirfarandi þjónustur innifaldar (ókeypis)
                          </div>
                          <div className="max-h-48 overflow-y-auto border rounded-md p-3 bg-slate-50 space-y-2">
                            {settings?.services?.filter(s => s.id !== service.id).map(otherService => (
                              <div key={otherService.id} className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  id={`include-${service.id}-${otherService.id}`}
                                  defaultChecked={(service.includes || []).includes(otherService.id)}
                                  onChange={(e) => {
                                    if (!service.tempIncludes) {
                                      service.tempIncludes = [...(service.includes || [])];
                                    }
                                    if (e.target.checked) {
                                      if (!service.tempIncludes.includes(otherService.id)) {
                                        service.tempIncludes.push(otherService.id);
                                      }
                                    } else {
                                      service.tempIncludes = service.tempIncludes.filter(id => id !== otherService.id);
                                    }
                                  }}
                                  className="w-4 h-4"
                                />
                                <Label 
                                  htmlFor={`include-${service.id}-${otherService.id}`} 
                                  className="cursor-pointer flex-1 text-sm"
                                >
                                  {otherService.name}
                                </Label>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Button 
                            size="sm" 
                            onClick={() => updateService(service.id, {
                              name: service.tempName || service.name,
                              description: service.tempDescription || service.description,
                              staff_description: service.tempStaffDescription !== undefined ? service.tempStaffDescription : (service.staff_description || ''),
                              product_code: service.tempProductCode !== undefined ? service.tempProductCode : (service.product_code || ''),
                              category: service.tempCategory || service.category || 'general',
                              available: service.tempAvailable !== undefined ? service.tempAvailable : (service.available !== false),
                              price_editable: service.tempPriceEditable !== undefined ? service.tempPriceEditable : (service.price_editable === true),
                              allow_discount: service.tempAllowDiscount !== undefined ? service.tempAllowDiscount : (service.allow_discount !== false),
                              max_discount_percent: service.tempMaxDiscountPercent !== undefined ? service.tempMaxDiscountPercent : service.max_discount_percent,
                              display_in_pricelist: service.tempDisplayInPricelist !== undefined ? service.tempDisplayInPricelist : (service.display_in_pricelist !== false),
                              includes: service.tempIncludes !== undefined ? service.tempIncludes : (service.includes || [])
                            })}
                            disabled={saving}
                          >
                            {saving ? 'Vistar...' : '✅ Vista'}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingService(null)}>
                            Hætta við
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-medium">{service.name}</div>
                            {(() => {
                              const cat = settings.categories?.find(c => c.id === service.category);
                              return cat ? (
                                <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                                  {cat.icon} {cat.name}
                                </span>
                              ) : (
                                <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                                  🧼 Almenn þrif
                                </span>
                              );
                            })()}
                            {service.available === false ? (
                              <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full">
                                ❌ Ekki í boði
                              </span>
                            ) : (
                              <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                                ✅ Í boði
                              </span>
                            )}
                            {service.price_editable && (
                              <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                                💰 Verð breytanlegt
                              </span>
                            )}
                            {service.allow_discount === false ? (
                              <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full">
                                🚫 Afsláttur óheimill
                              </span>
                            ) : service.max_discount_percent ? (
                              <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full">
                                💸 Hámark {service.max_discount_percent}% afsláttur
                              </span>
                            ) : (
                              <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                                💸 Afsláttur leyfilegur
                              </span>
                            )}
                            {service.includes && service.includes.length > 0 && (
                              <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                                🎁 Inniheldur {service.includes.length} þjónustur
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-slate-600">ID: {service.id}</div>
                          {service.description && (
                            <div className="text-sm text-slate-500">{service.description}</div>
                          )}
                          {service.includes && service.includes.length > 0 && (
                            <div className="text-xs text-slate-500 mt-1">
                              🎁 Innfaldar þjónustur: {service.includes.map(incId => {
                                const incService = settings?.services?.find(s => s.id === incId);
                                return incService ? incService.name : incId;
                              }).join(', ')}
                            </div>
                          )}
                        </div>
                        <div className="flex space-x-2">
                          <Button size="sm" variant="outline" onClick={() => setEditingService(service.id)}>
                            ✏️ Breyta
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-red-600 border-red-600 hover:bg-red-50"
                            onClick={() => deleteService(service.id)}
                          >
                            🗑️ Eyða
                          </Button>
                        </div>
                      </div>
                    )}
                    </div>
                  </div>
                ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

            {/* Categories Sub-tab */}
            <TabsContent value="categories" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Þjónustuflokkar</CardTitle>
                  <p className="text-sm text-slate-600">
                    Búðu til og stjórnaðu flokkum fyrir þjónustur. Flokkar hjálpa til við að skipuleggja þjónustur í "Nýtt verk".
                  </p>
                </div>
                <Button onClick={() => setShowAddCategory(true)} className="bg-green-600 hover:bg-green-700">
                  ➕ Bæta við flokki
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Add Category Dialog */}
              {showAddCategory && (
                <div className="mb-4 p-4 border rounded-lg bg-blue-50">
                  <h3 className="font-medium mb-3">Nýr flokkur</h3>
                  <div className="space-y-2">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <Label>ID (einstakt, enginn bil)</Label>
                        <Input
                          value={newCategory.id}
                          onChange={(e) => setNewCategory(prev => ({...prev, id: e.target.value.toLowerCase().replace(/\s+/g, '_')}))}
                          placeholder="t.d. 'urvals_thrif'"
                        />
                      </div>
                      <div>
                        <Label>Nafn flokks</Label>
                        <Input
                          value={newCategory.name}
                          onChange={(e) => setNewCategory(prev => ({...prev, name: e.target.value}))}
                          placeholder="t.d. 'Úrvals þrif'"
                        />
                      </div>
                      <div>
                        <Label>Emoji tákn</Label>
                        <Input
                          value={newCategory.icon}
                          onChange={(e) => setNewCategory(prev => ({...prev, icon: e.target.value}))}
                          placeholder="t.d. '💎'"
                        />
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button size="sm" onClick={addCategory} disabled={saving}>
                        {saving ? 'Vistar...' : '✅ Vista'}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => {
                        setShowAddCategory(false);
                        setNewCategory({ id: '', name: '', icon: '📦' });
                      }}>
                        Hætta við
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Existing Categories */}
              <div className="space-y-2">
                {settings.categories?.sort((a, b) => (a.order || 0) - (b.order || 0)).map((category) => (
                  <div key={category.id} className="p-3 border rounded-lg hover:bg-slate-50">
                    {editingCategory === category.id ? (
                      <div className="space-y-2">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <Label>Nafn flokks</Label>
                            <Input
                              defaultValue={category.name}
                              onChange={(e) => {
                                category.tempName = e.target.value;
                              }}
                              placeholder="t.d. 'Úrvals þrif'"
                            />
                          </div>
                          <div>
                            <Label>Emoji tákn</Label>
                            <Input
                              defaultValue={category.icon}
                              onChange={(e) => {
                                category.tempIcon = e.target.value;
                              }}
                              placeholder="t.d. '💎'"
                            />
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Button 
                            size="sm" 
                            onClick={() => updateCategory(category.id, {
                              name: category.tempName || category.name,
                              icon: category.tempIcon || category.icon
                            })}
                            disabled={saving}
                          >
                            {saving ? 'Vistar...' : '✅ Vista'}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingCategory(null)}>
                            Hætta við
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="text-2xl">{category.icon}</div>
                          <div>
                            <div className="font-medium">{category.name}</div>
                            <div className="text-sm text-slate-600">ID: {category.id}</div>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Button size="sm" variant="outline" onClick={() => setEditingCategory(category.id)}>
                            ✏️ Breyta
                          </Button>
                          {category.id !== 'general' && category.id !== 'special' && (
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="text-red-600 border-red-600 hover:bg-red-50"
                              onClick={() => deleteCategory(category.id)}
                            >
                              🗑️ Eyða
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Dealerships Tab */}
        <TabsContent value="dealerships" className="space-y-4">
          {/* Statistics Card */}
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                📊 Vinsælustu Bílasölurnar
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Top 5 Most Used */}
                <div>
                  <h3 className="font-semibold mb-3 text-slate-700 dark:text-slate-300">🔥 Top 5 Mest Notaðar</h3>
                  <div className="space-y-2">
                    {dealerships
                      .filter(d => d.is_active)
                      .sort((a, b) => (b.usage_count || 0) - (a.usage_count || 0))
                      .slice(0, 5)
                      .map((d, index) => {
                        const maxUsage = Math.max(...dealerships.map(x => x.usage_count || 0));
                        const percentage = maxUsage > 0 ? ((d.usage_count || 0) / maxUsage) * 100 : 0;
                        return (
                          <div key={d.id} className="space-y-1">
                            <div className="flex justify-between text-sm">
                              <span className="font-medium">
                                {index === 0 && '🥇 '}
                                {index === 1 && '🥈 '}
                                {index === 2 && '🥉 '}
                                {index > 2 && `${index + 1}. `}
                                {d.name}
                              </span>
                              <span className="text-blue-600 dark:text-blue-400 font-bold">{d.usage_count || 0}x</span>
                            </div>
                            <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                              <div 
                                className="bg-gradient-to-r from-blue-500 to-indigo-500 h-2 rounded-full transition-all duration-500"
                                style={{width: `${percentage}%`}}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                    {dealerships.filter(d => d.is_active && (d.usage_count || 0) > 0).length === 0 && (
                      <p className="text-sm text-slate-500 italic">Engar bílasölur notaðar ennþá</p>
                    )}
                  </div>
                </div>

                {/* Statistics Summary */}
                <div>
                  <h3 className="font-semibold mb-3 text-slate-700 dark:text-slate-300">📈 Tölfræði</h3>
                  <div className="space-y-2">
                    <div className="p-3 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700">
                      <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                        {dealerships.filter(d => d.is_active).length}
                      </div>
                      <div className="text-sm text-slate-600 dark:text-slate-400">Heildarfjöldi bílasala</div>
                    </div>
                    <div className="p-3 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700">
                      <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                        {dealerships.filter(d => d.is_active && (d.usage_count || 0) > 0).length}
                      </div>
                      <div className="text-sm text-slate-600 dark:text-slate-400">Bílasölur í notkun</div>
                    </div>
                    <div className="p-3 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700">
                      <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                        {dealerships.reduce((sum, d) => sum + (d.usage_count || 0), 0)}
                      </div>
                      <div className="text-sm text-slate-600 dark:text-slate-400">Heildarnotkun</div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Management Card */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>🏢 Allar Bílasölur</CardTitle>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Raðað eftir vinsældum - mest notuðu efst
                  </p>
                </div>
                <Button onClick={() => setShowAddDealership(true)} className="bg-green-600 hover:bg-green-700">
                  ➕ Bæta við bílasölu
                </Button>
              </div>
              {/* Search bar */}
              <div className="mt-4">
                <Input
                  type="text"
                  placeholder="🔍 Leita að bílasölu..."
                  value={dealershipSearchTerm}
                  onChange={(e) => setDealershipSearchTerm(e.target.value)}
                  className="max-w-md"
                />
              </div>
            </CardHeader>
            <CardContent>
              {/* Add Dealership Dialog */}
              {showAddDealership && (
                <div className="mb-4 p-4 border rounded-lg bg-blue-50 dark:bg-blue-950">
                  <h3 className="font-medium mb-3">Ný bílasala</h3>
                  <div className="space-y-3">
                    {/* Kennitala lookup row */}
                    <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border">
                      <Label className="text-sm font-medium mb-2 block">🔍 Fletta upp fyrirtæki í þjóðskrá</Label>
                      <div className="flex gap-2">
                        <Input
                          value={newDealership.kennitala}
                          onChange={(e) => setNewDealership(prev => ({...prev, kennitala: e.target.value.replace(/\D/g, '').slice(0, 10)}))}
                          placeholder="Kennitala (10 stafir)"
                          className="flex-1"
                          maxLength={10}
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={!newDealership.kennitala || newDealership.kennitala.length !== 10 || saving}
                          onClick={async () => {
                            try {
                              setSaving(true);
                              const res = await axios.get(`${API}/person/lookup/${newDealership.kennitala}`);
                              if (res.data && res.data.found && (res.data.full_name || res.data.company_name)) {
                                const companyName = res.data.company_name || res.data.full_name;
                                setNewDealership(prev => ({
                                  ...prev,
                                  company_name: companyName,
                                  name: prev.name || companyName
                                }));
                                toast.success(`✅ Fyrirtæki fundið: ${companyName}`);
                              } else {
                                toast.error('Engin gögn fundust fyrir þessa kennitölu');
                              }
                            } catch (error) {
                              toast.error('Villa við að fletta upp kennitölu');
                            } finally {
                              setSaving(false);
                            }
                          }}
                        >
                          {saving ? '...' : '🔍 Fletta'}
                        </Button>
                      </div>
                      {newDealership.company_name && (
                        <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/30 rounded text-sm">
                          <span className="text-green-700 dark:text-green-300">✅ Fyrirtækjanafn: <strong>{newDealership.company_name}</strong></span>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label>Auðkenni/Vöruheiti bílasölu *</Label>
                        <Input
                          value={newDealership.name}
                          onChange={(e) => setNewDealership(prev => ({...prev, name: e.target.value}))}
                          placeholder="t.d. 'Bílaland', 'Bílabankinn'"
                        />
                      </div>
                      <div>
                        <Label>Fyrirtækjanafn (frá þjóðskrá)</Label>
                        <Input
                          value={newDealership.company_name}
                          onChange={(e) => setNewDealership(prev => ({...prev, company_name: e.target.value}))}
                          placeholder="t.d. 'BL ehf.', 'Mare ehf.'"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label>Netfang</Label>
                        <Input
                          value={newDealership.email}
                          onChange={(e) => setNewDealership(prev => ({...prev, email: e.target.value}))}
                          placeholder="t.d. 'birgi@brimborg.is'"
                          type="email"
                        />
                      </div>
                      <div>
                        <Label>Símanúmer</Label>
                        <Input
                          value={newDealership.phone}
                          onChange={(e) => setNewDealership(prev => ({...prev, phone: e.target.value}))}
                          placeholder="t.d. '5151000'"
                        />
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="send_sms"
                        checked={newDealership.send_sms}
                        onCheckedChange={(checked) => setNewDealership(prev => ({...prev, send_sms: checked}))}
                      />
                      <Label htmlFor="send_sms" className="text-sm cursor-pointer">
                        📱 Senda SMS tilkynningar
                      </Label>
                    </div>
                    <div className="flex space-x-2">
                      <Button 
                        size="sm" 
                        onClick={async () => {
                          if (!newDealership.name) {
                            toast.error('Nafn bílasölu vantar');
                            return;
                          }
                          try {
                            setSaving(true);
                            await axios.post(`${API}/dealerships`, newDealership);
                            toast.success('Bílasala bætt við');
                            setShowAddDealership(false);
                            setNewDealership({ name: '', company_name: '', kennitala: '', email: '', phone: '', send_sms: false });
                            await loadSettings();
                          } catch (error) {
                            toast.error('Villa við að bæta við bílasölu');
                          } finally {
                            setSaving(false);
                          }
                        }} 
                        disabled={saving}
                      >
                        {saving ? 'Vistar...' : '✅ Vista'}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => {
                        setShowAddDealership(false);
                        setNewDealership({ name: '', company_name: '', kennitala: '', email: '', phone: '', send_sms: false });
                      }}>
                        Hætta við
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Existing Dealerships */}
              <div className="space-y-2">
                {dealerships
                  .filter(d => d.is_active)
                  .filter(d => {
                    if (!dealershipSearchTerm) return true;
                    const search = dealershipSearchTerm.toLowerCase();
                    return (
                      (d.name || '').toLowerCase().includes(search) ||
                      (d.company_name || '').toLowerCase().includes(search) ||
                      (d.kennitala || '').includes(search) ||
                      (d.email || '').toLowerCase().includes(search)
                    );
                  })
                  .sort((a, b) => (b.usage_count || 0) - (a.usage_count || 0))
                  .map((dealership) => (
                  <div key={dealership.id} className="p-3 border rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800">
                    {editingDealership === dealership.id ? (
                      <div className="space-y-2">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div>
                            <Label>Nafn bílasölu</Label>
                            <Input
                              defaultValue={dealership.name}
                              onChange={(e) => {
                                dealership.tempName = e.target.value;
                              }}
                            />
                          </div>
                          <div>
                            <Label>Netfang</Label>
                            <Input
                              defaultValue={dealership.email}
                              onChange={(e) => {
                                dealership.tempEmail = e.target.value;
                              }}
                              type="email"
                            />
                          </div>
                          <div>
                            <Label>Símanúmer</Label>
                            <Input
                              defaultValue={dealership.phone}
                              onChange={(e) => {
                                dealership.tempPhone = e.target.value;
                              }}
                            />
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id={`send_sms_${dealership.id}`}
                            defaultChecked={dealership.send_sms}
                            onCheckedChange={(checked) => {
                              dealership.tempSendSms = checked;
                            }}
                          />
                          <Label htmlFor={`send_sms_${dealership.id}`} className="text-sm cursor-pointer">
                            📱 Senda SMS tilkynningar
                          </Label>
                        </div>
                        <div className="flex space-x-2">
                          <Button 
                            size="sm" 
                            onClick={async () => {
                              try {
                                setSaving(true);
                                await axios.put(`${API}/dealerships/${dealership.id}`, {
                                  name: dealership.tempName || dealership.name,
                                  email: dealership.tempEmail || dealership.email,
                                  phone: dealership.tempPhone || dealership.phone,
                                  send_sms: dealership.tempSendSms !== undefined ? dealership.tempSendSms : dealership.send_sms,
                                  is_active: true
                                });
                                toast.success('Bílasala uppfærð');
                                setEditingDealership(null);
                                await loadSettings();
                              } catch (error) {
                                toast.error('Villa við að uppfæra bílasölu');
                              } finally {
                                setSaving(false);
                              }
                            }}
                            disabled={saving}
                          >
                            {saving ? 'Vistar...' : '✅ Vista'}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingDealership(null)}>
                            Hætta við
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <div className="font-medium text-lg">🏢 {dealership.name}</div>
                            {dealership.send_sms && (
                              <span className="text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-2 py-1 rounded-full">
                                📱 SMS
                              </span>
                            )}
                            {dealership.usage_count > 0 ? (
                              <div className="flex items-center gap-2">
                                <span className={`text-sm font-bold px-3 py-1 rounded-full ${
                                  dealership.usage_count > 50 ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200' :
                                  dealership.usage_count > 20 ? 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200' :
                                  dealership.usage_count > 5 ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
                                  'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                                }`}>
                                  🔥 {dealership.usage_count}x
                                </span>
                                {dealership.usage_count > 50 && <span className="text-xl">🌟</span>}
                                {dealership.usage_count > 100 && <span className="text-xl">⭐</span>}
                              </div>
                            ) : (
                              <span className="text-xs bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 px-2 py-1 rounded-full">
                                Ónotað
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                            {dealership.email && <span>📧 {dealership.email}</span>}
                            {dealership.email && dealership.phone && <span className="mx-2">•</span>}
                            {dealership.phone && <span>📞 {dealership.phone}</span>}
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Button size="sm" variant="outline" onClick={() => setEditingDealership(dealership.id)}>
                            ✏️ Breyta
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-red-600 border-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                            onClick={() => {
                              setDealershipToDelete(dealership);
                              setShowDeleteDialog(true);
                            }}
                          >
                            🗑️ Eyða
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                
                {dealerships.filter(d => d.is_active).length === 0 && (
                  <div className="text-center py-8 text-slate-600 dark:text-slate-400">
                    Engar bílasölur í kerfinu. Smelltu á "Bæta við bílasölu" til að byrja.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Delete Dealership Confirmation Dialog */}
          <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>⚠️ Eyða bílasölu</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Ertu viss um að þú viljir eyða <strong>{dealershipToDelete?.name}</strong>?
                </p>
                <p className="text-sm text-orange-600 dark:text-orange-400 font-medium">
                  ⚠️ Athugaðu: Ekki er hægt að eyða bílasölu ef hún hefur virk verk (pending eða in_progress).
                </p>
                <div>
                  <Label htmlFor="delete_password">Admin lykilorð *</Label>
                  <Input
                    id="delete_password"
                    type="password"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    placeholder="Sláðu inn admin lykilorð"
                  />
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setShowDeleteDialog(false);
                      setDealershipToDelete(null);
                      setDeletePassword('');
                    }}
                  >
                    Hætta við
                  </Button>
                  <Button
                    className="flex-1 bg-red-600 hover:bg-red-700"
                    disabled={!deletePassword || saving}
                    onClick={async () => {
                      try {
                        setSaving(true);
                        await axios.delete(`${API}/dealerships/${dealershipToDelete.id}?password=${encodeURIComponent(deletePassword)}`);
                        toast.success('Bílasölu eytt');
                        setShowDeleteDialog(false);
                        setDealershipToDelete(null);
                        setDeletePassword('');
                        await loadSettings();
                      } catch (error) {
                        const errorMsg = error.response?.data?.detail || 'Villa við að eyða bílasölu';
                        toast.error(errorMsg);
                      } finally {
                        setSaving(false);
                      }
                    }}
                  >
                    {saving ? 'Eyði...' : '🗑️ Eyða'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

            {/* Sizes Sub-tab */}
            <TabsContent value="sizes" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Bílastærðir</CardTitle>
                  <p className="text-sm text-slate-600">
                    Stjórna bílastærðum fyrir verðlagningu. Bæta við nýjum og breyta núverandi.
                  </p>
                </div>
                <Button onClick={() => setShowAddSize(true)} className="bg-green-600 hover:bg-green-700">
                  ➕ Bæta við stærð
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Add Size Form */}
              {showAddSize && (
                <Card className="mb-4 border-green-200">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                      <div>
                        <Label>ID stærðar *</Label>
                        <Input
                          value={newSize.id}
                          onChange={(e) => setNewSize(prev => ({...prev, id: e.target.value}))}
                          placeholder="t.d. xl_jeppi"
                        />
                      </div>
                      <div>
                        <Label>Nafn stærðar *</Label>
                        <Input
                          value={newSize.name}
                          onChange={(e) => setNewSize(prev => ({...prev, name: e.target.value}))}
                          placeholder="t.d. Mjög stór jeppi"
                        />
                      </div>
                      <div>
                        <Label>Röð *</Label>
                        <Input
                          type="number"
                          min="0"
                          value={newSize.order !== undefined ? newSize.order : settings.sizes?.length || 0}
                          onChange={(e) => setNewSize(prev => ({...prev, order: parseInt(e.target.value) || 0}))}
                          placeholder="0"
                        />
                        <div className="text-xs text-slate-500 mt-1">
                          💡 Lægri tölur birtast fyrst
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button onClick={addSize} disabled={saving} className="bg-green-600 hover:bg-green-700">
                        {saving ? 'Bætir við...' : '✅ Bæta við'}
                      </Button>
                      <Button onClick={() => setShowAddSize(false)} variant="outline">
                        Hætta við
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Existing Sizes */}
              <div className="space-y-2">
                {settings.sizes?.sort((a, b) => (a.order || 0) - (b.order || 0)).map(size => (
                  <div key={size.id} className="p-3 border rounded-lg hover:bg-slate-50">
                    {editingSize === size.id ? (
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label>Nafn bílastærðar</Label>
                            <Input
                              defaultValue={size.name}
                              onChange={(e) => {
                                size.tempName = e.target.value;
                              }}
                              placeholder="t.d. Smábíll"
                            />
                          </div>
                          <div>
                            <Label>Röð</Label>
                            <Input
                              type="number"
                              min="0"
                              defaultValue={size.order !== undefined ? size.order : 0}
                              onChange={(e) => {
                                size.tempOrder = parseInt(e.target.value) || 0;
                              }}
                              placeholder="0"
                            />
                            <div className="text-xs text-slate-500 mt-1">
                              💡 Lægri tölur birtast fyrst
                            </div>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Button 
                            size="sm" 
                            onClick={() => updateSize(size.id, {
                              name: size.tempName || size.name,
                              order: size.tempOrder !== undefined ? size.tempOrder : (size.order !== undefined ? size.order : 0)
                            })}
                            disabled={saving}
                          >
                            {saving ? 'Vistar...' : '✅ Vista'}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingSize(null)}>
                            Hætta við
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{size.name}</div>
                          <div className="text-sm text-slate-600">ID: {size.id}</div>
                          {size.order !== undefined && (
                            <div className="text-xs text-slate-500">Röð: {size.order}</div>
                          )}
                        </div>
                        <div className="flex space-x-2">
                          <Button size="sm" variant="outline" onClick={() => setEditingSize(size.id)}>
                            ✏️ Breyta
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-red-600 border-red-600 hover:bg-red-50"
                            onClick={() => deleteSize(size.id)}
                          >
                            🗑️ Eyða
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Products (Vörur) Sub-tab */}
        <TabsContent value="products" className="space-y-4">
          <ProductServicesManager />
        </TabsContent>

        {/* Pickup/Delivery Zones Sub-tab */}
        <TabsContent value="pickup-zones" className="space-y-4">
          <PickupZonesManager />
        </TabsContent>

        {/* Import/Export Tab */}
        <TabsContent value="import-export" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>📥 Import/Export Stillingar</CardTitle>
              <p className="text-sm text-slate-600">
                Sækja eða hlaða upp öllum stillingum (þjónustur, verð, vörunúmer)
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Export Section */}
              <div className="space-y-3">
                <h3 className="font-medium">📤 Sækja Stillingar (Export)</h3>
                <p className="text-sm text-slate-600">
                  Sækja JSON backup af öllum stillingum til að vista eða færa á annan server
                </p>
                <Button
                  onClick={() => {
                    // Export current settings as JSON
                    const backup = {
                      id: "settings",
                      version: settings.version || 5,
                      services: settings.services || [],
                      sizes: settings.sizes || [],
                      prices: settings.prices || {},
                      categories: settings.categories || []
                    };
                    
                    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `verdskra_backup_${new Date().toISOString().split('T')[0]}.json`;
                    a.click();
                    URL.revokeObjectURL(url);
                    
                    toast.success('✅ Backup sótt!');
                  }}
                  className="w-full sm:w-auto"
                >
                  📥 Sækja Backup (JSON)
                </Button>
              </div>

              <div className="border-t pt-4"></div>

              {/* Import Section */}
              <div className="space-y-3">
                <h3 className="font-medium">📤 Hlaða upp Stillingum (Import)</h3>
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm">
                  <p className="font-medium text-yellow-900">⚠️ Varúð:</p>
                  <p className="text-yellow-800">
                    Import mun SKIPTA ÚT öllum núverandi stillingum. Gakktu úr skugga um að þú sért með backup!
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="import-file">Veldu JSON backup skrá:</Label>
                  <Input
                    id="import-file"
                    type="file"
                    accept=".json"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      
                      try {
                        const text = await file.text();
                        const backup = JSON.parse(text);
                        
                        // Validate backup
                        if (backup.id !== 'settings' || !backup.services || !backup.prices) {
                          toast.error('❌ Ógild backup skrá');
                          return;
                        }
                        
                        // Confirm with user
                        if (!window.confirm(
                          `Ertu viss um að þú viljir import-a þessa backup?\n\n` +
                          `Þjónustur: ${backup.services.length}\n` +
                          `Verð: ${Object.keys(backup.prices).length}\n` +
                          `Bílastærðir: ${backup.sizes?.length || 0}\n\n` +
                          `Þetta mun SKIPTA ÚT öllum núverandi stillingum!`
                        )) {
                          return;
                        }
                        
                        toast.info('⏳ Import-ar...', { duration: 2000 });
                        
                        // Import to backend
                        await axios.post(`${API}/settings/import-full-backup`, backup);
                        
                        // Reload settings
                        await loadSettings();
                        
                        toast.success(`✅ Import tókst! ${backup.services.length} þjónustur, ${Object.keys(backup.prices).length} verð`);
                        
                        // Clear file input
                        e.target.value = '';
                        
                      } catch (error) {
                        console.error('Import error:', error);
                        toast.error('❌ Import mistókst: ' + (error.response?.data?.detail || error.message));
                      }
                    }}
                  />
                  <p className="text-xs text-slate-500">
                    Veldu JSON backup skrá sem þú sóttir með "Sækja Backup" takkanum
                  </p>
                </div>
              </div>

              <div className="border-t pt-4"></div>

              {/* Quick Info */}
              <div className="bg-blue-50 border border-blue-200 rounded p-4 space-y-2">
                <h4 className="font-medium text-blue-900">💡 Hvernig virkar þetta?</h4>
                <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                  <li><strong>Export:</strong> Sækir backup af öllum stillingum sem JSON skrá</li>
                  <li><strong>Import:</strong> Hleður upp backup og endurheimtir allar stillingar</li>
                  <li><strong>Notkunar dæmi:</strong> Eftir deployment á production, sæktu backup frá preview og import-aðu</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </TabsContent>



        {/* Label Print Settings Tab */}
        <TabsContent value="labels" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                🖨️ Límiða prentun
              </CardTitle>
              <p className="text-sm text-slate-600">
                Stilltu stærð og útlit á límiðum fyrir lakkvörur í birgðum. Stuðlar við litaprentun.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900">
                  💡 <strong>Ábending:</strong> Fyrir bestu niðurstöðu, notaðu litaprentara. Litur preview er prentaður á límiðanum.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Breidd (mm)</Label>
                  <Input
                    type="number"
                    min="10"
                    max="300"
                    value={labelSettings.width}
                    onChange={(e) => setLabelSettings({...labelSettings, width: parseInt(e.target.value) || 100})}
                  />
                  <p className="text-xs text-slate-500 mt-1">10-300 mm</p>
                </div>
                <div>
                  <Label>Hæð (mm)</Label>
                  <Input
                    type="number"
                    min="10"
                    max="300"
                    value={labelSettings.height}
                    onChange={(e) => setLabelSettings({...labelSettings, height: parseInt(e.target.value) || 50})}
                  />
                  <p className="text-xs text-slate-500 mt-1">10-300 mm</p>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-medium mb-3">📏 Font stærðir</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Litakóði (px)</Label>
                    <Input
                      type="number"
                      min="12"
                      max="72"
                      value={labelSettings.font_size_code}
                      onChange={(e) => setLabelSettings({...labelSettings, font_size_code: parseInt(e.target.value) || 32})}
                    />
                  </div>
                  <div>
                    <Label>Nafn lakks (px)</Label>
                    <Input
                      type="number"
                      min="8"
                      max="48"
                      value={labelSettings.font_size_name}
                      onChange={(e) => setLabelSettings({...labelSettings, font_size_name: parseInt(e.target.value) || 16})}
                    />
                  </div>
                  <div>
                    <Label>Smáatriði (px)</Label>
                    <Input
                      type="number"
                      min="6"
                      max="24"
                      value={labelSettings.font_size_details}
                      onChange={(e) => setLabelSettings({...labelSettings, font_size_details: parseInt(e.target.value) || 12})}
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-medium mb-3">👁️ Sýna á límiða</h3>
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={labelSettings.show_color_box}
                      onChange={(e) => setLabelSettings({...labelSettings, show_color_box: e.target.checked})}
                      className="rounded"
                    />
                    <span className="text-sm">Litur preview (reitur með lit)</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={labelSettings.show_manufacturer}
                      onChange={(e) => setLabelSettings({...labelSettings, show_manufacturer: e.target.checked})}
                      className="rounded"
                    />
                    <span className="text-sm">Framleiðandi (BMW, Toyota...)</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={labelSettings.show_size}
                      onChange={(e) => setLabelSettings({...labelSettings, show_size: e.target.checked})}
                      className="rounded"
                    />
                    <span className="text-sm">Stærð (250ml, 500ml...)</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={labelSettings.show_location}
                      onChange={(e) => setLabelSettings({...labelSettings, show_location: e.target.checked})}
                      className="rounded"
                    />
                    <span className="text-sm">Staðsetning/Hilla (A1, B2...)</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={labelSettings.show_sequence}
                      onChange={(e) => setLabelSettings({...labelSettings, show_sequence: e.target.checked})}
                      className="rounded"
                    />
                    <span className="text-sm">Raðnúmer (#1, #2...)</span>
                  </label>
                </div>
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <Button 
                  onClick={saveLabelSettings}
                  disabled={savingLabelSettings}
                  className="flex-1"
                >
                  {savingLabelSettings ? '⏳ Vista...' : '💾 Vista stillingar'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Blocked Times Tab */}
        <TabsContent value="blocked-times" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                🔒 Lokaðir tímar
              </CardTitle>
              <p className="text-sm text-slate-600">
                Stilltu daga og tíma sem eru lokaðir fyrir nýjar bókanir. Lokaðir tímar birtast með rauðum lit á dagatalinu.
              </p>
            </CardHeader>
            <CardContent>
              <BlockedTimesManager />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integration Settings Tab (Payday vs DK) */}

        {/* Opening Hours Tab */}
        <TabsContent value="opening-hours" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                🕐 Opnunartímar og Tímaáætlun
              </CardTitle>
              <p className="text-sm text-slate-600">
                Stjórna opnunartímum og lengd hverrar þjónustu
              </p>
            </CardHeader>
            <CardContent>
              <OpeningHoursManager settings={settings} onSave={null} />
            </CardContent>
          </Card>
        </TabsContent>


        {/* Statistics Tab */}
        <TabsContent value="statistics" className="space-y-4">
          {/* Cache Settings Section */}
          <Card>
            <CardHeader>
              <CardTitle>🗄️ Cache og Uppflettinga Stillingar</CardTitle>
              <p className="text-sm text-slate-600">
                Hversu lengi geyma uppflettingar í cache og kostnaður per leit
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900">
                  💡 <strong>Cache sparar peninga:</strong> Uppflettingar úr cache kosta ekkert. Aðeins API uppflettingar kosta.
                </p>
              </div>
              
              <div className="border-b pb-4">
                <h3 className="font-medium mb-3">💰 Verð per uppflettingu (m/VSK)</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>🚗 Bílnúmer (kr)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="1000"
                      value={cacheSettings.vehicle_lookup_price}
                      onChange={(e) => setCacheSettings({...cacheSettings, vehicle_lookup_price: parseInt(e.target.value) || 41})}
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Núverandi: {cacheSettings.vehicle_lookup_price} kr per leit
                    </p>
                  </div>
                  <div>
                    <Label>👤 Kennitala (kr)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="1000"
                      value={cacheSettings.person_lookup_price}
                      onChange={(e) => setCacheSettings({...cacheSettings, person_lookup_price: parseInt(e.target.value) || 23})}
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Núverandi: {cacheSettings.person_lookup_price} kr per leit
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="border-b pb-4">
                <h3 className="font-medium mb-3">📞 Já.is Símtalsleit</h3>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                  <p className="text-sm text-yellow-900">
                    ⚡ <strong>Sjálfvirk símanúmeraleit:</strong> Þegar nýr viðskiptavinur er skráður, leitar kerfið sjálfkrafa að símanúmeri á já.is ef það vantar.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="ja-lookup-enabled"
                      checked={jaLookupSettings.enabled}
                      onChange={(e) => setJaLookupSettings({...jaLookupSettings, enabled: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="ja-lookup-enabled">Virkja já.is uppflettingu</Label>
                  </div>
                  <div>
                    <Label>💰 Verð per leit (kr)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={jaLookupSettings.price_per_lookup}
                      onChange={(e) => setJaLookupSettings({...jaLookupSettings, price_per_lookup: parseInt(e.target.value) || 11})}
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Núverandi: {jaLookupSettings.price_per_lookup} kr per leit
                    </p>
                  </div>
                </div>
                {/* Lykill er bara settur hér, ekki sýndur aftur – bara virkja/afvirkja í UI */}
                <div className="mt-3">
                  <Label>🔑 JA.is API lykill (sláðu inn einu sinni)</Label>
                  {jaLookupSettings.has_api_key && (
                    <p className="text-xs text-slate-500 mb-1">
                      Lykill hefur þegar verið stilltur. Ef þú setur inn nýtt gildi hér, verður hann uppfærður.
                    </p>
                  )}
                  <Input
                    type="password"
                    placeholder={jaLookupSettings.has_api_key ? 'Sláðu inn nýjan JA.is lykil til að uppfæra' : 'Sláðu inn JA.is API lykil'}
                    value={jaLookupSettings.api_key}
                    onChange={(e) => setJaLookupSettings({...jaLookupSettings, api_key: e.target.value})}
                  />
                </div>
                <div className="mt-3 flex items-center space-x-3">
                  <Button 
                    onClick={saveJaLookupSettings}
                    disabled={savingJaSettings}
                    size="sm"
                    variant="outline"
                  >
                    {savingJaSettings ? '⏳ Vistar...' : '💾 Vista já.is stillingar'}
                  </Button>
                  {jaLookupSettings.has_api_key && (
                    <span className="text-xs text-green-700 flex items-center">
                      ✅ Lykill geymdur í kerfisstillingum
                    </span>
                  )}
                </div>
              </div>


              
              <div>
                <h3 className="font-medium mb-3">⏱️ Cache tími</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>🚗 Bílnúmer cache (dagar)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="365"
                      value={cacheSettings.vehicle_cache_days}
                      onChange={(e) => setCacheSettings({...cacheSettings, vehicle_cache_days: parseInt(e.target.value) || 0})}
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      0 = endalaust, 1-365 = dagar. <strong>Núna: {cacheSettings.vehicle_cache_days === 0 ? 'Endalaust' : `${cacheSettings.vehicle_cache_days} dagar`}</strong>
                    </p>
                  </div>
                  <div>
                    <Label>👤 Kennitölur cache (dagar)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="365"
                      value={cacheSettings.person_cache_days}
                      onChange={(e) => setCacheSettings({...cacheSettings, person_cache_days: parseInt(e.target.value) || 0})}
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      0 = endalaust, 1-365 = dagar. <strong>Núna: {cacheSettings.person_cache_days === 0 ? 'Endalaust' : `${cacheSettings.person_cache_days} dagar`}</strong>
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2 pt-4 border-t">
                <Button 
                  onClick={saveCacheSettings}
                  disabled={savingCacheSettings}
                  className="flex-1"
                >
                  {savingCacheSettings ? '⏳ Vista...' : '💾 Vista stillingar'}
                </Button>
              </div>
            </CardContent>
          </Card>
          
          <LookupStatistics />
        </TabsContent>

        {/* Cleanup Tab */}
        <TabsContent value="cleanup" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>🗑️ Þrif og eyðing gagna</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="p-4 border border-yellow-300 bg-yellow-50 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>Varúð:</strong> Þessi virkni eyðir gögnum varanlega og er ekki hægt að afturkalla hana.
                  </p>
                </div>
                {/* Add cleanup options here if needed */}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bílasölur Tab */}
        <TabsContent value="dealerships" className="space-y-4">
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                📊 Vinsælustu Bílasölurnar
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Top 5 Most Used */}
                <div>
                  <h3 className="font-semibold mb-3 text-slate-700 dark:text-slate-300">🔥 Top 5 Mest Notaðar</h3>
                  <div className="space-y-2">
                    {dealerships
                      .filter(d => d.is_active)
                      .sort((a, b) => (b.usage_count || 0) - (a.usage_count || 0))
                      .slice(0, 5)
                      .map((d, index) => {
                        const maxUsage = Math.max(...dealerships.map(x => x.usage_count || 0));
                        const percentage = maxUsage > 0 ? ((d.usage_count || 0) / maxUsage) * 100 : 0;
                        return (
                          <div key={d.id} className="space-y-1">
                            <div className="flex justify-between text-sm">
                              <span className="font-medium">
                                {index === 0 && '🥇 '}
                                {index === 1 && '🥈 '}
                                {index === 2 && '🥉 '}
                                {index > 2 && `${index + 1}. `}
                                {d.name}
                              </span>
                              <span className="text-blue-600 dark:text-blue-400 font-bold">{d.usage_count || 0}x</span>
                            </div>
                            <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                              <div 
                                className="bg-gradient-to-r from-blue-500 to-indigo-500 h-2 rounded-full transition-all duration-500"
                                style={{width: `${percentage}%`}}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                    {dealerships.filter(d => d.is_active && (d.usage_count || 0) > 0).length === 0 && (
                      <p className="text-sm text-slate-500 italic">Engar bílasölur notaðar ennþá</p>
                    )}
                  </div>
                </div>

                {/* Statistics Summary */}
                <div>
                  <h3 className="font-semibold mb-3 text-slate-700 dark:text-slate-300">📈 Tölfræði</h3>
                  <div className="space-y-2">
                    <div className="p-3 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700">
                      <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                        {dealerships.filter(d => d.is_active).length}
                      </div>
                      <div className="text-sm text-slate-600 dark:text-slate-400">Heildarfjöldi bílasala</div>
                    </div>
                    <div className="p-3 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700">
                      <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                        {dealerships.filter(d => d.is_active && (d.usage_count || 0) > 0).length}
                      </div>
                      <div className="text-sm text-slate-600 dark:text-slate-400">Bílasölur í notkun</div>
                    </div>
                    <div className="p-3 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700">
                      <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                        {dealerships.reduce((sum, d) => sum + (d.usage_count || 0), 0)}
                      </div>
                      <div className="text-sm text-slate-600 dark:text-slate-400">Heildarnotkun</div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Management Card */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>🏢 Allar Bílasölur</CardTitle>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Raðað eftir vinsældum - mest notuðu efst
                  </p>
                </div>
                <Button onClick={() => setShowAddDealership(true)} className="bg-green-600 hover:bg-green-700">
                  ➕ Bæta við bílasölu
                </Button>
              </div>
              {/* Search bar */}
              <div className="mt-4">
                <Input
                  type="text"
                  placeholder="🔍 Leita að bílasölu..."
                  value={dealershipSearchTerm}
                  onChange={(e) => setDealershipSearchTerm(e.target.value)}
                  className="max-w-md"
                />
              </div>
            </CardHeader>
            <CardContent>
              {/* Add Dealership Dialog */}
              {showAddDealership && (
                <div className="mb-4 p-4 border rounded-lg bg-blue-50 dark:bg-blue-950">
                  <h3 className="font-medium mb-3">Ný bílasala</h3>
                  <div className="space-y-3">
                    {/* Kennitala lookup row */}
                    <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border">
                      <Label className="text-sm font-medium mb-2 block">🔍 Fletta upp fyrirtæki í þjóðskrá</Label>
                      <div className="flex gap-2">
                        <Input
                          value={newDealership.kennitala}
                          onChange={(e) => setNewDealership(prev => ({...prev, kennitala: e.target.value.replace(/\D/g, '').slice(0, 10)}))}
                          placeholder="Kennitala (10 stafir)"
                          className="flex-1"
                          maxLength={10}
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={!newDealership.kennitala || newDealership.kennitala.length !== 10 || saving}
                          onClick={async () => {
                            try {
                              setSaving(true);
                              const res = await axios.get(`${API}/person/lookup/${newDealership.kennitala}`);
                              if (res.data && res.data.found && (res.data.full_name || res.data.company_name)) {
                                const companyName = res.data.company_name || res.data.full_name;
                                setNewDealership(prev => ({
                                  ...prev,
                                  company_name: companyName,
                                  // Auto-fill display name if empty
                                  name: prev.name || companyName
                                }));
                                toast.success(`✅ Fyrirtæki fundið: ${companyName}`);
                              } else {
                                toast.error('Engin gögn fundust fyrir þessa kennitölu');
                              }
                            } catch (error) {
                              console.error('Lookup error:', error);
                              toast.error('Villa við að fletta upp kennitölu');
                            } finally {
                              setSaving(false);
                            }
                          }}
                        >
                          {saving ? '...' : '🔍 Fletta'}
                        </Button>
                      </div>
                      {newDealership.company_name && (
                        <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/30 rounded text-sm">
                          <span className="text-green-700 dark:text-green-300">✅ Fyrirtækjanafn: <strong>{newDealership.company_name}</strong></span>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label>Auðkenni/Vöruheiti bílasölu *</Label>
                        <Input
                          value={newDealership.name}
                          onChange={(e) => setNewDealership(prev => ({...prev, name: e.target.value}))}
                          placeholder="t.d. 'Bílaland', 'Bílabankinn'"
                        />
                        <p className="text-xs text-slate-500 mt-1">Nafnið sem birtist í listum og verkum</p>
                      </div>
                      <div>
                        <Label>Fyrirtækjanafn (frá þjóðskrá)</Label>
                        <Input
                          value={newDealership.company_name}
                          onChange={(e) => setNewDealership(prev => ({...prev, company_name: e.target.value}))}
                          placeholder="t.d. 'BL ehf.', 'Mare ehf.'"
                        />
                        <p className="text-xs text-slate-500 mt-1">Notað fyrir reikninga og greiðanda</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label>Netfang</Label>
                        <Input
                          value={newDealership.email}
                          onChange={(e) => setNewDealership(prev => ({...prev, email: e.target.value}))}
                          placeholder="t.d. 'birgi@brimborg.is'"
                          type="email"
                        />
                      </div>
                      <div>
                        <Label>Símanúmer</Label>
                        <Input
                          value={newDealership.phone}
                          onChange={(e) => setNewDealership(prev => ({...prev, phone: e.target.value}))}
                          placeholder="t.d. '5151000'"
                        />
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="send_sms_new"
                        checked={newDealership.send_sms}
                        onCheckedChange={(checked) => setNewDealership(prev => ({...prev, send_sms: checked}))}
                      />
                      <Label htmlFor="send_sms_new" className="text-sm cursor-pointer">
                        📱 Senda SMS tilkynningar
                      </Label>
                    </div>
                    <div className="flex space-x-2">
                      <Button 
                        size="sm" 
                        onClick={async () => {
                          if (!newDealership.name) {
                            toast.error('Nafn bílasölu vantar');
                            return;
                          }
                          try {
                            setSaving(true);
                            await axios.post(`${API}/dealerships`, newDealership);
                            toast.success('Bílasala bætt við');
                            setShowAddDealership(false);
                            setNewDealership({ name: '', company_name: '', kennitala: '', email: '', phone: '', send_sms: false });
                            await loadSettings();
                          } catch (error) {
                            toast.error('Villa við að bæta við bílasölu');
                          } finally {
                            setSaving(false);
                          }
                        }} 
                        disabled={saving}
                      >
                        {saving ? 'Vistar...' : '✅ Vista'}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => {
                        setShowAddDealership(false);
                        setNewDealership({ name: '', company_name: '', kennitala: '', email: '', phone: '', send_sms: false });
                      }}>
                        Hætta við
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Existing Dealerships */}
              <div className="space-y-2">
                {dealerships
                  .filter(d => d.is_active)
                  .filter(d => {
                    if (!dealershipSearchTerm) return true;
                    const search = dealershipSearchTerm.toLowerCase();
                    return (
                      (d.name || '').toLowerCase().includes(search) ||
                      (d.company_name || '').toLowerCase().includes(search) ||
                      (d.kennitala || '').includes(search) ||
                      (d.email || '').toLowerCase().includes(search)
                    );
                  })
                  .sort((a, b) => (b.usage_count || 0) - (a.usage_count || 0))
                  .map((dealership) => (
                  <div key={dealership.id} className="p-3 border rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800">
                    {editingDealership === dealership.id ? (
                      <div className="space-y-3">
                        {/* Kennitala lookup for editing */}
                        <div className="p-3 bg-white dark:bg-slate-900 rounded-lg border">
                          <Label className="text-sm font-medium mb-2 block">🔍 Fletta upp fyrirtæki</Label>
                          <div className="flex gap-2">
                            <Input
                              defaultValue={dealership.kennitala}
                              onChange={(e) => {
                                dealership.tempKennitala = e.target.value.replace(/\D/g, '').slice(0, 10);
                              }}
                              placeholder="Kennitala (10 stafir)"
                              className="flex-1"
                              maxLength={10}
                            />
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              disabled={saving}
                              onClick={async () => {
                                const kt = dealership.tempKennitala || dealership.kennitala;
                                if (!kt || kt.length !== 10) {
                                  toast.error('Kennitala verður að vera 10 stafir');
                                  return;
                                }
                                try {
                                  setSaving(true);
                                  const res = await axios.get(`${API}/person/lookup/${kt}`);
                                  if (res.data && res.data.found && (res.data.full_name || res.data.company_name)) {
                                    dealership.tempCompanyName = res.data.company_name || res.data.full_name;
                                    toast.success(`✅ Fyrirtæki fundið: ${dealership.tempCompanyName}`);
                                    // Force re-render
                                    setEditingDealership(null);
                                    setTimeout(() => setEditingDealership(dealership.id), 50);
                                  } else {
                                    toast.error('Engin gögn fundust');
                                  }
                                } catch (error) {
                                  toast.error('Villa við að fletta upp');
                                } finally {
                                  setSaving(false);
                                }
                              }}
                            >
                              {saving ? '...' : '🔍 Fletta'}
                            </Button>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <Label>Auðkenni/Vöruheiti</Label>
                            <Input
                              defaultValue={dealership.name}
                              onChange={(e) => {
                                dealership.tempName = e.target.value;
                              }}
                            />
                          </div>
                          <div>
                            <Label>Fyrirtækjanafn (þjóðskrá)</Label>
                            <Input
                              defaultValue={dealership.tempCompanyName || dealership.company_name}
                              onChange={(e) => {
                                dealership.tempCompanyName = e.target.value;
                              }}
                              placeholder="t.d. 'BL ehf.'"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <Label>Netfang</Label>
                            <Input
                              defaultValue={dealership.email}
                              onChange={(e) => {
                                dealership.tempEmail = e.target.value;
                              }}
                              type="email"
                            />
                          </div>
                          <div>
                            <Label>Símanúmer</Label>
                            <Input
                              defaultValue={dealership.phone}
                              onChange={(e) => {
                                dealership.tempPhone = e.target.value;
                              }}
                            />
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id={`send_sms_edit_${dealership.id}`}
                            defaultChecked={dealership.send_sms}
                            onCheckedChange={(checked) => {
                              dealership.tempSendSms = checked;
                            }}
                          />
                          <Label htmlFor={`send_sms_edit_${dealership.id}`} className="text-sm cursor-pointer">
                            📱 Senda SMS tilkynningar
                          </Label>
                        </div>
                        <div className="flex space-x-2">
                          <Button 
                            size="sm" 
                            onClick={async () => {
                              try {
                                setSaving(true);
                                await axios.put(`${API}/dealerships/${dealership.id}`, {
                                  name: dealership.tempName || dealership.name,
                                  company_name: dealership.tempCompanyName || dealership.company_name,
                                  kennitala: dealership.tempKennitala || dealership.kennitala,
                                  email: dealership.tempEmail || dealership.email,
                                  phone: dealership.tempPhone || dealership.phone,
                                  send_sms: dealership.tempSendSms !== undefined ? dealership.tempSendSms : dealership.send_sms,
                                  is_active: true
                                });
                                toast.success('Bílasala uppfærð');
                                setEditingDealership(null);
                                await loadSettings();
                              } catch (error) {
                                toast.error('Villa við að uppfæra bílasölu');
                              } finally {
                                setSaving(false);
                              }
                            }}
                            disabled={saving}
                          >
                            {saving ? 'Vistar...' : '✅ Vista'}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingDealership(null)}>
                            Hætta við
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <div className="font-medium text-lg">🏢 {dealership.name}</div>
                            {dealership.company_name && dealership.company_name !== dealership.name && (
                              <span className="text-xs bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 px-2 py-1 rounded-full">
                                🏛️ {dealership.company_name}
                              </span>
                            )}
                            {dealership.kennitala && (
                              <span className="text-xs bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 px-2 py-1 rounded-full">
                                kt: {dealership.kennitala}
                              </span>
                            )}
                            {dealership.send_sms && (
                              <span className="text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-2 py-1 rounded-full">
                                📱 SMS
                              </span>
                            )}
                            {dealership.usage_count > 0 ? (
                              <div className="flex items-center gap-2">
                                <span className={`text-sm font-bold px-3 py-1 rounded-full ${
                                  dealership.usage_count > 50 ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200' :
                                  dealership.usage_count > 20 ? 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200' :
                                  dealership.usage_count > 5 ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
                                  'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                                }`}>
                                  🔥 {dealership.usage_count}x
                                </span>
                                {dealership.usage_count > 50 && <span className="text-xl">🌟</span>}
                                {dealership.usage_count > 100 && <span className="text-xl">⭐</span>}
                              </div>
                            ) : (
                              <span className="text-xs bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 px-2 py-1 rounded-full">
                                Ónotað
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                            {dealership.email && <span>📧 {dealership.email}</span>}
                            {dealership.email && dealership.phone && <span className="mx-2">•</span>}
                            {dealership.phone && <span>📞 {dealership.phone}</span>}
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Button size="sm" variant="outline" onClick={() => setEditingDealership(dealership.id)}>
                            ✏️ Breyta
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-red-600 border-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                            onClick={() => {
                              setDealershipToDelete(dealership);
                              setShowDeleteDialog(true);
                            }}
                          >
                            🗑️ Eyða
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                
                {dealerships.filter(d => d.is_active).length === 0 && (
                  <div className="text-center py-8 text-slate-600 dark:text-slate-400">
                    Engar bílasölur í kerfinu. Smelltu á "Bæta við bílasölu" til að byrja.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Delete Dealership Confirmation Dialog */}
          <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>⚠️ Eyða bílasölu</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Ertu viss um að þú viljir eyða <strong>{dealershipToDelete?.name}</strong>?
                </p>
                <p className="text-sm text-orange-600 dark:text-orange-400 font-medium">
                  ⚠️ Athugaðu: Ekki er hægt að eyða bílasölu ef hún hefur virk verk (pending eða in_progress).
                </p>
                <div>
                  <Label htmlFor="delete_password">Admin lykilorð *</Label>
                  <Input
                    id="delete_password"
                    type="password"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    placeholder="Sláðu inn admin lykilorð"
                  />
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setShowDeleteDialog(false);
                      setDealershipToDelete(null);
                      setDeletePassword('');
                    }}
                  >
                    Hætta við
                  </Button>
                  <Button
                    className="flex-1 bg-red-600 hover:bg-red-700"
                    disabled={!deletePassword || saving}
                    onClick={async () => {
                      try {
                        setSaving(true);
                        await axios.delete(`${API}/dealerships/${dealershipToDelete.id}?password=${encodeURIComponent(deletePassword)}`);
                        toast.success('Bílasölu eytt');
                        setShowDeleteDialog(false);
                        setDealershipToDelete(null);
                        setDeletePassword('');
                        await loadSettings();
                      } catch (error) {
                        const errorMsg = error.response?.data?.detail || 'Villa við að eyða bílasölu';
                        toast.error(errorMsg);
                      } finally {
                        setSaving(false);
                      }
                    }}
                  >
                    {saving ? 'Eyði...' : '🗑️ Eyða'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>


        {/* Online Booking Settings Tab (includes Discount Codes) */}
        <TabsContent value="online-booking" className="space-y-6">
          <OnlineBookingSettings />
        </TabsContent>


        {/* System Settings Tab - Nested tabs like pricing */}
        <TabsContent value="system" className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <h3 className="font-medium text-blue-900 mb-2">⚙️ Kerfisstillingar</h3>
            <p className="text-sm text-blue-700">
              Stillingar fyrir tölvupóst, SMS, greiðslur, kerfisvöl og notendur
            </p>
          </div>

          {/* Sub-tabs for system settings sections */}
          <Tabs defaultValue="email">
            <TabsList className="flex-wrap h-auto">
              <TabsTrigger value="email">📧 Tölvupóstur</TabsTrigger>
              <TabsTrigger value="sms">📱 SMS Stillingar</TabsTrigger>
              <TabsTrigger value="ja">📞 Já.is</TabsTrigger>
              <TabsTrigger value="key-locations">🔑 Lyklastaðsetningar</TabsTrigger>
              <TabsTrigger value="pause-statuses">⏸️ Pásastöður</TabsTrigger>
              <TabsTrigger value="mypos">💳 MyPOS Greiðslur</TabsTrigger>
              <TabsTrigger value="integration">🔄 Kerfisvöl</TabsTrigger>
              <TabsTrigger value="users">👥 Notendastjórnun</TabsTrigger>
            </TabsList>
            
            {/* Email Sub-tab - will add content later */}
            <TabsContent value="email" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    📧 Office 365 Tölvupóststillingar
                  </CardTitle>
                  <p className="text-sm text-slate-600">
                    Stillingar fyrir Microsoft Graph API til að senda staðfestingarlinka á tölvupóst
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
                  
                  {/* Connection Status */}
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 border dark:border-slate-700 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium dark:text-slate-200">Tengingarstaða</div>
                        <div className="text-sm text-slate-600 dark:text-slate-400">
                          {emailSettings.enabled ? '✅ Virkt' : '❌ Óvirkt'}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          checked={emailSettings.enabled}
                          onCheckedChange={(checked) => 
                            setEmailSettings(prev => ({...prev, enabled: checked}))
                          }
                        />
                        <Label>Virkja tölvupóst</Label>
                      </div>
                    </div>
                  </div>

                  {/* Outlook Calendar Sync Toggle */}
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium dark:text-slate-200 flex items-center gap-2">
                          📅 Outlook Calendar samstilling
                        </div>
                        <div className="text-sm text-slate-600 dark:text-slate-400">
                          {emailSettings.calendar_sync_enabled ? '✅ Verk samstillast við Outlook Calendar' : '❌ Samstilling slökkt'}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                          Þegar virkt, eru öll verk sjálfkrafa samstillt við Outlook dagatalið þitt
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          checked={emailSettings.calendar_sync_enabled}
                          onCheckedChange={(checked) => 
                            setEmailSettings(prev => ({...prev, calendar_sync_enabled: checked}))
                          }
                          disabled={!emailSettings.enabled}
                        />
                        <Label>Virkja dagatal</Label>
                      </div>
                    </div>
                    {!emailSettings.enabled && (
                      <div className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                        ⚠️ Tölvupóstur verður að vera virkur til að nota dagatalssamstillingu
                      </div>
                    )}
                  </div>

                  {/* Office 365 Configuration */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="tenant_id">Tenant ID *</Label>
                      <Input
                        id="tenant_id"
                        value={emailSettings.tenant_id}
                        onChange={(e) => setEmailSettings(prev => ({...prev, tenant_id: e.target.value}))}
                        placeholder="12345678-1234-1234-1234-123456789012"
                      />
                      <div className="text-xs text-slate-500 mt-1">
                        Azure AD Tenant ID úr app registration
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="client_id">Client ID (Application ID) *</Label>
                      <Input
                        id="client_id"
                        value={emailSettings.client_id}
                        onChange={(e) => setEmailSettings(prev => ({...prev, client_id: e.target.value}))}
                        placeholder="abcdef12-3456-7890-abcd-123456789012"
                      />
                      <div className="text-xs text-slate-500 mt-1">
                        Application ID úr app registration
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <Label htmlFor="client_secret">Client Secret *</Label>
                      <Input
                        id="client_secret"
                        type="password"
                        value={emailSettings.client_secret}
                        onChange={(e) => setEmailSettings(prev => ({...prev, client_secret: e.target.value}))}
                        placeholder="Leyndarmálið úr Certificates & secrets"
                      />
                      <div className="text-xs text-slate-500 mt-1">
                        Client secret value úr Azure AD app registration
                      </div>
                    </div>
                  </div>

                  {/* Email Configuration */}
                  <div className="border-t pt-4">
                    <h3 className="font-medium text-slate-900 mb-4">📩 Sendingarstillingar</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="sender_email">Senda frá netfangi *</Label>
                        <Input
                          id="sender_email"
                          type="email"
                          value={emailSettings.sender_email}
                          onChange={(e) => setEmailSettings(prev => ({...prev, sender_email: e.target.value}))}
                          placeholder="bilathjonusta@fyrirtaekid.is"
                        />
                        <div className="text-xs text-slate-500 mt-1">
                          Netfang sem sendir tölvupóstinn
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="sender_name">Nafn sendanda</Label>
                        <Input
                          id="sender_name"
                          value={emailSettings.sender_name}
                          onChange={(e) => setEmailSettings(prev => ({...prev, sender_name: e.target.value}))}
                          placeholder="Alvörubón ehf."
                        />
                        <div className="text-xs text-slate-500 mt-1">
                          Nafnið sem birtist í tölvupóstinum
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Test Email */}
                  <div className="border-t pt-4">
                    <h3 className="font-medium text-slate-900 mb-4">🧪 Prófa tengingu</h3>
                    <div className="flex items-end space-x-2">
                      <div className="flex-1">
                        <Label htmlFor="test_email">Prófa netfang</Label>
                        <Input
                          id="test_email"
                          type="email"
                          value={testEmailAddress}
                          onChange={(e) => setTestEmailAddress(e.target.value)}
                          placeholder="test@example.com"
                        />
                      </div>
                      <Button 
                        onClick={testEmailConnection}
                        disabled={!emailSettings.enabled || !testEmailAddress || saving}
                        variant="outline"
                      >
                        {saving ? 'Sendir...' : '📤 Prófa'}
                      </Button>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      Sendir prófunar tölvupóst til að staðfesta stillingar
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end pt-4 border-t">
                    <Button onClick={saveEmailSettings} disabled={saving}>
                      {saving ? 'Vistar...' : '💾 Vista tölvupóststillingar'}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Email Template Customization Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    ✉️ Snið reiknings-tölvupósts
                  </CardTitle>
                  <p className="text-sm text-slate-600">
                    Sérsníða texta í reikningum sem eru sendir með tölvupósti
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
                  
                  {/* Info Banner */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <div className="text-2xl">💡</div>
                      <div className="flex-1">
                        <div className="font-medium text-blue-900 mb-1">Sérstillingar fyrir reikningspósta</div>
                        <div className="text-sm text-blue-700">
                          Þú getur sérsniðið texta í tölvupóstum sem eru sendir með reikningum. Skildu reitina eftir tóma til að nota sjálfgefinn texta.
                          <div className="mt-2 text-xs">
                            <strong>Breytur sem þú getur notað:</strong><br/>
                            <code className="bg-blue-100 px-1 rounded">{`{customer_name}`}</code> - Nafn viðskiptavinar<br/>
                            <code className="bg-blue-100 px-1 rounded">{`{invoice_number}`}</code> - Reikningsnúmer<br/>
                            <code className="bg-blue-100 px-1 rounded">{`{invoice_amount}`}</code> - Upphæð<br/>
                            <code className="bg-blue-100 px-1 rounded">{`{invoice_date}`}</code> - Dagsetning
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Subject Line */}
                  <div>
                    <Label htmlFor="email_subject">Efnislína / Subject</Label>
                    <Input
                      id="email_subject"
                      value={emailSettings.email_subject || ''}
                      onChange={(e) => setEmailSettings(prev => ({...prev, email_subject: e.target.value}))}
                      placeholder="Sjálfgefið: Reikningur nr. {invoice_number} - Alvörubón ehf."
                    />
                    <div className="text-xs text-slate-500 mt-1">
                      Efnislína tölvupóstsins
                    </div>
                  </div>

                  {/* Greeting */}
                  <div>
                    <Label htmlFor="email_greeting">Kveðja</Label>
                    <Input
                      id="email_greeting"
                      value={emailSettings.email_greeting || ''}
                      onChange={(e) => setEmailSettings(prev => ({...prev, email_greeting: e.target.value}))}
                      placeholder="Sjálfgefið: Góðan daginn {customer_name},"
                    />
                    <div className="text-xs text-slate-500 mt-1">
                      Upphafskveðja í tölvupóstinum
                    </div>
                  </div>

                  {/* Body Text */}
                  <div>
                    <Label htmlFor="email_body">Meginmáls texti</Label>
                    <textarea
                      id="email_body"
                      value={emailSettings.email_body || ''}
                      onChange={(e) => setEmailSettings(prev => ({...prev, email_body: e.target.value}))}
                      placeholder="Sjálfgefið: Þakka þér fyrir viðskiptin! Hér fyrir neðan er reikningurinn þinn."
                      className="w-full p-2 border rounded-md h-24 resize-none"
                    />
                    <div className="text-xs text-slate-500 mt-1">
                      Aðaltexti tölvupóstsins
                    </div>
                  </div>

                  {/* Footer Text */}
                  <div>
                    <Label htmlFor="email_footer">Lokaorð</Label>
                    <textarea
                      id="email_footer"
                      value={emailSettings.email_footer || ''}
                      onChange={(e) => setEmailSettings(prev => ({...prev, email_footer: e.target.value}))}
                      placeholder="Sjálfgefið: Bestu kveðjur, Alvörubón ehf."
                      className="w-full p-2 border rounded-md h-20 resize-none"
                    />
                    <div className="text-xs text-slate-500 mt-1">
                      Lokakveðja í tölvupóstinum (HTML er stutt, t.d. &lt;br&gt; fyrir nýja línu)
                    </div>
                  </div>

                  {/* Preview Button */}
                  <div className="border-t pt-4">
                    <Button 
                      onClick={async () => {
                        try {
                          const response = await axios.post(`${API}/admin/email-template-preview`, {
                            email_subject: emailSettings.email_subject,
                            email_greeting: emailSettings.email_greeting,
                            email_body: emailSettings.email_body,
                            email_footer: emailSettings.email_footer
                          });
                          
                          if (response.data.success) {
                            // Open preview in new window
                            const previewWindow = window.open('', '_blank', 'width=700,height=800');
                            if (previewWindow) {
                              previewWindow.document.write(response.data.html);
                              previewWindow.document.title = `Forskoðun: ${response.data.subject}`;
                            }
                            toast.success('Forskoðun opnuð í nýjum glugga');
                          }
                        } catch (error) {
                          console.error('Preview error:', error);
                          toast.error('Villa við að búa til forskoðun');
                        }
                      }}
                      variant="outline"
                      className="w-full"
                    >
                      👁️ Forskoða tölvupóst
                    </Button>
                  </div>

                  {/* Info about PDF attachment */}
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                    <strong>📎 PDF viðhengi:</strong> DK reikningur er sjálfkrafa tekinn með sem PDF viðhengi í öllum reikningspóstum.
                  </div>

                </CardContent>
              </Card>

            </TabsContent>

            {/* SMS Sub-tab */}
            <TabsContent value="sms" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    📱 SMS Stillingar
                  </CardTitle>
                  <p className="text-sm text-slate-600">
                    Stjórna SMS sniðmátum og sjálfvirkum tilkynningum
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
                  
                  {/* GatewayAPI Configuration */}
                  <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50 space-y-4">
                    <h3 className="font-semibold text-blue-900 flex items-center gap-2">
                      🔐 GatewayAPI Stillingar
                    </h3>
                    <p className="text-sm text-blue-700">
                      API lykill fyrir GatewayAPI SMS þjónustuna. Fáanlegur hjá gatewayapi.com
                    </p>
                    
                    <div>
                      <Label htmlFor="gatewayapi_token">GatewayAPI Token</Label>
                      <Input
                        id="gatewayapi_token"
                        type="password"
                        value={smsSettings.gatewayapi_token || ''}
                        onChange={(e) => setSmsSettings(prev => ({...prev, gatewayapi_token: e.target.value}))}
                        placeholder="Sláðu inn GatewayAPI token..."
                      />
                      <div className="text-xs text-slate-500 mt-1">
                        API lykill frá GatewayAPI fyrir SMS sendingar
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="gatewayapi_sender">Sendandi nafn (GatewayAPI)</Label>
                      <Input
                        id="gatewayapi_sender"
                        value={smsSettings.gatewayapi_sender || 'Alvorubon'}
                        onChange={(e) => setSmsSettings(prev => ({...prev, gatewayapi_sender: e.target.value}))}
                        placeholder="Alvorubon"
                        maxLength="11"
                      />
                      <div className="text-xs text-slate-500 mt-1">
                        Sendandi nafn sem birtist á SMS (allt að 11 stafir)
                      </div>
                    </div>

                    <div className="flex items-center justify-between py-2">
                      <div>
                        <div className="font-medium">Mock Mode (Prófa án raunverulegra SMS)</div>
                        <div className="text-sm text-slate-600">Kveiktu á þessu til að prófa án þess að senda raunveruleg SMS</div>
                      </div>
                      <Checkbox
                        checked={smsSettings.use_mock}
                        onCheckedChange={(checked) => 
                          setSmsSettings(prev => ({...prev, use_mock: checked}))
                        }
                      />
                    </div>
                  </div>
                  
                  {/* Sender Name */}
                  <div>
                    <Label htmlFor="sms_sender">Sendandi nafn</Label>
                    <Input
                      id="sms_sender"
                      value={smsSettings.sender_name}
                      onChange={(e) => setSmsSettings(prev => ({...prev, sender_name: e.target.value}))}
                      placeholder="Alvorubon"
                      maxLength="11"
                    />
                    <div className="text-xs text-slate-500 mt-1">
                      Nafn sem birtist á SMS (allt að 11 stafir)
                    </div>
                  </div>

                  {/* Automatic SMS Options */}
                  <div className="border rounded-lg p-4 space-y-4">
                    <h3 className="font-medium">Sjálfvirkar SMS tilkynningar</h3>
                    
                    <div className="flex items-center justify-between py-2">
                      <div>
                        <div className="font-medium">Staðfesting bókunar</div>
                        <div className="text-sm text-slate-600">Senda SMS þegar verk er bókað</div>
                      </div>
                      <Checkbox
                        checked={smsSettings.booking_confirmation_enabled}
                        onCheckedChange={(checked) => 
                          setSmsSettings(prev => ({...prev, booking_confirmation_enabled: checked}))
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between py-2">
                      <div>
                        <div className="font-medium">Áminning fyrir bókun</div>
                        <div className="text-sm text-slate-600">Senda áminningu fyrir bókaðan tíma</div>
                      </div>
                      <Checkbox
                        checked={smsSettings.booking_reminder_enabled}
                        onCheckedChange={(checked) => 
                          setSmsSettings(prev => ({...prev, booking_reminder_enabled: checked}))
                        }
                      />
                    </div>

                    {smsSettings.booking_reminder_enabled && (
                      <div className="ml-6 space-y-3">
                        <div>
                          <Label>Hvenær á að senda áminninguna?</Label>
                          <Select
                            value={smsSettings.reminder_timing_type || 'hours_before'}
                            onValueChange={(value) => setSmsSettings(prev => ({...prev, reminder_timing_type: value}))}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Veldu tímasetningu" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1_hour_before">⏰ Nákvæmlega 1 klukkustund fyrir bókun</SelectItem>
                              <SelectItem value="24_hours_before">📅 Nákvæmlega 24 klukkustundir fyrir bókun</SelectItem>
                              <SelectItem value="day_before_at_time">🌅 Daginn fyrir á ákveðnum tíma</SelectItem>
                              <SelectItem value="custom_hours">⚙️ Sérsniðinn fjöldi klukkustunda</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Show time picker for "day before at time" option */}
                        {smsSettings.reminder_timing_type === 'day_before_at_time' && (
                          <div className="ml-4">
                            <Label htmlFor="reminder_time">Klukkustund</Label>
                            <Input
                              id="reminder_time"
                              type="time"
                              value={smsSettings.reminder_time_of_day || '18:00'}
                              onChange={(e) => setSmsSettings(prev => ({...prev, reminder_time_of_day: e.target.value}))}
                              className="w-48"
                            />
                            <div className="text-xs text-slate-500 mt-1">
                              T.d. 18:00 = kvöldið fyrir bókun
                            </div>
                          </div>
                        )}

                        {/* Show hours input for custom option */}
                        {smsSettings.reminder_timing_type === 'custom_hours' && (
                          <div className="ml-4">
                            <Label htmlFor="custom_hours">Klukkustundir fyrir bókun</Label>
                            <Input
                              id="custom_hours"
                              type="number"
                              value={smsSettings.reminder_hours_before || 24}
                              onChange={(e) => setSmsSettings(prev => ({...prev, reminder_hours_before: parseInt(e.target.value) || 24}))}
                              min="1"
                              max="168"
                              className="w-32"
                            />
                            <div className="text-xs text-slate-500 mt-1">
                              T.d. 48 = 2 dögum fyrir, 12 = hálfum degi fyrir
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex items-center justify-between py-2">
                      <div>
                        <div className="font-medium">Verk lokið</div>
                        <div className="text-sm text-slate-600">Senda SMS þegar verk er lokið</div>
                      </div>
                      <Checkbox
                        checked={smsSettings.completion_notification_enabled}
                        onCheckedChange={(checked) => 
                          setSmsSettings(prev => ({...prev, completion_notification_enabled: checked}))
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between py-2">
                      <div>
                        <div className="font-medium">Greiðsluáminning</div>
                        <div className="text-sm text-slate-600">Senda áminningar um ógreiddar greiðslur</div>
                      </div>
                      <Checkbox
                        checked={smsSettings.payment_reminder_enabled}
                        onCheckedChange={(checked) => 
                          setSmsSettings(prev => ({...prev, payment_reminder_enabled: checked}))
                        }
                      />
                    </div>
                  </div>

                  {/* SMS Templates */}
                  <div className="border-t pt-6">
                    <h3 className="font-medium text-slate-900 mb-4">📝 SMS Sniðmát</h3>
                    <p className="text-sm text-slate-600 mb-4">
                      Notaðu breytur: {'{customer_name}'}, {'{plate}'}, {'{date}'}, {'{time}'}, {'{payment_url}'}
                    </p>
                    
                    <div className="space-y-4">
                      <div>
                        <Label>Staðfesting bókunar</Label>
                        <textarea
                          className="w-full min-h-[80px] p-2 border rounded-md"
                          value={smsSettings.templates.booking_confirmation}
                          onChange={(e) => setSmsSettings(prev => ({
                            ...prev,
                            templates: {...prev.templates, booking_confirmation: e.target.value}
                          }))}
                          placeholder="Góðan dag {customer_name}! Bókun staðfest fyrir {plate} þann {date} kl. {time}. Alvörubón ehf."
                        />
                      </div>

                      <div>
                        <Label>Áminning fyrir bókun</Label>
                        <textarea
                          className="w-full min-h-[80px] p-2 border rounded-md"
                          value={smsSettings.templates.booking_reminder}
                          onChange={(e) => setSmsSettings(prev => ({
                            ...prev,
                            templates: {...prev.templates, booking_reminder: e.target.value}
                          }))}
                          placeholder="Góðan dag {customer_name}! Áminning: Bíllinn þinn {plate} kemur í þrif á morgun kl. {time}. Alvörubón ehf."
                        />
                      </div>

                      <div>
                        <Label>Verk lokið</Label>
                        <textarea
                          className="w-full min-h-[80px] p-2 border rounded-md"
                          value={smsSettings.templates.job_completed}
                          onChange={(e) => setSmsSettings(prev => ({
                            ...prev,
                            templates: {...prev.templates, job_completed: e.target.value}
                          }))}
                          placeholder="Góðan dag {customer_name}! Bíllinn þinn {plate} er tilbúinn til afhendingar hjá Alvörubón ehf. Takk fyrir!"
                        />
                      </div>

                      <div>
                        <Label>Greiðsluslóð</Label>
                        <textarea
                          className="w-full min-h-[80px] p-2 border rounded-md"
                          value={smsSettings.templates.payment_link}
                          onChange={(e) => setSmsSettings(prev => ({
                            ...prev,
                            templates: {...prev.templates, payment_link: e.target.value}
                          }))}
                          placeholder="Góðan dag {customer_name}! Hér er greiðsluslóðin þín: {payment_url} Takk fyrir!"
                        />
                      </div>

                      <div>
                        <Label>Greiðsluáminning</Label>
                        <textarea
                          className="w-full min-h-[80px] p-2 border rounded-md"
                          value={smsSettings.templates.payment_reminder}
                          onChange={(e) => setSmsSettings(prev => ({
                            ...prev,
                            templates: {...prev.templates, payment_reminder: e.target.value}
                          }))}
                          placeholder="Góðan dag {customer_name}! Vinsamlegast greiddu fyrir þrif á {plate}. Greiðsluslóð: {payment_url}"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end pt-4 border-t">
                    <Button onClick={saveSmsSettings} disabled={savingSms}>
                      {savingSms ? 'Vistar...' : '💾 Vista SMS stillingar'}
                    </Button>
                  </div>

                  {/* Test SMS Section */}
                  <div className="border-2 border-green-200 rounded-lg p-4 bg-green-50 space-y-4 mt-6">
                    <h3 className="font-semibold text-green-900 flex items-center gap-2">
                      📤 Prófa SMS Sending
                    </h3>
                    <p className="text-sm text-green-700">
                      Sendu próf SMS til að staðfesta að tengingin virki
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="test_sms_phone">Símanúmer</Label>
                        <Input
                          id="test_sms_phone"
                          type="tel"
                          placeholder="+3548888900"
                          onChange={(e) => setTestSmsPhone(e.target.value)}
                        />
                        <div className="text-xs text-slate-500 mt-1">
                          Með landskóða, t.d. +3548888900
                        </div>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="test_sms_message">Skilaboð</Label>
                      <textarea
                        id="test_sms_message"
                        className="w-full min-h-[100px] p-2 border rounded-md"
                        placeholder="Þetta er próf SMS frá Alvörubón CRM kerfinu."
                        onChange={(e) => setTestSmsMessage(e.target.value)}
                      />
                    </div>

                    <Button 
                      onClick={async () => {
                        if (!testSmsPhone || !testSmsMessage) {
                          toast.error('Vinsamlegast fylltu út símanúmer og skilaboð');
                          return;
                        }
                        try {
                          setSendingTestSms(true);
                          await axios.post(`${API}/api/admin/send-test-sms`, {
                            phone_number: testSmsPhone,
                            message: testSmsMessage
                          }, { headers: { Authorization: `Bearer ${token}` } });
                          toast.success('Próf SMS sent!');
                          setTestSmsPhone('');
                          setTestSmsMessage('');
                        } catch (error) {
                          toast.error('Villa við að senda próf SMS: ' + (error.response?.data?.detail || error.message));
                        } finally {
                          setSendingTestSms(false);
                        }
                      }}
                      disabled={sendingTestSms}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {sendingTestSms ? 'Sendi...' : '📤 Senda próf SMS'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Já.is Sub-tab */}
            <TabsContent value="ja" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    📞 Já.is stillingar
                  </CardTitle>
                  <p className="text-sm text-slate-600">
                    Stillingar fyrir sjálfvirka símanúmera-uppflettingu í gegnum já.is
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                    <p className="text-sm text-yellow-900">
                      ⚡ <strong>Sjálfvirk símanúmeraleit:</strong> Þegar nýr viðskiptavinur er skráður, leitar kerfið sjálfkrafa að símanúmeri á já.is ef það vantar.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="ja-lookup-enabled-ja-tab"
                        checked={jaLookupSettings.enabled}
                        onChange={(e) => setJaLookupSettings({...jaLookupSettings, enabled: e.target.checked})}
                        className="w-4 h-4"
                      />
                      <Label htmlFor="ja-lookup-enabled-ja-tab">Virkja já.is uppflettingu</Label>
                    </div>

                    <div>
                      <Label>💰 Verð per leit (kr)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={jaLookupSettings.price_per_lookup}
                        onChange={(e) => setJaLookupSettings({...jaLookupSettings, price_per_lookup: parseInt(e.target.value) || 11})}
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Núverandi: {jaLookupSettings.price_per_lookup} kr per leit
                      </p>
                    </div>
                  </div>

                  <div className="mt-3">
                    <Label>🔑 JA.is API lykill (sláðu inn einu sinni)</Label>
                    {jaLookupSettings.has_api_key && (
                      <p className="text-xs text-slate-500 mb-1">
                        Lykill hefur þegar verið stilltur. Ef þú setur inn nýtt gildi hér, verður hann uppfærður.
                      </p>
                    )}
                    <Input
                      type="password"
                      placeholder={jaLookupSettings.has_api_key ? 'Sláðu inn nýjan JA.is lykil til að uppfæra' : 'Sláðu inn JA.is API lykil'}
                      value={jaLookupSettings.api_key}
                      onChange={(e) => setJaLookupSettings({...jaLookupSettings, api_key: e.target.value})}
                    />
                  </div>

                  <div className="mt-4 flex items-center space-x-3">
                    <Button 
                      onClick={saveJaLookupSettings}
                      disabled={savingJaSettings}
                      size="sm"
                      variant="outline"
                    >
                      {savingJaSettings ? '⏳ Vistar...' : '💾 Vista já.is stillingar'}
                    </Button>
                    {jaLookupSettings.has_api_key && (
                      <span className="text-xs text-green-700 flex items-center">
                        ✅ Lykill geymdur í kerfisstillingum
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Key Locations Sub-tab */}
            <TabsContent value="key-locations" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    🔑 Lyklastaðsetningar
                  </CardTitle>
                  <p className="text-sm text-slate-600">
                    Stjórna staðsetningum fyrir lykla bíla - þessar birtast í "Verk" flipanum
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
                  
                  {/* Add new location */}
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 border dark:border-slate-700 rounded-lg">
                    <h4 className="font-medium mb-3">Bæta við nýrri staðsetningu</h4>
                    <div className="flex gap-3">
                      <div className="w-20">
                        <Label htmlFor="new-key-icon">Tákn</Label>
                        <Input
                          id="new-key-icon"
                          value={newKeyLocation.icon}
                          onChange={(e) => setNewKeyLocation(prev => ({...prev, icon: e.target.value}))}
                          placeholder="🔑"
                          maxLength={2}
                          className="text-center text-xl"
                        />
                      </div>
                      <div className="flex-1">
                        <Label htmlFor="new-key-label">Nafn staðsetningar</Label>
                        <Input
                          id="new-key-label"
                          value={newKeyLocation.label}
                          onChange={(e) => setNewKeyLocation(prev => ({...prev, label: e.target.value}))}
                          placeholder="t.d. Í skrifborðsskúffu"
                        />
                      </div>
                      <div className="flex items-end">
                        <Button
                          onClick={async () => {
                            if (!newKeyLocation.label.trim()) {
                              toast.error('Nafn vantar');
                              return;
                            }
                            try {
                              setSavingKeyLocation(true);
                              const res = await axios.post(`${API}/settings/key-locations`, {
                                label: newKeyLocation.label.trim(),
                                icon: newKeyLocation.icon || '🔑'
                              });
                              setKeyLocations(res.data.locations);
                              setNewKeyLocation({ label: '', icon: '🔑' });
                              toast.success('Staðsetning bætt við');
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Villa við að bæta við');
                            } finally {
                              setSavingKeyLocation(false);
                            }
                          }}
                          disabled={savingKeyLocation}
                        >
                          {savingKeyLocation ? '...' : '+ Bæta við'}
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {/* List of locations */}
                  <div className="space-y-2">
                    <h4 className="font-medium">Núverandi staðsetningar</h4>
                    {keyLocations.length === 0 ? (
                      <p className="text-sm text-slate-500 py-4 text-center">Engar staðsetningar skráðar</p>
                    ) : (
                      <div className="space-y-2">
                        {keyLocations.sort((a, b) => (a.order || 0) - (b.order || 0)).map((location) => (
                          <div 
                            key={location.id} 
                            className="flex items-center gap-3 p-3 bg-white dark:bg-slate-700 border dark:border-slate-600 rounded-lg"
                          >
                            {editingKeyLocation?.id === location.id ? (
                              // Edit mode
                              <>
                                <Input
                                  value={editingKeyLocation.icon}
                                  onChange={(e) => setEditingKeyLocation(prev => ({...prev, icon: e.target.value}))}
                                  className="w-16 text-center text-xl"
                                  maxLength={2}
                                />
                                <Input
                                  value={editingKeyLocation.label}
                                  onChange={(e) => setEditingKeyLocation(prev => ({...prev, label: e.target.value}))}
                                  className="flex-1"
                                />
                                <Button
                                  size="sm"
                                  onClick={async () => {
                                    try {
                                      setSavingKeyLocation(true);
                                      const res = await axios.put(`${API}/settings/key-locations/${location.id}`, {
                                        label: editingKeyLocation.label,
                                        icon: editingKeyLocation.icon
                                      });
                                      setKeyLocations(res.data.locations);
                                      setEditingKeyLocation(null);
                                      toast.success('Staðsetning uppfærð');
                                    } catch (error) {
                                      toast.error(error.response?.data?.detail || 'Villa við að uppfæra');
                                    } finally {
                                      setSavingKeyLocation(false);
                                    }
                                  }}
                                  disabled={savingKeyLocation}
                                >
                                  Vista
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setEditingKeyLocation(null)}
                                >
                                  Hætta
                                </Button>
                              </>
                            ) : (
                              // View mode
                              <>
                                <span className="text-2xl w-10 text-center">{location.icon}</span>
                                <span className="flex-1 font-medium">{location.label}</span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setEditingKeyLocation({ ...location })}
                                >
                                  ✏️ Breyta
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={async () => {
                                    if (!confirm(`Eyða "${location.label}"?`)) return;
                                    try {
                                      const res = await axios.delete(`${API}/settings/key-locations/${location.id}`);
                                      setKeyLocations(res.data.locations);
                                      toast.success('Staðsetning eytt');
                                    } catch (error) {
                                      toast.error(error.response?.data?.detail || 'Villa við að eyða');
                                    }
                                  }}
                                >
                                  🗑️
                                </Button>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Preview */}
                  <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                    <h4 className="font-medium mb-2 text-amber-800 dark:text-amber-200">💡 Forskoðun</h4>
                    <p className="text-sm text-amber-700 dark:text-amber-300 mb-3">
                      Svona mun dropdown valmyndin líta út í "Verk" flipanum:
                    </p>
                    <div className="bg-white dark:bg-slate-800 border rounded-lg p-2 max-w-[200px]">
                      {keyLocations.map((loc) => (
                        <div key={loc.id} className="px-3 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-700 rounded">
                          {loc.icon} {loc.label}
                        </div>
                      ))}
                      <div className="px-3 py-2 text-sm text-red-600 hover:bg-slate-100 dark:hover:bg-slate-700 rounded">
                        ❌ Fjarlægja
                      </div>
                    </div>
                  </div>
                  
                </CardContent>
              </Card>
            </TabsContent>

            {/* Pause Statuses Sub-tab */}
            <TabsContent value="pause-statuses" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    ⏸️ Pásastöður
                  </CardTitle>
                  <p className="text-sm text-slate-600">
                    Stjórna stöðum sem starfsmenn geta valið þegar þeir setja verk í pása - t.d. "Skolaður", "Þrifinn að innan"
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
                  
                  {/* Add new pause status */}
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 border dark:border-slate-700 rounded-lg">
                    <h4 className="font-medium mb-3">Bæta við nýrri stöðu</h4>
                    <div className="flex gap-3">
                      <div className="w-20">
                        <Label htmlFor="new-pause-icon">Tákn</Label>
                        <Input
                          id="new-pause-icon"
                          value={newPauseStatus.icon}
                          onChange={(e) => setNewPauseStatus(prev => ({...prev, icon: e.target.value}))}
                          placeholder="⏸️"
                          maxLength={2}
                          className="text-center text-xl"
                        />
                      </div>
                      <div className="flex-1">
                        <Label htmlFor="new-pause-label">Nafn stöðu</Label>
                        <Input
                          id="new-pause-label"
                          value={newPauseStatus.label}
                          onChange={(e) => setNewPauseStatus(prev => ({...prev, label: e.target.value}))}
                          placeholder="t.d. Þakið þrifið"
                        />
                      </div>
                      <div className="flex items-end">
                        <Button
                          onClick={async () => {
                            if (!newPauseStatus.label.trim()) {
                              toast.error('Nafn vantar');
                              return;
                            }
                            try {
                              setSavingPauseStatus(true);
                              const res = await axios.post(`${API}/settings/pause-statuses`, {
                                label: newPauseStatus.label.trim(),
                                icon: newPauseStatus.icon || '⏸️'
                              });
                              setPauseStatuses(res.data.statuses);
                              setNewPauseStatus({ label: '', icon: '⏸️' });
                              toast.success('Staða bætt við');
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Villa við að bæta við');
                            } finally {
                              setSavingPauseStatus(false);
                            }
                          }}
                          disabled={savingPauseStatus}
                        >
                          {savingPauseStatus ? '...' : '+ Bæta við'}
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {/* List of pause statuses */}
                  <div className="space-y-2">
                    <h4 className="font-medium">Núverandi stöður</h4>
                    {pauseStatuses.length === 0 ? (
                      <p className="text-sm text-slate-500 py-4 text-center">Engar stöður skráðar</p>
                    ) : (
                      <div className="space-y-2">
                        {pauseStatuses.sort((a, b) => (a.order || 0) - (b.order || 0)).map((status) => (
                          <div 
                            key={status.id} 
                            className="flex items-center gap-3 p-3 bg-white dark:bg-slate-700 border dark:border-slate-600 rounded-lg"
                          >
                            {editingPauseStatus?.id === status.id ? (
                              // Edit mode
                              <>
                                <Input
                                  value={editingPauseStatus.icon}
                                  onChange={(e) => setEditingPauseStatus(prev => ({...prev, icon: e.target.value}))}
                                  className="w-16 text-center text-xl"
                                  maxLength={2}
                                />
                                <Input
                                  value={editingPauseStatus.label}
                                  onChange={(e) => setEditingPauseStatus(prev => ({...prev, label: e.target.value}))}
                                  className="flex-1"
                                />
                                <Button
                                  size="sm"
                                  onClick={async () => {
                                    try {
                                      setSavingPauseStatus(true);
                                      const res = await axios.put(`${API}/settings/pause-statuses/${status.id}`, {
                                        label: editingPauseStatus.label,
                                        icon: editingPauseStatus.icon
                                      });
                                      setPauseStatuses(res.data.statuses);
                                      setEditingPauseStatus(null);
                                      toast.success('Staða uppfærð');
                                    } catch (error) {
                                      toast.error(error.response?.data?.detail || 'Villa við að uppfæra');
                                    } finally {
                                      setSavingPauseStatus(false);
                                    }
                                  }}
                                  disabled={savingPauseStatus}
                                >
                                  Vista
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setEditingPauseStatus(null)}
                                >
                                  Hætta
                                </Button>
                              </>
                            ) : (
                              // View mode
                              <>
                                <span className="text-2xl w-10 text-center">{status.icon}</span>
                                <span className="flex-1 font-medium">{status.label}</span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setEditingPauseStatus({ ...status })}
                                >
                                  ✏️ Breyta
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={async () => {
                                    if (!confirm(`Eyða "${status.label}"?`)) return;
                                    try {
                                      const res = await axios.delete(`${API}/settings/pause-statuses/${status.id}`);
                                      setPauseStatuses(res.data.statuses);
                                      toast.success('Stöðu eytt');
                                    } catch (error) {
                                      toast.error(error.response?.data?.detail || 'Villa við að eyða');
                                    }
                                  }}
                                >
                                  🗑️
                                </Button>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Preview */}
                  <div className="p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                    <h4 className="font-medium mb-2 text-purple-800 dark:text-purple-200">💡 Forskoðun</h4>
                    <p className="text-sm text-purple-700 dark:text-purple-300 mb-3">
                      Svona mun valmyndin líta út þegar starfsmaður smellir á "⏸️ Pása" í "Verk" flipanum:
                    </p>
                    <div className="bg-white dark:bg-slate-800 border rounded-lg p-2 max-w-[200px]">
                      <p className="text-xs text-slate-500 px-3 py-1">Hvar er bíllinn?</p>
                      {pauseStatuses.map((s) => (
                        <div key={s.id} className="px-3 py-2 text-sm hover:bg-purple-100 dark:hover:bg-purple-700 rounded">
                          {s.icon} {s.label}
                        </div>
                      ))}
                      <hr className="my-1" />
                      <div className="px-3 py-2 text-sm text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 rounded">
                        ❌ Hætta við
                      </div>
                    </div>
                  </div>
                  
                </CardContent>
              </Card>
            </TabsContent>

            {/* MyPOS Sub-tab */}
            <TabsContent value="mypos" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    💳 MyPOS Online Greiðslur
                  </CardTitle>
                  <p className="text-sm text-slate-600">
                    Stillingar fyrir MyPOS online greiðslur - viðskiptavinir geta greitt með korti í gegnum netið
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
                  
                  {/* Enable/Disable Toggle */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <div className="font-medium">Virkja MyPOS greiðslur</div>
                      <div className="text-sm text-slate-600">
                        Gera viðskiptavinum kleift að greiða með korti í gegnum MyPOS
                      </div>
                    </div>
                    <Checkbox
                      checked={myposSettings.enabled}
                      onCheckedChange={(checked) => 
                        setMyposSettings(prev => ({...prev, enabled: checked}))
                      }
                    />
                  </div>

                  {/* Environment Selection */}
                  <div>
                    <Label>Umhverfi</Label>
                    <select
                      className="w-full mt-1 px-3 py-2 border rounded-md"
                      value={myposSettings.environment}
                      onChange={(e) => setMyposSettings(prev => ({...prev, environment: e.target.value}))}
                      disabled={!myposSettings.enabled}
                    >
                      <option value="production">Production (raunverulegur)</option>
                      <option value="test">Test/Sandbox (prófunarumhverfi)</option>
                    </select>
                    <div className="text-xs text-slate-500 mt-1">
                      {myposSettings.environment === 'production' 
                        ? '⚠️ Raunverulegar greiðslur verða unnar'
                        : '✅ Prófunarumhverfi - engar raunverulegar greiðslur'}
                    </div>
                  </div>

                  {/* Store ID (SID) */}
                  <div>
                    <Label htmlFor="mypos_sid">Store ID (SID)</Label>
                    <Input
                      id="mypos_sid"
                      value={myposSettings.sid}
                      onChange={(e) => setMyposSettings(prev => ({...prev, sid: e.target.value}))}
                      placeholder="hnxNNP9tiCHYVlmomtI5w9wj"
                      disabled={!myposSettings.enabled}
                    />
                    <div className="text-xs text-slate-500 mt-1">
                      Store ID frá myPOS dashboard (Online Stores)
                    </div>
                  </div>

                  {/* Wallet Number */}
                  <div>
                    <Label htmlFor="mypos_wallet">Wallet Number (Client Number)</Label>
                    <Input
                      id="mypos_wallet"
                      value={myposSettings.wallet_number}
                      onChange={(e) => setMyposSettings(prev => ({...prev, wallet_number: e.target.value}))}
                      placeholder="Rgk2popYxM5jOFvlfUHe40MGZvPIaaF3TSLMWDHBAHHEsGxr"
                      disabled={!myposSettings.enabled}
                    />
                    <div className="text-xs text-slate-500 mt-1">
                      Wallet/Client Number frá myPOS reikningnum þínum
                    </div>
                  </div>

                  {/* Key Index */}
                  <div>
                    <Label htmlFor="mypos_key_index">Key Index</Label>
                    <Input
                      id="mypos_key_index"
                      type="number"
                      value={myposSettings.key_index}
                      onChange={(e) => setMyposSettings(prev => ({...prev, key_index: parseInt(e.target.value) || 2}))}
                      placeholder="2"
                      disabled={!myposSettings.enabled}
                      min="1"
                      max="10"
                    />
                    <div className="text-xs text-slate-500 mt-1">
                      Key Index númer sem þú fékkst þegar þú hleðst upp public certificate í myPOS dashboard
                    </div>
                  </div>

                  {/* RSA Key Status */}
                  <div className="border rounded-lg p-4 bg-slate-50">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium">RSA Key Status</span>
                      {myposSettings.has_private_key ? (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                          ✅ Lyklar til staðar
                        </span>
                      ) : (
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">
                          ❌ Lyklar vantar
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-600 space-y-1">
                      <p>• Private key (/app/backend/keys/mypos_private_key.pem): {myposSettings.has_private_key ? '✅' : '❌'}</p>
                      <p>• Public certificate verður að vera uploadað í myPOS dashboard</p>
                      <p>• RSA lyklarnir eru notaðir til að skrifa undir greiðslubeiðnir</p>
                    </div>
                  </div>

                  {/* How it Works */}
                  <div className="border-l-4 border-blue-400 bg-blue-50 p-4 rounded">
                    <h4 className="font-medium text-blue-900 mb-2">📘 Hvernig virkar þetta?</h4>
                    <div className="text-sm text-blue-800 space-y-2">
                      <p><strong>1.</strong> Viðskiptavinur velur að greiða með korti</p>
                      <p><strong>2.</strong> Hann er sendur á örugga MyPOS greiðslusíðu</p>
                      <p><strong>3.</strong> Færir inn kortaupplýsingar á MyPOS síðunni</p>
                      <p><strong>4.</strong> MyPOS vinnur greiðsluna og sendir hann til baka</p>
                      <p><strong>5.</strong> Verkið er sjálfkrafa merkt sem greitt í kerfinu</p>
                    </div>
                  </div>

                  {/* Security Notice */}
                  <div className="border-l-4 border-yellow-400 dark:border-yellow-600 bg-yellow-50 dark:bg-yellow-900/30 p-4 rounded">
                    <h4 className="font-medium text-yellow-900 dark:text-yellow-200 mb-2">🔒 Öryggi</h4>
                    <div className="text-sm text-yellow-800 dark:text-yellow-300 space-y-1">
                      <p>• Engar kortaupplýsingar eru vistaðar í þessu kerfi</p>
                      <p>• Allar kortaupplýsingar eru unnar af MyPOS (PCI compliant)</p>
                      <p>• RSA digital signatures tryggja öryggi</p>
                      <p>• Greiðslur eru með 3D Secure þegar það á við</p>
                    </div>
                  </div>

                  {/* Save Button */}
                  <Button 
                    onClick={saveMyposSettings}
                    disabled={savingMyposSettings}
                    className="w-full"
                  >
                    {savingMyposSettings ? '⏳ Vista...' : '💾 Vista MyPOS stillingar'}
                  </Button>

                </CardContent>
              </Card>
            </TabsContent>

            {/* Integration Sub-tab */}
            <TabsContent value="integration" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    🔄 Val á bókhaldskerfis
                  </CardTitle>
                  <p className="text-sm text-slate-600">
                    Veldu hvaða bókhaldskerfi á að nota fyrir reikninga og greiðslur
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
                  
                  {/* Current Status */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="font-medium text-blue-900 mb-2">Núverandi kerfi:</div>
                    <div className="text-2xl font-bold text-blue-700">
                      {integrationSettings.active_integration === 'payday' ? '💳 Payday' : '📊 DK Plus ERP'}
                    </div>
                  </div>

                  {/* Integration Toggle Options */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    
                    {/* Payday Option */}
                    <Card className={`cursor-pointer transition-all ${integrationSettings.active_integration === 'payday' ? 'border-2 border-blue-600 bg-blue-50' : 'border hover:border-slate-400'}`}>
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="text-4xl">💳</div>
                          {integrationSettings.active_integration === 'payday' && (
                            <span className="text-green-600 font-bold text-sm">✅ VIRKT</span>
                          )}
                        </div>
                        <h3 className="text-xl font-bold mb-2">Payday</h3>
                        <p className="text-sm text-slate-600 mb-4">
                          Íslenskt greiðslukerfi
                        </p>
                        <Button 
                          onClick={() => saveIntegrationSettings('payday')}
                          disabled={savingIntegration || integrationSettings.active_integration === 'payday'}
                          className="w-full"
                          variant={integrationSettings.active_integration === 'payday' ? 'outline' : 'default'}
                        >
                          {integrationSettings.active_integration === 'payday' ? 'Núverandi' : 'Skipta'}
                        </Button>
                      </CardContent>
                    </Card>

                    {/* DK Plus Option */}
                    <Card className={`cursor-pointer transition-all ${integrationSettings.active_integration === 'dk' ? 'border-2 border-green-600 bg-green-50' : 'border hover:border-slate-400'}`}>
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="text-4xl">📊</div>
                          {integrationSettings.active_integration === 'dk' && (
                            <span className="text-green-600 font-bold text-sm">✅ VIRKT</span>
                          )}
                        </div>
                        <h3 className="text-xl font-bold mb-2">DK Plus ERP</h3>
                        <p className="text-sm text-slate-600 mb-4">
                          Íslenskt ERP kerfi
                        </p>
                        <Button 
                          onClick={() => saveIntegrationSettings('dk')}
                          disabled={savingIntegration || integrationSettings.active_integration === 'dk'}
                          className="w-full"
                          variant={integrationSettings.active_integration === 'dk' ? 'outline' : 'default'}
                        >
                          {integrationSettings.active_integration === 'dk' ? 'Núverandi' : 'Skipta'}
                        </Button>
                      </CardContent>
                    </Card>

                  </div>

                </CardContent>
              </Card>
              
              {/* Admin Password Dialog for Integration Switching */}
              {showIntegrationPasswordDialog && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <Card className="w-full max-w-md">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        🔐 Admin staðfesting
                      </CardTitle>
                      <p className="text-sm text-slate-600">
                        Sláðu inn lykilorð til að skipta um bókhaldskerfi
                      </p>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm text-blue-800">
                          <strong>Skipta yfir í:</strong> {pendingIntegration === 'payday' ? '💳 Payday' : '📊 DK Plus ERP'}
                        </p>
                      </div>
                      
                      <div>
                        <Label htmlFor="integration-password">Admin lykilorð</Label>
                        <Input
                          id="integration-password"
                          type="password"
                          value={integrationPassword}
                          onChange={(e) => setIntegrationPassword(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              verifyIntegrationPassword();
                            }
                          }}
                          placeholder="Sláðu inn lykilorð"
                          className="mt-1"
                          autoFocus
                        />
                      </div>

                      <div className="flex space-x-2">
                        <Button
                          onClick={verifyIntegrationPassword}
                          disabled={savingIntegration || !integrationPassword.trim()}
                          className="flex-1"
                        >
                          {savingIntegration ? 'Staðfestir...' : '✅ Staðfesta'}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setShowIntegrationPasswordDialog(false);
                            setIntegrationPassword('');
                            setPendingIntegration(null);
                          }}
                          disabled={savingIntegration}
                        >
                          Hætta við
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>

            {/* Users Sub-tab */}
            <TabsContent value="users" className="space-y-4">
              <UserManagement />
            </TabsContent>

          </Tabs>
        </TabsContent>

        {/* Opening Hours Tab - Nested tabs */}
        <TabsContent value="hours" className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <h3 className="font-medium text-blue-900 mb-2">🕐 Opnunartímar</h3>
            <p className="text-sm text-blue-700">
              Opnunartímar og lokaðir tímar
            </p>
          </div>

          {/* Sub-tabs for opening hours sections */}
          <Tabs defaultValue="hours">
            <TabsList className="flex-wrap h-auto">
              <TabsTrigger value="hours">🕐 Opnunartímar vikudaga</TabsTrigger>
              <TabsTrigger value="blocked">🔒 Lokaðir tímar / Frídagar</TabsTrigger>
            </TabsList>
            
            {/* Opening Hours Sub-tab */}
            <TabsContent value="hours" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    🕐 Opnunartímar og Tímaáætlun
                  </CardTitle>
                  <p className="text-sm text-slate-600">
                    Stjórna opnunartímum og lengd hverrar þjónustu
                  </p>
                </CardHeader>
                <CardContent>
                  <OpeningHoursManager settings={settings} onSave={null} />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Blocked Times Sub-tab */}
            <TabsContent value="blocked" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    🔒 Lokaðir tímar
                  </CardTitle>
                  <p className="text-sm text-slate-600">
                    Stilltu daga og tíma sem eru lokaðir fyrir nýjar bókanir
                  </p>
                </CardHeader>
                <CardContent>
                  <BlockedTimesManager />
                </CardContent>
              </Card>
            </TabsContent>

          </Tabs>
        </TabsContent>

        {/* Invoice Settings Tab - Nested tabs for Payday/DK/Defaults */}
        <TabsContent value="invoices" className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <h3 className="font-medium text-blue-900 mb-2">💰 Reikningsstillingar</h3>
            <p className="text-sm text-blue-700">
              Payday og DK Plus stillingar og sjálfgefnir eindagar
            </p>
          </div>

          {/* Sub-tabs for invoice settings */}
          {/* Use controlled tabs - always show DK first, then Payday */}
          <Tabs value={activeInvoiceTab} onValueChange={setActiveInvoiceTab}>
            <TabsList className="flex-wrap h-auto">
              <TabsTrigger value="dk" className={integrationSettings.active_integration === 'dk' ? 'border-2 border-green-600' : ''}>
                📊 DK Plus {integrationSettings.active_integration === 'dk' && <span className="ml-2 text-xs bg-green-600 text-white px-2 py-0.5 rounded-full">VIRKT</span>}
              </TabsTrigger>
              <TabsTrigger value="payday" className={integrationSettings.active_integration === 'payday' ? 'border-2 border-green-600' : ''}>
                💳 Payday {integrationSettings.active_integration === 'payday' && <span className="ml-2 text-xs bg-green-600 text-white px-2 py-0.5 rounded-full">VIRKT</span>}
              </TabsTrigger>
              <TabsTrigger value="rapyd">
                🔗 Rapyd
              </TabsTrigger>
              <TabsTrigger value="defaults">📅 Sjálfgefnar stillingar</TabsTrigger>
            </TabsList>
            
            {/* DK Plus Sub-tab */}
            <TabsContent value="dk" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    📊 DK Plus API Stillingar
                  </CardTitle>
                  <p className="text-sm text-slate-600">
                    Tengistillingar fyrir DK Plus ERP kerfi
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
              
              {/* Connection Status */}
              <div className="p-4 bg-slate-50 dark:bg-slate-800 border dark:border-slate-700 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium dark:text-slate-200">Tengingarstaða</div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      {dkSettings.enabled ? '✅ Virkt' : '❌ Óvirkt'}
                      {dkSettings.use_mock && <span className="ml-2 text-orange-600 dark:text-orange-400">🎭 Mock mode</span>}
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        checked={dkSettings.use_mock}
                        onCheckedChange={(checked) => 
                          setDkSettings(prev => ({...prev, use_mock: checked}))
                        }
                      />
                      <Label>Mock Mode (test)</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        checked={dkSettings.enabled}
                        onCheckedChange={(checked) => 
                          setDkSettings(prev => ({...prev, enabled: checked}))
                        }
                      />
                      <Label>Virkja DK Plus</Label>
                    </div>
                  </div>
                </div>
              </div>

              {/* DK Plus Configuration */}
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label htmlFor="dk_api_url">API URL *</Label>
                  <Input
                    id="dk_api_url"
                    value={dkSettings.api_url}
                    onChange={(e) => setDkSettings(prev => ({...prev, api_url: e.target.value}))}
                    placeholder="https://api.dkplus.is/api/v1"
                  />
                  <div className="text-xs text-slate-500 mt-1">
                    DK Plus API grunnslóð
                  </div>
                </div>

                <div>
                  <Label htmlFor="dk_api_key">API Key *</Label>
                  <Input
                    id="dk_api_key"
                    type="password"
                    value={dkSettings.api_key}
                    onChange={(e) => setDkSettings(prev => ({...prev, api_key: e.target.value}))}
                    placeholder="Sláðu inn DK Plus API lykil"
                  />
                  <div className="text-xs text-slate-500 mt-1">
                    API lykill frá DK Plus - <a href="https://dkplus.is" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">fá lykil</a>
                  </div>
                </div>

                <div>
                  <Label htmlFor="dk_salesperson">Sölumaður</Label>
                  <Input
                    id="dk_salesperson"
                    value={dkSettings.salesperson || 'Rafrænn'}
                    onChange={(e) => setDkSettings(prev => ({...prev, salesperson: e.target.value}))}
                    placeholder="Rafrænn"
                  />
                  <div className="text-xs text-slate-500 mt-1">
                    Sjálfgefinn sölumaður fyrir alla DK reikninga (t.d. "Rafrænn")
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <Button onClick={testDkConnection} variant="outline" disabled={savingDkSettings}>
                  {savingDkSettings ? 'Prófar...' : '🧪 Prófa tengingu'}
                </Button>
                <Button onClick={saveDkSettings} disabled={savingDkSettings}>
                  {savingDkSettings ? 'Vistar...' : '💾 Vista stillingar'}
                </Button>
              </div>

              {/* Info Box */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>💡 Ábending:</strong> Ef þú ert ekki með API lykil, kveiktu á "Mock Mode" til að prófa kerfið. 
                  Þá verða búnir til test reikningar í stað raunverulegra DK reikninga.
                </p>
              </div>

            </CardContent>
              </Card>
            </TabsContent>

            {/* Payday Sub-tab */}
            <TabsContent value="payday" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    💳 Payday API Stillingar
                  </CardTitle>
                  <p className="text-sm text-slate-600">
                    Tengistillingar fyrir Payday
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
              
              {/* Connection Status */}
              <div className="p-4 bg-slate-50 dark:bg-slate-800 border dark:border-slate-700 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium dark:text-slate-200">Tengingarstaða</div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      {paydaySettings.enabled ? '✅ Virkt' : '❌ Óvirkt'}
                      {paydaySettings.use_mock && <span className="ml-2 text-orange-600 dark:text-orange-400">🎭 Mock mode</span>}
                      {paydaySettings.sandbox_mode && <span className="ml-2 text-blue-600 dark:text-blue-400">🧪 Sandbox</span>}
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        checked={paydaySettings.use_mock}
                        onCheckedChange={(checked) => 
                          setPaydaySettings(prev => ({...prev, use_mock: checked}))
                        }
                      />
                      <Label>Mock Mode (test)</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        checked={paydaySettings.sandbox_mode}
                        onCheckedChange={(checked) => 
                          setPaydaySettings(prev => ({...prev, sandbox_mode: checked}))
                        }
                      />
                      <Label>Sandbox Mode</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        checked={paydaySettings.enabled}
                        onCheckedChange={(checked) => 
                          setPaydaySettings(prev => ({...prev, enabled: checked}))
                        }
                      />
                      <Label>Virkja Payday</Label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payday Configuration */}
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label htmlFor="payday_api_url">API URL *</Label>
                  <Input
                    id="payday_api_url"
                    value={paydaySettings.api_url}
                    onChange={(e) => setPaydaySettings(prev => ({...prev, api_url: e.target.value}))}
                    placeholder="https://api.test.payday.is"
                  />
                  <div className="text-xs text-slate-500 mt-1">
                    Payday API grunnslóð (test eða production)
                  </div>
                </div>

                <div>
                  <Label htmlFor="payday_client_id">Client ID *</Label>
                  <Input
                    id="payday_client_id"
                    value={paydaySettings.client_id}
                    onChange={(e) => setPaydaySettings(prev => ({...prev, client_id: e.target.value}))}
                    placeholder="Sláðu inn Payday Client ID"
                  />
                  <div className="text-xs text-slate-500 mt-1">
                    OAuth2 Client ID frá Payday
                  </div>
                </div>

                <div>
                  <Label htmlFor="payday_client_secret">Client Secret *</Label>
                  <Input
                    id="payday_client_secret"
                    type="password"
                    value={paydaySettings.client_secret}
                    onChange={(e) => setPaydaySettings(prev => ({...prev, client_secret: e.target.value}))}
                    placeholder="Sláðu inn Payday Client Secret"
                  />
                  <div className="text-xs text-slate-500 mt-1">
                    OAuth2 Client Secret frá Payday
                  </div>
                </div>

                {/* Payment Terms */}
                <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t">
                  <div>
                    <Label htmlFor="payday_due_days">Gjalddagi (dagar)</Label>
                    <Input
                      id="payday_due_days"
                      type="number"
                      min="0"
                      max="90"
                      value={paydaySettings.due_days}
                      onChange={(e) => setPaydaySettings(prev => ({...prev, due_days: parseInt(e.target.value) || 0}))}
                    />
                    <div className="text-xs text-slate-500 mt-1">
                      Fjöldi daga frá útgáfu þar til gjalddagi er. 0 = sama dag.
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="payday_final_due_days">Eindagi (dagar)</Label>
                    <Input
                      id="payday_final_due_days"
                      type="number"
                      min="1"
                      max="90"
                      value={paydaySettings.final_due_days}
                      onChange={(e) => setPaydaySettings(prev => ({...prev, final_due_days: parseInt(e.target.value) || 1}))}
                    />
                    <div className="text-xs text-slate-500 mt-1">
                      Fjöldi daga frá útgáfu þar til eindagi er.
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <Button onClick={savePaydaySettings} disabled={savingPaydaySettings}>
                  {savingPaydaySettings ? 'Vistar...' : '💾 Vista stillingar'}
                </Button>
              </div>

              {/* Info Box */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>💡 Ábending:</strong> Ef þú ert ekki með Payday aðgang, kveiktu á "Mock Mode" til að prófa kerfið. 
                  Þá verða búnir til test reikningar í stað raunverulegra Payday reikninga.
                </p>
              </div>

            </CardContent>
            </Card>
            </TabsContent>

            {/* Rapyd Sub-tab */}
            <TabsContent value="rapyd" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    🔗 Rapyd API Stillingar
                  </CardTitle>
                  <p className="text-sm text-slate-600">
                    Tengistillingar fyrir Rapyd greiðslulinka
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
              
              {/* Connection Status */}
              <div className="p-4 bg-slate-50 dark:bg-slate-800 border dark:border-slate-700 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium dark:text-slate-200">Tengingarstaða</div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      {rapydSettings.enabled ? '✅ Virkt' : '❌ Óvirkt'}
                      {rapydSettings.use_mock && <span className="ml-2 text-orange-600 dark:text-orange-400">🎭 Mock mode</span>}
                      {rapydSettings.sandbox_mode && <span className="ml-2 text-blue-600 dark:text-blue-400">🧪 Sandbox</span>}
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        checked={rapydSettings.use_mock}
                        onCheckedChange={(checked) => 
                          setRapydSettings(prev => ({...prev, use_mock: checked}))
                        }
                      />
                      <Label>Mock Mode (test)</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        checked={rapydSettings.sandbox_mode}
                        onCheckedChange={(checked) => 
                          setRapydSettings(prev => ({...prev, sandbox_mode: checked}))
                        }
                      />
                      <Label>Sandbox Mode</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        checked={rapydSettings.enabled}
                        onCheckedChange={(checked) => 
                          setRapydSettings(prev => ({...prev, enabled: checked}))
                        }
                      />
                      <Label>Virkja Rapyd</Label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Rapyd Configuration */}
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label htmlFor="rapyd_base_url">API URL *</Label>
                  <Input
                    id="rapyd_base_url"
                    value={rapydSettings.base_url}
                    onChange={(e) => setRapydSettings(prev => ({...prev, base_url: e.target.value}))}
                    placeholder="https://api.rapyd.net"
                  />
                  <div className="text-xs text-slate-500 mt-1">
                    Rapyd API grunnslóð (sandbox eða production)
                  </div>
                </div>

                <div>
                  <Label htmlFor="rapyd_access_key">Access Key *</Label>
                  <Input
                    id="rapyd_access_key"
                    value={rapydSettings.access_key}
                    onChange={(e) => setRapydSettings(prev => ({...prev, access_key: e.target.value}))}
                    placeholder="Sláðu inn Rapyd Access Key"
                  />
                  <div className="text-xs text-slate-500 mt-1">
                    Rapyd Access Key frá Rapyd Dashboard
                  </div>
                </div>

                <div>
                  <Label htmlFor="rapyd_secret_key">Secret Key *</Label>
                  <Input
                    id="rapyd_secret_key"
                    type="password"
                    value={rapydSettings.secret_key}
                    onChange={(e) => setRapydSettings(prev => ({...prev, secret_key: e.target.value}))}
                    placeholder="Sláðu inn Rapyd Secret Key"
                  />
                  <div className="text-xs text-slate-500 mt-1">
                    Rapyd Secret Key frá Rapyd Dashboard
                  </div>
                </div>

                <div>
                  <Label htmlFor="rapyd_webhook_secret">Webhook Secret</Label>
                  <Input
                    id="rapyd_webhook_secret"
                    type="password"
                    value={rapydSettings.webhook_secret}
                    onChange={(e) => setRapydSettings(prev => ({...prev, webhook_secret: e.target.value}))}
                    placeholder="Sláðu inn Webhook Secret"
                  />
                  <div className="text-xs text-slate-500 mt-1">
                    Webhook Secret fyrir signature verification
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <Button onClick={saveRapydSettings} disabled={savingRapydSettings}>
                  {savingRapydSettings ? 'Vistar...' : '💾 Vista stillingar'}
                </Button>
              </div>

              {/* Info Box */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>💡 Ábending:</strong> Rapyd er notað fyrir greiðslulinka í SMS. Ef þú ert ekki með Rapyd aðgang, 
                  kveiktu á "Mock Mode" til að prófa kerfið.
                </p>
              </div>

            </CardContent>
            </Card>
            </TabsContent>

            {/* Invoice Defaults Sub-tab */}
            <TabsContent value="defaults" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    📅 Reikningsstillingar
                  </CardTitle>
                  <p className="text-sm text-slate-600">
                    Stillingar fyrir gjaldadaga og eindaga
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="invoice_due_days">Gjaldagi (dagar)</Label>
                  <Input
                    id="invoice_due_days"
                    type="number"
                    min="0"
                    max="90"
                    value={invoiceSettings.invoice_due_days}
                    onChange={(e) => setInvoiceSettings(prev => ({...prev, invoice_due_days: Number(e.target.value)}))}
                    placeholder="0"
                  />
                  <div className="text-xs text-slate-500 mt-1">
                    Fjöldi daga frá útgáfu þar til gjaldagi er. 0 = sama dag.
                  </div>
                </div>

                <div>
                  <Label htmlFor="invoice_final_due_days">Eindagi (dagar)</Label>
                  <Input
                    id="invoice_final_due_days"
                    type="number"
                    min="0"
                    max="90"
                    value={invoiceSettings.invoice_final_due_days}
                    onChange={(e) => setInvoiceSettings(prev => ({...prev, invoice_final_due_days: Number(e.target.value)}))}
                    placeholder="7"
                  />
                  <div className="text-xs text-slate-500 mt-1">
                    Fjöldi daga frá útgáfu þar til eindagi er.
                  </div>
                </div>
              </div>

              <Button onClick={saveInvoiceSettings} disabled={savingInvoiceSettings}>
                {savingInvoiceSettings ? 'Vistar...' : '💾 Vista stillingar'}
              </Button>

              {/* Info Box */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>💡 Ábending:</strong> Þessar stillingar eru sjálfgefnar fyrir alla reikninga. 
                  Þú getur breytt eindaga í greiðsluglugga þegar þú merkir verk sem greitt.
                </p>
              </div>
                </CardContent>
              </Card>
            </TabsContent>

          </Tabs>
        </TabsContent>


        {/* Integrations Tab - API Integrations */}
        <TabsContent value="integrations" className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <h3 className="font-medium text-blue-900 mb-2">🔌 Tengingar við utanaðkomandi kerfi</h3>
            <p className="text-sm text-blue-700">
              Stillingar fyrir tengingar við Rogg.is, SMS, Email og önnur kerfi
            </p>
          </div>

          {/* Rogg.is Integration Card */}
          <RoggIntegrationSettings />
        </TabsContent>


        {/* Other Settings Tab - Nested tabs */}
        <TabsContent value="other" className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <h3 className="font-medium text-blue-900 mb-2">🔧 Aðrar stillingar</h3>
            <p className="text-sm text-blue-700">
              Tölfræði, þrif, límmiðar og lánsbílar
            </p>
          </div>

          {/* Sub-tabs for other settings sections */}
          <Tabs defaultValue="statistics">
            <TabsList className="flex-wrap h-auto">
              <TabsTrigger value="statistics">📈 Tölfræði</TabsTrigger>
              <TabsTrigger value="cleanup">🗑️ Þrif og eyðing</TabsTrigger>
              <TabsTrigger value="labels">🖨️ Límmiða prentun</TabsTrigger>
              <TabsTrigger value="loan-cars">🚗 Lánsbílar</TabsTrigger>
              <TabsTrigger value="qr-code">📱 QR Kóði</TabsTrigger>
            </TabsList>
            
            {/* Statistics Sub-tab */}
            <TabsContent value="statistics" className="space-y-4">
              {/* Material Cost Statistics Section */}
              <MaterialCostStatistics />
              
              {/* Cache Settings Section */}
              <Card>
                <CardHeader>
                  <CardTitle>🗄️ Cache og Uppflettinga Stillingar</CardTitle>
                  <p className="text-sm text-slate-600">
                    Hversu lengi geyma uppflettingar í cache og kostnaður per leit
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-900">
                      💡 <strong>Cache sparar peninga:</strong> Uppflettingar úr cache kosta ekkert. Aðeins API uppflettingar kosta.
                    </p>
                  </div>
                  
                  <div className="border-b pb-4">
                    <h3 className="font-medium mb-3">💰 Verð per uppflettingu (m/VSK)</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>🚗 Bílnúmer (kr)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="1000"
                          value={cacheSettings.vehicle_lookup_price}
                          onChange={(e) => setCacheSettings({...cacheSettings, vehicle_lookup_price: parseInt(e.target.value) || 41})}
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Núverandi: {cacheSettings.vehicle_lookup_price} kr per leit
                        </p>
                      </div>
                      <div>
                        <Label>👤 Kennitala (kr)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="1000"
                          value={cacheSettings.person_lookup_price}
                          onChange={(e) => setCacheSettings({...cacheSettings, person_lookup_price: parseInt(e.target.value) || 23})}
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Núverandi: {cacheSettings.person_lookup_price} kr per leit
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="font-medium mb-3">⏱️ Cache tími</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>🚗 Bílnúmer cache (dagar)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="365"
                          value={cacheSettings.vehicle_cache_days}
                          onChange={(e) => setCacheSettings({...cacheSettings, vehicle_cache_days: parseInt(e.target.value) || 0})}
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          0 = endalaust, 1-365 = dagar. <strong>Núna: {cacheSettings.vehicle_cache_days === 0 ? 'Endalaust' : `${cacheSettings.vehicle_cache_days} dagar`}</strong>
                        </p>
                      </div>
                      <div>
                        <Label>👤 Kennitölur cache (dagar)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="365"
                          value={cacheSettings.person_cache_days}
                          onChange={(e) => setCacheSettings({...cacheSettings, person_cache_days: parseInt(e.target.value) || 0})}
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          0 = endalaust, 1-365 = dagar. <strong>Núna: {cacheSettings.person_cache_days === 0 ? 'Endalaust' : `${cacheSettings.person_cache_days} dagar`}</strong>
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 pt-4 border-t">
                    <Button 
                      onClick={saveCacheSettings}
                      disabled={savingCacheSettings}
                      className="flex-1"
                    >
                      {savingCacheSettings ? '⏳ Vista...' : '💾 Vista stillingar'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              <LookupStatistics />
            </TabsContent>

            {/* Cleanup Sub-tab */}
            <TabsContent value="cleanup" className="space-y-4">
              <CleanupManagement />
            </TabsContent>

            {/* Labels Sub-tab */}
            <TabsContent value="labels" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    🖨️ Límiða prentun
                  </CardTitle>
                  <p className="text-sm text-slate-600">
                    Stilltu stærð og útlit á límiðum fyrir lakkvörur í birgðum
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-900">
                      💡 <strong>Ábending:</strong> Fyrir bestu niðurstöðu, notaðu litaprentara. Litur preview er prentaður á límiðanum.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Breidd (mm)</Label>
                      <Input
                        type="number"
                        min="10"
                        max="300"
                        value={labelSettings.width}
                        onChange={(e) => setLabelSettings({...labelSettings, width: parseInt(e.target.value) || 100})}
                      />
                      <p className="text-xs text-slate-500 mt-1">10-300 mm</p>
                    </div>
                    <div>
                      <Label>Hæð (mm)</Label>
                      <Input
                        type="number"
                        min="10"
                        max="300"
                        value={labelSettings.height}
                        onChange={(e) => setLabelSettings({...labelSettings, height: parseInt(e.target.value) || 50})}
                      />
                      <p className="text-xs text-slate-500 mt-1">10-300 mm</p>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h3 className="font-medium mb-3">📏 Font stærðir</h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Litakóði (px)</Label>
                        <Input
                          type="number"
                          min="12"
                          max="72"
                          value={labelSettings.font_size_code}
                          onChange={(e) => setLabelSettings({...labelSettings, font_size_code: parseInt(e.target.value) || 32})}
                        />
                      </div>
                      <div>
                        <Label>Nafn lakks (px)</Label>
                        <Input
                          type="number"
                          min="8"
                          max="48"
                          value={labelSettings.font_size_name}
                          onChange={(e) => setLabelSettings({...labelSettings, font_size_name: parseInt(e.target.value) || 16})}
                        />
                      </div>
                      <div>
                        <Label>Smáatriði (px)</Label>
                        <Input
                          type="number"
                          min="6"
                          max="24"
                          value={labelSettings.font_size_details}
                          onChange={(e) => setLabelSettings({...labelSettings, font_size_details: parseInt(e.target.value) || 12})}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h3 className="font-medium mb-3">👁️ Sýna á límiða</h3>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={labelSettings.show_color_box}
                          onChange={(e) => setLabelSettings({...labelSettings, show_color_box: e.target.checked})}
                          className="rounded"
                        />
                        <span className="text-sm">Litur preview (reitur með lit)</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={labelSettings.show_manufacturer}
                          onChange={(e) => setLabelSettings({...labelSettings, show_manufacturer: e.target.checked})}
                          className="rounded"
                        />
                        <span className="text-sm">Framleiðandi (BMW, Toyota...)</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={labelSettings.show_size}
                          onChange={(e) => setLabelSettings({...labelSettings, show_size: e.target.checked})}
                          className="rounded"
                        />
                        <span className="text-sm">Stærð (250ml, 500ml...)</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={labelSettings.show_location}
                          onChange={(e) => setLabelSettings({...labelSettings, show_location: e.target.checked})}
                          className="rounded"
                        />
                        <span className="text-sm">Staðsetning/Hilla (A1, B2...)</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={labelSettings.show_sequence}
                          onChange={(e) => setLabelSettings({...labelSettings, show_sequence: e.target.checked})}
                          className="rounded"
                        />
                        <span className="text-sm">Raðnúmer (#1, #2...)</span>
                      </label>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-4 border-t">
                    <Button 
                      onClick={saveLabelSettings}
                      disabled={savingLabelSettings}
                      className="flex-1"
                    >
                      {savingLabelSettings ? '⏳ Vista...' : '💾 Vista stillingar'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Loan Cars Sub-tab */}
            <TabsContent value="loan-cars" className="space-y-4">
              <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Lánsbílar</CardTitle>
                  <p className="text-sm text-slate-600">
                    Stjórna lánsbílum sem viðskiptavinir geta fengið til láns á meðan bíll þeirra er í þrifum.
                  </p>
                </div>
                <Button onClick={() => setShowAddLoanCar(true)} className="bg-green-600 hover:bg-green-700">
                  ➕ Bæta við lánsbíl
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Add Loan Car Form */}
              {showAddLoanCar && (
                <Card className="mb-4 border-green-200">
                  <CardContent className="p-4">
                    <div className="space-y-2">
                      <div>
                        <Label>Bílnúmer *</Label>
                        <div className="flex gap-2">
                          <Input
                            value={newLoanCar.plate}
                            onChange={(e) => setNewLoanCar(prev => ({...prev, plate: e.target.value.toUpperCase()}))}
                            placeholder="ABC12"
                            maxLength={5}
                          />
                          <Button 
                            onClick={async () => {
                              if (!newLoanCar.plate || newLoanCar.plate.length < 5) {
                                toast.error('Sláðu inn gilt bílnúmer');
                                return;
                              }
                              try {
                                setLookingUpLoanCar(true);
                                const response = await axios.get(`${API}/vehicles/lookup/${newLoanCar.plate}`);
                                console.log('Vehicle lookup response:', response.data);
                                if (response.data) {
                                  const vehicleData = {
                                    manufacturer: response.data.manufacturer || '',
                                    model: response.data.model || '',
                                    year: response.data.year || '',
                                    color: response.data.color || ''
                                  };
                                  console.log('Setting loan car data:', vehicleData);
                                  setNewLoanCar(prev => ({
                                    ...prev,
                                    ...vehicleData
                                  }));
                                  toast.success('Bílaupplýsingar sóttar úr skrá');
                                }
                              } catch (error) {
                                console.error('Error fetching vehicle info:', error);
                                toast.error('Gat ekki sótt bílaupplýsingar');
                              } finally {
                                setLookingUpLoanCar(false);
                              }
                            }}
                            disabled={lookingUpLoanCar}
                            variant="outline"
                          >
                            {lookingUpLoanCar ? '🔍 Sæki...' : '🔍 Sækja'}
                          </Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <Label>Framleiðandi</Label>
                          <Input
                            value={newLoanCar.manufacturer}
                            onChange={(e) => setNewLoanCar(prev => ({...prev, manufacturer: e.target.value}))}
                            placeholder="t.d. Toyota"
                          />
                        </div>
                        <div>
                          <Label>Gerð</Label>
                          <Input
                            value={newLoanCar.model}
                            onChange={(e) => setNewLoanCar(prev => ({...prev, model: e.target.value}))}
                            placeholder="t.d. Yaris"
                          />
                        </div>
                        <div>
                          <Label>Árgerð</Label>
                          <Input
                            value={newLoanCar.year}
                            onChange={(e) => setNewLoanCar(prev => ({...prev, year: e.target.value}))}
                            placeholder="t.d. 2020"
                            type="number"
                          />
                        </div>
                        <div>
                          <Label>Litur</Label>
                          <Input
                            value={newLoanCar.color}
                            onChange={(e) => setNewLoanCar(prev => ({...prev, color: e.target.value}))}
                            placeholder="t.d. Hvítur"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-2 mt-4">
                      <Button 
                        onClick={async () => {
                          if (!newLoanCar.plate) {
                            toast.error('Bílnúmer er nauðsynlegt');
                            return;
                          }
                          try {
                            setSaving(true);
                            await axios.post(`${API}/loan-cars`, newLoanCar);
                            toast.success('Lánsbíl bætt við');
                            setShowAddLoanCar(false);
                            setNewLoanCar({ plate: '', manufacturer: '', model: '', year: '', color: '' });
                            await loadSettings();
                          } catch (error) {
                            toast.error('Villa við að bæta við lánsbíl');
                          } finally {
                            setSaving(false);
                          }
                        }} 
                        disabled={saving} 
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {saving ? 'Bætir við...' : '✅ Bæta við'}
                      </Button>
                      <Button onClick={() => {
                        setShowAddLoanCar(false);
                        setNewLoanCar({ plate: '', manufacturer: '', model: '', year: '', color: '' });
                      }} variant="outline">
                        Hætta við
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Existing Loan Cars */}
              <div className="space-y-2">
                {loanCars.filter(car => car.is_active).map((car) => (
                  <div key={car.id} className="p-3 border rounded-lg hover:bg-slate-50">
                    {editingLoanCar === car.id ? (
                      <div className="space-y-2">
                        <div>
                          <Label>Bílnúmer</Label>
                          <Input
                            defaultValue={car.plate}
                            onChange={(e) => {
                              car.tempPlate = e.target.value.toUpperCase();
                            }}
                            maxLength={5}
                          />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <Label>Framleiðandi</Label>
                            <Input
                              defaultValue={car.manufacturer}
                              onChange={(e) => {
                                car.tempManufacturer = e.target.value;
                              }}
                            />
                          </div>
                          <div>
                            <Label>Gerð</Label>
                            <Input
                              defaultValue={car.model}
                              onChange={(e) => {
                                car.tempModel = e.target.value;
                              }}
                            />
                          </div>
                          <div>
                            <Label>Árgerð</Label>
                            <Input
                              defaultValue={car.year}
                              onChange={(e) => {
                                car.tempYear = e.target.value;
                              }}
                              type="number"
                            />
                          </div>
                          <div>
                            <Label>Litur</Label>
                            <Input
                              defaultValue={car.color}
                              onChange={(e) => {
                                car.tempColor = e.target.value;
                              }}
                            />
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Button 
                            size="sm" 
                            onClick={async () => {
                              try {
                                setSaving(true);
                                await axios.put(`${API}/loan-cars/${car.id}`, {
                                  plate: car.tempPlate || car.plate,
                                  manufacturer: car.tempManufacturer || car.manufacturer,
                                  model: car.tempModel || car.model,
                                  year: car.tempYear || car.year,
                                  color: car.tempColor || car.color
                                });
                                toast.success('Lánsbíll uppfærður');
                                setEditingLoanCar(null);
                                await loadSettings();
                              } catch (error) {
                                toast.error('Villa við að uppfæra lánsbíl');
                              } finally {
                                setSaving(false);
                              }
                            }}
                            disabled={saving}
                          >
                            {saving ? 'Vistar...' : '✅ Vista'}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingLoanCar(null)}>
                            Hætta við
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-lg">🚗 {car.plate}</div>
                          <div className="text-sm text-slate-600 mt-1">
                            {car.manufacturer} {car.model} {car.year && `(${car.year})`}
                            {car.color && ` • ${car.color}`}
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Button size="sm" variant="outline" onClick={() => setEditingLoanCar(car.id)}>
                            ✏️ Breyta
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-red-600 border-red-600 hover:bg-red-50"
                            onClick={async () => {
                              if (window.confirm(`Ertu viss um að þú viljir eyða "${car.plate}"?`)) {
                                try {
                                  setSaving(true);
                                  await axios.delete(`${API}/loan-cars/${car.id}`);
                                  toast.success('Lánsbíl eytt');
                                  await loadSettings();
                                } catch (error) {
                                  toast.error('Villa við að eyða lánsbíl');
                                } finally {
                                  setSaving(false);
                                }
                              }
                            }}
                          >
                            🗑️ Eyða
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                
                {loanCars.filter(car => car.is_active).length === 0 && (
                  <div className="text-center py-8 text-slate-600">
                    Engir lánsbílar í kerfinu. Smelltu á "Bæta við lánsbíl" til að byrja.
                  </div>
                )}
              </div>
              
              {/* PDF Templates Upload Section - Two Separate Templates */}
              <div className="mt-8 pt-8 border-t">
                <h3 className="text-lg font-medium mb-4">📄 PDF Templates fyrir lánaskilmála</h3>
                <p className="text-sm text-slate-600 mb-4">
                  Hægt er að hlaða upp tveimur mismunandi PDF templates - einu fyrir prentun og öðru fyrir rafræna undirskrift
                </p>
                
                <div className="space-y-4">
                  {/* Print Template Upload */}
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="p-4">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium">📄 Prenta Template</p>
                            <p className="text-xs text-slate-600 mt-1">
                              Þetta PDF er notað þegar smellt er á "📄 Prenta" valmöguleikann
                            </p>
                          </div>
                          <div className="text-sm">
                            {printTemplateExists ? (
                              <span className="text-green-600 font-medium">✅ Template til staðar</span>
                            ) : (
                              <span className="text-slate-500">⚠️ Ekkert template</span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex gap-2 items-end">
                          <div className="flex-1">
                            <Label htmlFor="print-pdf-upload" className="text-sm">
                              Hlaða upp prenta template
                            </Label>
                            <Input
                              id="print-pdf-upload"
                              type="file"
                              accept=".pdf"
                              onChange={async (e) => {
                                const file = e.target.files[0];
                                if (!file) return;
                                
                                if (!file.name.endsWith('.pdf')) {
                                  toast.error('Vinsamlegast veldu PDF skrá');
                                  return;
                                }
                                
                                if (file.size > 5 * 1024 * 1024) { // 5MB limit
                                  toast.error('PDF skrá má ekki vera stærri en 5MB');
                                  return;
                                }
                                
                                try {
                                  setSaving(true);
                                  const formData = new FormData();
                                  formData.append('pdf', file);
                                  
                                  await axios.post(`${API}/loan-cars/upload-print-template`, formData, {
                                    headers: {
                                      'Content-Type': 'multipart/form-data'
                                    }
                                  });
                                  
                                  toast.success('📄 Prenta PDF template uppfært!');
                                  setPrintTemplateExists(true);
                                  
                                  // Clear the input
                                  e.target.value = '';
                                } catch (error) {
                                  console.error('Failed to upload print PDF:', error);
                                  toast.error('Villa við að hlaða upp prenta PDF');
                                } finally {
                                  setSaving(false);
                                }
                              }}
                              className="mt-1"
                            />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Electronic Template Upload */}
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="p-4">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium">✍️ Rafrænt Template</p>
                            <p className="text-xs text-slate-600 mt-1">
                              Þetta PDF er notað þegar smellt er á "✍️ Senda rafrænt" valmöguleikann
                            </p>
                          </div>
                          <div className="text-sm">
                            {electronicTemplateExists ? (
                              <span className="text-green-600 font-medium">✅ Template til staðar</span>
                            ) : (
                              <span className="text-slate-500">⚠️ Ekkert template</span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex gap-2 items-end">
                          <div className="flex-1">
                            <Label htmlFor="electronic-pdf-upload" className="text-sm">
                              Hlaða upp rafrænt template
                            </Label>
                            <Input
                              id="electronic-pdf-upload"
                              type="file"
                              accept=".pdf"
                              onChange={async (e) => {
                                const file = e.target.files[0];
                                if (!file) return;
                                
                                if (!file.name.endsWith('.pdf')) {
                                  toast.error('Vinsamlegast veldu PDF skrá');
                                  return;
                                }
                                
                                if (file.size > 5 * 1024 * 1024) { // 5MB limit
                                  toast.error('PDF skrá má ekki vera stærri en 5MB');
                                  return;
                                }
                                
                                try {
                                  setSaving(true);
                                  const formData = new FormData();
                                  formData.append('pdf', file);
                                  
                                  await axios.post(`${API}/loan-cars/upload-electronic-template`, formData, {
                                    headers: {
                                      'Content-Type': 'multipart/form-data'
                                    }
                                  });
                                  
                                  toast.success('✍️ Rafrænt PDF template uppfært!');
                                  setElectronicTemplateExists(true);
                                  
                                  // Clear the input
                                  e.target.value = '';
                                } catch (error) {
                                  console.error('Failed to upload electronic PDF:', error);
                                  toast.error('Villa við að hlaða upp rafrænt PDF');
                                } finally {
                                  setSaving(false);
                                }
                              }}
                              className="mt-1"
                            />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Info about templates */}
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                    <p className="text-xs text-slate-600">
                      <strong>Athugasemd:</strong> Ef aðeins eitt template er til staðar mun kerfið nota það fyrir báða valkosti. 
                      PDF templates þurfa að hafa form fields með nöfnunum: customer_name, customer_kt, customer_phone, 
                      customer_address, car_model, car_reg, km_out, delivery_date, comments, date_company, date_customer
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Undirritaðir samningar Section */}
              <div className="mt-8 pt-8 border-t">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="text-lg font-medium">✍️ Undirritaðir lánsbílasamningar</h3>
                    <p className="text-sm text-slate-600">Yfirlit yfir alla rafræna undirskriftarbeiðnir</p>
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={signatureRequestsFilter}
                      onChange={(e) => setSignatureRequestsFilter(e.target.value)}
                      className="px-3 py-2 border rounded-md text-sm"
                    >
                      <option value="all">Allar beiðnir</option>
                      <option value="pending">⏳ Í bið</option>
                      <option value="signed">✅ Undirritað</option>
                      <option value="expired">⌛ Útrunnið</option>
                    </select>
                    <Button
                      onClick={loadSignatureRequests}
                      variant="outline"
                      size="sm"
                    >
                      🔄 Endurhlaða
                    </Button>
                  </div>
                </div>
                
                {signatureRequestsLoading ? (
                  <div className="text-center py-8 text-slate-500">⏳ Hleður...</div>
                ) : signatureRequests.length === 0 ? (
                  <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg">
                    Engar undirskriftarbeiðnir fundust
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-slate-100">
                          <th className="text-left p-3 text-sm font-medium">Dagsetning</th>
                          <th className="text-left p-3 text-sm font-medium">Viðskiptavinur</th>
                          <th className="text-left p-3 text-sm font-medium">Bílnúmer</th>
                          <th className="text-left p-3 text-sm font-medium">Lánsbíll</th>
                          <th className="text-left p-3 text-sm font-medium">Staða</th>
                          <th className="text-left p-3 text-sm font-medium">Aðgerðir</th>
                        </tr>
                      </thead>
                      <tbody>
                        {signatureRequests.map((req) => {
                          const jobData = req.job_data || {};
                          const displayName = jobData.renter_name || jobData.owner_name || 'Óþekkt';
                          const loanCar = loanCars.find(c => c.id === jobData.loan_car_id);
                          const createdDate = new Date(req.created_at).toLocaleDateString('is-IS');
                          const signedDate = req.signed_at ? new Date(req.signed_at).toLocaleDateString('is-IS') : '-';
                          
                          return (
                            <tr key={req.id} className="border-b hover:bg-slate-50">
                              <td className="p-3 text-sm">
                                <div>{createdDate}</div>
                                {req.status === 'signed' && (
                                  <div className="text-xs text-green-600">Undirritað: {signedDate}</div>
                                )}
                              </td>
                              <td className="p-3 text-sm">
                                <div className="font-medium">{displayName}</div>
                                {jobData.renter_name && (
                                  <div className="text-xs text-slate-500">Leigjandi</div>
                                )}
                              </td>
                              <td className="p-3 text-sm font-medium">{jobData.plate}</td>
                              <td className="p-3 text-sm">
                                {loanCar ? `${loanCar.manufacturer} ${loanCar.model}` : '-'}
                              </td>
                              <td className="p-3 text-sm">
                                {req.status === 'pending' && <span className="text-orange-600">⏳ Í bið</span>}
                                {req.status === 'signed' && <span className="text-green-600">✅ Undirritað</span>}
                                {req.status === 'expired' && <span className="text-slate-500">⌛ Útrunnið</span>}
                              </td>
                              <td className="p-3 text-sm">
                                <div className="flex gap-2">
                                  {req.status === 'pending' && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        const link = `${window.location.origin}/sign/${req.id}`;
                                        navigator.clipboard.writeText(link);
                                        toast.success('Link afritaður!');
                                      }}
                                    >
                                      📋 Afrita link
                                    </Button>
                                  )}
                                  {req.status === 'signed' && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => window.open(`${API}/signature-requests/${req.id}/download`, '_blank')}
                                    >
                                      📥 Sækja PDF
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
            </TabsContent>

            {/* QR Code Sub-tab */}
            <TabsContent value="qr-code" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>📱 QR Kóði fyrir fljótlega skráningu</CardTitle>
                  <p className="text-sm text-slate-600">
                    Búðu til QR kóða sem starfsfólk getur skannað til að skrá bíla fljótt
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-900">
                      💡 <strong>Hvernig virkar það:</strong> Prenta QR kóðann og setja á vegg. Starfsfólk skannar hann með símanum til að skrá bíl án þess að þurfa að skrá sig inn.
                    </p>
                  </div>

                  <div className="flex flex-col items-center space-y-4 p-6 bg-white border-2 border-dashed border-gray-300 rounded-lg">
                    {/* QR Code with branding */}
                    <div className="bg-white p-6 rounded-lg shadow-lg border-4 border-blue-500">
                      <div className="text-center mb-3">
                        <h3 className="text-2xl font-bold text-blue-600">Alvörubón</h3>
                        <p className="text-sm text-gray-600">Fljótleg bílaskráning</p>
                      </div>
                      {(() => {
                        // Nota alltaf núverandi vefslóð fyrir QR (frontend), ekki REACT_APP_BACKEND_URL
                        const qrUrl = `${window.location.origin}/mobile-registration`;
                        return (
                          <>
                            <QRCodeSVG 
                              value={qrUrl}
                              size={256}
                              level="H"
                              includeMargin={true}
                            />
                            <div className="text-center mt-3">
                              <p className="text-base font-bold text-slate-800 mb-2">Skannaðu til að skrá bíl</p>
                              <p className="text-sm font-semibold text-blue-700">
                                {qrUrl.replace('https://', '').replace('http://', '')}
                              </p>
                            </div>
                          </>
                        );
                      })()}

                    </div>

                    {/* Download Button - A4 PDF */}
                    <Button 
                      onClick={() => {
                        // Create high-resolution canvas for QR code (4x scale for sharp printing)
                        const scale = 4;
                        const qrSize = 200 * scale; // 200mm at 4x = 800px
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');
                        canvas.width = qrSize;
                        canvas.height = qrSize;
                        
                        const svgElement = document.querySelector('.border-blue-500 svg');
                        if (svgElement) {
                          const svgData = new XMLSerializer().serializeToString(svgElement);
                          const img = new Image();
                          
                          img.onload = () => {
                            // Draw QR code at high resolution
                            ctx.fillStyle = 'white';
                            ctx.fillRect(0, 0, canvas.width, canvas.height);
                            ctx.drawImage(img, 0, 0, qrSize, qrSize);
                            
                            // Create A4 PDF (210 x 297 mm)
                            const pdf = new jsPDF({
                              orientation: 'portrait',
                              unit: 'mm',
                              format: 'a4'
                            });
                            
                            const pageWidth = 210;
                            const pageHeight = 297;
                            const qrDisplaySize = 140; // QR code size in mm on PDF
                            const qrX = (pageWidth - qrDisplaySize) / 2;
                            const qrY = 70;
                            
                            // Blue border
                            pdf.setDrawColor(59, 130, 246);
                            pdf.setLineWidth(3);
                            pdf.rect(10, 10, pageWidth - 20, pageHeight - 20);
                            
                            // Title
                            pdf.setFont('helvetica', 'bold');
                            pdf.setFontSize(48);
                            pdf.setTextColor(30, 64, 175);
                            pdf.text('Alvörubón', pageWidth / 2, 40, { align: 'center' });
                            
                            // Subtitle
                            pdf.setFont('helvetica', 'normal');
                            pdf.setFontSize(24);
                            pdf.setTextColor(71, 85, 105);
                            pdf.text('Fljótleg bílaskráning', pageWidth / 2, 55, { align: 'center' });
                            
                            // Add QR code image (high resolution)
                            const qrDataUrl = canvas.toDataURL('image/png', 1.0);
                            pdf.addImage(qrDataUrl, 'PNG', qrX, qrY, qrDisplaySize, qrDisplaySize);
                            
                            // Instruction text
                            pdf.setFont('helvetica', 'bold');
                            pdf.setFontSize(28);
                            pdf.setTextColor(30, 41, 59);
                            pdf.text('Skannaðu til að skrá bíl', pageWidth / 2, qrY + qrDisplaySize + 20, { align: 'center' });
                            
                            // URL
                            const url = window.location.hostname === 'localhost' 
                              ? 'crm.abon.is/mobile-registration'
                              : `${window.location.hostname}/mobile-registration`;
                            pdf.setFont('helvetica', 'bold');
                            pdf.setFontSize(18);
                            pdf.setTextColor(100, 116, 139);
                            pdf.text(url, pageWidth / 2, qrY + qrDisplaySize + 35, { align: 'center' });
                            
                            // Footer text
                            pdf.setFont('helvetica', 'normal');
                            pdf.setFontSize(16);
                            pdf.setTextColor(148, 163, 184);
                            pdf.text('Bílþrif og Bón', pageWidth / 2, pageHeight - 25, { align: 'center' });
                            
                            // Save PDF
                            pdf.save('alvorubon-qr-skraning.pdf');
                            toast.success('✅ A4 PDF niðurhalað');
                          };
                          
                          img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
                        }
                      }}
                      className="w-full max-w-xs"
                      size="lg"
                    >
                      📥 Hlaða niður QR kóða (A4 PDF)
                    </Button>

                    <div className="text-center mt-4">
                      <p className="text-xs text-slate-500 font-medium">
                        Hlekkur: {window.location.hostname === 'localhost' 
                          ? 'crm.abon.is/mobile-registration'
                          : `${window.location.hostname}/mobile-registration`}
                      </p>
                    </div>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-900">
                      ⚠️ <strong>Athugið:</strong> Verð eru falin fyrir notendur sem skrá bíla í gegnum QR kóðann. Stjórnendur geta síðan úthlutað verkum og yfirfarið þau í kerfinu.
                    </p>
                  </div>

                  {/* QR Security Settings */}
                  <div className="border-t pt-6 mt-6">
                    <h3 className="text-lg font-semibold mb-4">🔒 Öryggis stillingar</h3>
                    
                    <div className="space-y-4">
                      {/* Enable/Disable QR Feature */}
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div className="flex-1">
                          <label className="flex items-center space-x-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={qrSecuritySettings.enabled}
                              onChange={(e) => setQrSecuritySettings({ ...qrSecuritySettings, enabled: e.target.checked })}
                              className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm font-medium">
                              Virkja QR skráningu
                            </span>
                          </label>
                          <p className="text-xs text-slate-600 mt-1 ml-8">
                            Þegar óvirkt geta starfsmenn ekki notað QR kóðann til að skrá bíla
                          </p>
                        </div>
                      </div>

                      {/* PIN Code Setting */}
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <Label className="text-sm font-medium mb-2 block">
                          PIN kóði (4 tölustafir)
                        </Label>
                        <Input
                          type="password"
                          maxLength={4}
                          placeholder={qrSecuritySettings.has_pin ? "●●●● (núverandi)" : "Settu 4 tölustafi"}
                          value={qrSecuritySettings.pin_code}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, ''); // Only digits
                            if (value.length <= 4) {
                              setQrSecuritySettings({ ...qrSecuritySettings, pin_code: value });
                            }
                          }}
                          className="max-w-xs"
                          disabled={!qrSecuritySettings.enabled}
                        />
                        <p className="text-xs text-slate-600 mt-1">
                          {qrSecuritySettings.has_pin ? 
                            'Hafðu reitinn auðan til að halda núverandi PIN' : 
                            'Skildu eftir autt ef PIN er ekki þörf'}
                        </p>
                      </div>

                      {/* Time-based PIN Requirement */}
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div className="flex-1">
                          <label className="flex items-center space-x-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={qrSecuritySettings.require_pin_outside_hours}
                              onChange={(e) => setQrSecuritySettings({ ...qrSecuritySettings, require_pin_outside_hours: e.target.checked })}
                              className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              disabled={!qrSecuritySettings.enabled || (!qrSecuritySettings.pin_code && !qrSecuritySettings.has_pin)}
                            />
                            <span className="text-sm font-medium">
                              Krefjast PIN aðeins utan opnunartíma
                            </span>
                          </label>
                          <p className="text-xs text-slate-600 mt-1 ml-8">
                            Starfsfólk þarf bara að slá inn PIN utan opnunartíma. Innan opnunartíma er PIN ekki nauðsynlegur.
                          </p>
                        </div>
                      </div>

                      {/* Info box */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-blue-900">
                          💡 <strong>Ábending:</strong> Ef þú stillir PIN kóða mun starfsfólk þurfa að slá hann inn áður en þeir geta skráð bíl í gegnum QR kóðann. Þetta veitir aukið öryggi.
                        </p>
                      </div>

                      {/* Save Button */}
                      <Button
                        onClick={saveQrSecuritySettings}
                        disabled={savingQrSecurity || !qrSecuritySettings.enabled}
                        className="w-full"
                      >
                        {savingQrSecurity ? 'Vistar...' : 'Vista QR öryggis stillingar'}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

          </Tabs>
        </TabsContent>

      </Tabs>
      
      {/* Auto-fill Product Numbers Dialog - Outside tabs so it shows on any tab */}
      {showAutoFillDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                🔢 Útfylla vörunúmer sjálfkrafa
              </CardTitle>
              <p className="text-sm text-slate-600">
                Þetta mun útfylla öll tóm vörunúmer í verðtöflunni
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-yellow-50 border border-yellow-300 rounded-lg">
                <p className="text-sm text-yellow-900">
                  ⚠️ <strong>Athugið:</strong> Þetta mun útfylla vörunúmer í verðtöflunni.
                </p>
              </div>
              
              <div>
                <Label htmlFor="auto-fill-start-number">Byrjunarnúmer</Label>
                <Input
                  id="auto-fill-start-number"
                  type="number"
                  min="1"
                  value={autoFillStartNumber}
                  onChange={(e) => setAutoFillStartNumber(parseInt(e.target.value) || 101)}
                  placeholder="101"
                  className="mt-1"
                />
                <div className="text-xs text-slate-500 mt-1">
                  💡 Vörunúmer byrja á þessu númeri og telja upp
                </div>
              </div>
              
              <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded">
                <Checkbox
                  id="auto-fill-overwrite"
                  checked={autoFillOverwrite}
                  onCheckedChange={setAutoFillOverwrite}
                />
                <Label htmlFor="auto-fill-overwrite" className="cursor-pointer text-sm">
                  <span className="font-semibold text-red-700">⚠️ Skrifa yfir núverandi vörunúmer</span>
                  <div className="text-xs text-red-600 mt-1">
                    Ef hakað: öll vörunúmer verða endurstillt. Ef ekki: aðeins tóm númer útfylld.
                  </div>
                </Label>
              </div>
              
              <div>
                <Label htmlFor="auto-fill-password">Admin lykilorð</Label>
                <Input
                  id="auto-fill-password"
                  type="password"
                  value={autoFillPassword}
                  onChange={(e) => setAutoFillPassword(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAutoFillProductNumbers();
                    }
                  }}
                  placeholder="Sláðu inn lykilorð"
                  className="mt-1"
                  autoFocus
                />
              </div>

              <div className="flex space-x-2">
                <Button
                  onClick={handleAutoFillProductNumbers}
                  disabled={autoFilling || !autoFillPassword.trim()}
                  className="flex-1"
                >
                  {autoFilling ? 'Útfyllir...' : '✅ Útfylla'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAutoFillDialog(false);
                    setAutoFillPassword('');
                    setAutoFillStartNumber(101);
                    setAutoFillOverwrite(false);
                  }}
                  disabled={autoFilling}
                >
                  Hætta við
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Send Pricelist Dialog */}
      <Dialog open={pricelistDialogOpen} onOpenChange={setPricelistDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>📧 Senda verðskrá á tölvupóst</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Recipient info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Netfang *</Label>
                <Input
                  type="email"
                  placeholder="fyrirtaeki@example.is"
                  value={pricelistForm.recipient_email}
                  onChange={(e) => setPricelistForm(prev => ({...prev, recipient_email: e.target.value}))}
                />
              </div>
              <div>
                <Label>Nafn tengiliðs</Label>
                <Input
                  placeholder="Jón Jónsson"
                  value={pricelistForm.recipient_name}
                  onChange={(e) => setPricelistForm(prev => ({...prev, recipient_name: e.target.value}))}
                />
              </div>
            </div>
            
            <div>
              <Label>Fyrirtækjanafn</Label>
              <Input
                placeholder="Fyrirtækið ehf."
                value={pricelistForm.company_name}
                onChange={(e) => setPricelistForm(prev => ({...prev, company_name: e.target.value}))}
              />
            </div>
            
            <div>
              <Label>Sérsniðin skilaboð (valfrjálst)</Label>
              <textarea
                className="w-full p-2 border rounded-md min-h-[80px]"
                placeholder="Bæta við sérstökum skilaboðum..."
                value={pricelistForm.custom_message}
                onChange={(e) => setPricelistForm(prev => ({...prev, custom_message: e.target.value}))}
              />
            </div>
            
            {/* Discount option */}
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <div className="flex items-center gap-3 mb-3">
                <input
                  type="checkbox"
                  id="show_discount"
                  checked={pricelistForm.show_discount}
                  onChange={(e) => setPricelistForm(prev => ({...prev, show_discount: e.target.checked}))}
                  className="h-4 w-4"
                />
                <Label htmlFor="show_discount" className="mb-0 cursor-pointer">Sýna fyrirtækjaafslátt</Label>
              </div>
              {pricelistForm.show_discount && (
                <div className="flex items-center gap-2">
                  <Label className="mb-0">Afsláttur:</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    className="w-24"
                    value={pricelistForm.discount_percentage}
                    onChange={(e) => setPricelistForm(prev => ({...prev, discount_percentage: parseInt(e.target.value) || 0}))}
                  />
                  <span>%</span>
                </div>
              )}
            </div>
            
            {/* Select sizes */}
            <div>
              <Label className="mb-2 block">Veldu bílastærðir (autt = allar)</Label>
              <div className="flex flex-wrap gap-2">
                {settings?.sizes?.map(size => (
                  <button
                    key={size.id}
                    type="button"
                    onClick={() => {
                      const isSelected = pricelistForm.selected_sizes.includes(size.id);
                      setPricelistForm(prev => ({
                        ...prev,
                        selected_sizes: isSelected 
                          ? prev.selected_sizes.filter(id => id !== size.id)
                          : [...prev.selected_sizes, size.id]
                      }));
                    }}
                    className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                      pricelistForm.selected_sizes.includes(size.id)
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'bg-white text-slate-700 border-slate-300 hover:border-blue-300'
                    }`}
                  >
                    {size.name}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Select services */}
            <div>
              <Label className="mb-2 block">Veldu þjónustur (autt = allar)</Label>
              <div className="max-h-48 overflow-y-auto border rounded-md p-2 space-y-1">
                {settings?.services?.filter(s => s.display_in_pricelist !== false).map(service => (
                  <label
                    key={service.id}
                    className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={pricelistForm.selected_services.length === 0 || pricelistForm.selected_services.includes(service.id)}
                      onChange={() => {
                        const isSelected = pricelistForm.selected_services.includes(service.id);
                        setPricelistForm(prev => ({
                          ...prev,
                          selected_services: isSelected 
                            ? prev.selected_services.filter(id => id !== service.id)
                            : [...prev.selected_services, service.id]
                        }));
                      }}
                      className="h-4 w-4"
                    />
                    <span className="text-sm">{service.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setPricelistDialogOpen(false)}>
              Hætta við
            </Button>
            <Button 
              onClick={sendPricelist}
              disabled={sendingPricelist || !pricelistForm.recipient_email}
              className="bg-green-600 hover:bg-green-700"
            >
              {sendingPricelist ? '⏳ Sendir...' : '📧 Senda verðskrá'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Icelandic postal code → area name lookup
const POSTAL_CODE_AREAS = {
  '101': 'Miðborg', '102': 'Miðborg', '103': 'Miðborg', '104': 'Laugardalur',
  '105': 'Hlíðar', '107': 'Vesturbær', '108': 'Háaleiti', '109': 'Breiðholt',
  '110': 'Árbær', '111': 'Breiðholt', '112': 'Grafarvogur', '113': 'Grafarholt',
  '116': 'Kjalarnes', '170': 'Seltjarnarnes',
  '200': 'Kópavogur', '201': 'Kópavogur', '202': 'Kópavogur', '203': 'Kópavogur',
  '210': 'Garðabær', '212': 'Garðabær', '220': 'Hafnarfjörður', '221': 'Hafnarfjörður',
  '222': 'Hafnarfjörður', '225': 'Álftanes',
  '230': 'Reykjanesbær', '232': 'Reykjanesbær', '233': 'Reykjanesbær',
  '235': 'Keflavíkurflugvöllur',
  '240': 'Grindavík', '245': 'Sandgerði', '250': 'Garður', '260': 'Reykjanesbær',
  '270': 'Mosfellsbær', '271': 'Mosfellsbær',
  '300': 'Akranes', '301': 'Akranes', '310': 'Borgarnes', '311': 'Borgarnes',
  '340': 'Stykkishólmur', '345': 'Flatey',
  '350': 'Grundarfjörður', '355': 'Ólafsvík', '360': 'Hellissandur',
  '400': 'Ísafjörður', '401': 'Ísafjörður',
  '500': 'Staður', '510': 'Hólmavík',
  '540': 'Blönduós', '550': 'Sauðárkrókur', '560': 'Siglufjörður',
  '580': 'Dalvík', '600': 'Akureyri', '601': 'Akureyri', '602': 'Akureyri', '603': 'Akureyri',
  '610': 'Grenivík', '611': 'Grímsey', '620': 'Dalvík',
  '630': 'Hrísey', '640': 'Húsavík', '650': 'Laugar',
  '660': 'Mývatn', '670': 'Kópasker', '680': 'Þórshöfn',
  '700': 'Egilsstaðir', '701': 'Egilsstaðir', '710': 'Seyðisfjörður',
  '720': 'Borgarfjörður eystri', '730': 'Reyðarfjörður', '735': 'Eskifjörður',
  '740': 'Neskaupstaður', '750': 'Fáskrúðsfjörður', '755': 'Stöðvarfjörður',
  '760': 'Breiðdalsvík', '765': 'Djúpivogur',
  '780': 'Höfn', '785': 'Öræfi',
  '800': 'Selfoss', '801': 'Selfoss', '802': 'Selfoss',
  '810': 'Hveragerði', '815': 'Þorlákshöfn', '820': 'Eyrarbakki',
  '825': 'Stokkseyri', '840': 'Laugarvatn', '845': 'Flúðir',
  '850': 'Hella', '851': 'Hella', '860': 'Hvolsvöllur',
  '870': 'Vík', '880': 'Kirkjubæjarklaustur',
  '900': 'Vestmannaeyjar', '902': 'Vestmannaeyjar',
};

function PickupZonesManager() {
  const [zones, setZones] = useState([]);
  const [saving, setSaving] = useState(false);
  
  useEffect(() => {
    loadZones();
  }, []);
  
  const loadZones = async () => {
    try {
      const response = await axios.get(`${API}/settings/pickup-zones`);
      setZones(response.data.zones || []);
    } catch (error) {
      toast.error('Tókst ekki að hlaða sækja/skila svæðum');
    }
  };
  
  const saveZones = async () => {
    if (zones.length === 0) {
      toast.error('Engin póstnúmer til að vista. Bættu við póstnúmeri fyrst.');
      return;
    }
    try {
      setSaving(true);
      await axios.put(`${API}/settings/pickup-zones`, { zones });
      toast.success(`Sækja/skila verð vistuð (${zones.length} svæði)`);
      loadZones();
    } catch (error) {
      console.error('Save zones error:', error);
      toast.error('Tókst ekki að vista sækja/skila verð');
    } finally {
      setSaving(false);
    }
  };
  
  const addNewZone = () => {
    setZones(prev => [...prev, { postal_code: '', area_name: '', price: 0, enabled: true }]);
  };
  
  const deleteZone = (index) => {
    if (window.confirm('Ertu viss um að þú viljir eyða þessu svæði?')) {
      setZones(prev => prev.filter((_, i) => i !== index));
      toast.success('Svæði eytt');
    }
  };
  
  const updateZone = (index, field, value) => {
    setZones(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      // Auto-fill area name when postal code is 3 digits
      if (field === 'postal_code' && value.length === 3 && POSTAL_CODE_AREAS[value]) {
        updated[index].area_name = POSTAL_CODE_AREAS[value];
      }
      return updated;
    });
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>🚚 Sækja/skila verð eftir hverfum</CardTitle>
        <p className="text-sm text-slate-600">
          Stilltu verð fyrir sækja/skila þjónustu eftir póstnúmerum. Hverfi fyllast sjálfkrafa út eftir póstnúmeri.
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid grid-cols-12 gap-3 font-medium text-sm text-slate-700 border-b pb-2">
            <div className="col-span-2">Póstnúmer</div>
            <div className="col-span-4">Svæðisnafn</div>
            <div className="col-span-2">Verð (ISK)</div>
            <div className="col-span-2">Virkt</div>
            <div className="col-span-2">Aðgerðir</div>
          </div>
          
          {zones.map((zone, index) => (
            <div key={index} className="grid grid-cols-12 gap-3 items-center">
              <div className="col-span-2">
                <Input
                  type="text"
                  value={zone.postal_code}
                  onChange={(e) => updateZone(index, 'postal_code', e.target.value)}
                  placeholder="101"
                  maxLength="3"
                  className="text-sm"
                />
              </div>
              <div className="col-span-4">
                <Input
                  type="text"
                  value={zone.area_name}
                  onChange={(e) => updateZone(index, 'area_name', e.target.value)}
                  placeholder="Miðborg"
                  className="text-sm"
                />
              </div>
              <div className="col-span-2">
                <Input
                  type="number"
                  value={zone.price}
                  onChange={(e) => updateZone(index, 'price', parseFloat(e.target.value) || 0)}
                  className="text-sm"
                  min="0"
                  step="100"
                />
              </div>
              <div className="col-span-2 flex items-center justify-center">
                <input
                  type="checkbox"
                  checked={zone.enabled}
                  onChange={() => updateZone(index, 'enabled', !zone.enabled)}
                  className="w-4 h-4"
                />
              </div>
              <div className="col-span-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => deleteZone(index)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-300 w-full"
                >
                  🗑️ Eyða
                </Button>
              </div>
            </div>
          ))}
          
          <div className="pt-4 border-t flex justify-between">
            <Button 
              variant="outline" 
              onClick={addNewZone}
              className="text-green-600 hover:text-green-700 hover:bg-green-50 border-green-300"
            >
              ➕ Bæta við póstnúmeri
            </Button>
            <Button onClick={saveZones} disabled={saving}>
              {saving ? 'Vistar...' : '💾 Vista póstnúmer'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Cleanup Management Component
function CleanupManagement() {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedCleanupType, setSelectedCleanupType] = useState('');
  
  // Additional cleanup parameters
  const [dealerships, setDealerships] = useState([]);
  const [selectedDealership, setSelectedDealership] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  useEffect(() => {
    loadDealerships();
  }, []);
  
  const loadDealerships = async () => {
    try {
      const response = await axios.get(`${API}/dealerships?sort_by=popularity`);
      setDealerships(response.data || []); // Sorted by popularity
    } catch (error) {
      console.error('Failed to load dealerships:', error);
    }
  };
  
  const cleanupTypes = [
    { value: 'pos_sales', label: '💳 POS sölur', description: 'Eyða öllum POS sölum (Posi og MyPOS færslum)' },
    { value: 'test_jobs', label: 'Prófunarverk', description: 'Eyða öllum prófunarverkum og gömlum test gögnum' },
    { value: 'old_jobs', label: 'Gömul verk', description: 'Eyða verkum eldri en 30 daga' },
    { value: 'paid_jobs', label: 'Greidd verk', description: 'Eyða öllum greiddum verkum (status: paid)' },
    { value: 'specific_dealership', label: 'Verk frá bílasölu', description: 'Eyða öllum verkum frá ákveðinni bílasölu' },
    { value: 'by_date_range', label: 'Verk eftir tímabili', description: 'Eyða verkum í ákveðnu tímabili' },
    { value: 'all_customers', label: '⚠️ Allir viðskiptavinir', description: 'Eyða ÖLLUM viðskiptavinum og tengdum gögnum (verk, reikningar)' },
    { value: 'all_chats', label: 'Öll spjöll', description: 'Eyða öllum spjallskilaboðum frá öllum bílasölum' },
    { value: 'cache', label: 'Cache', description: 'Hreinsa öll cache gögn (bíla og persónu uppflettingar)' },
    { value: 'logs', label: 'Gamlar skrár', description: 'Eyða gömlum audit logs (eldri en 30 dagar)' },
    { value: 'all', label: 'Allt', description: 'Framkvæma alla þrif að ofan' }
  ];

  const handleCleanup = async (cleanupType) => {
    if (!password.trim()) {
      toast.error('Lykilorð þarf');
      return;
    }

    setLoading(true);
    try {
      let response;
      
      // Special handling for POS sales deletion
      if (cleanupType === 'pos_sales') {
        response = await axios.delete(`${API}/jobs/delete-pos-sales`, {
          params: { password: password }
        });
      }
      // Special handling for delete all customers
      else if (cleanupType === 'all_customers') {
        response = await axios.delete(`${API}/customers/delete-all`, {
          params: { password: password }
        });
      } else {
        // Build cleanup parameters for regular cleanup
        const cleanupParams = {
          cleanup_type: cleanupType,
          password: password
        };
        
        // Add extra parameters based on cleanup type
        if (cleanupType === 'specific_dealership' && selectedDealership) {
          cleanupParams.dealership = selectedDealership;
        }
        
        if (cleanupType === 'by_date_range' && startDate && endDate) {
          cleanupParams.start_date = startDate;
          cleanupParams.end_date = endDate;
        }
        
        response = await axios.delete(`${API}/admin/cleanup`, {
          params: cleanupParams
        });
      }

      toast.success(`✅ ${response.data.message} - ${response.data.deleted_count} hlutir eyddir`);
      
      // Handle details - could be array or object
      if (response.data.details) {
        if (Array.isArray(response.data.details)) {
          response.data.details.forEach(detail => {
            toast.info(detail);
          });
        } else if (typeof response.data.details === 'object') {
          // Display object details
          Object.entries(response.data.details).forEach(([key, value]) => {
            toast.info(`${key}: ${value}`);
          });
        }
      }
      
      setPassword('');
      setShowConfirm(false);
      setSelectedCleanupType('');
    } catch (error) {
      console.error('Cleanup failed:', error);
      const message = error.response?.status === 403 ? 
        'Rangt lykilorð' : 
        error.response?.data?.detail || 'Villa við þrif';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const startCleanup = (cleanupType) => {
    setSelectedCleanupType(cleanupType);
    setShowConfirm(true);
  };

  const generateMonthlyInvoices = async () => {
    setMonthlyLoading(true);
    try {
      const response = await axios.post(`${API}/admin/generate-monthly-invoices`);
      toast.success(`✅ ${response.data.message} - ${response.data.generated_count} reikningar búnir til`);
    } catch (error) {
      console.error('Monthly invoice generation failed:', error);
      const message = error.response?.data?.detail || 'Villa við að búa til mánaðarreikninga';
      toast.error(message);
    } finally {
      setMonthlyLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Monthly Invoice Generation */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            📄 Samanlaga reikningar
          </CardTitle>
          <p className="text-sm text-slate-600">
            Búa til mánaðarreikninga fyrir öll fyrirtæki í reikningsviðskiptum
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 border rounded-lg bg-blue-50 border-blue-200">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-medium text-blue-900">Mánaðarlegir samanlaga reikningar</h3>
                <p className="text-sm text-blue-700">
                  Búa til einn reikning fyrir hvert fyrirtæki sem safnar saman öllum verkum mánaðarins
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  💡 Aðeins fyrirtæki með "Reikningsviðskipti" hakað fá samanlaga reikninga
                </p>
              </div>
              <Button
                onClick={generateMonthlyInvoices}
                disabled={monthlyLoading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {monthlyLoading ? 'Býr til...' : '📄 Búa til'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* System Cleanup */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            🧹 Kerfi þrif og eyðing
          </CardTitle>
          <p className="text-sm text-red-600">
            ⚠️ Aðeins stjórnendur geta notað þessa virkni. Krefst lykilorðsstaðfestingar.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4">
            {cleanupTypes.map((cleanup) => (
              <div 
                key={cleanup.value} 
                className={`p-4 border rounded-lg ${
                  cleanup.value === 'all_customers' 
                    ? 'border-red-500 bg-red-50' 
                    : ''
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className={`font-medium ${
                      cleanup.value === 'all_customers' ? 'text-red-700' : ''
                    }`}>
                      {cleanup.label}
                    </h3>
                    <p className={`text-sm ${
                      cleanup.value === 'all_customers' 
                        ? 'text-red-600 font-medium' 
                        : 'text-slate-600'
                    }`}>
                      {cleanup.description}
                    </p>
                    
                    {/* Extra Parameters */}
                    {cleanup.value === 'specific_dealership' && (
                      <div className="mt-3">
                        <Label className="text-sm">Veldu bílasölu:</Label>
                        <Select onValueChange={setSelectedDealership} value={selectedDealership}>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Veldu bílasölu til að eyða verkum frá" />
                          </SelectTrigger>
                          <SelectContent>
                            {dealerships.map(dealership => {
                              const name = typeof dealership === 'string' ? dealership : dealership?.name || '';
                              return (
                                <SelectItem key={name} value={name}>
                                  {name}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    {cleanup.value === 'by_date_range' && (
                      <div className="mt-3 grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-sm">Frá dagsetningu:</Label>
                          <Input 
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                          />
                        </div>
                        <div>
                          <Label className="text-sm">Til dagsetningar:</Label>
                          <Input 
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <Button
                    variant={cleanup.value === 'all_customers' ? 'destructive' : 'outline'}
                    onClick={() => startCleanup(cleanup.value)}
                    className={
                      cleanup.value === 'all_customers'
                        ? 'ml-4 bg-red-600 hover:bg-red-700 text-white'
                        : 'text-red-600 hover:text-red-700 hover:bg-red-50 ml-4'
                    }
                  >
                    🗑️ Eyða
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Staðfesta eyðingu</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Ertu viss um að þú viljir framkvæma: 
              <strong> {cleanupTypes.find(c => c.value === selectedCleanupType)?.label}</strong>?
            </p>
            <p className="text-sm text-red-600">
              Þessi aðgerð er ekki afturkræf.
            </p>
            <div className="space-y-2">
              <Label htmlFor="admin-password">Admin lykilorð</Label>
              <Input
                id="admin-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Sláðu inn þitt admin lykilorð"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowConfirm(false)}>
              Hætta við
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleCleanup(selectedCleanupType)}
              disabled={loading || !password.trim()}
            >
              {loading ? 'Eyðir...' : 'Staðfesta eyðingu'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// DK Plus ERP Management Component  
function DKManagement() {
  const [dkStatus, setDkStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [dkInvoices, setDkInvoices] = useState([]);
  const [syncingCustomer, setSyncingCustomer] = useState(null);
  const [creatingInvoice, setCreatingInvoice] = useState(null);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  
  // Invoice date dialog state
  const [invoiceDateDialogOpen, setInvoiceDateDialogOpen] = useState(false);
  const [selectedJobForInvoice, setSelectedJobForInvoice] = useState(null);
  const [invoiceDates, setInvoiceDates] = useState({
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: new Date().toISOString().split('T')[0],
    final_due_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  });
  // Toggle between date picker and days input for eindagi
  const [dkMgmtFinalDueMode, setDkMgmtFinalDueMode] = useState('days'); // 'days' or 'date'
  const [dkMgmtFinalDueDays, setDkMgmtFinalDueDays] = useState(7);


  useEffect(() => {
    loadDkData();
  }, []);

  const loadDkData = async () => {
    try {
      const [statusRes, customersRes, invoicesRes] = await Promise.all([
        axios.get(`${API}/dk/status`),
        axios.get(`${API}/customers`), // Our local customers
        axios.get(`${API}/dk/invoices`)
      ]);
      
      // Backend now returns paginated response
      const customersData = customersRes.data.customers || customersRes.data || [];
      
      setDkStatus(statusRes.data);
      setCustomers(customersData);
      setDkInvoices(invoicesRes.data?.invoices || []);
    } catch (error) {
      toast.error('Tókst ekki að hlaða DK gögnum');
      console.error('DK data loading error:', error);
    } finally {
      setLoading(false);
    }
  };

  const syncCustomerToDK = async (customerId) => {
    setSyncingCustomer(customerId);
    try {
      const response = await axios.post(`${API}/dk/sync-customer-force`, { customer_id: customerId });
      
      if (response.data.success) {
        if (response.data.created) {
          toast.success(`Viðskiptavinur stofnaður í DK: ${response.data.dk_customer_number}`);
        } else {
          toast.success(`Viðskiptavinur samstilltur í DK: ${response.data.dk_customer_number}`);
        }
      } else {
        toast.error('Tókst ekki að samstilla við DK');
      }
      
      await loadDkData(); // Refresh data
    } catch (error) {
      toast.error('Tókst ekki að samstilla við DK');
      console.error('DK customer sync error:', error);
    } finally {
      setSyncingCustomer(null);
    }
  };

  // Open invoice date dialog instead of creating directly
  const openInvoiceDateDialog = (jobId) => {
    setSelectedJobForInvoice(jobId);
    // Set default dates
    const today = new Date().toISOString().split('T')[0];
    const sevenDaysLater = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    setInvoiceDates({
      invoice_date: today,
      due_date: today,
      final_due_date: sevenDaysLater
    });
    setInvoiceDateDialogOpen(true);
  };
  
  const createDKInvoice = async (jobId, dates = null) => {
    setCreatingInvoice(jobId);
    setInvoiceDateDialogOpen(false);
    try {
      const payload = { job_id: jobId };
      
      // Add custom dates if provided
      if (dates) {
        payload.invoice_date = dates.invoice_date;
        payload.due_date = dates.due_date;
        payload.final_due_date = dates.final_due_date;
      }
      
      const response = await axios.post(`${API}/dk/create-invoice`, payload);
      
      if (response.data.success) {
        toast.success(`Reikningur búinn til í DK: ${response.data.dk_invoice_number}`);
        await loadDkData(); // Refresh data to show new invoice
      } else {
        toast.error('Tókst ekki að búa til reikning í DK');
      }
    } catch (error) {
      toast.error('Tókst ekki að búa til reikning í DK');
      console.error('DK invoice creation error:', error);
    } finally {
      setCreatingInvoice(null);
      setSelectedJobForInvoice(null);
    }
  };

  const refreshInvoices = async () => {
    setLoadingInvoices(true);
    try {
      const invoicesRes = await axios.get(`${API}/dk/invoices`);
      setDkInvoices(invoicesRes.data?.invoices || []);
      toast.success('DK reikningar endurnýjaðir');
    } catch (error) {
      toast.error('Tókst ekki að endurnýja reikninga');
    } finally {
      setLoadingInvoices(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 text-center">
        <div className="text-sm text-slate-500">Hleður DK samþættingu...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* DK Status */}
      <Card>
        <CardHeader>
          <CardTitle>DK Plus Staða</CardTitle>
        </CardHeader>
        <CardContent>
          {dkStatus ? (
            <div className="grid grid-cols-4 gap-4 text-center">
              <div className="text-center">
                <div className={`text-2xl font-bold ${dkStatus.status === 'connected' ? 'text-green-600' : 'text-red-600'}`}>
                  {dkStatus.status === 'connected' ? '✅' : '❌'}
                </div>
                <div className="text-sm text-slate-600">
                  {dkStatus.status === 'connected' ? 'Tengt' : 'Ekki tengt'}
                </div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-blue-600">
                  DK ERP
                </div>
                <div className="text-sm text-slate-600">Kerfi</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-purple-600">
                  {dkStatus.api_url ? '🌐' : '❌'}
                </div>
                <div className="text-sm text-slate-600">API Tengill</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-orange-600">
                  📊
                </div>
                <div className="text-sm text-slate-600">ERP Integration</div>
              </div>
            </div>
          ) : (
            <div className="text-center text-red-600">Tókst ekki að hlaða DK stöðu</div>
          )}
          
          {dkStatus?.message && (
            <div className="mt-4 p-3 bg-green-50 text-green-700 rounded-lg text-sm">
              {dkStatus.message}
            </div>
          )}
          
          {dkStatus?.company && (
            <div className="mt-4 p-3 bg-blue-50 text-blue-700 rounded-lg text-sm">
              <strong>Fyrirtæki:</strong> {dkStatus.company.Name} (SSN: {dkStatus.company.SSNumber})
            </div>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="customers">
        <TabsList>
          <TabsTrigger value="customers">Viðskiptavinir</TabsTrigger>
          <TabsTrigger value="invoices">Reikningar</TabsTrigger>
          <TabsTrigger value="sync">Samstilla</TabsTrigger>
        </TabsList>

        {/* Customers Tab */}
        <TabsContent value="customers" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Viðskiptavinir - DK Samstilling</CardTitle>
              <p className="text-sm text-slate-600">
                Samstilltu viðskiptavini frá carwash kerfi yfir í DK Plus ERP
              </p>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nafn</TableHead>
                    <TableHead>Kennitala</TableHead>
                    <TableHead>Sími</TableHead>
                    <TableHead>DK Númer</TableHead>
                    <TableHead>Aðgerðir</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customers.map(customer => (
                    <TableRow key={customer.id}>
                      <TableCell className="font-medium">{customer.name}</TableCell>
                      <TableCell>{customer.kennitala}</TableCell>
                      <TableCell>{customer.phone}</TableCell>
                      <TableCell>
                        {customer.dk_customer_number ? (
                          <Badge variant="secondary">{customer.dk_customer_number}</Badge>
                        ) : (
                          <span className="text-slate-400">Ekki samstillt</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button 
                          size="sm" 
                          onClick={() => syncCustomerToDK(customer.id)}
                          disabled={syncingCustomer === customer.id}
                          variant={customer.dk_customer_number ? "secondary" : "default"}
                        >
                          {syncingCustomer === customer.id ? "Samstilli..." : (customer.dk_customer_number ? "Endursamstilla" : "Samstilla")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex justify-between items-center">
                <span>Reikningar - DK ERP ({dkInvoices.length})</span>
                <Button 
                  size="sm" 
                  onClick={refreshInvoices}
                  disabled={loadingInvoices}
                >
                  {loadingInvoices ? 'Hleður...' : '🔄 Endurnýja'}
                </Button>
              </CardTitle>
              <p className="text-sm text-slate-600">
                Reikningar sem hafa verið sendir í DK Plus ERP
              </p>
            </CardHeader>
            <CardContent>
              {dkInvoices.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>DK Nr.</TableHead>
                      <TableHead>Plata</TableHead>
                      <TableHead>Viðskiptavinur</TableHead>
                      <TableHead>Upphæð</TableHead>
                      <TableHead>Staða</TableHead>
                      <TableHead>Búinn til</TableHead>
                      <TableHead>Aðgerðir</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dkInvoices.map(invoice => (
                      <TableRow key={invoice.dk_invoice_number}>
                        <TableCell className="font-mono">{invoice.dk_invoice_number}</TableCell>
                        <TableCell>{invoice.plate}</TableCell>
                        <TableCell>{invoice.customer_name}</TableCell>
                        <TableCell>{invoice.amount?.toLocaleString()} ISK</TableCell>
                        <TableCell>
                          <Badge variant={invoice.status === 'Paid' ? 'default' : 'secondary'}>
                            {invoice.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {invoice.created ? new Date(invoice.created).toLocaleDateString('is') : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <div className="flex space-x-2">
                            <Button 
                              size="xs" 
                              variant="outline"
                              onClick={() => window.open(`https://www.dkplus.is`, '_blank')}
                            >
                              📊 Opna DK
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center text-slate-500 py-8">
                  <div className="text-lg mb-2">📋</div>
                  <div>Engir DK reikningar ennþá</div>
                  <div className="text-sm mt-2">Notaðu DK payment option í Greiðslur til að búa til reikninga</div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sync Tab */}
        <TabsContent value="sync" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Sjálfvirk Samstilling</CardTitle>
              <p className="text-sm text-slate-600">
                Stillingar fyrir sjálfvirka samstillingu við DK Plus
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 border rounded-lg">
                  <h3 className="font-medium mb-2">🔄 Samstillingarvalkostir</h3>
                  <div className="text-sm text-slate-600">
                    • Sjálfvirk sending viðskiptavina í DK<br/>
                    • Sjálfvirk reikningagerð fyrir lokið verk<br/>
                    • Webhook integration fyrir real-time updates
                  </div>
                </div>
                
                <Button onClick={loadDkData}>
                  🔄 Endurhlaða DK gögnum
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Invoice Date Selection Dialog */}
      <Dialog open={invoiceDateDialogOpen} onOpenChange={setInvoiceDateDialogOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>📅 Veldu dagsetningar</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="invoice_date">Reikningsdagur</Label>
              <Input
                id="invoice_date"
                type="date"
                value={invoiceDates.invoice_date}
                onChange={(e) => setInvoiceDates(prev => ({...prev, invoice_date: e.target.value}))}
              />
              <p className="text-xs text-slate-500 mt-1">Dagsetning reikningsins</p>
            </div>
            <div>
              <Label htmlFor="due_date">Gjalddagi</Label>
              <Input
                id="due_date"
                type="date"
                value={invoiceDates.due_date}
                onChange={(e) => setInvoiceDates(prev => ({...prev, due_date: e.target.value}))}
              />
              <p className="text-xs text-slate-500 mt-1">Dagsetning greiðslukröfu (gjaldagi)</p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label htmlFor="final_due_date">Eindagi</Label>
                <div className="flex gap-1 text-xs">
                  <button
                    type="button"
                    onClick={() => setDkMgmtFinalDueMode('days')}
                    className={`px-2 py-0.5 rounded ${dkMgmtFinalDueMode === 'days' ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                  >
                    Dagar
                  </button>
                  <button
                    type="button"
                    onClick={() => setDkMgmtFinalDueMode('date')}
                    className={`px-2 py-0.5 rounded ${dkMgmtFinalDueMode === 'date' ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                  >
                    Dagsetning
                  </button>
                </div>
              </div>
              {dkMgmtFinalDueMode === 'days' ? (
                <>
                  <Input
                    id="dk_mgmt_final_due_days"
                    type="number"
                    min="0"
                    max="90"
                    value={dkMgmtFinalDueDays}
                    onChange={(e) => {
                      const days = parseInt(e.target.value) || 0;
                      setDkMgmtFinalDueDays(days);
                      // Update the date based on invoice_date + days
                      const invoiceDate = new Date(invoiceDates.invoice_date);
                      const finalDate = new Date(invoiceDate);
                      finalDate.setDate(finalDate.getDate() + days);
                      setInvoiceDates(prev => ({...prev, final_due_date: finalDate.toISOString().split('T')[0]}));
                    }}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Fjöldi daga frá reikningsdegi → {new Date(invoiceDates.final_due_date).toLocaleDateString('is-IS')}
                  </p>
                </>
              ) : (
                <>
                  <Input
                    id="final_due_date"
                    type="date"
                    value={invoiceDates.final_due_date}
                    onChange={(e) => setInvoiceDates(prev => ({...prev, final_due_date: e.target.value}))}
                  />
                  <p className="text-xs text-slate-500 mt-1">Síðasti greiðsludagur (eindagi)</p>
                </>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setInvoiceDateDialogOpen(false)}>
              Hætta við
            </Button>
            <Button 
              onClick={() => createDKInvoice(selectedJobForInvoice, invoiceDates)}
              disabled={creatingInvoice}
            >
              {creatingInvoice ? 'Býr til...' : 'Búa til reikning'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Main Authenticated App
function AuthenticatedApp() {
  const { user, logout } = useAuth();
  // Initialize currentView from URL hash so a browser refresh lands the user
  // back on the same page. Fallback to 'registration' if hash is missing/invalid.
  const [currentView, setCurrentView] = useState(() => {
    try {
      const allowed = ['registration','jobs','payments','dealerships','reports','customers','payday','dk','users','settings','qrscanner','calendar','store','pricelist','accounting','inventory','products'];
      const hash = (window.location.hash || '').replace('#', '');
      if (allowed.includes(hash)) return hash;
    } catch (_e) { /* no-op */ }
    return 'registration';
  });
  const [language, setLanguage] = useState(() => {
    // Get language from localStorage or default to Icelandic
    return localStorage.getItem('app_language') || 'is';
  });
  
  // State to trigger opening add job dialog in CalendarView from other views
  const [shouldOpenAddJobDialog, setShouldOpenAddJobDialog] = useState(false);
  const [openedFromJobsView, setOpenedFromJobsView] = useState(false);
  
  // Function to navigate to calendar and open add job dialog
  const openAddJobFromOtherView = () => {
    setShouldOpenAddJobDialog(true);
    setOpenedFromJobsView(true);
    setCurrentView('calendar');
  };
  
  // Function to handle when job is created - navigate back to jobs if opened from there
  const handleJobCreatedFromDialog = () => {
    if (openedFromJobsView) {
      setCurrentView('jobs');
      setOpenedFromJobsView(false);
    } else {
      // Always switch to jobs view after job creation
      setCurrentView('jobs');
    }
  };
  
  // Global chat state
  const [chatWindowOpen, setChatWindowOpen] = useState(false);
  const [selectedChatJob, setSelectedChatJob] = useState(null);
  const [chatReplyMessage, setChatReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [expandedDealerships, setExpandedDealerships] = useState({}); // Track which dealerships are expanded
  
  // Pending approval state
  const [pendingApprovalJobs, setPendingApprovalJobs] = useState([]);
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
  
  // Pending pickups state (cars that need to be picked up)
  const [pendingPickups, setPendingPickups] = useState([]);
  const [pickupDropdownOpen, setPickupDropdownOpen] = useState(false);
  
  // Pending deliveries state (cars that need to be delivered back)
  const [pendingDeliveries, setPendingDeliveries] = useState([]);
  const [deliveryDropdownOpen, setDeliveryDropdownOpen] = useState(false);
  
  // Dealership notification popup state (for ALL users)
  const [dealershipPopupNotifications, setDealershipPopupNotifications] = useState([]);
  const [showDealershipPopup, setShowDealershipPopup] = useState(false);
  const [lastNotificationCount, setLastNotificationCount] = useState(0);
  
  // Pickup confirmation state (for portal jobs - ask if need to pick up car from dealership)
  const [pickupDialogOpen, setPickupDialogOpen] = useState(false);
  const [pendingPickupJob, setPendingPickupJob] = useState(null);
  
  const handlePickupConfirm = async (needsPickup) => {
    if (!pendingPickupJob) return;
    
    try {
      if (needsPickup) {
        const token = localStorage.getItem('access_token');
        await axios.put(
          `${API}/jobs/${pendingPickupJob.id}/pickup-pending?dealership_name=${encodeURIComponent(pendingPickupJob.dealership)}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`📍 ${pendingPickupJob.plate} bíður eftir að verða sóttur hjá ${pendingPickupJob.dealership}`);
      }
    } catch (error) {
      console.error('Error setting pickup:', error);
      toast.error('Villa við að merkja sókn');
    }
    
    setPickupDialogOpen(false);
    setPendingPickupJob(null);
  };
  
  // General dealership chat state
  const [generalChatOpen, setGeneralChatOpen] = useState(false);
  const [dealershipsWithChat, setDealershipsWithChat] = useState([]);
  const [totalGeneralUnread, setTotalGeneralUnread] = useState(0);
  const [selectedDealershipChat, setSelectedDealershipChat] = useState(null);
  const [generalChatReply, setGeneralChatReply] = useState('');
  const [sendingGeneralChat, setSendingGeneralChat] = useState(false);

  // Save language preference
  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    localStorage.setItem('app_language', newLanguage);
  };

  // Dark mode state and toggle
  const [darkMode, setDarkMode] = useState(() => {
    // Get dark mode preference from localStorage or default to light
    return localStorage.getItem('app_dark_mode') === 'true';
  });

  // Toggle dark mode
  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('app_dark_mode', newDarkMode.toString());
    
    // Apply dark mode class to document
    if (newDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  // Apply dark mode on mount
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);
  
  // Load pending approval jobs
  const loadPendingApprovalJobs = async () => {
    try {
      const response = await axios.get(`${API}/jobs`);
      const allJobs = response.data.jobs || response.data; // Handle both array and object response
      
      // Filter jobs with confirmation_status = 'pending_approval'
      const pending = allJobs.filter(job => job.confirmation_status === 'pending_approval');
      setPendingApprovalJobs(pending);
      setPendingApprovalCount(pending.length);
    } catch (error) {
      console.error('Failed to load pending approval jobs:', error);
    }
  };
  
  // Load pending pickups (cars that need to be picked up)
  const loadPendingPickups = async () => {
    try {
      const response = await axios.get(`${API}/jobs/pending-pickups`);
      setPendingPickups(response.data || []);
    } catch (error) {
      console.error('Failed to load pending pickups:', error);
    }
  };
  
  // Load pending deliveries (cars that need to be delivered back)
  const loadPendingDeliveries = async () => {
    try {
      const response = await axios.get(`${API}/jobs/pending-deliveries`);
      setPendingDeliveries(response.data || []);
    } catch (error) {
      console.error('Failed to load pending deliveries:', error);
    }
  };
  
  // Load undismissed dealership notifications for popup (ALL users can see)
  const loadDealershipPopupNotifications = async () => {
    try {
      const response = await axios.get(`${API}/dealership-notifications/undismissed`);
      const notifications = response.data || [];
      
      // Check if there are new notifications since last check
      if (notifications.length > 0 && notifications.length > lastNotificationCount) {
        // Play notification sound
        try {
          const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleA4KSZW/5Pt7FCYckb/h+IB6AABcl77W7J1sJg1Hp9Xu25FfGQA9kt7u9pp0LxJBl87g7ZNhFAI4nNDk6Y1kKAo8l9Lq9ZFfHxI9ltHh7I5nJAE8l9Hp9JRpKAw9ldDl7ZNrKQk8l9Tm8JRtLhQ/ltLn75NvMRQ/l9Xp8JRyMxg/l9Xp8JRyMxg/l9Xp8JRyMxg/l9Xp8JRyMxg/l9Xp8JRyMxg/l9Xp8JRyMxg/l9Xp8JRyMxc+l9bp8ZV0Nhk/mNfr85d2ORpAl9br85h4PRtBl9Xr9Jl6PxtAltPp85l9QR1CldPn8ph/RB9DldLm8ZmASCBDlNHk8JqDSyJEk8/h75yGTiRFksza7J2ITiRFksza7J2ITiRFksza7J2ITiRFksza7J2ITiRFksza7J2ITiRFkszh/JV2LhQ=');
          audio.volume = 0.5;
          audio.play().catch(() => {}); // Ignore errors if autoplay is blocked
        } catch (e) {
          console.log('Could not play notification sound');
        }
        
        // Show popup automatically
        setShowDealershipPopup(true);
      }
      
      setDealershipPopupNotifications(notifications);
      setLastNotificationCount(notifications.length);
    } catch (error) {
      console.error('Failed to load dealership popup notifications:', error);
    }
  };
  
  // Dismiss a notification
  const dismissDealershipNotification = async (notificationId) => {
    try {
      await axios.post(`${API}/dealership-notifications/${notificationId}/dismiss`);
      
      // Remove from local state
      setDealershipPopupNotifications(prev => prev.filter(n => n.id !== notificationId));
      
      // If no more notifications, close popup
      if (dealershipPopupNotifications.length <= 1) {
        setShowDealershipPopup(false);
      }
      
      toast.success('Tilkynning afgreidd');
    } catch (error) {
      console.error('Failed to dismiss notification:', error);
      toast.error('Villa við að afgreiða tilkynningu');
    }
  };
  
  // Dismiss all notifications
  const dismissAllDealershipNotifications = async () => {
    try {
      for (const notif of dealershipPopupNotifications) {
        await axios.post(`${API}/dealership-notifications/${notif.id}/dismiss`);
      }
      
      setDealershipPopupNotifications([]);
      setShowDealershipPopup(false);
      toast.success('Allar tilkynningar afgreiddar');
    } catch (error) {
      console.error('Failed to dismiss all notifications:', error);
      toast.error('Villa við að afgreiða tilkynningar');
    }
  };
  
  // Mark pickup as complete
  const handleMarkPickupComplete = async (jobId) => {
    try {
      await axios.put(`${API}/jobs/${jobId}/pickup-complete`);
      toast.success('✅ Bíll merktur sem sóttur');
      loadPendingPickups(); // Refresh the list
      setPickupDropdownOpen(false);
    } catch (error) {
      console.error('Failed to mark pickup complete:', error);
      toast.error('Villa við að merkja sókn');
    }
  };
  
  // Mark delivery as complete
  const handleMarkDeliveryComplete = async (jobId) => {
    try {
      await axios.put(`${API}/jobs/${jobId}/delivery-complete`);
      toast.success('✅ Bíll merktur sem skilað');
      loadPendingDeliveries(); // Refresh the list
      setDeliveryDropdownOpen(false);
    } catch (error) {
      console.error('Failed to mark delivery complete:', error);
      toast.error('Villa við að merkja skil');
    }
  };
  
  // Approve a job
  const handleApproveJob = async (job) => {
    try {
      await axios.put(`${API}/jobs/${job.id}/approve`, {
        action: 'approve',
        scheduled_time: job.scheduled_time || '09:00',
        admin_notes: ''
      });
      
      toast.success(`✅ Beiðni samþykkt fyrir ${job.plate}`);
      
      // Reload pending jobs
      await loadPendingApprovalJobs();
      
      // Close dropdown if no more pending
      if (pendingApprovalJobs.length <= 1) {
        setNotificationDropdownOpen(false);
      }
    } catch (error) {
      console.error('Failed to approve job:', error);
      toast.error('Villa við að samþykkja beiðni');
    }
  };
  
  // Reject a job
  const handleRejectJob = async (job) => {
    try {
      await axios.put(`${API}/jobs/${job.id}/approve`, {
        action: 'reject',
        rejection_reason: 'Hafnað af admin'
      });
      
      toast.success(`❌ Beiðni hafnað fyrir ${job.plate}`);
      
      // Reload pending jobs
      await loadPendingApprovalJobs();
      
      // Close dropdown if no more pending
      if (pendingApprovalJobs.length <= 1) {
        setNotificationDropdownOpen(false);
      }
    } catch (error) {
      console.error('Failed to reject job:', error);
      toast.error('Villa við að hafna beiðni');
    }
  };
  
  // Load general dealership chat
  const loadGeneralDealershipChat = async () => {
    try {
      const response = await axios.get(`${API}/dealerships/all`);
      const allDealerships = response.data;
      
      console.log('📊 Loaded dealerships:', allDealerships.length);
      
      // Filter dealerships that have chat messages
      const withChat = allDealerships.filter(d => 
        d.chat_messages && d.chat_messages.length > 0
      );
      
      console.log('💬 Dealerships with chat:', withChat.length);
      console.log('💬 Chat messages:', withChat.map(d => ({name: d.name, count: d.chat_messages?.length})));
      
      setDealershipsWithChat(withChat);
      
      // Auto-expand dealerships with unread messages
      const newExpanded = {};
      withChat.forEach(dealership => {
        const hasUnread = dealership.chat_messages?.some(
          m => m.sender === 'dealership' && !m.read
        );
        if (hasUnread) {
          newExpanded[dealership.name] = true;
        }
      });
      
      // Merge with existing expanded state (keep manually expanded ones)
      setExpandedDealerships(prev => ({...prev, ...newExpanded}));
      
      // Calculate total unread from dealerships
      const unread = withChat.reduce((total, dealership) => {
        const dealershipUnread = dealership.chat_messages?.filter(
          m => m.sender === 'dealership' && !m.read
        ).length || 0;
        return total + dealershipUnread;
      }, 0);
      
      console.log('🔴 Total unread:', unread);
      
      setTotalGeneralUnread(unread);
    } catch (error) {
      console.error('Failed to load general dealership chat:', error);
    }
  };
  
  // Send reply in general chat
  const handleSendGeneralChatReply = async () => {
    if (!selectedDealershipChat || !generalChatReply.trim()) {
      toast.error('Vinsamlegast skrifaðu skilaboð');
      return;
    }
    
    try {
      setSendingGeneralChat(true);
      
      await axios.post(`${API}/dealerships/${selectedDealershipChat.name}/chat`, {
        message: generalChatReply.trim()
      });
      
      toast.success('✅ Skilaboð send');
      setGeneralChatReply('');
      
      // Mark dealership messages as read
      await axios.put(`${API}/dealerships/${selectedDealershipChat.name}/chat/mark-read`);
      
      // Reload chat
      await loadGeneralDealershipChat();
    } catch (error) {
      console.error('Error sending general chat:', error);
      toast.error('Villa við að senda skilaboð');
    } finally {
      setSendingGeneralChat(false);
    }
  };
  
  // Toggle dealership expand/collapse
  const toggleDealership = (dealership) => {
    setExpandedDealerships(prev => ({
      ...prev,
      [dealership]: !prev[dealership]
    }));
  };
  
  // Handle send chat reply (for dealership-level chat)
  const handleSendChatReply = async (dealershipName) => {
    if (!chatReplyMessage.trim()) {
      toast.error('Vinsamlegast skrifaðu skilaboð');
      return;
    }
    
    try {
      setSendingReply(true);
      
      await axios.post(`${API}/dealerships/${encodeURIComponent(dealershipName)}/chat`, {
        message: chatReplyMessage.trim()
      });
      
      // Mark all dealership messages as read
      await axios.put(`${API}/dealerships/${encodeURIComponent(dealershipName)}/chat/mark-read`, {});
      
      toast.success('✅ Skilaboð send');
      setChatReplyMessage('');
      
      // Reload general dealership chat
      await loadGeneralDealershipChat();
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Villa við að senda skilaboð');
    } finally {
      setSendingReply(false);
    }
  };
  
  // Load messages on mount and refresh every 30 seconds
  useEffect(() => {
    loadPendingApprovalJobs(); // Load pending approval jobs
    loadPendingPickups(); // Load pending pickups
    loadPendingDeliveries(); // Load pending deliveries
    loadGeneralDealershipChat(); // Load general dealership chat
    loadDealershipPopupNotifications(); // Load popup notifications for ALL users
    
    const interval = setInterval(() => {
      loadPendingApprovalJobs(); // Refresh pending jobs too
      loadPendingPickups(); // Refresh pending pickups
      loadPendingDeliveries(); // Refresh pending deliveries
      loadGeneralDealershipChat(); // Refresh general chat too
      loadDealershipPopupNotifications(); // Check for new notifications
    }, 15000); // Refresh every 15 seconds for faster notification
    
    return () => clearInterval(interval);
  }, []);

  // Sync current view with URL hash for simple navigation between sections
  useEffect(() => {
    const syncFromHash = () => {
      const hash = (window.location.hash || '').replace('#', '');
      const allowed = ['registration','jobs','payments','dealerships','reports','customers','payday','dk','users','settings','qrscanner','calendar','store','pricelist','accounting','inventory','products'];
      if (allowed.includes(hash)) {
        setCurrentView(hash);
      }
    };
    syncFromHash();
    window.addEventListener('hashchange', syncFromHash);
    return () => window.removeEventListener('hashchange', syncFromHash);
  }, []);

  // Write the current view back to the URL hash so that a browser refresh
  // (F5 / Ctrl+R) lands the user on the same page they were on.
  useEffect(() => {
    try {
      const targetHash = `#${currentView}`;
      if (window.location.hash !== targetHash) {
        // Use replaceState so we don't pollute browser history on every navigation
        window.history.replaceState(null, '', targetHash);
      }
    } catch (_err) {
      // Ignore — non-critical
    }
  }, [currentView]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <div className="no-print">
        <UserHeader 
        user={user}
        onLogout={logout}
        onNavigate={setCurrentView}
        currentView={currentView}
        language={language}
        onLanguageChange={handleLanguageChange}
        darkMode={darkMode}
        onDarkModeToggle={toggleDarkMode}
        pendingApprovalJobs={pendingApprovalJobs}
        pendingApprovalCount={pendingApprovalCount}
        notificationDropdownOpen={notificationDropdownOpen}
        setNotificationDropdownOpen={setNotificationDropdownOpen}
        onApproveJob={handleApproveJob}
        onRejectJob={handleRejectJob}
        generalChatOpen={generalChatOpen}
        setGeneralChatOpen={setGeneralChatOpen}
        dealershipsWithChat={dealershipsWithChat}
        totalGeneralUnread={totalGeneralUnread}
        selectedDealershipChat={selectedDealershipChat}
        setSelectedDealershipChat={setSelectedDealershipChat}
        generalChatReply={generalChatReply}
        setGeneralChatReply={setGeneralChatReply}
        onSendGeneralChatReply={handleSendGeneralChatReply}
        sendingGeneralChat={sendingGeneralChat}
        dealershipPopupNotifications={dealershipPopupNotifications}
        onShowDealershipPopup={() => setShowDealershipPopup(true)}
        pendingPickups={pendingPickups}
        pickupDropdownOpen={pickupDropdownOpen}
        setPickupDropdownOpen={setPickupDropdownOpen}
        onMarkPickupComplete={handleMarkPickupComplete}
        pendingDeliveries={pendingDeliveries}
        deliveryDropdownOpen={deliveryDropdownOpen}
        setDeliveryDropdownOpen={setDeliveryDropdownOpen}
        onMarkDeliveryComplete={handleMarkDeliveryComplete}
      />
      </div>
      
      <main>
        {currentView === 'registration' && (user.role === 'admin' || user.role === 'staff' || user.permissions?.jobs) && <CarwashRegistrationForm language={language} />}
        {currentView === 'calendar' && (user.role === 'admin' || user.role === 'staff' || user.permissions?.jobs) && (
          <Suspense fallback={<div className="p-6 flex items-center justify-center"><div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div></div>}>
            <CalendarView onNavigate={setCurrentView} shouldOpenAddJobDialog={shouldOpenAddJobDialog} onAddJobDialogOpened={() => setShouldOpenAddJobDialog(false)} openedFromJobsView={openedFromJobsView} onJobCreated={handleJobCreatedFromDialog} />
          </Suspense>
        )}
        {currentView === 'jobs' && (user.role === 'admin' || user.role === 'staff' || user.permissions?.jobs) && (
          <Suspense fallback={<div className="p-6 flex items-center justify-center"><div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div></div>}>
            <JobsView language={language} user={user} onNavigate={setCurrentView} onOpenAddJob={openAddJobFromOtherView} />
          </Suspense>
        )}
        {user && user.role !== 'staff' && currentView === 'reports' && (user.role === 'admin' || user.permissions?.reports) && (
          <Suspense fallback={<div className="p-6 flex items-center justify-center"><div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div></div>}>
            <ReportsPage />
          </Suspense>
        )}
        {user && user.role !== 'staff' && currentView === 'customers' && (user.role === 'admin' || user.permissions?.customers) && (
          <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="text-lg">Hleð viðskiptavinum...</div></div>}>
            <CustomersManagement />
          </Suspense>
        )}
        {user && user.role !== 'staff' && currentView === 'store' && (user.role === 'admin' || user.permissions?.customers) && (
          <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="text-lg">Hleð verslun...</div></div>}>
            <Store
            onNavigateToPayments={() => {
              if (typeof window !== 'undefined') {
                window.__paymentsDefaultFilter = 'all';
              }
              setCurrentView('payments');
            }}
            onCreateStoreJob={(jobId) => {
              // Redirect to unpaid jobs view
              if (typeof window !== 'undefined') {
                window.__paymentsDefaultFilter = 'unpaid';
                window.__storeJobId = jobId; // Store job ID to highlight it
              }
              setCurrentView('payments');
              // Show success message
              toast.success('Verk búið til - farðu í "Ógreidd verk" til að greiða');
            }}
          />
          </Suspense>
        )}
        {user && user.role !== 'staff' && currentView === 'dealerships' && (user.role === 'admin' || user.permissions?.dealerships) && (
          <Suspense fallback={<div className="p-6 flex items-center justify-center"><div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div></div>}>
            <DealershipsPage />
          </Suspense>
        )}
        {user && (user.role === 'admin' || (user.role === 'manager' && (user.permissions?.payday || user.permissions?.accounting))) && currentView === 'accounting' && <AccountingManagement />}
        {user && (user.role === 'admin' || user.permissions?.settings) && currentView === 'inventory' && <InventoryPage />}
        {user && (user.role === 'admin' || user.permissions?.settings) && currentView === 'products' && <ProductsManagement />}
        {user && (user.role === 'admin' || user.permissions?.settings) && currentView === 'settings' && <SystemSettings />}
        {user && user.role === 'admin' && currentView === 'corporate' && <CorporateManagement />}
        {user && user.role === 'admin' && currentView === 'orders' && <OrdersManagement />}
        {currentView === 'payments' && (user.role === 'admin' || user.role === 'staff' || user.permissions?.payments) && (
          <Suspense fallback={<div className="p-6 flex items-center justify-center"><div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div></div>}>
            <PaymentsPage />
          </Suspense>
        )}
        {currentView === 'pricelist' && <PriceListPage />}
        {currentView === 'qrscanner' && (user.role === 'admin' || user.role === 'staff' || user.role === 'manager') && <QRScanner />}
      </main>
      
      {/* Booking Notification Popup - For dealership and customer portal bookings */}
      {showDealershipPopup && dealershipPopupNotifications.length > 0 && (
        <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden animate-bounce-once">
            {/* Header - Dynamic based on notification source */}
            {(() => {
              // Check source field to determine if from website or dealership portal
              const hasWebsiteBookings = dealershipPopupNotifications.some(n => n.source === 'website');
              const hasDealershipBookings = dealershipPopupNotifications.some(n => n.source === 'dealership_portal' || n.dealership);
              let headerText = 'Nýjar bókanir!';
              let headerIcon = '🔔';
              if (hasWebsiteBookings && !hasDealershipBookings) {
                headerText = 'Ný bókun frá heimasíðunni!';
                headerIcon = '🌐';
              } else if (hasDealershipBookings && !hasWebsiteBookings) {
                headerText = 'Nýtt verk frá bílasölu!';
                headerIcon = '🚗';
              }
              return (
                <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-4xl animate-pulse">{headerIcon}</span>
                      <div>
                        <h2 className="text-2xl font-bold">{headerText}</h2>
                        <p className="text-orange-100">{dealershipPopupNotifications.length} tilkynning{dealershipPopupNotifications.length !== 1 ? 'ar' : ''} bíða</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowDealershipPopup(false)}
                      className="text-white/80 hover:text-white text-2xl p-2 hover:bg-white/10 rounded-lg transition-colors"
                      title="Loka"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              );
            })()}
            
            {/* Notifications List */}
            <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
              {dealershipPopupNotifications.map((notif) => {
                // Check source field: 'website' = from public website, 'dealership_portal' = from dealership
                const isFromWebsite = notif.source === 'website';
                const isFromDealership = notif.source === 'dealership_portal' || notif.dealership;
                // For portal bookings, prefer owner_name (car owner from rogg.is), fallback to customer_name
                // For dealership bookings, prefer owner_name
                const displayName = notif.owner_name || notif.customer_name || 'Óþekktur';
                return (
                <div 
                  key={notif.id}
                  className={`${isFromWebsite ? 'bg-blue-50 dark:bg-slate-700 border-2 border-blue-200 dark:border-blue-600' : 'bg-orange-50 dark:bg-slate-700 border-2 border-orange-200 dark:border-orange-600'} rounded-xl p-4 shadow-sm`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-2xl">{isFromWebsite ? '🌐' : '🏢'}</span>
                        <span className={`font-bold text-lg ${isFromWebsite ? 'text-blue-800 dark:text-blue-300' : 'text-orange-800 dark:text-orange-300'}`}>
                          {isFromWebsite ? 'Heimasíða' : (notif.dealership || 'Bílasala')}
                        </span>
                        {isFromWebsite && (
                          <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">Netbókun</span>
                        )}
                        {isFromDealership && !isFromWebsite && (
                          <span className="bg-orange-100 text-orange-800 text-xs px-2 py-0.5 rounded-full">Bílasölu portal</span>
                        )}
                      </div>
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">🚘 Bílnúmer:</span>
                          <span className="font-bold text-slate-900 dark:text-slate-100">{notif.plate}</span>
                          {(notif.car_make || notif.car_model) && (
                            <span className="text-slate-500">({[notif.car_make, notif.car_model].filter(Boolean).join(' ')})</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">👤 Viðskiptavinur:</span>
                          <span>{displayName}</span>
                        </div>
                        {notif.phone && (
                          <div className="flex items-center gap-2">
                            <span className="font-medium">📞 Sími:</span>
                            <span>{notif.phone}</span>
                          </div>
                        )}
                        {notif.email && (
                          <div className="flex items-center gap-2">
                            <span className="font-medium">📧 Netfang:</span>
                            <span>{notif.email}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <span className="font-medium">📅 Dagsetning:</span>
                          <span>{notif.scheduled_date} {notif.scheduled_time && `kl. ${notif.scheduled_time}`}</span>
                        </div>
                        {notif.items && notif.items.length > 0 && (
                          <div className="flex items-start gap-2">
                            <span className="font-medium">🧽 Þjónusta:</span>
                            <span>{notif.items.map(i => i.service || i.name).filter(Boolean).join(', ')}</span>
                          </div>
                        )}
                        {notif.total > 0 && (
                          <div className="flex items-center gap-2">
                            <span className="font-medium">💰 Verð:</span>
                            <span className="font-bold">{notif.total.toLocaleString()} ISK</span>
                          </div>
                        )}
                        {(notif.wants_pickup_delivery || notif.needs_pickup_delivery) && (
                          <div className="flex items-center gap-2">
                            <span className="font-medium">🚚 Sækja/skila:</span>
                            <span className="text-orange-700 font-medium">Já{notif.pickup_address ? ` - ${notif.pickup_address}` : ''}</span>
                          </div>
                        )}
                        {notif.wants_callback && (
                          <div className="flex items-center gap-2">
                            <span className="font-medium">📞 Óskað eftir símtali:</span>
                            <span className="text-blue-700 font-medium">Já</span>
                          </div>
                        )}
                        {notif.notes && (
                          <div className="flex items-start gap-2 mt-1">
                            <span className="font-medium">💬 Athugasemd:</span>
                            <span className="text-slate-700 italic">{notif.notes}</span>
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-2">
                        {notif.created_at ? new Date(notif.created_at).toLocaleString('is-IS') : ''}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={async () => {
                          if (notif.job_id && notif.type === 'new_job_submission') {
                            // This is a dealership portal job - approve it
                            try {
                              const token = localStorage.getItem('access_token');
                              
                              // Step 1: Approve job - pass scheduled_date and time from booking
                              await axios.put(`${API}/jobs/${notif.job_id}/approve`, {
                                action: 'approve',
                                scheduled_date: notif.scheduled_date || undefined,
                                scheduled_time: notif.scheduled_time || '09:00'
                              }, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              
                              // Step 2: Dismiss notification (non-critical - don't fail if this errors)
                              try {
                                await axios.post(`${API}/dealership-notifications/${notif.id}/dismiss`, {}, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                              } catch (dismissError) {
                                console.warn('Failed to dismiss notification (non-critical):', dismissError);
                              }
                              
                              // Step 3: Update UI
                              setDealershipPopupNotifications(prev => prev.filter(n => n.id !== notif.id));
                              if (dealershipPopupNotifications.length <= 1) {
                                setShowDealershipPopup(false);
                              }
                              
                              toast.success(`✅ Verk samþykkt fyrir ${notif.plate}!`);
                              
                              // Step 4: Show pickup dialog for portal jobs
                              if (notif.dealership) {
                                setPendingPickupJob({
                                  id: notif.job_id,
                                  plate: notif.plate,
                                  dealership: notif.dealership
                                });
                                setPickupDialogOpen(true);
                              }
                              
                              // Step 5: Trigger jobs reload in JobsView component
                              window.dispatchEvent(new CustomEvent('jobApproved', { detail: { jobId: notif.job_id } }));
                              
                              // Step 6: Reload product data (non-critical)
                              try {
                                await loadData();
                              } catch (loadError) {
                                console.warn('Failed to reload data (non-critical):', loadError);
                              }
                            } catch (error) {
                              console.error('Failed to approve job:', error);
                              toast.error('Villa við að samþykkja verk');
                            }
                          } else {
                            // Regular notification - just dismiss
                            dismissDealershipNotification(notif.id);
                          }
                        }}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
                      >
                        ✓ Afgreiða
                      </button>
                      <button
                        onClick={async () => {
                          if (notif.job_id) {
                            // Ask for rejection reason
                            const reason = window.prompt('Ástæða höfnunar:', '');
                            if (reason === null) return; // User cancelled
                            if (!reason.trim()) {
                              toast.error('Vinsamlegast skráðu ástæðu höfnunar');
                              return;
                            }
                            try {
                              const token = localStorage.getItem('access_token');
                              if (notif.type === 'new_job_submission') {
                                // Dealership portal job - reject it with reason
                                await axios.put(`${API}/jobs/${notif.job_id}/approve`, {
                                  action: 'reject',
                                  rejection_reason: reason.trim()
                                }, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                              } else {
                                // Customer portal booking - cancel it with reason
                                const cancelledByName = user?.name || user?.email || 'Stjórnandi';
                                await axios.post(`${API}/jobs/${notif.job_id}/cancel`, {
                                  reason: reason.trim(),
                                  cancelled_by_name: cancelledByName
                                }, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                              }
                              // Dismiss the notification
                              await axios.post(`${API}/dealership-notifications/${notif.id}/dismiss`, {}, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              setDealershipPopupNotifications(prev => prev.filter(n => n.id !== notif.id));
                              if (dealershipPopupNotifications.length <= 1) {
                                setShowDealershipPopup(false);
                              }
                              toast.success(`❌ Bókun hafnað fyrir ${notif.plate}`);
                            } catch (error) {
                              console.error('Failed to reject booking:', error);
                              toast.error('Villa við að hafna bókun');
                            }
                          } else {
                            // If no job_id, just dismiss the notification
                            dismissDealershipNotification(notif.id);
                          }
                        }}
                        className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
                      >
                        ✕ Hafna
                      </button>
                    </div>
                  </div>
                </div>
              )})}
            </div>
            
            {/* Footer */}
            <div className="border-t border-slate-200 dark:border-slate-700 p-4 bg-slate-50 dark:bg-slate-900 flex justify-between items-center gap-2">
              <button
                onClick={() => setShowDealershipPopup(false)}
                className="bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 px-4 py-2 rounded-lg font-medium transition-colors"
              >
                ← Loka
              </button>
              <span className="text-sm text-slate-600 dark:text-slate-400 hidden sm:block">
                Smelltu á 🔔 til að sjá beiðnir
              </span>
              <button
                onClick={dismissAllDealershipNotifications}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Afgreiða allt
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Pickup Confirmation Dialog (for portal jobs - ask if need to pick up car from dealership) */}
      <Dialog open={pickupDialogOpen} onOpenChange={setPickupDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">📍 Sækja bíl?</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-lg mb-4">
              Þarf að sækja bílinn <strong>{pendingPickupJob?.plate}</strong> hjá <strong className="text-blue-600">{pendingPickupJob?.dealership}</strong>?
            </p>
            
            <div className="flex flex-col gap-3">
              <Button 
                onClick={() => handlePickupConfirm(true)}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg"
              >
                📍 Já, þarf að sækja
              </Button>
              <Button 
                onClick={() => handlePickupConfirm(false)}
                variant="outline"
                className="w-full py-6 text-lg"
              >
                ❌ Nei, bíllinn kemur sjálfur
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Chat Floating Button - Always visible, blue color */}
      <button
        onClick={() => setChatWindowOpen(!chatWindowOpen)}
        className="fixed bottom-4 right-4 md:bottom-6 md:right-6 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-3 md:p-4 shadow-lg transition-all z-50 flex items-center gap-2"
      >
        <span className="text-xl md:text-2xl">💬</span>
        {totalGeneralUnread > 0 && (
          <span className="absolute -top-1 -right-1 md:-top-2 md:-right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 md:h-6 md:w-6 flex items-center justify-center animate-pulse">
            {totalGeneralUnread}
          </span>
        )}
      </button>
      
      {/* Global Chat Panel - Dealership Chat (NOT job-specific) */}
      {chatWindowOpen && (
        <div className="fixed bottom-20 right-4 md:bottom-24 md:right-20 w-[calc(100vw-2rem)] md:w-96 bg-white rounded-lg shadow-2xl z-50 max-h-[70vh] md:max-h-[600px] flex flex-col border-2 border-blue-200">
          {/* Chat Header */}
          <div className="bg-blue-600 text-white p-3 md:p-4 rounded-t-lg flex items-center justify-between">
            <div>
              <div className="font-bold text-sm md:text-base">💬 Spjall við bílasölur</div>
              <div className="text-xs opacity-90">
                {dealershipsWithChat.length} bílasölur • {totalGeneralUnread} ólesið
              </div>
            </div>
            <button
              onClick={() => {
                setChatWindowOpen(false);
                setSelectedChatJob(null);
              }}
              className="text-white hover:bg-blue-700 rounded p-1"
            >
              ✕
            </button>
          </div>
          
          {/* Chat Body - Scrollable */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
            {dealershipsWithChat.length === 0 ? (
              <div className="text-center text-slate-400 py-8">
                📭 Engin skilaboð
              </div>
            ) : (
              dealershipsWithChat.map((dealership) => {
                const unreadCount = dealership.chat_messages?.filter(
                  m => m.sender === 'dealership' && !m.read
                ).length || 0;
                const isExpanded = expandedDealerships[dealership.name];
                
                return (
                  <div key={dealership.name} className="space-y-2">
                    {/* Dealership Header - Clickable */}
                    <div 
                      onClick={() => toggleDealership(dealership.name)}
                      className="p-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg cursor-pointer transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-lg transition-transform" style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                            ▶
                          </span>
                          <span className="text-lg">🏢</span>
                          <div>
                            <div className="font-bold">{dealership.name}</div>
                            <div className="text-xs opacity-90">{dealership.chat_messages?.length || 0} skilaboð</div>
                          </div>
                        </div>
                        {unreadCount > 0 && (
                          <span className="bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
                            {unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Messages - Show when expanded */}
                    {isExpanded && (
                      <div className="ml-3 space-y-2">
                        {/* Message History */}
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {dealership.chat_messages.map((msg, idx) => (
                            <div key={idx} className={`p-2 rounded text-sm ${
                              msg.sender === 'dealership' 
                                ? 'bg-purple-50 border-l-2 border-purple-400' 
                                : 'bg-green-50 border-l-2 border-green-400'
                            }`}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-medium">
                                  {msg.sender === 'dealership' ? `🏢 ${dealership.name}` : `👔 ${msg.sender_name || 'Admin'}`}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {new Date(msg.timestamp).toLocaleString('is-IS', { 
                                    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
                                  })}
                                </span>
                              </div>
                              <div className="text-xs whitespace-pre-wrap">{msg.message}</div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Reply Box */}
                        <div className="space-y-2">
                          <textarea
                            value={chatReplyMessage}
                            onChange={(e) => setChatReplyMessage(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                if (chatReplyMessage.trim() && !sendingReply) {
                                  handleSendChatReply(dealership.name);
                                }
                              }
                            }}
                            placeholder="Skrifaðu svar... (Enter til að senda)"
                            className="w-full min-h-[60px] p-2 border rounded text-sm"
                            disabled={sendingReply}
                          />
                          <Button
                            size="sm"
                            onClick={() => handleSendChatReply(dealership.name)}
                            disabled={!chatReplyMessage.trim() || sendingReply}
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            {sendingReply ? '⏳ Sendi...' : '📤 Senda'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Dealership Portal Page - Public
// Car manufacturer logo helper function - reusable
const getCarLogo = (make) => {
  if (!make) return null;
  const makeKey = make.toLowerCase().replace(/\s+/g, '');
  const logoMap = {
    'toyota': 'https://www.carlogos.org/car-logos/toyota-logo.png',
    'honda': 'https://www.carlogos.org/car-logos/honda-logo.png',
    'ford': 'https://www.carlogos.org/car-logos/ford-logo.png',
    'bmw': 'https://www.carlogos.org/car-logos/bmw-logo.png',
    'mercedesbenz': 'https://www.carlogos.org/car-logos/mercedes-benz-logo.png',
    'mercedes-benz': 'https://www.carlogos.org/car-logos/mercedes-benz-logo.png',
    'volkswagen': 'https://www.carlogos.org/car-logos/volkswagen-logo.png',
    'audi': 'https://www.carlogos.org/car-logos/audi-logo.png',
    'nissan': 'https://www.carlogos.org/car-logos/nissan-logo.png',
    'hyundai': 'https://www.carlogos.org/car-logos/hyundai-logo.png',
    'kia': 'https://www.carlogos.org/car-logos/kia-logo.png',
    'mazda': 'https://www.carlogos.org/car-logos/mazda-logo.png',
    'subaru': 'https://www.carlogos.org/car-logos/subaru-logo.png',
    'lexus': 'https://www.carlogos.org/car-logos/lexus-logo.png',
    'tesla': 'https://www.carlogos.org/car-logos/tesla-logo.png',
    'volvo': 'https://www.carlogos.org/car-logos/volvo-logo.png',
    'porsche': 'https://www.carlogos.org/car-logos/porsche-logo.png',
    'landrover': 'https://www.carlogos.org/car-logos/land-rover-logo.png',
    'land rover': 'https://www.carlogos.org/car-logos/land-rover-logo.png',
    'jeep': 'https://www.carlogos.org/car-logos/jeep-logo.png',
    'chevrolet': 'https://www.carlogos.org/car-logos/chevrolet-logo.png',
    'dodge': 'https://www.carlogos.org/car-logos/dodge-logo.png',
    'chrysler': 'https://www.carlogos.org/car-logos/chrysler-logo.png',
    'mitsubishi': 'https://www.carlogos.org/car-logos/mitsubishi-logo.png',
    'peugeot': 'https://www.carlogos.org/car-logos/peugeot-logo.png',
    'renault': 'https://www.carlogos.org/car-logos/renault-logo.png',
    'skoda': 'https://www.carlogos.org/car-logos/skoda-logo.png',
    'suzuki': 'https://www.carlogos.org/car-logos/suzuki-logo.png',
    'fiat': 'https://www.carlogos.org/car-logos/fiat-logo.png',
    'seat': 'https://www.carlogos.org/car-logos/seat-logo.png',
    'mini': 'https://www.carlogos.org/car-logos/mini-logo.png',
    'jaguar': 'https://www.carlogos.org/car-logos/jaguar-logo.png',
    'alfa romeo': 'https://www.carlogos.org/car-logos/alfa-romeo-logo.png',
    'alfaromeo': 'https://www.carlogos.org/car-logos/alfa-romeo-logo.png'
  };
  return logoMap[makeKey] || null;
};

function DealershipPortalPage() {
  const [portalData, setPortalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Theme state - light is default
  const [darkMode, setDarkMode] = useState(() => {
    try { return localStorage.getItem('portal_theme') === 'dark'; } catch { return false; }
  });
  
  // Filter state for status cards
  const [statusFilter, setStatusFilter] = useState(null); // null = show all, 'pending', 'in_progress', 'done'
  
  // Chat states (not per-job anymore)
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [sendingChat, setSendingChat] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // New job submission states
  const [newJobDialogOpen, setNewJobDialogOpen] = useState(false);
  const [submittingJob, setSubmittingJob] = useState(false);
  
  // History states
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyJobs, setHistoryJobs] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyShowPrices, setHistoryShowPrices] = useState(false);
  const [historySearch, setHistorySearch] = useState('');
  const [services, setServices] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [prices, setPrices] = useState({});
  const [defaultPrices, setDefaultPrices] = useState({}); // Store original prices for discount calculation
  const [lookingUpVehicle, setLookingUpVehicle] = useState(false);
  const [dealershipDiscount, setDealershipDiscount] = useState(0);
  const [customerDiscount, setCustomerDiscount] = useState(null);
  const [categoryDiscounts, setCategoryDiscounts] = useState({}); // Category-specific discounts
  const [hasCustomPricing, setHasCustomPricing] = useState(false);
  const [customPriceServices, setCustomPriceServices] = useState(new Set()); // Track which services have custom prices
  const [payerKennitalaLoading, setPayerKennitalaLoading] = useState(false);
  const [newJobForm, setNewJobForm] = useState({
    plate: '',
    owner_name: '',
    owner_kt: '',
    phone_number: '',
    scheduled_date: new Date().toISOString().split('T')[0],
    scheduled_time: '',
    notes: '',
    services: [],
    car_size: '',
    car_make: '',
    car_model: '',
    payer_is_owner: true,
    payer_name: '',
    payer_kt: ''
  });
  
  // Accounting system name state
  const [accountingSystemName, setAccountingSystemName] = useState('Bókhaldskerfið');

  const toggleTheme = () => {
    setDarkMode(prev => {
      const next = !prev;
      try { localStorage.setItem('portal_theme', next ? 'dark' : 'light'); } catch {}
      return next;
    });
  };
  
  const loadHistory = async () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistorySearch('');
    try {
      const urlPath = window.location.pathname;
      const token = urlPath.split('/').pop();
      const response = await axios.get(`${API}/dealership-portal/${token}/history`);
      setHistoryJobs(response.data.jobs || []);
      setHistoryShowPrices(response.data.show_prices || false);
    } catch (error) {
      console.error('Error loading history:', error);
      toast.error('Villa við að sækja sögu');
    } finally {
      setHistoryLoading(false);
    }
  }; // Display name for accounting system

  useEffect(() => {
    // Get token from URL
    const urlPath = window.location.pathname;
    const tokenMatch = urlPath.match(/\/dealership-portal\/([^\/]+)/);
    
    if (tokenMatch && tokenMatch[1]) {
      loadPortalData(tokenMatch[1]);
      loadSettings();  // Load services and prices
      loadChatMessages(tokenMatch[1]);  // Load chat messages
      loadAccountingSystem();  // Load accounting system name
      
      // Set up auto-refresh every 30 seconds
      const interval = setInterval(() => {
        loadPortalData(tokenMatch[1], true); // Silent refresh
        loadChatMessages(tokenMatch[1], true); // Silent chat refresh
      }, 30000);
      
      return () => clearInterval(interval);
    } else {
      setError('Invalid portal link');
      setLoading(false);
    }
  }, []);

  // Load custom pricing based on PAYER (not owner)
  useEffect(() => {
    const loadPayerPricing = async () => {
      // Get token from URL
      const urlPath = window.location.pathname;
      const tokenMatch = urlPath.match(/\/dealership-portal\/([^\/]+)/);
      const token = tokenMatch ? tokenMatch[1] : null;
      
      if (!token) return;
      
      // Reset pricing first
      setHasCustomPricing(false);
      setCustomerDiscount(null);
      setCategoryDiscounts({});
      setCustomPriceServices(new Set());
      await loadSettings(); // Reload default prices
      
      // Determine who the payer is
      let payerKT = null;
      if (newJobForm.payer_is_owner && newJobForm.owner_kt) {
        // Payer is owner
        payerKT = newJobForm.owner_kt;
      } else if (!newJobForm.payer_is_owner && newJobForm.payer_kt) {
        // Payer is different person
        payerKT = newJobForm.payer_kt;
      }
      
      // Load custom pricing for payer
      if (payerKT) {
        try {
          const customerRes = await axios.get(`${API}/dealership-portal/${token}/customer-pricing/${payerKT}`);
          if (customerRes.data && customerRes.data.found) {
            const customerData = customerRes.data;
            
            // Set general discount
            if (customerData.discount_percentage) {
              setCustomerDiscount(customerData.discount_percentage);
            }
            
            // Set category discounts
            if (customerData.category_discounts) {
              setCategoryDiscounts(customerData.category_discounts);
              console.log('💰 Category discounts loaded:', customerData.category_discounts);
            }
            
            // Check for custom pricing
            if (customerData.custom_pricing_enabled && customerData.custom_prices) {
              setHasCustomPricing(true);
              
              // Track which services have custom prices
              const customServices = new Set();
              Object.keys(customerData.custom_prices).forEach(serviceId => {
                // Only mark as custom if there's actually a price defined
                const servicePrices = customerData.custom_prices[serviceId];
                if (servicePrices && Object.keys(servicePrices).length > 0) {
                  customServices.add(serviceId);
                }
              });
              setCustomPriceServices(customServices);
              
              // Merge custom prices with default prices
              setPrices(prevPrices => {
                const merged = {...prevPrices};
                Object.keys(customerData.custom_prices).forEach(serviceId => {
                  merged[serviceId] = customerData.custom_prices[serviceId];
                });
                return merged;
              });
              console.log('💰 Custom pricing loaded for payer:', payerKT, 'Services with custom prices:', [...customServices]);
              toast.info('💰 Sérverðskrá viðskiptavinar hlaðin');
            }
          }
        } catch (err) {
          console.log('Could not fetch payer pricing:', err);
        }
      }
    };
    
    // Load payer pricing when payer changes (don't require owner_kt)
    if (newJobForm.payer_kt || newJobForm.owner_kt) {
      loadPayerPricing();
    }
  }, [newJobForm.payer_is_owner, newJobForm.payer_kt, newJobForm.owner_kt]);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      const settingsData = response.data;
      
      // Filter services based on BOTH enabled and available fields
      const enabledServices = (settingsData.services || []).filter(s => {
        // Check 'available' field first (used in backend)
        if (s.available === false) return false;
        
        // Check 'enabled' field (newer field)
        if (s.enabled === false) return false;
        
        // For klippikort services, require explicit enabled=true OR available=true
        if (s.name?.toLowerCase().includes('klippikort')) {
          return s.enabled === true || s.available === true;
        }
        
        // For other services, allow if not explicitly disabled
        return true;
      });
      
      // For dealership portal, hide "Skutla til" from form (it will be auto-added on backend)
      const servicesForPortal = enabledServices.filter(s => !s.name?.toLowerCase().includes('skutla'));
      
      setServices(servicesForPortal);
      setSizes(settingsData.sizes || []);
      setPrices(settingsData.prices || {});
      setDefaultPrices(settingsData.prices || {}); // Store original prices
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };
  
  const loadChatMessages = async (token, silent = false) => {
    try {
      const response = await axios.get(`${API}/dealership-portal/${token}/chat`);
      setChatMessages(response.data.messages || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error loading chat messages:', error);
    }
  };

  const loadPortalData = async (token, silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await axios.get(`${API}/dealership-portal/${token}`);
      setPortalData(response.data);
      setDealershipDiscount(response.data.dealership_discount || 0);
      setError(null);
    } catch (error) {
      console.error('Error loading portal data:', error);
      if (error.response?.status === 404) {
        setError('Portal link fannst ekki');
      } else {
        setError('Villa við að sækja portal gögn');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadAccountingSystem = async () => {
    try {
      const response = await axios.get(`${API}/accounting/status`);
      const system = response.data.accounting_system;
      const systems = response.data.systems || {};
      
      // Priority: Show the system that is NOT in mock mode, or the one that is connected
      let activeName = 'Bókhaldskerfið'; // Default
      
      if (system === 'DK') {
        activeName = 'DK';
      } else if (system === 'PAYDAY') {
        activeName = 'Payday';
      } else if (system === 'BOTH') {
        // Check which one is actually being used (not mock)
        const dkInMock = systems.dk?.message === 'Mock mode';
        const paydayInMock = systems.payday?.message === 'Mock mode';
        
        if (!dkInMock && systems.dk?.enabled) {
          activeName = 'DK'; // DK is real, use it
        } else if (!paydayInMock && systems.payday?.enabled) {
          activeName = 'Payday'; // Payday is real, use it
        } else if (systems.payday?.enabled) {
          activeName = 'Payday'; // Default to Payday if both are mock
        } else if (systems.dk?.enabled) {
          activeName = 'DK';
        }
      }
      
      setAccountingSystemName(activeName);
    } catch (error) {
      console.error('Error loading accounting system:', error);
      setAccountingSystemName('Bókhaldskerfið'); // Default
    }
  };

  const handleSendChatMessage = async () => {
    if (!chatInput.trim()) return;
    
    try {
      setSendingChat(true);
      const urlPath = window.location.pathname;
      const token = urlPath.split('/').pop();
      
      await axios.post(`${API}/dealership-portal/${token}/chat`, {
        message: chatInput.trim()
      });
      
      toast.success('✅ Skilaboð send');
      setChatInput('');
      
      // Reload chat messages
      await loadChatMessages(token, true);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Villa við að senda skilaboð');
    } finally {
      setSendingChat(false);
    }
  };
  
  const handleOpenChat = async () => {
    setChatOpen(true);
    // Mark messages as read
    try {
      const urlPath = window.location.pathname;
      const token = urlPath.split('/').pop();
      await axios.put(`${API}/dealership-portal/${token}/chat/mark-read`);
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };
  
  const handleSubmitJob = async () => {
    // Validation
    if (!newJobForm.plate) {
      toast.error('Vinsamlegast sláðu inn númeraplötu');
      return;
    }
    if (!newJobForm.owner_name) {
      toast.error('Vinsamlegast sláðu inn nafn eiganda');
      return;
    }
    if (!newJobForm.owner_kt) {
      toast.error('Vinsamlegast sláðu inn kennitölu eiganda');
      return;
    }
    if (!newJobForm.scheduled_date) {
      toast.error('Vinsamlegast veldu dagsetningu');
      return;
    }
    if (newJobForm.services.length === 0) {
      toast.error('Vinsamlegast veldu að minnsta kosti eina þjónustu');
      return;
    }
    if (!newJobForm.car_size) {
      toast.error('Vinsamlegast veldu bílastærð');
      return;
    }
    
    try {
      setSubmittingJob(true);
      const urlPath = window.location.pathname;
      const token = urlPath.split('/').pop();
      
      const response = await axios.post(`${API}/dealership-portal/${token}/submit-job`, newJobForm);
      
      if (response.data.success) {
        toast.success('✅ Verk sent til samþykkis! Þú færð tilkynningu þegar það er samþykkt.');
        setNewJobDialogOpen(false);
        
        // Reset form
        setNewJobForm({
          plate: '',
          owner_name: '',
          owner_kt: '',
          phone_number: '',
          scheduled_date: new Date().toISOString().split('T')[0],
          scheduled_time: '',
          notes: '',
          services: [],
          car_size: '',
          car_make: '',
          car_model: '',
          payer_is_owner: true,
          payer_name: '',
          payer_kt: ''
        });
        setCustomerDiscount(null);
        setHasCustomPricing(false);
        setCategoryDiscounts({});
        setCustomPriceServices(new Set());
        // Reload default prices
        loadSettings();
        
        // Reload data
        loadPortalData(token, true);
      }
    } catch (error) {
      console.error('Error submitting job:', error);
      toast.error(error.response?.data?.detail || 'Villa við að skrá verk');
    } finally {
      setSubmittingJob(false);
    }
  };
  
  const calculateJobTotal = () => {
    if (!newJobForm.car_size || newJobForm.services.length === 0) return 0;

    // Finna innifaldar þjónustur út frá "includes" stillingum (eins og í aðal verðsreikningi)
    const includedServiceIds = new Set();
    newJobForm.services.forEach(serviceId => {
      const service = services.find(s => s.id === serviceId);
      if (service && Array.isArray(service.includes)) {
        service.includes.forEach(incId => {
          // Markaðu bara sem innifalda ef hún er líka valin
          if (newJobForm.services.includes(incId)) {
            includedServiceIds.add(incId);
          }
        });
      }
    });

    // Leggja saman bara þjónustur sem EKKI eru innifaldar
    // Athuga hverja þjónustu fyrir sig - sérverð fær engan afslátt
    let total = 0;
    newJobForm.services.forEach(serviceId => {
      if (includedServiceIds.has(serviceId)) return;
      
      const price = prices[serviceId]?.[newJobForm.car_size] || 0;
      const defaultPrice = defaultPrices[serviceId]?.[newJobForm.car_size] || 0;
      const service = services.find(s => s.id === serviceId);
      const serviceCategory = service?.category || 'general';
      
      // Athuga hvort þessi þjónusta hefur sérverð FYRIR ÞESSA STÆRÐ
      // Ef verðið er frábrugðið default verði OG viðskiptavinur hefur sérverðskrá virka
      const hasCustomPriceForThisSize = hasCustomPricing && 
        customPriceServices.has(serviceId) && 
        price !== defaultPrice;
      
      if (hasCustomPriceForThisSize) {
        // Sérverð - enginn afsláttur
        total += price;
      } else {
        // Ekki sérverð fyrir þessa stærð - athuga afslátt
        // Nota default verð og bæta afslætti við
        const priceToUse = defaultPrice || price;
        
        // Forgangsröð: flokka-afsláttur > almennur viðskiptavinur afsláttur > bílasölu afsláttur
        let discountPercent = 0;
        
        // 1. Athuga flokka-afslátt
        if (categoryDiscounts && categoryDiscounts[serviceCategory]) {
          discountPercent = categoryDiscounts[serviceCategory];
        }
        // 2. Ef enginn flokka-afsláttur, nota almennan viðskiptavina-afslátt
        else if (customerDiscount && customerDiscount > 0) {
          discountPercent = customerDiscount;
        }
        // 3. Ef enginn viðskiptavina-afsláttur, nota bílasölu-afslátt
        else if (dealershipDiscount > 0) {
          discountPercent = dealershipDiscount;
        }
        
        if (discountPercent > 0) {
          total += priceToUse * (1 - discountPercent / 100);
        } else {
          total += priceToUse;
        }
      }
    });

    return Math.round(total);
  };
  
  const toggleService = (serviceId) => {
    setNewJobForm(prev => ({
      ...prev,
      services: prev.services.includes(serviceId)
        ? prev.services.filter(id => id !== serviceId)
        : [...prev.services, serviceId]
    }));
  };
  
  const handleVehicleLookup = async (plate) => {
    if (!plate || plate.length < 3) return;
    
    try {
      setLookingUpVehicle(true);
      const urlPath = window.location.pathname;
      const tokenMatch = urlPath.match(/\/dealership-portal\/([^\/]+)/);
      const portalToken = tokenMatch ? tokenMatch[1] : null;
      if (!portalToken) return;
      const response = await axios.get(`${API}/dealership-portal/${portalToken}/vehicle-lookup/${plate}`);
      
      if (response.data) {
        const vehicleData = response.data;
        const ownerKt = vehicleData.owner_kt || vehicleData.owner_kennitala;
        
        // Auto-fill form - handle both owner_kt and owner_kennitala from API
        setNewJobForm(prev => ({
          ...prev,
          owner_name: vehicleData.owner_name || prev.owner_name,
          owner_kt: ownerKt || prev.owner_kt,
          phone_number: vehicleData.phone_number || prev.phone_number,
          car_size: vehicleData.size || prev.car_size,
          car_make: vehicleData.manufacturer || vehicleData.make || prev.car_make,
          car_model: vehicleData.model || prev.car_model
        }));
        
        // DO NOT load custom pricing here - only load when payer is confirmed
        // Custom pricing should follow the PAYER, not the owner
        // Owner might not be the payer (can use "Velja annan greiðanda")
        
        toast.success('✅ Upplýsingar fundust og fyllt sjálfkrafa');
      }
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error(error.response?.data?.detail || 'Dagstakmarki uppflettinga náð');
      }
      console.log('Vehicle lookup failed (not critical):', error);
    } finally {
      setLookingUpVehicle(false);
    }
  };

  const handlePayerKennitalaChange = async (kennitala) => {
    setNewJobForm(prev => ({...prev, payer_kt: kennitala}));
    
    // If kennitala is 10 digits, trigger lookup for NAME only
    if (kennitala.replace('-', '').length === 10) {
      try {
        setPayerKennitalaLoading(true);
        const response = await axios.get(`${API}/person/lookup/${kennitala}`);
        
        if (response.data && (response.data.full_name || response.data.name)) {
          setNewJobForm(prev => ({
            ...prev,
            payer_name: response.data.full_name || response.data.name
          }));
          toast.success('✅ Greiðandi fannst í Þjóðskrá');
        }
        
        // Custom pricing will be loaded automatically by useEffect
        // when payer_kt changes
        
      } catch (error) {
        console.log('Person lookup failed:', error);
      } finally {
        setPayerKennitalaLoading(false);
      }
    }
  };

  const getStatusBadge = (status, shuttleStatus, shuttleTo, pickupStatus, pickupFrom) => {
    const d = darkMode;
    if (pickupStatus === 'pending' && pickupFrom) {
      return <span className={`px-2.5 py-1 rounded-md text-xs font-semibold border ${d ? 'bg-cyan-500/10 text-cyan-300 border-cyan-500/20' : 'bg-cyan-50 text-cyan-700 border-cyan-200'}`}>Bíður eftir að verða sóttur</span>;
    }
    if (pickupStatus === 'completed' && (status === 'pending' || status === 'in_progress')) {
      return <span className={`px-2.5 py-1 rounded-md text-xs font-semibold border ${d ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>Sóttur - í biðröð</span>;
    }
    if (status === 'done' && shuttleStatus === 'pending' && shuttleTo) {
      return <span className={`px-2.5 py-1 rounded-md text-xs font-semibold border ${d ? 'bg-orange-500/10 text-orange-300 border-orange-500/20' : 'bg-orange-50 text-orange-700 border-orange-200'}`}>Beðið eftir skutli</span>;
    }
    if (status === 'done' && shuttleStatus === 'completed') {
      return <span className={`px-2.5 py-1 rounded-md text-xs font-semibold border ${d ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>Lokið og afhent</span>;
    }
    const configs = {
      'pending_approval': { dark: 'bg-purple-500/10 text-purple-300 border-purple-500/20', light: 'bg-purple-50 text-purple-700 border-purple-200', text: 'Bíður samþykkis' },
      'pending': { dark: 'bg-orange-500/10 text-orange-300 border-orange-500/20', light: 'bg-orange-50 text-orange-700 border-orange-200', text: 'Bíður' },
      'in_progress': { dark: 'bg-blue-500/10 text-blue-300 border-blue-500/20', light: 'bg-blue-50 text-blue-700 border-blue-200', text: 'Í vinnslu' },
      'paused': { dark: 'bg-blue-500/10 text-blue-300 border-blue-500/20', light: 'bg-blue-50 text-blue-700 border-blue-200', text: 'Í vinnslu' },
      'done': { dark: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20', light: 'bg-emerald-50 text-emerald-700 border-emerald-200', text: 'Tilbúið' },
      'rejected': { dark: 'bg-red-500/10 text-red-300 border-red-500/20', light: 'bg-red-50 text-red-700 border-red-200', text: 'Hafnað' }
    };
    const config = configs[status] || configs['pending'];
    return <span className={`px-2.5 py-1 rounded-md text-xs font-semibold border ${d ? config.dark : config.light}`}>{config.text}</span>;
  };

  const formatTimeInQueue = (minutes) => {
    if (minutes === null || minutes === undefined || isNaN(minutes)) return 'Óþekkt';
    if (minutes < 60) return `${Math.round(minutes)} mín`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}:${String(mins).padStart(2, '0')} klst`;
  };
  
  const formatEstimatedTime = (minutes) => {
    if (minutes === null || minutes === undefined) return null;
    if (minutes <= 0) return 'Á eftir strax';
    if (minutes < 60) return `~${Math.round(minutes)} mín`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `~${hours}:${String(mins).padStart(2, '0')} klst`;
  };
  
  const formatEstimatedDateTime = (isoString) => {
    if (!isoString) return null;
    const date = new Date(isoString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const isTomorrow = date.toDateString() === tomorrow.toDateString();
    
    const time = date.toLocaleTimeString('is-IS', { hour: '2-digit', minute: '2-digit', hour12: false });
    
    if (isToday) return time;
    if (isTomorrow) return `á morgun ${time}`;
    
    const dayNames = ['sun.', 'mán.', 'þri.', 'mið.', 'fim.', 'fös.', 'lau.'];
    const day = dayNames[date.getDay()];
    return `${day} ${time}`;
  };

  if (loading) {
    return (
      <div className={`${darkMode ? 'portal-dark' : 'portal-light'} flex items-center justify-center`}>
        <div className="text-center">
          <div className={`w-12 h-12 border-2 ${darkMode ? 'border-blue-500/30 border-t-blue-500' : 'border-blue-300 border-t-blue-600'} rounded-full animate-spin mx-auto mb-4`}></div>
          <p className={`${darkMode ? 'text-slate-400' : 'text-slate-500'} text-sm font-medium tracking-wide`}>Hleður gögnum...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${darkMode ? 'portal-dark' : 'portal-light'} flex items-center justify-center p-4`}>
        <div className={`${darkMode ? 'portal-card' : 'portal-card-light'} rounded-xl p-8 max-w-md w-full text-center`}>
          <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <span className="text-red-400 text-xl">✕</span>
          </div>
          <h2 className={`text-lg font-heading font-bold ${darkMode ? 'text-slate-100' : 'text-slate-800'} mb-2`}>Villa</h2>
          <p className={`${darkMode ? 'text-slate-400' : 'text-slate-500'} text-sm`}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={darkMode ? 'portal-dark' : 'portal-light'}>
      {/* Header - Glassmorphism */}
      <div className={darkMode ? 'portal-glass-header sticky top-0 z-50' : 'portal-glass-header-light sticky top-0 z-50'}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              {/* Dealership Logo - Higher resolution */}
              {portalData?.dealership_logo ? (
                <div className={`${darkMode ? 'bg-white/10 border-white/10' : 'bg-white border-slate-200 shadow-sm'} backdrop-blur rounded-lg p-1.5 border`}>
                  <img 
                    src={`https://bilasolur.is/Images/ImgFactory.aspx?p=false&y=200&x=400&f=${portalData.dealership_logo}&r=logos`}
                    alt={`${portalData.dealership_name} logo`}
                    className={`h-9 w-auto object-contain ${darkMode ? 'brightness-110' : ''}`}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div class="text-2xl">🚗</div>';
                    }}
                  />
                </div>
              ) : (
                <div className="text-2xl">🚗</div>
              )}
              <div>
                <h1 className={`text-base sm:text-lg font-heading font-bold ${darkMode ? 'text-white' : 'text-slate-800'} tracking-tight leading-tight`}>
                  {portalData?.dealership}
                </h1>
                <p className={`text-[10px] sm:text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'} tracking-wide hidden sm:block`}>Rauntíma staða á verkum</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Theme toggle */}
              <button 
                onClick={toggleTheme} 
                className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${darkMode ? 'bg-white/5 border border-white/10 hover:bg-white/10 text-yellow-300' : 'bg-slate-100 border border-slate-200 hover:bg-slate-200 text-slate-600'}`}
                title={darkMode ? 'Ljóst þema' : 'Dökkt þema'}
              >
                {darkMode ? (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" /></svg>
                ) : (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" /></svg>
                )}
              </button>
              
              {/* History button */}
              <button 
                onClick={loadHistory} 
                className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${darkMode ? 'bg-white/5 border border-white/10 hover:bg-white/10 text-slate-300' : 'bg-slate-100 border border-slate-200 hover:bg-slate-200 text-slate-600'}`}
                title="Óreikningsfærð verk"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
              
              <Button 
                onClick={() => setNewJobDialogOpen(true)}
                className="bg-blue-600 hover:bg-blue-500 text-white font-semibold px-4 sm:px-5 py-2 text-sm rounded-lg shadow-lg shadow-blue-600/20 hover:shadow-blue-500/30 transition-all duration-200"
              >
                <span className="mr-1.5">+</span> Nýtt verk
              </Button>
              
              {/* Live indicator */}
              <div className={`flex items-center gap-1.5 sm:gap-2 ${darkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'} border rounded-lg px-2 sm:px-3 py-1.5`}>
                <div className="flex items-center gap-1 sm:gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 portal-pulse-dot"></div>
                  <span className={`text-[10px] ${darkMode ? 'text-slate-400' : 'text-slate-500'} font-medium hidden sm:inline`}>LIVE</span>
                </div>
                <div className={`w-px h-3 ${darkMode ? 'bg-white/10' : 'bg-slate-200'} hidden sm:block`}></div>
                <span className={`text-[10px] sm:text-xs ${darkMode ? 'text-slate-300' : 'text-slate-600'} font-mono font-medium`}>
                  {portalData?.last_updated 
                    ? new Date(portalData.last_updated).toLocaleTimeString('is-IS', {hour: '2-digit', minute: '2-digit', hour12: false}) 
                    : new Date().toLocaleTimeString('is-IS', {hour: '2-digit', minute: '2-digit', hour12: false})}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-4">
          {/* Í biðröð */}
          <div 
            className={`${darkMode ? 'portal-stat' : 'portal-stat-light'} rounded-xl p-5 ${statusFilter === 'pending' ? 'active' : ''}`}
            onClick={() => setStatusFilter(statusFilter === 'pending' ? null : 'pending')}
            data-testid="status-pending"
          >
            <div className="flex items-center justify-between mb-3">
              <div className={`w-8 h-8 rounded-lg ${darkMode ? 'bg-orange-500/10' : 'bg-orange-100'} flex items-center justify-center`}>
                <svg className={`w-4 h-4 ${darkMode ? 'text-orange-400' : 'text-orange-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              {statusFilter === 'pending' && (
                <span className="text-[10px] font-medium text-blue-400 bg-blue-400/10 px-2 py-0.5 rounded-full">SÝNIR</span>
              )}
            </div>
            <div className={`text-4xl font-heading font-extrabold ${darkMode ? 'text-white' : 'text-slate-800'} mb-1`}>
              {portalData?.jobs?.filter(j => j.status === 'pending').length || 0}
            </div>
            <div className={`text-xs font-semibold ${darkMode ? 'text-slate-400' : 'text-slate-500'} uppercase tracking-wider`}>Í biðröð</div>
          </div>

          {/* Í vinnslu */}
          <div 
            className={`${darkMode ? 'portal-stat' : 'portal-stat-light'} rounded-xl p-5 ${statusFilter === 'in_progress' ? 'active' : ''}`}
            onClick={() => setStatusFilter(statusFilter === 'in_progress' ? null : 'in_progress')}
            data-testid="status-in-progress"
          >
            <div className="flex items-center justify-between mb-3">
              <div className={`w-8 h-8 rounded-lg ${darkMode ? 'bg-blue-500/10' : 'bg-blue-100'} flex items-center justify-center`}>
                <svg className={`w-4 h-4 ${darkMode ? 'text-blue-400' : 'text-blue-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              {statusFilter === 'in_progress' && (
                <span className="text-[10px] font-medium text-blue-400 bg-blue-400/10 px-2 py-0.5 rounded-full">SÝNIR</span>
              )}
            </div>
            <div className={`text-4xl font-heading font-extrabold ${darkMode ? 'text-white' : 'text-slate-800'} mb-1`}>
              {portalData?.jobs?.filter(j => j.status === 'in_progress' || j.status === 'paused').length || 0}
            </div>
            <div className={`text-xs font-semibold ${darkMode ? 'text-slate-400' : 'text-slate-500'} uppercase tracking-wider`}>Í vinnslu</div>
          </div>

          {/* Tilbúin */}
          <div 
            className={`${darkMode ? 'portal-stat' : 'portal-stat-light'} rounded-xl p-5 ${statusFilter === 'done' ? 'active' : ''}`}
            onClick={() => setStatusFilter(statusFilter === 'done' ? null : 'done')}
            data-testid="status-done"
          >
            <div className="flex items-center justify-between mb-3">
              <div className={`w-8 h-8 rounded-lg ${darkMode ? 'bg-emerald-500/10' : 'bg-emerald-100'} flex items-center justify-center`}>
                <svg className={`w-4 h-4 ${darkMode ? 'text-emerald-400' : 'text-emerald-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              {statusFilter === 'done' && (
                <span className="text-[10px] font-medium text-blue-400 bg-blue-400/10 px-2 py-0.5 rounded-full">SÝNIR</span>
              )}
            </div>
            <div className={`text-4xl font-heading font-extrabold ${darkMode ? 'text-white' : 'text-slate-800'} mb-1`}>
              {portalData?.jobs?.filter(j => j.status === 'done').length || 0}
            </div>
            <div className={`text-xs font-semibold ${darkMode ? 'text-slate-400' : 'text-slate-500'} uppercase tracking-wider`}>Tilbúin</div>
          </div>
        </div>

        {/* Filter indicator */}
        {statusFilter && (
          <div className={`flex items-center justify-between ${darkMode ? 'bg-white/5 border-white/10' : 'bg-slate-100 border-slate-200'} border rounded-lg p-2.5 px-4`}>
            <span className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Sýnir: <span className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                {statusFilter === 'pending' ? 'Í biðröð' : statusFilter === 'in_progress' ? 'Í vinnslu' : 'Tilbúin'}
              </span>
            </span>
            <button onClick={() => setStatusFilter(null)} className={`${darkMode ? 'text-slate-500 hover:text-white' : 'text-slate-400 hover:text-slate-700'} text-sm transition-colors`}>
              ✕ Sýna allt
            </button>
          </div>
        )}

        {/* Jobs List */}
        <div className="space-y-3 portal-stagger">
          {portalData?.jobs && portalData.jobs.length > 0 ? (
            [...portalData.jobs]
              .filter(job => {
                if (!statusFilter) return true;
                if (statusFilter === 'in_progress') return job.status === 'in_progress' || job.status === 'paused';
                return job.status === statusFilter;
              })
              .sort((a, b) => {
                const statusOrder = { 'done': 0, 'in_progress': 1, 'paused': 1.5, 'pending': 2 };
                return (statusOrder[a.status] || 3) - (statusOrder[b.status] || 3);
              }).map((job) => {
                const statusColor = job.confirmation_status === 'pending_approval' ? 'bg-purple-500' :
                  job.status === 'pending' ? 'bg-orange-500' : 
                  (job.status === 'in_progress' || job.status === 'paused') ? 'bg-blue-500' : 
                  'bg-emerald-500';
                
                return (
                  <div key={job.id} className={`${darkMode ? 'portal-job-card' : 'portal-job-card-light'} ${job.recently_updated ? (darkMode ? 'ring-1 ring-purple-500/50' : 'ring-2 ring-purple-300') : ''}`}>
                    <div className="flex">
                      {/* Left status strip */}
                      <div className={`w-1 ${statusColor} flex-shrink-0`}></div>
                      
                      <div className="flex-1 p-4">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            {/* Car Logo */}
                            {job.car_make && (() => {
                              const logoUrl = getCarLogo(job.car_make);
                              return logoUrl ? (
                                <div className={`w-10 h-10 rounded-lg ${darkMode ? 'bg-white/5 border-white/5' : 'bg-slate-50 border-slate-200'} flex items-center justify-center flex-shrink-0 border`}>
                                  <img src={logoUrl} alt={`${job.car_make} logo`} className="w-7 h-7 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                                </div>
                              ) : (
                                <div className={`w-10 h-10 rounded-lg ${darkMode ? 'bg-white/5 border-white/5' : 'bg-slate-50 border-slate-200'} flex items-center justify-center flex-shrink-0 border`}>
                                  <span className="text-slate-500 text-sm">🚙</span>
                                </div>
                              );
                            })()}
                            {!job.car_make && (
                              <div className={`w-10 h-10 rounded-lg ${darkMode ? 'bg-white/5 border-white/5' : 'bg-slate-50 border-slate-200'} flex items-center justify-center flex-shrink-0 border`}>
                                <span className="text-slate-500 text-sm">🚙</span>
                              </div>
                            )}
                            
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`text-base font-mono font-bold ${darkMode ? 'text-white' : 'text-slate-800'} tracking-wider`}>{job.plate}</span>
                                {job.car_make && <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{job.car_make} {job.car_model}</span>}
                                {job.recently_updated && (
                                  <span className={`px-1.5 py-0.5 ${darkMode ? 'bg-purple-500/20 text-purple-300 border-purple-500/30' : 'bg-purple-100 text-purple-700 border-purple-200'} text-[10px] font-bold rounded-md border`}>NÝ STAÐA</span>
                                )}
                                {job.unread_messages > 0 && (
                                  <span className={`px-1.5 py-0.5 ${darkMode ? 'bg-red-500/20 text-red-300 border-red-500/30' : 'bg-red-100 text-red-700 border-red-200'} text-[10px] font-bold rounded-md border`}>💬 {job.unread_messages}</span>
                                )}
                              </div>
                              {job.car_size && (
                                <div className={`text-[11px] ${darkMode ? 'text-slate-500' : 'text-slate-400'} mt-0.5`}>{job.car_size}</div>
                              )}
                              {/* Auto-detect: dealership car vs customer car */}
                              {(() => {
                                const dealershipKt = (portalData?.dealership_kennitala || '').replace(/-/g, '');
                                const ownerKt = (job.owner_kt || '').replace(/-/g, '');
                                const payerKt = (job.payer_kt || '').replace(/-/g, '');
                                const isDealershipCar = dealershipKt && (ownerKt === dealershipKt || payerKt === dealershipKt);
                                return (
                                  <div className={`text-[11px] mt-0.5`}>
                                    {isDealershipCar ? (
                                      <span className={`${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                        👤 {job.owner_name || 'Eigandi'} <span className={`font-medium ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>• {portalData?.dealership_company_name || portalData?.dealership_name || 'Bílasalan'}</span>
                                      </span>
                                    ) : (
                                      <span className={`${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                                        👤 Viðskiptavinur: {job.owner_name || ''}
                                      </span>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                          
                          <div className="flex-shrink-0">
                            {job.confirmation_status === 'pending_approval' ? 
                              getStatusBadge('pending_approval', null, null, null, null) : 
                              getStatusBadge(job.status, job.shuttle_status, job.shuttle_to_dealership, job.pickup_status, job.pickup_from_dealership)
                            }
                          </div>
                        </div>

                        {/* Info badges - time estimates + queue */}
                        {(job.status === 'pending' || job.status === 'in_progress') && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {/* PENDING: queue position + estimated start time */}
                            {job.status === 'pending' && job.queue_position && (
                              <span className={`px-2 py-1 ${darkMode ? 'bg-orange-500/10 text-orange-300 border-orange-500/20' : 'bg-orange-50 text-orange-600 border-orange-200'} text-xs font-mono font-bold rounded-md border`}>
                                #{job.queue_position} í röð
                              </span>
                            )}
                            {portalData?.show_estimated_times && job.status === 'pending' && job.estimated_start_at && (
                              <span className={`px-2 py-1 ${darkMode ? 'bg-blue-500/10 text-blue-300 border-blue-500/20' : 'bg-blue-50 text-blue-600 border-blue-200'} text-xs font-mono font-bold rounded-md border`}>
                                ⏳ Áætluð byrjun: {formatEstimatedDateTime(job.estimated_start_at)}
                              </span>
                            )}
                            {/* IN PROGRESS: estimated completion time only */}
                            {portalData?.show_estimated_times && (job.status === 'in_progress' || job.status === 'paused') && job.estimated_completion_at && (
                              <span className={`px-2 py-1 ${darkMode ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20' : 'bg-emerald-50 text-emerald-600 border-emerald-200'} text-xs font-mono font-bold rounded-md border`}>
                                ✅ Áætluð verklok: {formatEstimatedDateTime(job.estimated_completion_at)}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Services */}
                        {job.services && job.services.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-1.5">
                            {job.services.map((service, idx) => (
                              <span key={idx} className={`px-2 py-0.5 ${darkMode ? 'bg-white/5 text-slate-300 border-white/5' : 'bg-slate-100 text-slate-600 border-slate-200'} text-xs font-medium rounded-md border`}>
                                {service}
                              </span>
                            ))}
                          </div>
                        )}
                        
                        {/* Price */}
                        {portalData?.show_prices && job.total !== null && job.total !== undefined && (
                          <div className={`mt-3 flex items-center justify-between ${darkMode ? 'bg-white/5 border-white/5' : 'bg-blue-50 border-blue-100'} rounded-lg px-3 py-2 border`}>
                            <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'} font-medium`}>
                              {job.discount_percent > 0 ? `${job.discount_percent}% afsl.` : 'Verð'}
                            </span>
                            <span className={`text-base font-mono font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                              {job.total.toLocaleString()} <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>kr.</span>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
          ) : (
            <div className={`${darkMode ? 'portal-card' : 'portal-card-light'} rounded-xl p-16 text-center`}>
              <div className={`w-16 h-16 rounded-2xl ${darkMode ? 'bg-emerald-500/10' : 'bg-emerald-100'} flex items-center justify-center mx-auto mb-4`}>
                <svg className={`w-8 h-8 ${darkMode ? 'text-emerald-400' : 'text-emerald-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className={`${darkMode ? 'text-slate-300' : 'text-slate-600'} text-xl font-heading font-bold mb-2`}>Engin verk í vinnslu</div>
              <div className={`${darkMode ? 'text-slate-500' : 'text-slate-400'} text-sm`}>Allt er tilbúið!</div>
            </div>
          )}
        </div>
      </div>

      {/* Floating Chat Button */}
      <button
        onClick={handleOpenChat}
        className="fixed bottom-6 right-6 bg-blue-600 hover:bg-blue-500 text-white rounded-full w-14 h-14 flex items-center justify-center shadow-lg shadow-blue-600/25 transition-all hover:scale-105 z-50 border border-blue-500/20"
        data-testid="chat-button"
      >
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1 animate-pulse border-2 border-slate-950">
            {unreadCount}
          </span>
        )}
      </button>

      {/* Chat Panel */}
      {chatOpen && (
        <div className="fixed bottom-6 right-6 w-96 portal-chat-panel rounded-xl z-50 flex flex-col" style={{height: '520px', maxHeight: '80vh'}}>
          {/* Chat Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-blue-600/20 flex items-center justify-center">
                <svg className="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <div>
                <div className="font-heading font-bold text-white text-sm">Spjall</div>
                <div className="text-xs text-slate-500">Alvörubón ehf.</div>
              </div>
            </div>
            <button
              onClick={() => setChatOpen(false)}
              className="w-8 h-8 rounded-lg hover:bg-white/5 flex items-center justify-center text-slate-400 hover:text-white transition-colors"
            >
              ✕
            </button>
          </div>
          
          {/* Chat Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {chatMessages.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <div className="text-slate-500 text-sm">Engin skilaboð enn</div>
                <div className="text-slate-600 text-xs mt-1">Byrjaðu spjallið!</div>
              </div>
            ) : (
              chatMessages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.sender === 'admin' ? 'justify-start' : 'justify-end'}`}
                >
                  <div
                    className={`max-w-[75%] px-3.5 py-2.5 rounded-xl text-sm ${
                      msg.sender === 'admin'
                        ? 'bg-white/5 text-slate-200 rounded-tl-sm border border-white/5'
                        : 'bg-blue-600 text-white rounded-tr-sm'
                    }`}
                  >
                    <div className={`font-medium text-[10px] mb-1.5 ${msg.sender === 'admin' ? 'text-slate-500' : 'text-blue-200'}`}>
                      {msg.sender === 'admin' ? `${msg.sender_name || 'Alvörubón'}` : 'Þú'}
                    </div>
                    <div className="whitespace-pre-wrap leading-relaxed">{msg.message}</div>
                    <div className={`text-[10px] mt-1.5 ${msg.sender === 'admin' ? 'text-slate-600' : 'text-blue-300'}`}>
                      {new Date(msg.timestamp).toLocaleString('is-IS', { 
                        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false 
                      })}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          
          {/* Chat Input */}
          <div className="px-4 pb-4 pt-2 border-t border-white/5">
            <div className="flex gap-2 items-end">
              <textarea
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendChatMessage();
                  }
                }}
                placeholder="Skrifaðu skilaboð..."
                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 resize-none focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20"
                rows="1"
                disabled={sendingChat}
              />
              <button
                onClick={handleSendChatMessage}
                disabled={!chatInput.trim() || sendingChat}
                className="h-9 w-9 flex-shrink-0 bg-blue-600 hover:bg-blue-500 disabled:opacity-30 disabled:hover:bg-blue-600 text-white rounded-lg flex items-center justify-center transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Job Submission Dialog */}
      <Dialog open={newJobDialogOpen} onOpenChange={setNewJobDialogOpen}>
        <DialogContent className={`max-w-3xl max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-slate-950 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-800'}`}>
          <DialogHeader>
            <DialogTitle className={`font-heading text-lg font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>Nýtt verk</DialogTitle>
            <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'} mt-1`}>
              Skráðu nýtt verk sem bíður samþykkis frá Alvörubón
            </p>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Alert about approval */}
            <div className={`${darkMode ? 'bg-purple-500/10 border-purple-500/20' : 'bg-purple-50 border-purple-200'} p-3.5 rounded-lg border`}>
              <p className={`${darkMode ? 'text-purple-300' : 'text-purple-700'} text-sm`}>
                ⏳ Verkið fer í samþykktarferli. Þú færð tilkynningu þegar það er samþykkt.
              </p>
            </div>
            
            {/* Discount Info - REMOVED - No need to show discount banner */}
            
            {/* Vehicle Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="plate" className={`${darkMode ? 'text-slate-300' : 'text-slate-600'} text-xs font-semibold uppercase tracking-wider`}>Númeraplata *</Label>
                <div className="relative">
                  <Input
                    id="plate"
                    value={newJobForm.plate}
                    onChange={(e) => {
                      const plate = e.target.value.toUpperCase();
                      setNewJobForm({...newJobForm, plate});
                      if (plate.length >= 3) {
                        setTimeout(() => handleVehicleLookup(plate), 500);
                      }
                    }}
                    placeholder="ABC12"
                    className={`mt-1 font-mono font-bold tracking-wider ${darkMode ? 'bg-white/5 border-white/10 text-white placeholder-slate-600 focus:border-blue-500/50' : 'bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400 focus:border-blue-500'}`}
                  />
                  {lookingUpVehicle && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2">
                      <div className={`w-4 h-4 border-2 ${darkMode ? 'border-blue-500/30 border-t-blue-400' : 'border-blue-300 border-t-blue-600'} rounded-full animate-spin`}></div>
                    </span>
                  )}
                </div>
                {(newJobForm.car_make || newJobForm.car_model) && (
                  <div className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'} mt-1.5 font-medium flex items-center gap-2`}>
                    {newJobForm.car_make && (() => {
                      const logoUrl = getCarLogo(newJobForm.car_make);
                      return logoUrl ? (
                        <img src={logoUrl} alt={`${newJobForm.car_make} logo`} className="w-5 h-5 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                      ) : ( <span>🚗</span> );
                    })()}
                    {!newJobForm.car_make && <span>🚗</span>}
                    <span>{newJobForm.car_make} {newJobForm.car_model}</span>
                  </div>
                )}
              </div>
              <div>
                <Label htmlFor="car_size" className={`${darkMode ? 'text-slate-300' : 'text-slate-600'} text-xs font-semibold uppercase tracking-wider`}>Bílastærð *</Label>
                <select
                  id="car_size"
                  value={newJobForm.car_size}
                  onChange={(e) => setNewJobForm({...newJobForm, car_size: e.target.value})}
                  className={`w-full mt-1 p-2 border rounded-lg ${darkMode ? 'bg-white/5 border-white/10 text-slate-400' : 'bg-slate-50 border-slate-200 text-slate-400'}`}
                  disabled={true}
                  title="Bílastærð er sjálfkrafa greind úr númeraplötu."
                >
                  <option value="">Sjálfvirkt greint</option>
                  {sizes.map(size => (
                    <option key={size.id} value={size.id}>{size.name}</option>
                  ))}
                </select>
                <p className={`text-xs ${darkMode ? 'text-slate-600' : 'text-slate-400'} mt-1`}>🔒 Sjálfvirkt greint úr skrá</p>
              </div>
            </div>
            
            {/* Owner Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="owner_name" className={`${darkMode ? 'text-slate-300' : 'text-slate-600'} text-xs font-semibold uppercase tracking-wider`}>Nafn eiganda *</Label>
                <Input id="owner_name" value={newJobForm.owner_name} onChange={(e) => setNewJobForm({...newJobForm, owner_name: e.target.value})} placeholder="Jón Jónsson" className={`mt-1 ${darkMode ? 'bg-white/5 border-white/10 text-slate-400 placeholder-slate-600' : 'bg-slate-50 border-slate-200 text-slate-400 placeholder-slate-400'}`} disabled={true} />
                <p className={`text-xs ${darkMode ? 'text-slate-600' : 'text-slate-400'} mt-1`}>🔒 Sjálfvirkt fyllt úr skrá</p>
              </div>
              <div>
                <Label htmlFor="owner_kt" className={`${darkMode ? 'text-slate-300' : 'text-slate-600'} text-xs font-semibold uppercase tracking-wider`}>Kennitala eiganda *</Label>
                <Input id="owner_kt" value={newJobForm.owner_kt} onChange={(e) => setNewJobForm({...newJobForm, owner_kt: e.target.value})} placeholder="123456-7890" className={`mt-1 font-mono ${darkMode ? 'bg-white/5 border-white/10 text-slate-400 placeholder-slate-600' : 'bg-slate-50 border-slate-200 text-slate-400 placeholder-slate-400'}`} disabled={true} />
                <p className={`text-xs ${darkMode ? 'text-slate-600' : 'text-slate-400'} mt-1`}>🔒 Sjálfvirkt fyllt úr skrá</p>
              </div>
            </div>
            
            {/* Contact Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone_number" className={`${darkMode ? 'text-slate-300' : 'text-slate-600'} text-xs font-semibold uppercase tracking-wider`}>Símanúmer</Label>
                <Input id="phone_number" value={newJobForm.phone_number} onChange={(e) => setNewJobForm({...newJobForm, phone_number: e.target.value})} placeholder="8881234" className={`mt-1 ${darkMode ? 'bg-white/5 border-white/10 text-white placeholder-slate-600 focus:border-blue-500/50' : 'bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400 focus:border-blue-500'}`} />
              </div>
              <div>
                <Label htmlFor="scheduled_date" className={`${darkMode ? 'text-slate-300' : 'text-slate-600'} text-xs font-semibold uppercase tracking-wider`}>Dagsetning *</Label>
                <Input id="scheduled_date" type="date" value={newJobForm.scheduled_date} onChange={(e) => setNewJobForm({...newJobForm, scheduled_date: e.target.value, scheduled_time: e.target.value > new Date().toISOString().split('T')[0] ? (newJobForm.scheduled_time || '09:00') : ''})} className={`mt-1 ${darkMode ? 'bg-white/5 border-white/10 text-white focus:border-blue-500/50 [color-scheme:dark]' : 'bg-slate-50 border-slate-200 text-slate-800 focus:border-blue-500'}`} />
              </div>
              {newJobForm.scheduled_date > new Date().toISOString().split('T')[0] && (
                <div>
                  <Label htmlFor="scheduled_time" className={`${darkMode ? 'text-slate-300' : 'text-slate-600'} text-xs font-semibold uppercase tracking-wider`}>Tími</Label>
                  <Input id="scheduled_time" type="time" value={newJobForm.scheduled_time || '09:00'} onChange={(e) => setNewJobForm({...newJobForm, scheduled_time: e.target.value})} className={`mt-1 ${darkMode ? 'bg-white/5 border-white/10 text-white focus:border-blue-500/50 [color-scheme:dark]' : 'bg-slate-50 border-slate-200 text-slate-800 focus:border-blue-500'}`} />
                </div>
              )}
            </div>
            
            {/* Payer Selection */}
            <div className={`p-4 ${darkMode ? 'bg-blue-500/5 border-blue-500/20' : 'bg-blue-50 border-blue-200'} rounded-lg border`}>
              <div className="flex items-center space-x-2 mb-3">
                <input type="checkbox" id="payer_is_owner" checked={!newJobForm.payer_is_owner} onChange={(e) => { setNewJobForm({ ...newJobForm, payer_is_owner: !e.target.checked, payer_name: '', payer_kt: '' }); setCustomerDiscount(null); setHasCustomPricing(false); setCategoryDiscounts({}); setCustomPriceServices(new Set()); loadSettings(); }} className={`w-4 h-4 rounded ${darkMode ? 'border-white/20 bg-white/5' : ''}`} />
                <Label htmlFor="payer_is_owner" className={`font-medium cursor-pointer ${darkMode ? 'text-slate-300' : 'text-slate-700'} text-sm`}>💳 Velja annan greiðanda</Label>
              </div>
              
              {!newJobForm.payer_is_owner && (
                <div className="space-y-3">
                  <Button type="button" variant="outline" onClick={async () => { const payerName = portalData?.dealership_company_name || portalData?.dealership || 'Bílasalan'; const payerKT = portalData?.dealership_kennitala || ''; setNewJobForm(prev => ({ ...prev, payer_name: payerName, payer_kt: payerKT })); toast.success(`✅ Valið: ${payerName}`); }} className={`w-full ${darkMode ? 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 hover:text-white' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'}`}>
                    🏢 Velja {portalData?.dealership_company_name || portalData?.dealership || 'bílasöluna'} sem greiðanda
                    {portalData?.dealership_company_name && portalData?.dealership_company_name !== portalData?.dealership && (
                      <span className="text-xs ml-1 opacity-70">({portalData?.dealership})</span>
                    )}
                  </Button>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="payer_kt" className={`${darkMode ? 'text-slate-300' : 'text-slate-600'} text-xs font-semibold uppercase tracking-wider`}>Kennitala greiðanda *</Label>
                      <Input id="payer_kt" value={newJobForm.payer_kt} onChange={(e) => handlePayerKennitalaChange(e.target.value)} placeholder="######-####" className={`mt-1 font-mono ${darkMode ? 'bg-white/5 border-white/10 text-white placeholder-slate-600 focus:border-blue-500/50' : 'bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400 focus:border-blue-500'} ${payerKennitalaLoading ? "animate-pulse" : ""}`} />
                      {payerKennitalaLoading && (
                        <div className={`text-xs ${darkMode ? 'text-blue-400' : 'text-blue-600'} mt-1 flex items-center gap-1`}>
                          <div className={`w-3 h-3 border ${darkMode ? 'border-blue-500/30 border-t-blue-400' : 'border-blue-300 border-t-blue-600'} rounded-full animate-spin`}></div>
                          Leita í Þjóðskrá...
                        </div>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="payer_name" className={`${darkMode ? 'text-slate-300' : 'text-slate-600'} text-xs font-semibold uppercase tracking-wider`}>Nafn greiðanda *</Label>
                      <Input id="payer_name" value={newJobForm.payer_name} onChange={(e) => setNewJobForm({...newJobForm, payer_name: e.target.value})} placeholder="Nafn (fyllist sjálfkrafa)" className={`mt-1 ${darkMode ? 'bg-white/5 border-white/10 text-white placeholder-slate-600 focus:border-blue-500/50' : 'bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400 focus:border-blue-500'} ${payerKennitalaLoading ? "animate-pulse" : ""}`} />
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Services */}
            <div>
              <Label className={`${darkMode ? 'text-slate-300' : 'text-slate-600'} text-xs font-semibold uppercase tracking-wider`}>Þjónustur * (Veldu að minnsta kosti eina)</Label>
              <div className={`mt-2 grid grid-cols-2 gap-2 max-h-64 overflow-y-auto p-3 border rounded-lg ${darkMode ? 'border-white/10 bg-white/[0.02]' : 'border-slate-200 bg-slate-50'}`}>
                {services.map(service => {
                  const price = newJobForm.car_size ? prices[service.id]?.[newJobForm.car_size] : null;
                  const isSelected = newJobForm.services.includes(service.id);
                  return (
                    <div key={service.id} className={`flex items-center space-x-2 p-2.5 rounded-lg border transition-all cursor-pointer ${
                      isSelected 
                        ? (darkMode ? 'bg-blue-500/10 border-blue-500/30 text-white' : 'bg-blue-50 border-blue-300 text-blue-800')
                        : (darkMode ? 'bg-white/[0.02] border-white/5 text-slate-300 hover:bg-white/5 hover:border-white/10' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300')
                    }`} onClick={() => toggleService(service.id)}>
                      <input type="checkbox" id={`service-${service.id}`} checked={isSelected} onChange={() => toggleService(service.id)} className="w-4 h-4 rounded" />
                      <label htmlFor={`service-${service.id}`} className="flex-1 cursor-pointer text-sm">
                        {service.name}
                        {portalData?.show_prices && price !== null && price !== undefined && (
                          <span className={`ml-2 ${darkMode ? 'text-slate-500' : 'text-slate-400'} font-mono text-xs`}>({price.toLocaleString()} kr)</span>
                        )}
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Total */}
            {portalData?.show_prices && newJobForm.car_size && newJobForm.services.length > 0 && (
              <div className={`p-4 ${darkMode ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-emerald-50 border-emerald-200'} rounded-lg border`}>
                <div className="flex justify-between items-center mb-2">
                  <span className={`font-medium ${darkMode ? 'text-emerald-300' : 'text-emerald-700'} text-sm`}>
                    {hasCustomPricing ? 'Heildarverð (sérverðskrá):' : 'Heildarverð með afslætti:'}
                  </span>
                  <span className={`text-2xl font-mono font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                    {calculateJobTotal().toLocaleString()} <span className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>ISK</span>
                  </span>
                </div>
                {hasCustomPricing ? (
                  <div className={`text-xs ${darkMode ? 'text-emerald-400' : 'text-emerald-600'} pt-2 border-t ${darkMode ? 'border-emerald-500/20' : 'border-emerald-200'}`}>
                    <p>💰 Sérverðskrá viðskiptavinar í notkun</p>
                  </div>
                ) : (dealershipDiscount > 0 || (customerDiscount && customerDiscount > 0)) ? (
                  <div className={`text-xs ${darkMode ? 'text-emerald-400' : 'text-emerald-600'} pt-2 border-t ${darkMode ? 'border-emerald-500/20' : 'border-emerald-200'}`}>
                    {customerDiscount && customerDiscount > dealershipDiscount ? (
                      <p>✓ Með {customerDiscount}% sérafslætti viðskiptavinar</p>
                    ) : dealershipDiscount > 0 ? (
                      <p>✓ Með {dealershipDiscount}% afslætti</p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            )}
            
            {/* Notes */}
            <div>
              <Label htmlFor="notes" className={`${darkMode ? 'text-slate-300' : 'text-slate-600'} text-xs font-semibold uppercase tracking-wider`}>Athugasemdir</Label>
              <textarea
                id="notes"
                value={newJobForm.notes}
                onChange={(e) => setNewJobForm({...newJobForm, notes: e.target.value})}
                placeholder="Sérstakar athugasemdir eða óskir..."
                className={`w-full min-h-[80px] p-3 border rounded-lg mt-1 text-sm resize-none focus:outline-none focus:ring-1 ${darkMode ? 'bg-white/5 border-white/10 text-white placeholder-slate-600 focus:border-blue-500/50 focus:ring-blue-500/20' : 'bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400 focus:border-blue-500 focus:ring-blue-500/20'}`}
              />
            </div>
            
            {/* Actions */}
            <div className={`flex justify-end space-x-2 pt-4 border-t ${darkMode ? 'border-white/5' : 'border-slate-200'}`}>
              <Button
                variant="outline"
                onClick={() => {
                  setNewJobDialogOpen(false);
                  setNewJobForm({ plate: '', owner_name: '', owner_kt: '', phone_number: '', scheduled_date: new Date().toISOString().split('T')[0], scheduled_time: '', notes: '', services: [], car_size: '', car_make: '', car_model: '', payer_is_owner: true, payer_name: '', payer_kt: '' });
                  setCustomerDiscount(null); setHasCustomPricing(false); setCategoryDiscounts({}); setCustomPriceServices(new Set()); loadSettings();
                }}
                disabled={submittingJob}
                className={darkMode ? 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 hover:text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}
              >
                Hætta við
              </Button>
              <Button
                onClick={handleSubmitJob}
                disabled={submittingJob}
                className="bg-blue-600 hover:bg-blue-500 text-white font-semibold shadow-lg shadow-blue-600/20"
              >
                {submittingJob ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Sendir...
                  </span>
                ) : 'Senda til samþykkis'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* History Dialog - Uninvoiced completed jobs */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className={`max-w-3xl max-h-[85vh] overflow-y-auto ${darkMode ? 'bg-slate-950 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-800'}`}>
          <DialogHeader>
            <DialogTitle className={`font-heading text-lg font-bold ${darkMode ? 'text-white' : 'text-slate-800'} flex items-center gap-2`}>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Óreikningsfærð verk
            </DialogTitle>
            <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'} mt-1`}>
              Lokin verk sem hafa ekki verið reikningsfærð
            </p>
          </DialogHeader>

          <div className="mt-4">
            {/* Search */}
            {!historyLoading && historyJobs.length > 0 && (
              <div className="mb-4">
                <Input
                  value={historySearch}
                  onChange={(e) => setHistorySearch(e.target.value)}
                  placeholder="Leita eftir plötu, eiganda, bíl..."
                  className={`${darkMode ? 'bg-white/5 border-white/10 text-white placeholder-slate-500 focus:border-blue-500/50' : 'bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400 focus:border-blue-500'}`}
                />
              </div>
            )}
            {(() => {
              const q = historySearch.toLowerCase().trim();
              const filtered = q ? historyJobs.filter(j => 
                (j.plate || '').toLowerCase().includes(q) ||
                (j.owner_name || '').toLowerCase().includes(q) ||
                (j.car_make || '').toLowerCase().includes(q) ||
                (j.car_model || '').toLowerCase().includes(q) ||
                (j.services || []).some(s => s.toLowerCase().includes(q))
              ) : historyJobs;
              
              return historyLoading ? (
              <div className="py-12 text-center">
                <div className={`w-10 h-10 border-2 ${darkMode ? 'border-blue-500/30 border-t-blue-500' : 'border-blue-300 border-t-blue-600'} rounded-full animate-spin mx-auto mb-3`}></div>
                <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Hleður...</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-12 text-center">
                <div className={`w-14 h-14 rounded-2xl ${darkMode ? 'bg-emerald-500/10' : 'bg-emerald-100'} flex items-center justify-center mx-auto mb-3`}>
                  <svg className={`w-7 h-7 ${darkMode ? 'text-emerald-400' : 'text-emerald-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className={`font-heading font-bold ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  {q ? 'Ekkert fannst' : 'Engin óreikningsfærð verk'}
                </p>
                <p className={`text-sm ${darkMode ? 'text-slate-500' : 'text-slate-400'} mt-1`}>
                  {q ? 'Reyndu aðra leit' : 'Öll lokin verk hafa verið reikningsfærð'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className={`text-xs font-semibold ${darkMode ? 'text-slate-500' : 'text-slate-400'} uppercase tracking-wider mb-2`}>
                  {filtered.length} {filtered.length === 1 ? 'verk' : 'verk'}{q ? ` (af ${historyJobs.length})` : ''}
                </div>
                {filtered.map((job) => (
                  <div key={job.id} className={`${darkMode ? 'bg-white/[0.03] border-white/5 hover:border-white/10' : 'bg-slate-50 border-slate-200 hover:border-slate-300'} border rounded-xl p-4 transition-colors`}>
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <div className="flex items-center gap-3">
                        {job.car_make && (() => {
                          const logoUrl = getCarLogo(job.car_make);
                          return logoUrl ? (
                            <div className={`w-9 h-9 rounded-lg ${darkMode ? 'bg-white/5 border-white/5' : 'bg-white border-slate-200'} flex items-center justify-center flex-shrink-0 border`}>
                              <img src={logoUrl} alt={job.car_make} className="w-6 h-6 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                            </div>
                          ) : null;
                        })()}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className={`font-mono font-bold ${darkMode ? 'text-white' : 'text-slate-800'} tracking-wider`}>{job.plate}</span>
                            {job.car_make && <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{job.car_make} {job.car_model}</span>}
                          </div>
                          {job.owner_name && <div className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{job.owner_name}</div>}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        {job.finished_at && (
                          <div className={`text-xs font-mono ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{job.finished_at}</div>
                        )}
                      </div>
                    </div>
                    
                    {job.services && job.services.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {job.services.map((s, i) => (
                          <span key={i} className={`px-2 py-0.5 ${darkMode ? 'bg-white/5 text-slate-300 border-white/5' : 'bg-white text-slate-600 border-slate-200'} text-xs rounded-md border`}>{s}</span>
                        ))}
                      </div>
                    )}
                    
                    {historyShowPrices && job.total != null && (
                      <div className={`flex items-center justify-between ${darkMode ? 'bg-white/5 border-white/5' : 'bg-blue-50 border-blue-100'} rounded-lg px-3 py-1.5 border`}>
                        <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          {job.discount_percent > 0 ? `${job.discount_percent}% afsl.` : 'Verð'}
                        </span>
                        <span className={`font-mono font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                          {job.total.toLocaleString()} <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>kr.</span>
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
            })()}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// Paint Item Page - Public route for QR code scanning on paint labels
function PaintItemPage() {
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const urlPath = window.location.pathname;
    const match = urlPath.match(/\/paint-item\/(.+)/);
    if (match && match[1]) {
      axios.get(`${API}/public/paint-item/${match[1]}`)
        .then(res => { setItem(res.data); setLoading(false); })
        .catch(err => {
          setError(err.response?.status === 404 ? 'Vara fannst ekki' : 'Villa við að sækja vöru');
          setLoading(false);
        });
    } else {
      setError('Ógild slóð');
      setLoading(false);
    }
  }, []);

  if (loading) return <div className="min-h-screen bg-slate-100 flex items-center justify-center"><p className="text-lg text-slate-500">Sæki upplýsingar...</p></div>;
  if (error) return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <Card className="max-w-md w-full"><CardHeader><CardTitle className="text-red-600">Villa</CardTitle></CardHeader><CardContent><p className="text-slate-600">{error}</p></CardContent></Card>
    </div>
  );
  if (!item) return null;

  return (
    <div className="min-h-screen flex flex-col" data-testid="paint-item-card">
      {/* Fullscreen color area */}
      <div 
        className="flex-1 min-h-[70vh]" 
        style={{ backgroundColor: item.paint_hex || '#e2e8f0', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }} 
      />
      
      {/* Info panel at bottom */}
      <div className="bg-white rounded-t-3xl -mt-6 relative z-10 shadow-[0_-4px_20px_rgba(0,0,0,0.1)] px-6 py-6 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800" data-testid="paint-item-code">{item.paint_code}</h1>
            <p className="text-sm text-slate-500">{item.manufacturer}</p>
          </div>
          {item.sequence_number && (
            <span className="text-lg font-mono font-bold text-slate-400">#{item.sequence_number}</span>
          )}
        </div>
        
        {(item.paint_name || item.product_name) && (
          <p className="text-lg font-medium text-slate-700" data-testid="paint-item-name">{item.paint_name || item.product_name}</p>
        )}
        
        <div className="grid grid-cols-2 gap-3 pt-2">
          {item.size && (
            <div className="bg-slate-50 rounded-lg p-3">
              <span className="text-xs font-medium text-slate-400 uppercase">Stærð</span>
              <p className="font-semibold text-slate-700">{item.size}</p>
            </div>
          )}
          {(item.location || item.shelf_number) && (
            <div className="bg-slate-50 rounded-lg p-3">
              <span className="text-xs font-medium text-slate-400 uppercase">Staðsetning</span>
              <p className="font-semibold text-slate-700">{item.location || `Hilla ${item.shelf_number}`}</p>
            </div>
          )}
          {item.product_number && (
            <div className="bg-slate-50 rounded-lg p-3">
              <span className="text-xs font-medium text-slate-400 uppercase">Vörunúmer</span>
              <p className="font-semibold text-slate-700">{item.product_number}</p>
            </div>
          )}
          {item.quantity !== undefined && (
            <div className="bg-slate-50 rounded-lg p-3">
              <span className="text-xs font-medium text-slate-400 uppercase">Magn</span>
              <p className="font-semibold text-slate-700">{item.quantity}</p>
            </div>
          )}
        </div>
        
        {item.notes && (
          <div className="bg-amber-50 rounded-lg p-3 mt-2">
            <span className="text-xs font-medium text-amber-600 uppercase">Athugasemd</span>
            <p className="text-sm text-amber-800">{item.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
}


// Color Preview Page - Public route for fullscreen color display
function ColorPreviewPage() {
  const [colorData, setColorData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    try {
      // Get color data from URL
      const urlPath = window.location.pathname;
      const dataMatch = urlPath.match(/\/color-preview\/(.+)/);
      
      if (dataMatch && dataMatch[1]) {
        // Decode the color data from URL
        const encodedData = decodeURIComponent(dataMatch[1]);
        const data = JSON.parse(encodedData);
        setColorData(data);
      } else {
        setError('Enginn litur fundinn í slóðinni');
      }
    } catch (err) {
      console.error('Failed to parse color data:', err);
      setError('Villa við að lesa litagögn');
    }
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="text-red-600">Villa</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!colorData) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-lg text-slate-600">Hleður litasýni...</div>
      </div>
    );
  }

  // Helper to determine if text should be light or dark based on background color
  const getTextColorForBackground = (hexColor) => {
    // Remove # if present
    const hex = hexColor.replace('#', '');
    
    // Convert to RGB
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calculate relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return white text for dark colors, black for light colors
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  };

  const textColor = getTextColorForBackground(colorData.hex);

  return (
    <div 
      className="min-h-screen flex flex-col items-center justify-center p-8"
      style={{ backgroundColor: colorData.hex }}
    >
      <div className="text-center space-y-6">
        {/* Large color swatch */}
        <div 
          className="w-80 h-80 mx-auto rounded-3xl shadow-2xl border-8"
          style={{ 
            backgroundColor: colorData.hex,
            borderColor: textColor,
            opacity: 0.95
          }}
        ></div>

        {/* Color information with contrasting text */}
        <div className="space-y-4 bg-black bg-opacity-20 backdrop-blur-sm p-8 rounded-2xl">
          <div>
            <div 
              className="text-sm font-medium mb-1"
              style={{ color: textColor, opacity: 0.8 }}
            >
              Lakkkóði
            </div>
            <div 
              className="text-6xl font-bold"
              style={{ color: textColor }}
            >
              {colorData.code}
            </div>
          </div>

          <div>
            <div 
              className="text-sm font-medium mb-1"
              style={{ color: textColor, opacity: 0.8 }}
            >
              Heiti
            </div>
            <div 
              className="text-3xl font-medium"
              style={{ color: textColor }}
            >
              {colorData.name}
            </div>
          </div>

          <div>
            <div 
              className="text-sm font-medium mb-1"
              style={{ color: textColor, opacity: 0.8 }}
            >
              HEX litur
            </div>
            <div 
              className="text-2xl font-mono"
              style={{ color: textColor }}
            >
              {colorData.hex}
            </div>
          </div>

          {colorData.stockStatus && (
            <div className="pt-4 border-t" style={{ borderColor: textColor, opacity: 0.3 }}>
              <div 
                className="text-sm font-medium mb-2"
                style={{ color: textColor, opacity: 0.8 }}
              >
                Birgðastaða
              </div>
              {colorData.stockStatus.in_stock ? (
                <div>
                  <div 
                    className="inline-block px-4 py-2 rounded-lg font-medium bg-green-500 text-white"
                  >
                    ✅ Á lager
                  </div>
                  <div 
                    className="mt-2"
                    style={{ color: textColor }}
                  >
                    {colorData.stockStatus.shelf_number ? `Hilla #${colorData.stockStatus.shelf_number}` : ''}{colorData.stockStatus.location ? ` - ${colorData.stockStatus.location}` : ''}{(colorData.stockStatus.shelf_number || colorData.stockStatus.location) ? ' - ' : ''}{colorData.stockStatus.quantity} stk
                  </div>
                </div>
              ) : (
                <div className="inline-block px-4 py-2 rounded-lg font-medium bg-red-500 text-white">
                  ⚠️ Ekki á lager
                </div>
              )}
            </div>
          )}
        </div>

        {/* Close hint */}
        <div 
          className="text-sm opacity-60 mt-8"
          style={{ color: textColor }}
        >
          Loka flipanum til að fara til baka
        </div>
      </div>
    </div>
  );
}



// Mobile Photo Upload Page - Public route for QR code scanning
function MobilePhotoUploadPage() {
  const [loading, setLoading] = useState(true);
  const [sessionId, setSessionId] = useState('');
  const [sessionValid, setSessionValid] = useState(false);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Extract session ID from URL
    const urlPath = window.location.pathname;
    const match = urlPath.match(/\/mobile-upload\/(.+)/);
    if (match) {
      const id = match[1];
      setSessionId(id);
      validateSession(id);
    } else {
      setError('Ógildur hlekkur');
      setLoading(false);
    }
  }, []);

  const validateSession = async (id) => {
    try {
      const response = await axios.get(`${API}/photo-upload/session/${id}`);
      setSessionValid(true);
      setImages(response.data.images || []);
      setLoading(false);
    } catch (error) {
      if (error.response?.status === 410) {
        setError('Þessi hlekkur er útrunninn (15 mínútur liðnar)');
      } else if (error.response?.status === 404) {
        setError('Hlekkur fannst ekki');
      } else {
        setError('Villa við að hlaða hlekk');
      }
      setLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Check total images limit
    const totalImages = images.length + files.length;
    if (totalImages > 5) {
      toast.error(`Hámark 5 myndir. Þú hefur ${images.length}, reyndir að bæta við ${files.length}`);
      return;
    }

    setUploading(true);

    try {
      // Convert files to base64
      const base64Images = await Promise.all(
        files.map(file => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
        })
      );

      // Upload to server
      await axios.post(`${API}/photo-upload/session/${sessionId}/upload`, base64Images);

      // Update local state
      setImages([...images, ...base64Images]);
      toast.success(`${files.length} mynd${files.length > 1 ? 'ir' : ''} hlaðið upp`);
    } catch (error) {
      console.error('Upload error:', error);
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Villa við upphleðslu');
      } else {
        toast.error('Villa við að hlaða upp myndum');
      }
    } finally {
      setUploading(false);
    }
  };

  const removeImage = async (index) => {
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);
    
    // Update on server
    try {
      await axios.post(`${API}/photo-upload/session/${sessionId}/upload`, newImages);
      toast.info('Mynd fjarlægð');
    } catch (error) {
      console.error('Remove error:', error);
    }
  };

  const completeUpload = async () => {
    if (images.length === 0) {
      toast.error('Enginn myndir valdar');
      return;
    }

    try {
      await axios.post(`${API}/photo-upload/session/${sessionId}/complete`);
      setCompleted(true);
      toast.success('Myndir sendar á tölvu! ✅');
    } catch (error) {
      console.error('Complete error:', error);
      toast.error('Villa við að ljúka upphleðslu');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-slate-100">
        <div className="text-center">
          <div className="text-4xl mb-4">📱</div>
          <div className="text-xl text-slate-600">Hleður...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-slate-100 p-4">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">⚠️</div>
          <div className="text-2xl font-bold text-red-600 mb-2">Villa</div>
          <div className="text-lg text-slate-600">{error}</div>
        </div>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-slate-100 p-4">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">✅</div>
          <div className="text-2xl font-bold text-green-600 mb-2">Tókst!</div>
          <div className="text-lg text-slate-600 mb-4">
            {images.length} mynd{images.length > 1 ? 'ir' : ''} sendar á tölvu
          </div>
          <div className="text-sm text-slate-500">
            Þú getur lokað þessum glugga
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-100 p-4">
      <div className="max-w-2xl mx-auto py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-5xl mb-4">📷</div>
          <h1 className="text-3xl font-bold text-slate-800 mb-2">
            Hlaða upp myndum
          </h1>
          <p className="text-slate-600">
            Veldu allt að 5 myndir af bílnum
          </p>
        </div>

        {/* Image Preview */}
        {images.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-slate-800">
                Valdar myndir ({images.length}/5)
              </h2>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {images.map((img, idx) => (
                <div key={idx} className="relative group">
                  <img
                    src={img}
                    alt={`Mynd ${idx + 1}`}
                    className="w-full h-48 object-cover rounded-lg border-2 border-slate-200"
                  />
                  <button
                    onClick={() => removeImage(idx)}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-10 h-10 flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity text-xl font-bold"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upload Button */}
        {images.length < 5 && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              disabled={uploading}
              className="hidden"
              id="mobile-photo-input"
            />
            <label
              htmlFor="mobile-photo-input"
              className={`block w-full py-6 text-center text-xl font-semibold rounded-xl border-2 border-dashed transition-all ${
                uploading
                  ? 'bg-slate-100 border-slate-300 text-slate-400 cursor-not-allowed'
                  : 'bg-blue-50 border-blue-300 text-blue-600 cursor-pointer hover:bg-blue-100'
              }`}
            >
              {uploading ? (
                <>
                  <div className="text-3xl mb-2">⏳</div>
                  <div>Hleður upp...</div>
                </>
              ) : (
                <>
                  <div className="text-3xl mb-2">📸</div>
                  <div>Taka mynd eða velja úr safni</div>
                  <div className="text-sm font-normal text-slate-500 mt-2">
                    {images.length > 0 ? `${5 - images.length} myndir eftir` : 'Allt að 5 myndir'}
                  </div>
                </>
              )}
            </label>
          </div>
        )}

        {/* Complete Button */}
        <button
          onClick={completeUpload}
          disabled={images.length === 0 || uploading}
          className={`w-full py-6 text-xl font-bold rounded-xl shadow-lg transition-all ${
            images.length === 0 || uploading
              ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
              : 'bg-green-500 text-white hover:bg-green-600 active:scale-95'
          }`}
        >
          {images.length === 0 ? (
            'Veldu myndir fyrst'
          ) : (
            <>
              ✅ Senda {images.length} mynd{images.length > 1 ? 'ir' : ''} á tölvu
            </>
          )}
        </button>

        {/* Info */}
        <div className="mt-6 text-center text-sm text-slate-500">
          Myndir eru sendar örugglega í gegnum dulkóðaða tengingu
        </div>
      </div>
    </div>
  );
}

// App Wrapper to handle public routes
function AppWrapper() {
  // Check if URL is a dealership confirmation link (public route)
  const urlPath = window.location.pathname;
  if (urlPath.startsWith('/dealership-confirmation/')) {
    return <DealershipConfirmationPage />;
  }
  
  // Check if URL is a dealership portal link (public route)
  if (urlPath.startsWith('/dealership-portal/')) {
    return <DealershipPortalPage />;
  }
  
  // Check if URL is an electronic signature link (public route)
  if (urlPath.startsWith('/sign/')) {
    return <ElectronicSignaturePage />;
  }
  
  // Check if URL is a set-password link (public route for invitation/reset)
  if (urlPath.startsWith('/set-password')) {
    return <SetPasswordPage />;
  }

  // Rebooking page (public route for rejected bookings)
  if (urlPath.startsWith('/rebook/')) {
    return <RebookPage />;
  }

  // Check if URL is a color preview link (public route)
  if (urlPath.startsWith('/color-preview/')) {
    return <ColorPreviewPage />;
  }

  // Check if URL is a paint item QR code link (public route)
  if (urlPath.startsWith('/paint-item/')) {
    return <PaintItemPage />;
  }

  // Check if URL is a mobile upload link (public route for QR code scanning)
  if (urlPath.startsWith('/mobile-upload/')) {
    return <MobilePhotoUploadPage />;
  }

  // Mobile registration QR code route is DISABLED to prevent abuse of the
  // public vehicle-lookup endpoint. Staff should register cars via the
  // logged-in CRM instead. (Re-enable by restoring this block when a
  // safer flow with API key / signed link is in place.)
  if (urlPath === '/mobile-registration') {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8fafc',
        padding: 24,
        textAlign: 'center',
        fontFamily: 'sans-serif'
      }}>
        <div>
          <h1 style={{ fontSize: 28, marginBottom: 12, color: '#0f172a' }}>🚗 Þjónustan er ekki í boði</h1>
          <p style={{ fontSize: 16, color: '#475569', maxWidth: 480 }}>
            Sjálfskráning bíla í gegnum QR-kóða hefur verið tímabundið slökkt.
            Vinsamlegast skráðu bílinn í afgreiðslunni og starfsmaður mun aðstoða þig.
          </p>
        </div>
      </div>
    );
  }

  // Corporate application form (public route)
  if (urlPath === '/corporate-apply') {
    return <CorporateApplicationForm />;
  }

  // Corporate dashboard (public route with kennitala login)
  if (urlPath === '/corporate-dashboard' || urlPath === '/corporate') {
    return <CorporateDashboard />;
  }

  // Gift card lookup (public route)
  if (urlPath === '/gjafakort' || urlPath === '/giftcard') {
    return <GiftCardLookup />;
  }

  // Dedicated QR Scanner page (requires login but minimal UI)
  if (urlPath === '/qrscanner' || urlPath === '/qr-scanner' || urlPath === '/qr') {
    return (
      <AuthProvider>
        <DedicatedQRScannerPage />
      </AuthProvider>
    );
  }

  // For all other routes, use AuthProvider
  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
}

// Dedicated QR Scanner Page - Minimal UI with just login and scanner
function DedicatedQRScannerPage() {
  const { user, login, logout, loading: authLoading } = useAuth();
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    try {
      await login(loginEmail, loginPassword);
    } catch (error) {
      setLoginError(error.response?.data?.detail || 'Villa við innskráningu');
    } finally {
      setLoginLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Hleður...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="text-5xl mb-4">📱</div>
            <h1 className="text-2xl font-bold text-slate-900">QR Kóða Skanni</h1>
            <p className="text-slate-600 mt-2">Skráðu þig inn til að halda áfram</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            {loginError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {loginError}
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Netfang</label>
              <input
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="netfang@fyrirtaeki.is"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Lykilorð</label>
              <input
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="••••••••"
                required
              />
            </div>
            
            <button
              type="submit"
              disabled={loginLoading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
            >
              {loginLoading ? '⌛ Skrái inn...' : 'Innskrá'}
            </button>
          </form>
          
          <div className="mt-6 text-center text-xs text-slate-500">
            Alvörubón CRM
          </div>
        </div>
      </div>
    );
  }

  // User is logged in - show QR Scanner
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50">
      {/* Minimal header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-2xl">📱</span>
            <span className="font-semibold text-slate-900">QR Skanni</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600">{user.name || user.email}</span>
            <button
              onClick={logout}
              className="text-sm text-slate-500 hover:text-slate-700 px-3 py-1 rounded border border-slate-300 hover:bg-slate-50"
            >
              Útskrá
            </button>
          </div>
        </div>
      </div>
      
      {/* QR Scanner Component */}
      <div className="py-6">
        <QRScanner />
      </div>
    </div>
  );
}
// Job Edit and Review Component
function ReviewJobs() {
  const [reviewJobs, setReviewJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [editData, setEditData] = useState({});
  const [services, setServices] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [prices, setPrices] = useState({});
  
  useEffect(() => {
    loadReviewJobs();
    loadDropdownData();
  }, []);
  
  const loadReviewJobs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/jobs-needing-review`);
      setReviewJobs(response.data || []);
    } catch (error) {
      toast.error('Tókst ekki að hlaða verkum sem þurfa breytingar');
    } finally {
      setLoading(false);
    }
  };
  
  const startEdit = (job) => {
    setEditingJob(job.id);
    setEditData({
      items: job.items || [],
      discount_percent: job.discount_percent || 0,
      total: job.total || 0,
      owner_name: job.owner_name || '',
      owner_kt: job.owner_kt || '',
      payer_name: job.payer_name || job.owner_name || '',
      payer_kt: job.payer_kt || job.owner_kt || '',
      admin_notes: '',
      car_size: job.size || job.car_size || '',
      scheduled_date: job.scheduled_date || '',
      plate: job.plate || '',
      car_make: job.car_make || job.manufacturer || '',
      car_model: job.car_model || job.model || ''
    });
  };
  
  const calculateTotal = () => {
    if (!editData.items?.length || !prices) return editData.total;
    
    let subtotal = 0;
    editData.items.forEach(item => {
      if (item.price) {
        subtotal += item.price;
      }
    });
    
    const discountPercent = editData.discount_percent || 0;
    const finalTotal = Math.round(subtotal * (1 - discountPercent/100));
    return finalTotal;
  };
  
  const removeService = (serviceIndex) => {
    const newItems = editData.items.filter((_, idx) => idx !== serviceIndex);
    setEditData(prev => ({
      ...prev,
      items: newItems,
      total: calculateTotalForItems(newItems, prev.discount_percent)
    }));
  };
  
  const calculateTotalForItems = (items, discountPercent) => {
    let subtotal = 0;
    items.forEach(item => {
      if (item.price) {
        subtotal += item.price;
      }
    });
    return Math.round(subtotal * (1 - discountPercent/100));
  };
  
  const updateJobAfterEdits = async (jobId, action) => {
    try {
      const finalTotal = calculateTotal();
      
      const reviewData = {
        action: action, // approve, reject
        items: editData.items,
        discount_percent: editData.discount_percent,
        total: finalTotal,
        owner_name: editData.owner_name,
        owner_kt: editData.owner_kt,
        payer_name: editData.payer_name,
        payer_kt: editData.payer_kt,
        admin_notes: editData.admin_notes,
        car_size: editData.car_size,
        scheduled_date: editData.scheduled_date
      };
      
      const response = await axios.put(`${API}/admin/jobs/${jobId}/review`, reviewData);
      
      if (response.data.status === 'success') {
        const actionText = action === 'approve' ? 'samþykkt og staðfest' : 'hafnað';
        toast.success(`Verk ${actionText} eftir breytingar`);
        setEditingJob(null);
        loadReviewJobs(); // Reload list
      }
    } catch (error) {
      toast.error('Villa við að uppfæra verk');
    }
  };
  
  const getConfirmationStatusBadge = (status) => {
    switch (status) {
      case 'needs_changes':
        return <Badge className="bg-orange-100 text-orange-800">🔧 Þarf breytingar</Badge>;
      case 'confirmed':
        return <Badge className="bg-green-100 text-green-800">✅ Staðfest</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800">❌ Hafnað</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>;
    }
  };
  
  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">🔧 Breyta verkum með athugasemdir</h1>
        <Button onClick={loadReviewJobs} variant="outline">
          🔄 Endurnýja lista
        </Button>
      </div>
      
      {loading ? (
        <div className="text-center py-8">
          <div className="text-slate-500">Hleður verkum sem þurfa breytingar...</div>
        </div>
      ) : reviewJobs.length === 0 ? (
        <Card>
          <CardContent className="text-center py-8">
            <div className="text-slate-500">✅ Engin verk þurfa breytingar</div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {reviewJobs.map((job) => (
            <Card key={job.id} className="border-orange-200">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-xl font-bold">{job.plate}</h3>
                      {getConfirmationStatusBadge(job.confirmation_status)}
                      {job.needs_review && (
                        <Badge className="bg-red-100 text-red-800">⚠️ Þarf breytingar</Badge>
                      )}
                    </div>
                    
                    {/* Dealership Comment - Most Important */}
                    {job.dealership_comment && (
                      <div className="mb-4 p-4 bg-orange-50 border-l-4 border-orange-400">
                        <div className="text-sm font-medium text-orange-800 mb-1">🗨️ Athugasemd frá bílasölu:</div>
                        <div className="text-orange-700 font-medium text-lg">"{job.dealership_comment}"</div>
                        <div className="text-xs text-orange-600 mt-1">Þessi athugasemd krefst breytinga á verkinu</div>
                      </div>
                    )}
                    
                    {editingJob === job.id ? (
                      /* Full Edit Form */
                      <div className="space-y-4 border-t pt-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Owner Info */}
                          <div>
                            <Label>Nafn eiganda</Label>
                            <Input
                              value={editData.owner_name}
                              onChange={(e) => setEditData(prev => ({...prev, owner_name: e.target.value}))}
                            />
                          </div>
                          <div>
                            <Label>Kennitala eiganda</Label>
                            <Input
                              value={editData.owner_kt}
                              onChange={(e) => setEditData(prev => ({...prev, owner_kt: e.target.value}))}
                            />
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Payer Info */}
                          <div>
                            <Label>Nafn greiðanda</Label>
                            <Input
                              value={editData.payer_name}
                              onChange={(e) => setEditData(prev => ({...prev, payer_name: e.target.value}))}
                            />
                          </div>
                          <div>
                            <Label>Kennitala greiðanda</Label>
                            <Input
                              value={editData.payer_kt}
                              onChange={(e) => setEditData(prev => ({...prev, payer_kt: e.target.value}))}
                            />
                          </div>
                        </div>
                        
                        {/* Car Size and Date */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label>🚗 Bílastærð</Label>
                            <Select 
                              onValueChange={(value) => {
                                setEditData(prev => ({...prev, car_size: value}));
                                // Save plate-level size override when editing
                                const plate = editData.plate || editData.plate_number || '';
                                if (plate) {
                                  axios.post(`${API}/vehicles/manual-size`, {
                                    manufacturer: editData.car_make || editData.manufacturer || '',
                                    model: editData.car_model || editData.model || '',
                                    size: value,
                                    plate: plate
                                  }).then(() => {
                                    toast.success(`Stærð vistuð fyrir ${plate}`);
                                  }).catch(() => {});
                                }
                              }} 
                              value={editData.car_size || ''}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Veldu bílastærð" />
                              </SelectTrigger>
                              <SelectContent>
                                {sizes.map(size => (
                                  <SelectItem key={size.id} value={size.id}>
                                    {size.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>📅 Dagsetning</Label>
                            <Input
                              type="date"
                              value={editData.scheduled_date || ''}
                              onChange={(e) => setEditData(prev => ({...prev, scheduled_date: e.target.value}))}
                            />
                          </div>
                        </div>
                        
                        {/* Services Editing */}
                        <div>
                          <Label className="text-lg font-medium">Þjónusta (smelltu á ❌ til að fjarlæga)</Label>
                          <div className="space-y-2 mt-2">
                            {editData.items.map((item, idx) => (
                              <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 border rounded">
                                <div>
                                  <span className="font-medium">{item.custom_name || item.service}</span>
                                  <span className="ml-2 text-slate-600">- {item.price?.toLocaleString()} ISK</span>
                                </div>
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="text-red-600 border-red-600 hover:bg-red-50"
                                  onClick={() => removeService(idx)}
                                >
                                  ❌ Fjarlæga
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label>Afsláttur (%)</Label>
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              value={editData.discount_percent}
                              onChange={(e) => {
                                const newDiscount = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                                const newTotal = calculateTotalForItems(editData.items, newDiscount);
                                setEditData(prev => ({
                                  ...prev, 
                                  discount_percent: newDiscount,
                                  total: newTotal
                                }));
                              }}
                            />
                          </div>
                          <div>
                            <Label>Heildarupphæð (ISK)</Label>
                            <div className="text-xl font-bold text-green-600 p-2">
                              {calculateTotal().toLocaleString()} ISK
                            </div>
                          </div>
                        </div>
                        
                        <div>
                          <Label>Athugasemdir stjórnanda</Label>
                          <Input
                            value={editData.admin_notes}
                            onChange={(e) => setEditData(prev => ({...prev, admin_notes: e.target.value}))}
                            placeholder="Athugasemd um þær breytingar sem gerðar voru..."
                          />
                        </div>
                        
                        <div className="flex space-x-2 pt-4">
                          <Button 
                            onClick={() => updateJobAfterEdits(job.id, 'approve')} 
                            className="bg-green-600 hover:bg-green-700"
                          >
                            ✅ Vista breytingar og staðfesta
                          </Button>
                          <Button 
                            onClick={() => updateJobAfterEdits(job.id, 'reject')} 
                            variant="outline" 
                            className="text-red-600 border-red-600 hover:bg-red-50"
                          >
                            ❌ Hafna verki
                          </Button>
                          <Button onClick={() => setEditingJob(null)} variant="outline">
                            Hætta við
                          </Button>
                        </div>
                      </div>
                    ) : (
                      /* View Mode */
                      <div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm mb-4">
                          <div>
                            <div className="text-slate-500">Eigandi:</div>
                            <div className="font-medium">{job.owner_name}</div>
                            <div className="text-xs text-slate-500">{job.owner_kt}</div>
                          </div>
                          <div>
                            <div className="text-slate-500">Greiðandi:</div>
                            <div className="font-medium">{job.payer_name || job.owner_name}</div>
                            <div className="text-xs text-slate-500">{job.payer_kt || job.owner_kt}</div>
                          </div>
                          <div>
                            <div className="text-slate-500">Heildarupphæð:</div>
                            <div className="font-medium">{job.total.toLocaleString()} ISK</div>
                            {job.discount_percent > 0 && (
                              <div className="text-xs text-green-600">Afsláttur: {job.discount_percent}%</div>
                            )}
                          </div>
                        </div>
                        
                        {/* Services */}
                        <div className="mb-4">
                          <div className="text-sm text-slate-500 mb-2">Þjónusta:</div>
                          <div className="flex flex-wrap gap-2">
                            {job.items.map((item, idx) => (
                              <Badge key={idx} variant="outline">
                                {item.custom_name || item.service} - {item.price?.toLocaleString()} ISK
                              </Badge>
                            ))}
                          </div>
                        </div>
                        
                        <div className="flex justify-end">
                          <Button onClick={() => startEdit(job)} variant="outline">
                            🔧 Breyta verki
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// Main App Component


// Electronic Signature Page - Public
function ElectronicSignaturePage() {
  const [signatureRequest, setSignatureRequest] = useState(null);
  const [loanCar, setLoanCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [signatureData, setSignatureData] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);
  const canvasRef = React.useRef(null);

  useEffect(() => {
    // Get request ID from URL
    const urlPath = window.location.pathname;
    const idMatch = urlPath.match(/\/sign\/([^\/]+)/);
    
    if (idMatch && idMatch[1]) {
      loadSignatureRequest(idMatch[1]);
    } else {
      setError('Ógildur linkur');
      setLoading(false);
    }
  }, []);

  const loadSignatureRequest = async (requestId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/signature-requests/${requestId}`);
      console.log('📝 Signature request loaded:', response.data);
      setSignatureRequest(response.data);
      
      // Use loan car data from response if available
      if (response.data.loan_car) {
        console.log('✅ Loan car data received from backend:', response.data.loan_car);
        setLoanCar(response.data.loan_car);
      } else {
        console.warn('⚠️ No loan car data in response');
      }
      
      setLoading(false);
    } catch (error) {
      console.error('❌ Failed to load signature request:', error);
      setError(error.response?.data?.detail || 'Villa við að hlaða samningi');
      setLoading(false);
    }
  };

  // Initialize canvas with better settings for smooth drawing
  React.useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#000000';
    }
  }, []);

  // Get coordinates from mouse or touch event
  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Handle touch events
    if (e.touches && e.touches.length > 0) {
      const touch = e.touches[0];
      return {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top
      };
    }
    
    // Handle mouse events
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  // Canvas drawing functions with touch support
  const startDrawing = (e) => {
    e.preventDefault(); // Prevent scrolling on touch
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault(); // Prevent scrolling on touch
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoordinates(e);
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    if (canvasRef.current) {
      setSignatureData(canvasRef.current.toDataURL());
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignatureData('');
  };

  const handleSign = async () => {
    if (!signatureData) {
      alert('Vinsamlegast skrifaðu undir');
      return;
    }

    try {
      setSigning(true);
      // Only send signature - all other data already in job_data
      await axios.post(
        `${API_BASE_URL}/api/signature-requests/${signatureRequest.id}/sign`,
        {
          signature_customer: signatureData
        }
      );
      setSigned(true);
    } catch (error) {
      alert(error.response?.data?.detail || 'Villa við að undirrita');
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl">
          <CardContent className="p-8 text-center">
            <div className="text-lg">⏳ Hleður samningi...</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl border-red-200">
          <CardContent className="p-8 text-center">
            <div className="text-6xl mb-4">❌</div>
            <div className="text-xl font-bold text-red-600 mb-2">Villa</div>
            <div className="text-slate-600">{error}</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (signed) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl border-green-200">
          <CardContent className="p-8 text-center">
            <div className="text-6xl mb-4">✅</div>
            <div className="text-2xl font-bold text-green-600 mb-4">Samningur undirritaður!</div>
            <div className="text-slate-600 mb-6">
              Þakka þér fyrir. Undirritaður samningur hefur verið vistaður.
            </div>
            <Button
              onClick={() => {
                window.location.href = `${API_BASE_URL}/api/signature-requests/${signatureRequest.id}/download`;
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              📥 Sækja undirritaðan samning
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (signatureRequest.status !== 'pending') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl">
          <CardContent className="p-8 text-center">
            <div className="text-6xl mb-4">⚠️</div>
            <div className="text-xl font-bold mb-2">
              {signatureRequest.status === 'signed' ? 'Þessi samningur hefur þegar verið undirritaður' : 'Þessi samningur er útrunninn'}
            </div>
            {signatureRequest.status === 'signed' && (
              <Button
                onClick={() => {
                  window.location.href = `${API_BASE_URL}/api/signature-requests/${signatureRequest.id}/download`;
                }}
                className="mt-4 bg-blue-600 hover:bg-blue-700"
              >
                📥 Sækja undirritaðan samning
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const jobData = signatureRequest.job_data;
  
  // Use renter info if provided, otherwise use owner info
  const displayName = jobData.renter_name || jobData.owner_name;
  const displayKennitala = jobData.renter_kennitala || jobData.owner_kt;
  const displayPhone = jobData.renter_phone || jobData.phone_number;

  return (
    <div className="min-h-screen bg-slate-50 p-4">
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardHeader className="bg-blue-600 text-white">
            <CardTitle className="text-center text-2xl">📄 Lánsbílasamningur - Alvörubón ehf.</CardTitle>
            <p className="text-center text-blue-100 text-sm">Rafræn undirritun</p>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {/* Loan Car Information (READ-ONLY) */}
            <div className="p-4 bg-slate-100 rounded-lg">
              <h3 className="font-medium mb-3 text-slate-700">🚗 Ökutæki til láns</h3>
              {loanCar ? (
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex flex-col">
                    <span className="text-slate-500 text-xs">Bílnúmer</span>
                    <span className="font-medium">{loanCar.plate}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-slate-500 text-xs">Gerð</span>
                    <span className="font-medium">{loanCar.manufacturer} {loanCar.model}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-slate-500 text-xs">Litur</span>
                    <span className="font-medium">{loanCar.color}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-slate-500 text-xs">Árgerð</span>
                    <span className="font-medium">{loanCar.year}</span>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-slate-500">Lánsbíll upplýsingar ekki tiltækar</div>
              )}
            </div>

            {/* Renter/Customer Info (READ-ONLY) */}
            <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
              <h3 className="font-medium mb-3 text-blue-900">
                {jobData.renter_name ? '👤 Leigjandi' : '👤 Viðskiptavinur'}
              </h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex flex-col">
                  <span className="text-slate-500 text-xs">Nafn</span>
                  <span className="font-medium">{displayName}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-slate-500 text-xs">Kennitala</span>
                  <span className="font-medium">{displayKennitala}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-slate-500 text-xs">Símanúmer</span>
                  <span className="font-medium">{displayPhone}</span>
                </div>
              </div>
              {jobData.renter_name && (
                <div className="mt-2 text-xs text-blue-700 bg-blue-100 p-2 rounded">
                  ℹ️ Leigjandi er annar en eigandi ökutækis
                </div>
              )}
            </div>

            {/* Loan Details (READ-ONLY) */}
            <div className="p-4 bg-green-50 rounded-lg border-2 border-green-200">
              <h3 className="font-medium mb-3 text-green-900">📋 Lánsupplýsingar</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex flex-col">
                  <span className="text-slate-500 text-xs">Km við afhendingu</span>
                  <span className="font-medium">{jobData.km_out || 'Ekki skráð'}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-slate-500 text-xs">Afhendingardagur</span>
                  <span className="font-medium">{jobData.delivery_date || new Date().toISOString().split('T')[0]}</span>
                </div>
                {jobData.comments && (
                  <div className="flex flex-col col-span-2">
                    <span className="text-slate-500 text-xs">Athugasemdir</span>
                    <span className="font-medium">{jobData.comments}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Signature Canvas */}
            <div className="mt-6">
              <Label className="text-lg font-medium">✍️ Undirskrift viðskiptavinar *</Label>
              <p className="text-sm text-slate-600 mb-2">Vinsamlegast skrifaðu undir hér að neðan</p>
              <div className="border-2 border-dashed border-blue-300 rounded-lg p-2 bg-white">
                <canvas
                  ref={canvasRef}
                  width={800}
                  height={300}
                  className="w-full cursor-crosshair touch-none"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                  onTouchCancel={stopDrawing}
                  style={{ touchAction: 'none' }}
                />
              </div>
              <div className="flex gap-2 mt-3">
                <Button
                  type="button"
                  onClick={clearSignature}
                  variant="outline"
                  className="text-base py-4 px-6"
                >
                  🗑️ Hreinsa undirskrift
                </Button>
                <div className="text-xs text-slate-500 flex items-center ml-2">
                  💡 Notaðu fingur eða stylus á snertiskjá
                </div>
              </div>
            </div>

            {/* Sign Button */}
            <div className="flex justify-end pt-6 border-t mt-6">
              <Button
                onClick={handleSign}
                disabled={signing || !signatureData}
                className="bg-green-600 hover:bg-green-700 text-lg px-8 py-6"
              >
                {signing ? '⏳ Undirrita...' : '✍️ Undirrita samning'}
              </Button>
            </div>

            <p className="text-xs text-slate-500 text-center mt-4">
              Með undirritun staðfestir þú að þú hafir lesið og samþykkt skilmála samningsins.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}


function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-center">Alvörubón CRM</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center text-slate-500">
              Hleður innskráningu...
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return user ? <AuthenticatedApp /> : <LoginPage />;
}

// Dealership Confirmation Page - Public
function DealershipConfirmationPage() {
  const [confirmationData, setConfirmationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [jobEdits, setJobEdits] = useState({}); // Store individual job edits
  const [editingJob, setEditingJob] = useState(null);
  const [editingPaymentMethod, setEditingPaymentMethod] = useState(null);
  const [accountingSystemName, setAccountingSystemName] = useState('Bókhaldskerfið');
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'dealership', 'others'
  
  const API = API_BASE_URL + '/api';

  useEffect(() => {
    // Get token from URL
    const urlPath = window.location.pathname;
    const tokenMatch = urlPath.match(/\/dealership-confirmation\/([^\/]+)/);
    
    if (tokenMatch && tokenMatch[1]) {
      loadConfirmationData(tokenMatch[1]);
    } else {
      setError('Invalid confirmation link');
      setLoading(false);
    }
    
    // Load accounting system name
    loadAccountingSystemName();
  }, []);
  
  const loadAccountingSystemName = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      const system = response.data.accounting_system;
      const activeName = system === 'DK' ? 'DK' : system === 'PAYDAY' ? 'Payday' : 'Bókhaldskerfið';
      setAccountingSystemName(activeName);
    } catch (error) {
      console.error('Failed to load accounting system:', error);
      setAccountingSystemName('Bókhaldskerfið'); // Default
    }
  };

  const loadConfirmationData = async (token) => {
    try {
      const response = await axios.get(`${API}/public/dealership-confirmation/${token}`);
      const data = response.data;
      
      // Calculate display_total for each job based on show_discounted_prices setting
      const showDiscounted = data.show_discounted_prices !== false;
      const discountPct = data.discount_percentage || 0;
      
      if (data.jobs) {
        data.jobs = data.jobs.map(job => {
          // Calculate item subtotal (sum of all item prices × quantity)
          const itemsSubtotal = (job.items || [])
            .filter(it => !it.is_included)
            .reduce((sum, it) => sum + ((it.original_price || it.price || 0) * (it.quantity || 1)), 0);
          
          const jobDiscount = job.discount_percent || discountPct || 0;
          
          if (!showDiscounted && jobDiscount > 0) {
            // Show price WITHOUT discount
            return { ...job, display_total: itemsSubtotal || job.total };
          } else {
            // Show price WITH discount (default)
            return { ...job, display_total: job.total };
          }
        });
        
        // Recalculate total_amount based on display setting
        data.total_amount = data.jobs.reduce((sum, j) => sum + (j.display_total || j.total || 0), 0);
      }
      
      setConfirmationData(data);
      
      // Initialize job edits with current data - safely check if jobs exist
      const edits = {};
      const jobs = data?.jobs || [];
      
      // First, initialize edits with basic data
      jobs.forEach(job => {
        if (job && job.id) {
          edits[job.id] = {
            payer_name: job.payer_name || job.owner_name || '',
            payer_kt: job.payer_kt || job.owner_kt || '',
            payer_email: job.payer_email || '',  // Add email field
            notes: '',
            payment_method: 'payday'  // Default to Payday for each job
          };
        }
      });
      setJobEdits(edits);
      
      // Then, fetch customer email for each job if kennitala exists
      // This ensures email is auto-populated on page load
      const confirmationId = response.data?.confirmation_id;
      if (confirmationId) {
        for (const job of jobs) {
          if (job && job.id) {
            // Get kennitala and remove any dashes
            const kt = (job.payer_kt || job.owner_kt || '').replace(/-/g, '');
            if (kt && kt.length === 10) {
              try {
                const pricingRes = await axios.get(
                  `${API}/dealership-confirmation/${confirmationId}/customer-pricing/${kt}`
                );
                const pricingData = pricingRes.data;
                
                // Auto-fill email if customer has one in database and job doesn't have email yet
                if (pricingData.found && pricingData.email && !edits[job.id].payer_email) {
                  edits[job.id].payer_email = pricingData.email;
                  setJobEdits(prev => ({
                    ...prev,
                    [job.id]: {
                      ...prev[job.id],
                      payer_email: pricingData.email
                    }
                  }));
                }
              } catch (err) {
                console.log('Could not fetch customer email for job:', job.id, err);
              }
            }
          }
        }
      }
      
    } catch (error) {
      console.error('Error loading confirmation data:', error);
      if (error.response?.status === 404) {
        setError('Staðfestingarlinkur fannst ekki eða er útrunninn');
      } else if (error.response?.status === 410) {
        setError('Staðfestingarlinkur er útrunninn');
      } else {
        setError('Villa við að sækja staðfestingargögn');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!confirmationData?.confirmation_id) return;
    
    setConfirming(true);
    try {
      // Build job_responses array - all jobs confirmed, no rejection option
      const job_responses = confirmationData.jobs.map(job => {
        const jobComment = jobEdits[job.id]?.notes?.trim() || '';
        const payerName = jobEdits[job.id]?.payer_name?.trim() || '';
        const payerKt = jobEdits[job.id]?.payer_kt?.trim() || '';
        const payerEmail = jobEdits[job.id]?.payer_email?.trim() || '';
        const paymentMethod = jobEdits[job.id]?.payment_method || 'payday';  // Per-job payment method
        
        return {
          job_id: job.id,
          confirmed: true,  // All jobs are always confirmed
          comment: jobComment,  // Job-specific comment if provided
          payer_name: payerName || job.payer_name || job.owner_name,  // Updated payer info
          payer_kt: payerKt || job.payer_kt || job.owner_kt,  // Updated payer kennitala
          payer_email: payerEmail || job.payer_email || '',  // Updated payer email
          payment_method: paymentMethod,  // Per-job payment method
          updated_total: job.recalculated ? job.display_total : undefined  // Send updated total if recalculated
        };
      });
      
      const response = await axios.post(`${API}/dealership-confirmation/${confirmationData.confirmation_id}/confirm`, {
        job_responses: job_responses
      });

      // Handle response based on whether review is required
      if (response.data.requires_review) {
        toast.success('Staðfesting móttekin! Athugasemdir þínar verða endurskoðaðar áður en reikningar eru sendir.');
      } else {
        toast.success('Staðfesting móttekin! Takk fyrir.');
      }
      
      // Show success screen by updating status
      setConfirmationData(prev => ({
        ...prev,
        status: 'confirmed'  // Update main status to trigger success screen
      }));
      
    } catch (error) {
      toast.error('Villa við staðfestingu');
    } finally {
      setConfirming(false);
    }
  };

  const updateJobEdit = (jobId, field, value) => {
    setJobEdits(prev => ({
      ...prev,
      [jobId]: {
        ...prev[jobId],
        [field]: value
      }
    }));
  };

  const recalculateJobPrice = async (jobId, payerKt) => {
    // Clean kennitala - remove dashes
    const cleanKt = (payerKt || '').replace(/-/g, '');
    if (!cleanKt || cleanKt.length !== 10 || !confirmationData?.confirmation_id) return;
    
    try {
      // Find the job
      const job = confirmationData.jobs.find(j => j.id === jobId);
      if (!job || !job.items) return;
      
      // Get vehicle size from job
      const vehicleSize = job.size || 'folksbill'; // Default to folksbill if not set
      
      // Fetch customer pricing - use clean kennitala without dashes
      const pricingRes = await axios.get(
        `${API}/dealership-confirmation/${confirmationData.confirmation_id}/customer-pricing/${cleanKt}`
      );
      const pricingData = pricingRes.data;
      
      if (!pricingData.found) {
        // No custom pricing, keep original price
        return;
      }
      
      let newTotal = 0;
      let hasAnyCustomPrice = false;
      let customPricedTotal = 0;
      let defaultPricedTotal = 0;
      
      // Check if customer has custom pricing enabled
      if (pricingData.custom_pricing_enabled && pricingData.custom_prices) {
        // Recalculate with custom pricing - SMART DISCOUNT: per-item
        for (const item of job.items) {
          // Try to get service_id from item, fallback to service name
          const serviceId = item.service_id || item.service || item.custom_name;
          
          // Custom prices are stored as: { service_id: { vehicle_size: price } }
          const servicePricing = pricingData.custom_prices[serviceId];
          
          let itemHasCustomPrice = false;
          let itemPrice = item.price || 0;
          
          if (servicePricing && typeof servicePricing === 'object') {
            // Get price for this vehicle size
            const customPrice = servicePricing[vehicleSize];
            
            if (customPrice !== undefined && customPrice !== null) {
              itemPrice = customPrice;
              itemHasCustomPrice = true;
              hasAnyCustomPrice = true;
            }
          } else if (servicePricing !== undefined && servicePricing !== null && typeof servicePricing === 'number') {
            // Old format: direct price without size
            itemPrice = servicePricing;
            itemHasCustomPrice = true;
            hasAnyCustomPrice = true;
          }
          
          const itemTotal = itemPrice * (item.quantity || 1);
          
          // Track custom-priced vs default-priced items separately
          if (itemHasCustomPrice) {
            customPricedTotal += itemTotal;
          } else {
            defaultPricedTotal += itemTotal;
          }
        }
        
        // Total before discount
        newTotal = customPricedTotal + defaultPricedTotal;
      } else {
        // No custom pricing, use original total
        newTotal = job.total || 0;
        defaultPricedTotal = newTotal;
      }
      
      // Apply discount ONLY to default-priced items
      if (pricingData.discount_percentage > 0 && defaultPricedTotal > 0) {
        const discountMultiplier = 1 - (pricingData.discount_percentage / 100);
        const discountedDefaultTotal = defaultPricedTotal * discountMultiplier;
        newTotal = customPricedTotal + discountedDefaultTotal;
      }
      
      // Update the job in confirmationData with new calculated price
      setConfirmationData(prev => ({
        ...prev,
        jobs: prev.jobs.map(j => {
          if (j.id === jobId) {
            return {
              ...j,
              display_total: Math.round(newTotal),
              recalculated: true,
              has_custom_price: hasAnyCustomPrice,
              applied_discount: pricingData.discount_percentage > 0 && defaultPricedTotal > 0 ? pricingData.discount_percentage : 0
            };
          }
          return j;
        })
      }));
      
      // Auto-fill customer name and email if found in database
      if (pricingData.found) {
        if (pricingData.name && !jobEdits[jobId]?.payer_name) {
          updateJobEdit(jobId, 'payer_name', pricingData.name);
        }
        // Auto-fill email if customer has one and email field is empty
        const currentEmail = jobEdits[jobId]?.payer_email?.trim() || '';
        if (pricingData.email && !currentEmail) {
          updateJobEdit(jobId, 'payer_email', pricingData.email);
          toast.info(`📧 Email fyllt inn sjálfkrafa: ${pricingData.email}`);
        }
      }
      
      // Show notification
      if (hasAnyCustomPrice && pricingData.discount_percentage > 0 && defaultPricedTotal > 0) {
        // Mixed pricing: some custom, some default with discount
        toast.success(`💰 Blandað verðlag - ${Math.round(customPricedTotal).toLocaleString()} kr (sérverð) + ${Math.round(defaultPricedTotal * (1 - pricingData.discount_percentage / 100)).toLocaleString()} kr (${pricingData.discount_percentage}% afsláttur) = ${Math.round(newTotal).toLocaleString()} kr`);
      } else if (hasAnyCustomPrice) {
        toast.success(`💰 Sérverðskrá fundin - verð uppfært í ${Math.round(newTotal).toLocaleString()} kr`);
      } else if (pricingData.discount_percentage > 0) {
        toast.success(`💰 Afsláttur (${pricingData.discount_percentage}%) notaður - verð uppfært í ${Math.round(newTotal).toLocaleString()} kr`);
      }
      
    } catch (error) {
      console.error('Failed to recalculate price:', error);
      // Don't show error to user, just keep original price
    }
  };

  const startEditingJob = (jobId) => {
    setEditingJob(jobId);
    // Pre-fill with current values instead of clearing
    const job = confirmationData?.jobs?.find(j => j.id === jobId);
    if (job) {
      const currentKt = jobEdits[jobId]?.payer_kt || job.payer_kt || job.owner_kt || '';
      const currentName = jobEdits[jobId]?.payer_name || job.payer_name || job.owner_name || '';
      updateJobEdit(jobId, 'payer_kt', currentKt);
      updateJobEdit(jobId, 'payer_name', currentName);
    }
  };

  const stopEditingJob = async (jobId) => {
    // Save payer changes to backend
    if (jobId && jobEdits[jobId]) {
      const edits = jobEdits[jobId];
      const payerKt = edits.payer_kt?.trim();
      const payerName = edits.payer_name?.trim();
      
      if (payerKt && payerName) {
        try {
          await axios.post(`${API}/dealership-confirmation/${confirmationData.confirmation_id}/update-payer`, {
            job_id: jobId,
            payer_kt: payerKt,
            payer_name: payerName,
            payer_email: edits.payer_email?.trim() || ''
          });
          
          // Update the job data locally so tabs reflect the change
          setConfirmationData(prev => ({
            ...prev,
            jobs: prev.jobs.map(j => j.id === jobId ? {
              ...j,
              payer_kt: payerKt,
              payer_name: payerName,
              payer_email: edits.payer_email?.trim() || j.payer_email
            } : j)
          }));
          
          toast.success(`Greiðandi vistaður: ${payerName}`);
        } catch (err) {
          console.error('Failed to save payer:', err);
          toast.error('Villa við að vista greiðanda');
        }
      }
    }
    setEditingJob(null);
  };

  const lookupPersonByKennitala = async (jobId, kennitala) => {
    if (!kennitala || kennitala.length !== 10) return;
    
    try {
      // Set loading state for this job
      updateJobEdit(jobId, 'lookingUp', true);
      
      const response = await axios.get(`${API}/person/lookup/${kennitala}`);
      const personData = response.data;
      
      console.log('Person data received:', personData);
      
      // Auto-fill name from person lookup
      if (personData.full_name) {
        updateJobEdit(jobId, 'payer_name', personData.full_name);
        toast.success(`Nafn sótt: ${personData.full_name}`);
      }
      
    } catch (error) {
      console.error('Person lookup error:', error);
      
      if (error.response?.status === 404) {
        toast.warning('Kennitala ekki í gagnagrunni - sláðu inn nafn manually');
      } else {
        toast.error('Villa við að sækja nafn');
      }
    } finally {
      updateJobEdit(jobId, 'lookingUp', false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-lg">Hleður staðfestingargögnum...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <div className="text-red-600 text-lg font-semibold mb-2">Villa</div>
            <div className="text-slate-600">{error}</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!confirmationData) {
    return null;
  }

  const { confirmation_id, jobs = [], dealership_kennitala = null, dealership_logo = null, total_amount = 0, total_jobs = 0, status = 'sent', allow_alternative_payment = false, show_prices = true, show_services = true } = confirmationData;
  const dealership = confirmationData.dealership || confirmationData.dealership_name || '';
  const isConfirmed = status === 'confirmed';

  // Split jobs into dealership-owned and others
  const cleanDealerKt = (dealership_kennitala || '').replace(/-/g, '');
  // Use only saved/committed payer_kt for tab filtering (from job data, not draft edits)
  // Jobs being edited won't move tabs until "Vista" is clicked and page reloads data
  const dealershipJobs = cleanDealerKt ? jobs.filter(job => {
    const ownerKt = (job.owner_kt || '').replace(/-/g, '');
    const payerKt = (job.payer_kt || '').replace(/-/g, '');
    return ownerKt === cleanDealerKt || payerKt === cleanDealerKt;
  }) : [];
  const otherJobs = cleanDealerKt ? jobs.filter(job => {
    const ownerKt = (job.owner_kt || '').replace(/-/g, '');
    const payerKt = (job.payer_kt || '').replace(/-/g, '');
    return ownerKt !== cleanDealerKt && payerKt !== cleanDealerKt;
  }) : jobs;
  
  const filteredJobs = activeTab === 'dealership' ? dealershipJobs : activeTab === 'others' ? otherJobs : jobs;

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-3 sm:px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header - Dealership logo/name first, then Alvörubón */}
        <div className="text-center mb-6">
          {dealership_logo ? (
            <div className="mb-3">
              <img 
                src={`https://bilasolur.is/Images/ImgFactory.aspx?p=false&y=120&x=180&f=${dealership_logo}&r=logos`}
                alt={dealership}
                className="h-14 w-auto object-contain mx-auto"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          ) : dealership ? (
            <div className="text-xl font-bold text-slate-800 mb-2">{dealership}</div>
          ) : null}
          <p className="text-slate-500 text-sm font-medium">
            Staðfesting verka hjá <span className="font-bold text-slate-700">Alvörubón</span>
          </p>
        </div>

        {isConfirmed ? (
          <div className="bg-emerald-600 text-white rounded-2xl p-10 text-center shadow-lg">
            <div className="text-6xl mb-4">✓</div>
            <div className="text-2xl font-bold mb-2">Staðfesting móttekin!</div>
            <div className="text-emerald-100 text-sm">
              Takk fyrir. Við sendum reikning fljótlega.
            </div>
          </div>
        ) : (
          <>
            {/* Summary - dynamic based on active tab */}
            {(() => {
              const displayCount = filteredJobs.length;
              const displayTotal = filteredJobs.reduce((sum, j) => sum + (j.display_total ?? j.total ?? 0), 0);
              return (
                <div className={`grid ${show_prices ? 'grid-cols-2' : 'grid-cols-1'} gap-3 mb-5`}>
                  <div className="bg-white border border-slate-200 rounded-xl p-4 text-center shadow-sm">
                    <div className="text-3xl font-black text-slate-800">{displayCount}</div>
                    <div className="text-xs text-slate-500 font-medium uppercase tracking-wide mt-1">Verk</div>
                  </div>
                  {show_prices && (
                    <div className="bg-white border border-slate-200 rounded-xl p-4 text-center shadow-sm">
                      <div className="text-3xl font-black text-slate-800">{displayTotal.toLocaleString()} <span className="text-lg">kr</span></div>
                      <div className="text-xs text-slate-500 font-medium uppercase tracking-wide mt-1">Samtals</div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Filter Tabs */}
            {cleanDealerKt && dealershipJobs.length > 0 && otherJobs.length > 0 && (
              <div className="flex bg-white border border-slate-200 rounded-xl p-1 mb-4 shadow-sm">
                <button
                  onClick={() => setActiveTab('all')}
                  className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition-all ${
                    activeTab === 'all' ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Öll ({jobs.length})
                </button>
                <button
                  onClick={() => setActiveTab('dealership')}
                  className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition-all ${
                    activeTab === 'dealership' ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {dealership} ({dealershipJobs.length})
                </button>
                <button
                  onClick={() => setActiveTab('others')}
                  className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition-all ${
                    activeTab === 'others' ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Aðrir ({otherJobs.length})
                </button>
              </div>
            )}

            {/* Jobs List */}
            <div className="space-y-3 mb-5">
              {filteredJobs.map((job, index) => (
                <div 
                  key={job.id}
                  className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden"
                >
                  {/* Compact Job Header */}
                  <div className="flex items-center justify-between p-3 border-b border-slate-100">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="bg-slate-800 text-white px-2.5 py-1 rounded-md font-bold text-sm tracking-wide whitespace-nowrap">
                        {job.plate}
                      </span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 text-slate-600 text-xs truncate">
                          {job.car_make && (() => {
                            const logoUrl = getCarLogo(job.car_make);
                            return logoUrl ? (
                              <img src={logoUrl} alt="" className="w-4 h-4 object-contain flex-shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />
                            ) : null;
                          })()}
                          <span className="truncate">{job.car_make} {job.car_model}</span>
                        </div>
                        {job.scheduled_date && (
                          <div className="text-xs text-slate-400 mt-0.5">
                            {job.scheduled_date}{job.scheduled_time ? ` · ${job.scheduled_time}` : ''}
                          </div>
                        )}
                      </div>
                    </div>
                    {show_prices && job.display_total != null && (
                      <div className="text-right flex-shrink-0 ml-2">
                        <div className="font-bold text-slate-800 text-lg">{job.display_total?.toLocaleString()} <span className="text-xs text-slate-500">kr</span></div>
                        {job.recalculated && (
                          <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-semibold">UPPFÆRT</span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="p-3 space-y-2">
                    {/* Owner */}
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <span className="font-semibold text-slate-600">Eigandi:</span>
                      <span>{job.owner_name}</span>
                      {job.owner_kt && <span className="text-slate-400">({job.owner_kt})</span>}
                    </div>

                    {/* Payer - Editable */}
                    {editingJob === job.id ? (
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="text-xs font-bold text-blue-800 mb-2">Breyta greiðanda</div>
                        
                        {dealership_kennitala && (
                          <button
                            onClick={async () => {
                              updateJobEdit(job.id, 'payer_kt', dealership_kennitala);
                              updateJobEdit(job.id, 'payer_name', dealership);
                              toast.success(`✅ ${dealership} valinn sem greiðandi`);
                              await recalculateJobPrice(job.id, dealership_kennitala);
                            }}
                            className="w-full mb-2 p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold transition-colors"
                          >
                            🏢 {dealership}
                          </button>
                        )}
                        
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-[10px] font-semibold text-slate-600">Kennitala *</Label>
                            <Input
                              value={jobEdits[job.id]?.payer_kt || ''}
                              onChange={async (e) => {
                                const kt = e.target.value;
                                updateJobEdit(job.id, 'payer_kt', kt);
                                if (kt.length === 10) {
                                  try {
                                    const response = await axios.get(`${API}/person/lookup/${kt}`);
                                    if (response.data.found) {
                                      const name = response.data.is_company && response.data.company_name 
                                        ? response.data.company_name 
                                        : response.data.full_name;
                                      updateJobEdit(job.id, 'payer_name', name);
                                      toast.success(`📋 ${name}`);
                                    }
                                    await recalculateJobPrice(job.id, kt);
                                  } catch (error) { console.error('Lookup failed:', error); }
                                }
                              }}
                              placeholder="1234567890"
                              className="h-8 text-xs"
                              maxLength={10}
                            />
                          </div>
                          <div>
                            <Label className="text-[10px] font-semibold text-slate-600">Nafn</Label>
                            <Input
                              value={jobEdits[job.id]?.payer_name || ''}
                              onChange={(e) => updateJobEdit(job.id, 'payer_name', e.target.value)}
                              placeholder="Nafn greiðanda"
                              className="h-8 text-xs"
                            />
                          </div>
                        </div>
                        <div className="flex gap-2 mt-2">
                          <Button size="sm" onClick={() => stopEditingJob(job.id)} className="flex-1 h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white">Vista</Button>
                          <Button size="sm" variant="outline" onClick={() => {
                            updateJobEdit(job.id, 'payer_name', job.payer_name || job.owner_name);
                            updateJobEdit(job.id, 'payer_kt', job.payer_kt || job.owner_kt);
                            setEditingJob(null);
                          }} className="flex-1 h-7 text-xs">Hætta við</Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                        <div className="text-xs">
                          <span className="font-semibold text-slate-600">Greiðandi:</span>{' '}
                          <span className="text-slate-800 font-medium">{jobEdits[job.id]?.payer_name || job.payer_name || job.owner_name}</span>
                          {(jobEdits[job.id]?.payer_kt || job.payer_kt || job.owner_kt) && (
                            <span className="text-slate-400 ml-1">({jobEdits[job.id]?.payer_kt || job.payer_kt || job.owner_kt})</span>
                          )}
                        </div>
                        <button 
                          onClick={() => startEditingJob(job.id)}
                          className="text-[10px] text-blue-600 hover:text-blue-800 font-bold whitespace-nowrap ml-2"
                        >
                          Breyta
                        </button>
                      </div>
                    )}

                    {/* Email field - show only if payer is different from dealership */}
                    {(() => {
                      const normalizeName = (name) => {
                        if (!name) return '';
                        return name.toLowerCase().trim().replace(/\s+/g, ' ').replace(/\s*(ehf\.?|hf\.?|sf\.?)\s*/gi, '').trim();
                      };
                      const payerName = normalizeName(jobEdits[job.id]?.payer_name || job.payer_name || job.owner_name);
                      const dealershipName = normalizeName(confirmationData.dealership || confirmationData.dealership_name);
                      return payerName !== dealershipName && (
                        <div className="p-2 bg-slate-50 rounded-lg">
                          <Label className="text-[10px] font-semibold text-slate-500 flex items-center gap-1">
                            📧 Netfang greiðanda
                          </Label>
                          <Input
                            type="email"
                            value={jobEdits[job.id]?.payer_email || ''}
                            onChange={(e) => updateJobEdit(job.id, 'payer_email', e.target.value)}
                            placeholder="email@example.is"
                            className="h-7 text-xs mt-1"
                          />
                        </div>
                      );
                    })()}

                    {/* Services */}
                    {show_services && job.items && job.items.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {job.items.map((item, idx) => (
                          <span key={idx} className="inline-flex items-center gap-1 bg-emerald-50 border border-emerald-200 text-emerald-800 px-2 py-1 rounded-md text-xs font-medium">
                            {item.custom_name || item.service}
                            {show_prices && item.price != null && (
                              <span className="text-emerald-600 font-bold">{item.price?.toLocaleString()}</span>
                            )}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Comment */}
                    <textarea
                      value={jobEdits[job.id]?.notes || ''}
                      onChange={(e) => updateJobEdit(job.id, 'notes', e.target.value)}
                      placeholder="Athugasemd..."
                      className="w-full p-2 border border-slate-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-400 rounded-lg h-10 resize-none text-xs placeholder:text-slate-300 bg-white outline-none"
                    />

                    {/* Payment Method */}
                    {allow_alternative_payment && (
                      <>
                        {editingPaymentMethod === job.id ? (
                          <div className="p-2 bg-slate-50 rounded-lg">
                            <div className="text-xs font-semibold text-slate-600 mb-1.5">Greiðslumáti</div>
                            <div className="space-y-1.5">
                              <label className="flex items-center gap-2 text-xs cursor-pointer">
                                <input type="radio" name={`pm_${job.id}`} value="payday"
                                  checked={(jobEdits[job.id]?.payment_method || 'payday') === 'payday'}
                                  onChange={(e) => updateJobEdit(job.id, 'payment_method', e.target.value)}
                                  className="w-3.5 h-3.5" />
                                Reikningur í netbanka
                              </label>
                              <label className="flex items-center gap-2 text-xs cursor-pointer">
                                <input type="radio" name={`pm_${job.id}`} value="cash"
                                  checked={(jobEdits[job.id]?.payment_method || 'payday') === 'cash'}
                                  onChange={(e) => updateJobEdit(job.id, 'payment_method', e.target.value)}
                                  className="w-3.5 h-3.5" />
                                Reiðufé / Annað
                              </label>
                            </div>
                            <Button size="sm" onClick={() => setEditingPaymentMethod(null)} className="mt-1.5 h-6 text-[10px]">Vista</Button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between text-xs text-slate-500">
                            <span>
                              <span className="font-semibold">Greiðslumáti:</span>{' '}
                              {(jobEdits[job.id]?.payment_method || 'payday') === 'payday' ? accountingSystemName : 'Reiðufé / Annað'}
                            </span>
                            <button onClick={() => setEditingPaymentMethod(job.id)} className="text-blue-600 hover:text-blue-800 font-bold text-[10px]">Breyta</button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Confirm Button */}
            <button 
              onClick={handleConfirm}
              disabled={confirming}
              className="w-full bg-slate-900 hover:bg-slate-800 disabled:bg-slate-400 text-white py-4 rounded-xl font-bold text-base shadow-lg transition-all active:scale-[0.98]"
            >
              {confirming ? 'Staðfestir...' : `Staðfesta ${total_jobs} verk`}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// Material Cost Statistics Component
function MaterialCostStatistics() {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonths, setSelectedMonths] = useState(6);
  const [editingMultipliers, setEditingMultipliers] = useState(false);
  const [multipliers, setMultipliers] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadStatistics();
  }, [selectedMonths]);

  const loadStatistics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/statistics/material-costs?months=${selectedMonths}`);
      setStatistics(response.data);
      setMultipliers(response.data.size_multipliers || {});
    } catch (error) {
      console.error('Failed to load material cost statistics:', error);
      toast.error('Tókst ekki að hlaða efniskostnað tölfræði');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSaveMultipliers = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/settings/vehicle-multipliers`, multipliers);
      toast.success('✅ Margfaldarar vistaðir');
      setEditingMultipliers(false);
      loadStatistics(); // Reload to show updated stats
    } catch (error) {
      console.error('Failed to save multipliers:', error);
      toast.error('Mistókst að vista margfaldara');
    } finally {
      setSaving(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('is-IS').format(Math.round(num));
  };

  const [costView, setCostView] = useState('month');

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-slate-500">
          Hleður efniskostnað tölfræði...
        </CardContent>
      </Card>
    );
  }

  if (!statistics) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-red-600">
          Villa við að hlaða tölfræði
        </CardContent>
      </Card>
    );
  }

  const { summary, by_size, by_month, by_day } = statistics;

  const monthNames = {
    '01': 'Jan', '02': 'Feb', '03': 'Mar', '04': 'Apr', '05': 'Maí', '06': 'Jún',
    '07': 'Júl', '08': 'Ágú', '09': 'Sep', '10': 'Okt', '11': 'Nóv', '12': 'Des'
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>💰 Efniskostnaður per bílastærð</CardTitle>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
              Sjálfvirkur útreikningur á efniskostnaði út frá vörum og bílastærð
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Label>Tímabil:</Label>
            <Select value={selectedMonths.toString()} onValueChange={(value) => setSelectedMonths(parseInt(value))}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 mánuður</SelectItem>
                <SelectItem value="3">3 mánuðir</SelectItem>
                <SelectItem value="6">6 mánuðir</SelectItem>
                <SelectItem value="12">12 mánuðir</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Info Banner */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
          <p className="text-sm text-blue-900 dark:text-blue-200">
            💡 <strong>Hvernig virkar þetta:</strong> Kerfið reiknar sjálfkrafa efniskostnað þegar verk klárast, 
            út frá þjónustum, tengdum vörum og bílastærð. Stærri bílar nota meira efni (t.d. 9 manna = 3x smábíll).
          </p>
        </div>

        {/* Multipliers Settings */}
        <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 bg-slate-50 dark:bg-slate-800/50">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold text-slate-700 dark:text-slate-300">⚙️ Efnisnotkun margfaldarar</h3>
            {!editingMultipliers && (
              <Button size="sm" variant="outline" onClick={() => setEditingMultipliers(true)}>
                ✏️ Breyta
              </Button>
            )}
          </div>
          
          {editingMultipliers ? (
            <div className="space-y-3">
              <p className="text-xs text-slate-600 dark:text-slate-400">
                Stilltu hversu mikið meira efni hver bílastærð notar miðað við smábíl (1.0x = baseline)
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(multipliers).map(([key, value]) => {
                  const labels = {
                    'smabill': 'Smábíll',
                    'folksbill': 'Fólksbíll',
                    'stor_folksbill': 'Stór fólksbíll',
                    'litill_jeppi': 'Lítill jeppi',
                    'stor_jeppi': 'Stór jeppi',
                    'litill_sendibill': 'Lítill sendibíll',
                    'sendibill': 'Sendibíll',
                    '9_manna': '9 manna sendibíll',
                    'stor_sendibill': 'Stór sendibíll',
                    'unknown': 'Óþekkt'
                  };
                  return (
                    <div key={key}>
                      <Label className="text-xs">{labels[key] || key}</Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0.1"
                        max="10"
                        value={value}
                        onChange={(e) => setMultipliers({
                          ...multipliers,
                          [key]: parseFloat(e.target.value) || 1.0
                        })}
                        className="h-8 text-sm"
                      />
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-2 justify-end">
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => {
                    setEditingMultipliers(false);
                    loadStatistics(); // Reset to original values
                  }}
                >
                  Hætta við
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleSaveMultipliers}
                  disabled={saving}
                >
                  {saving ? 'Vistar...' : '💾 Vista'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(multipliers).map(([key, value]) => {
                const labels = {
                  'smabill': 'Smábíll',
                  'folksbill': 'Fólksbíll',
                  'stor_folksbill': 'Stór fólksbíll',
                  'litill_jeppi': 'Lítill jeppi',
                  'stor_jeppi': 'Stór jeppi',
                  'litill_sendibill': 'Lítill sendibíll',
                  'sendibill': 'Sendibíll',
                  '9_manna': '9 manna sendibíll',
                  'stor_sendibill': 'Stór sendibíll',
                  'unknown': 'Óþekkt'
                };
                return (
                  <div key={key} className="text-sm">
                    <div className="text-slate-600 dark:text-slate-400">{labels[key] || key}</div>
                    <div className="font-semibold text-slate-900 dark:text-slate-100">{value}x</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200 dark:border-blue-700">
            <CardContent className="pt-6">
              <div className="text-sm text-blue-700 dark:text-blue-300 font-medium">Heildar verk</div>
              <div className="text-3xl font-bold text-blue-900 dark:text-blue-100 mt-2">
                {formatNumber(summary.total_jobs)}
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-200 dark:border-green-700">
            <CardContent className="pt-6">
              <div className="text-sm text-green-700 dark:text-green-300 font-medium">Heildarkostnaður</div>
              <div className="text-3xl font-bold text-green-900 dark:text-green-100 mt-2">
                {formatNumber(summary.total_cost)} kr
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200 dark:border-purple-700">
            <CardContent className="pt-6">
              <div className="text-sm text-purple-700 dark:text-purple-300 font-medium">Meðalkostnaður</div>
              <div className="text-3xl font-bold text-purple-900 dark:text-purple-100 mt-2">
                {formatNumber(summary.average_cost)} kr
              </div>
            </CardContent>
          </Card>
        </div>

        {/* By Size Table */}
        {by_size && by_size.length > 0 ? (
          <div>
            <h3 className="font-semibold mb-3 text-slate-700 dark:text-slate-300">📊 Sundurliðun eftir bílastærð</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                    <th className="text-left p-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Bílastærð</th>
                    <th className="text-right p-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Fjöldi verka</th>
                    <th className="text-right p-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Meðalkostnaður</th>
                    <th className="text-right p-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Lægsti</th>
                    <th className="text-right p-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Hæsti</th>
                    <th className="text-right p-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Heildarkostnaður</th>
                  </tr>
                </thead>
                <tbody>
                  {by_size.map((item, idx) => (
                    <tr key={idx} className="border-b border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="p-3 text-sm font-medium text-slate-900 dark:text-slate-100">
                        {item.size_label}
                      </td>
                      <td className="p-3 text-sm text-right text-slate-700 dark:text-slate-300">
                        {formatNumber(item.count)}
                      </td>
                      <td className="p-3 text-sm text-right font-semibold text-green-700 dark:text-green-400">
                        {formatNumber(item.average_cost)} kr
                      </td>
                      <td className="p-3 text-sm text-right text-slate-600 dark:text-slate-400">
                        {formatNumber(item.min_cost)} kr
                      </td>
                      <td className="p-3 text-sm text-right text-slate-600 dark:text-slate-400">
                        {formatNumber(item.max_cost)} kr
                      </td>
                      <td className="p-3 text-sm text-right font-semibold text-blue-700 dark:text-blue-400">
                        {formatNumber(item.total_cost)} kr
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-slate-500 dark:text-slate-400">
            <div className="text-4xl mb-2">📊</div>
            <p>Engin gögn fyrir valið tímabil</p>
            <p className="text-sm mt-1">Efniskostnaður er reiknaður þegar verk klárast</p>
          </div>
        )}

        {/* Monthly/Daily Cost Breakdown */}
        {(by_month?.length > 0 || by_day?.length > 0) && (
          <div className="border-t pt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-700 dark:text-slate-300">📈 Kostnaður yfir tíma</h3>
              <div className="flex gap-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
                <button onClick={() => setCostView('month')} className={`px-3 py-1 text-sm rounded-md transition-all ${costView === 'month' ? 'bg-white dark:bg-slate-700 shadow font-medium' : 'text-slate-600 hover:text-slate-800'}`} data-testid="cost-view-month">Mánuður</button>
                <button onClick={() => setCostView('day')} className={`px-3 py-1 text-sm rounded-md transition-all ${costView === 'day' ? 'bg-white dark:bg-slate-700 shadow font-medium' : 'text-slate-600 hover:text-slate-800'}`} data-testid="cost-view-day">Dagur</button>
              </div>
            </div>

            {/* Chart */}
            {costView === 'month' && by_month?.length > 0 && (
              <>
                <div className="h-64 mb-4" data-testid="monthly-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={by_month.map(m => ({ ...m, label: monthNames[m.month?.slice(5,7)] || m.month?.slice(5,7), year: m.month?.slice(0,4) }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${Math.round(v/1000)}k`} />
                      <Tooltip formatter={(value) => [`${formatNumber(value)} kr`, '']} labelFormatter={(label) => label} />
                      <Bar dataKey="total_cost" name="Heildarkostnaður" fill="#10b981" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-slate-100 dark:bg-slate-800 border-b">
                        <th className="text-left p-2 font-semibold">Mánuður</th>
                        <th className="text-right p-2 font-semibold">Verk</th>
                        <th className="text-right p-2 font-semibold">Heildarkostnaður</th>
                        <th className="text-right p-2 font-semibold">Meðalkostnaður</th>
                      </tr>
                    </thead>
                    <tbody>
                      {by_month.map((m, i) => (
                        <tr key={i} className="border-b hover:bg-slate-50">
                          <td className="p-2 font-medium">{monthNames[m.month?.slice(5,7)] || ''} {m.month?.slice(0,4)}</td>
                          <td className="p-2 text-right">{m.count}</td>
                          <td className="p-2 text-right font-semibold text-green-700">{formatNumber(m.total_cost)} kr</td>
                          <td className="p-2 text-right text-slate-600">{formatNumber(m.average_cost || 0)} kr</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {costView === 'day' && by_day?.length > 0 && (
              <>
                <div className="h-64 mb-4" data-testid="daily-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={by_day.map(d => ({ ...d, label: d.date?.slice(5) }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={Math.max(0, Math.floor(by_day.length / 15))} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${Math.round(v/1000)}k`} />
                      <Tooltip formatter={(value) => [`${formatNumber(value)} kr`, '']} labelFormatter={(label) => label} />
                      <Bar dataKey="total_cost" name="Dagskostnaður" fill="#3b82f6" radius={[3,3,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="overflow-x-auto max-h-80 overflow-y-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead className="sticky top-0 bg-white dark:bg-slate-900">
                      <tr className="bg-slate-100 dark:bg-slate-800 border-b">
                        <th className="text-left p-2 font-semibold">Dagsetning</th>
                        <th className="text-right p-2 font-semibold">Verk</th>
                        <th className="text-right p-2 font-semibold">Heildarkostnaður</th>
                        <th className="text-right p-2 font-semibold">Meðalkostnaður</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...by_day].reverse().map((d, i) => (
                        <tr key={i} className="border-b hover:bg-slate-50">
                          <td className="p-2 font-medium">{d.date}</td>
                          <td className="p-2 text-right">{d.count}</td>
                          <td className="p-2 text-right font-semibold text-blue-700">{formatNumber(d.total_cost)} kr</td>
                          <td className="p-2 text-right text-slate-600">{formatNumber(d.average_cost || 0)} kr</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Lookup Statistics Component
function LookupStatistics() {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonths, setSelectedMonths] = useState(6);
  const [topUsers, setTopUsers] = useState(null);
  const [auditOpenUser, setAuditOpenUser] = useState(null); // { user_id, email }
  const [auditRows, setAuditRows] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  useEffect(() => {
    loadStatistics();
    loadTopUsers();
  }, [selectedMonths]);

  const loadStatistics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/lookup-statistics?months=${selectedMonths}`);
      setStatistics(response.data);
    } catch (error) {
      console.error('Failed to load lookup statistics:', error);
      toast.error('Tókst ekki að hlaða tölfræði');
    } finally {
      setLoading(false);
    }
  };

  const loadTopUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/lookup-top-users?lookup_type=vehicle&limit=10`);
      setTopUsers(response.data);
    } catch (error) {
      console.error('Failed to load top users:', error);
      // non-blocking
    }
  };

  const openAuditFor = async (user) => {
    setAuditOpenUser(user);
    setAuditLoading(true);
    setAuditRows([]);
    try {
      const params = new URLSearchParams({
        user_id: user.user_id || '',
        days: '30',
        limit: '100',
      });
      const response = await axios.get(`${API}/admin/lookup-audit?${params.toString()}`);
      setAuditRows(response.data?.rows || []);
    } catch (error) {
      toast.error('Tókst ekki að hlaða uppflettingasögu');
    } finally {
      setAuditLoading(false);
    }
  };

  const closeAudit = () => {
    setAuditOpenUser(null);
    setAuditRows([]);
  };

  const formatTs = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString('is-IS', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      });
    } catch {
      return iso;
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('is-IS').format(num);
  };

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('is-IS', { year: 'numeric', month: 'long' });
  };

  if (loading) {
    return <div className="p-6">Hleður tölfræði...</div>;
  }

  if (!statistics) {
    return <div className="p-6 text-red-600">Villa við að hlaða tölfræði</div>;
  }

  const { person_statistics, vehicle_statistics, ja_phone_statistics, combined_totals, today_vehicle_audit } = statistics;
  const today = today_vehicle_audit || { total_attempts: 0, ok: 0, cached: 0, api_calls: 0, rate_limited: 0, invalid: 0, not_found: 0, estimated_cost: 0 };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">📊 Rogg.is uppflettinga tölfræði</h2>
        <div className="space-x-2">
          <Label htmlFor="months-select">Tímabil:</Label>
          <Select value={selectedMonths.toString()} onValueChange={(value) => setSelectedMonths(parseInt(value))}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1 mánuður</SelectItem>
              <SelectItem value="3">3 mánuðir</SelectItem>
              <SelectItem value="6">6 mánuðir</SelectItem>
              <SelectItem value="12">12 mánuðir</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Today's vehicle lookup card — sourced from audit log (ground truth) */}
      <Card className="border-2 border-amber-300 bg-amber-50" data-testid="lookup-today-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-900">
            🚗 Bílnúmera uppflettingar — Í DAG
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <div>
              <div className="text-3xl font-bold text-amber-900" data-testid="today-total-attempts">{formatNumber(today.total_attempts)}</div>
              <div className="text-xs text-slate-600">Heildartilraunir</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-700" data-testid="today-ok">{formatNumber(today.ok)}</div>
              <div className="text-xs text-slate-600">Heppnaðar</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-700" data-testid="today-cached">{formatNumber(today.cached)}</div>
              <div className="text-xs text-slate-600">Úr cache (0 kr)</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-red-700" data-testid="today-api-calls">{formatNumber(today.api_calls)}</div>
              <div className="text-xs text-slate-600">Rogg.is API kall</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-orange-700" data-testid="today-rate-limited">{formatNumber(today.rate_limited)}</div>
              <div className="text-xs text-slate-600">Stoppað (þak)</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-700" data-testid="today-invalid">{formatNumber((today.invalid || 0) + (today.not_found || 0))}</div>
              <div className="text-xs text-slate-600">Ógilt / fannst ekki</div>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-amber-200 text-sm text-amber-900">
            💰 Áætlaður kostnaður í dag: <strong>{formatNumber(today.estimated_cost || 0)} kr</strong>
            <span className="text-xs text-slate-600 ml-2">(úr <code>vehicle_lookup_audit</code> — telur ALLAR tilraunir)</span>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              👤 Kennitölur
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="text-2xl font-bold">{formatNumber(person_statistics.total_lookups)}</div>
              <div className="text-sm text-slate-600">
                Samtals uppflettingar
              </div>
              <div className="text-sm">
                <span className="text-green-600">{formatNumber(person_statistics.total_successful)}</span> árángursríkar
                ({person_statistics.overall_success_rate}%)
              </div>
              <div className="text-sm font-bold text-blue-700 mt-2 pt-2 border-t">
                💰 {formatNumber(person_statistics.total_cost || 0)} kr
              </div>
              <div className="text-xs text-slate-500">
                23 kr/leit (m/VSK)
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              🚗 Bílnúmer
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="text-2xl font-bold">{formatNumber(vehicle_statistics.total_lookups)}</div>
              <div className="text-sm text-slate-600">
                Samtals uppflettingar
              </div>
              <div className="text-sm">
                <span className="text-green-600">{formatNumber(vehicle_statistics.total_successful)}</span> árángursríkar
                ({vehicle_statistics.overall_success_rate}%)
              </div>
              <div className="text-sm font-bold text-blue-700 mt-2 pt-2 border-t">
                💰 {formatNumber(vehicle_statistics.total_cost || 0)} kr
              </div>
              <div className="text-xs text-slate-500">
                41 kr/leit (m/VSK)
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              📞 Já.is símtaleit
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="text-2xl font-bold">{formatNumber(ja_phone_statistics?.total_lookups || 0)}</div>
              <div className="text-sm text-slate-600">
                Samtals uppflettingar
              </div>
              <div className="text-sm">
                <span className="text-green-600">{formatNumber(ja_phone_statistics?.total_successful || 0)}</span> árángursríkar
                ({ja_phone_statistics?.overall_success_rate || 0}%)
              </div>
              <div className="text-sm font-bold text-blue-700 mt-2 pt-2 border-t">
                💰 {formatNumber(ja_phone_statistics?.total_cost || 0)} kr
              </div>
              <div className="text-xs text-slate-500">
                11 kr/leit (m/VSK)
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              💰 Kostnaður
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="text-2xl font-bold text-red-600">{formatNumber(combined_totals.total_cost || 0)} kr</div>
              <div className="text-sm text-slate-600">
                Samtals kostnaður
              </div>
              <div className="text-xs space-y-1 mt-2 pt-2 border-t">
                <div>Kennitölur: {formatNumber(combined_totals.person_cost || 0)} kr</div>
                <div>Bílnúmer: {formatNumber(combined_totals.vehicle_cost || 0)} kr</div>
                <div>Já.is: {formatNumber(combined_totals.ja_phone_cost || 0)} kr</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              📈 Samanlagt
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="text-2xl font-bold">{formatNumber(combined_totals.total_lookups)}</div>
              <div className="text-sm text-slate-600">
                Allar uppflettingar
              </div>
              <div className="text-xs text-slate-500">
                {formatNumber(combined_totals.total_person)} kennitölur + {formatNumber(combined_totals.total_vehicle)} bílnúmer + {formatNumber(combined_totals.total_ja_phone || 0)} já.is
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Person Statistics */}
        <Card>
          <CardHeader>
            <CardTitle>👤 Kennitölu uppflettingar eftir mánuðum</CardTitle>
          </CardHeader>
          <CardContent>
            {person_statistics.monthly_data.length > 0 ? (
              <div className="space-y-3">
                {person_statistics.monthly_data.map((monthData) => (
                  <div key={monthData.month} className="border-b pb-3">
                    <div className="flex justify-between items-center">
                      <h4 className="font-medium">{formatMonth(monthData.month)}</h4>
                      <span className="font-bold">{formatNumber(monthData.total_lookups)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-slate-600 mt-1">
                      <span>Árángursríkar: {formatNumber(monthData.successful_lookups)} ({monthData.success_rate}%)</span>
                      <span>Cache: {formatNumber(monthData.cached_lookups)} ({monthData.cache_rate}%)</span>
                    </div>
                    <div className="text-sm font-bold text-blue-700 mt-1">
                      💰 Kostnaður: {formatNumber(monthData.total_cost || 0)} kr
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-slate-500">
                Engar kennitölu uppflettingar í þessu tímabili
              </div>
            )}
          </CardContent>
        </Card>

        {/* Vehicle Statistics */}
        <Card>
          <CardHeader>
            <CardTitle>🚗 Bílnúmera uppflettingar eftir mánuðum</CardTitle>
          </CardHeader>
          <CardContent>
            {vehicle_statistics.monthly_data.length > 0 ? (
              <div className="space-y-3">
                {vehicle_statistics.monthly_data.map((monthData) => (
                  <div key={monthData.month} className="border-b pb-3">
                    <div className="flex justify-between items-center">
                      <h4 className="font-medium">{formatMonth(monthData.month)}</h4>
                      <span className="font-bold">{formatNumber(monthData.total_lookups)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-slate-600 mt-1">
                      <span>Árángursríkar: {formatNumber(monthData.successful_lookups)} ({monthData.success_rate}%)</span>
                      <span>Cache: {formatNumber(monthData.cached_lookups)} ({monthData.cache_rate}%)</span>
                    </div>
                    <div className="text-sm font-bold text-blue-700 mt-1">
                      💰 Kostnaður: {formatNumber(monthData.total_cost || 0)} kr
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-slate-500">
                Engar bílnúmera uppflettingar í þessu tímabili
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top users by vehicle lookups (current month) — abuse detection */}
      <Card data-testid="lookup-top-users-card">
        <CardHeader>
          <CardTitle>👥 Topp 10 notendur – bílnúmera uppflettingar í þessum mánuði</CardTitle>
        </CardHeader>
        <CardContent>
          {topUsers && topUsers.top_users && topUsers.top_users.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b text-slate-600">
                    <th className="py-2 pr-3">#</th>
                    <th className="py-2 pr-3">Notandi</th>
                    <th className="py-2 pr-3 text-right">Heildar</th>
                    <th className="py-2 pr-3 text-right">Cache</th>
                    <th className="py-2 pr-3 text-right">API</th>
                    <th className="py-2 pr-3 text-right">Kostnaður</th>
                  </tr>
                </thead>
                <tbody>
                  {topUsers.top_users.map((u, idx) => {
                    const isOutlier = u.total_lookups >= 30; // Hit daily cap → suspicious
                    return (
                      <tr
                        key={u.user_id || idx}
                        className={`border-b ${isOutlier ? 'bg-red-50' : ''}`}
                        data-testid={`lookup-top-user-row-${idx}`}
                      >
                        <td className="py-2 pr-3 font-medium">{idx + 1}</td>
                        <td className="py-2 pr-3">
                          <button
                            type="button"
                            onClick={() => openAuditFor(u)}
                            className="text-blue-700 hover:underline"
                            data-testid={`lookup-top-user-email-${idx}`}
                            title="Skoða uppflettingar þessa notanda"
                          >
                            {u.email}
                          </button>
                          {isOutlier && (
                            <span className="ml-2 inline-block px-1.5 py-0.5 text-xs bg-red-600 text-white rounded">
                              ⚠️ óeðlilegt
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-right font-semibold">
                          {formatNumber(u.total_lookups)}
                        </td>
                        <td className="py-2 pr-3 text-right text-slate-600">
                          {formatNumber(u.cached_lookups)} ({u.cache_rate}%)
                        </td>
                        <td className="py-2 pr-3 text-right text-slate-600">
                          {formatNumber(u.api_lookups)}
                        </td>
                        <td className="py-2 pr-3 text-right font-bold text-blue-700">
                          {formatNumber(u.total_cost)} kr
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="text-xs text-slate-500 mt-3">
                💡 Rauðar línur = notandi með ≥ 30 uppflettingar í þessum mánuði
                (líklega óeðlilegt — íhugaðu að endurnýja lykilinn ef þú þekkir
                ekki notkunina).
              </p>
            </div>
          ) : (
            <div className="text-center py-4 text-slate-500">
              Engar uppflettingar skráðar á notendur í þessum mánuði.
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>ℹ️ Um tölfræði</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-600 space-y-2">
          <p>• <strong>Kennitölu uppflettingar:</strong> Notað í viðskiptavinastýringu til að fylla út nafn og heimilisfang</p>
          <p>• <strong>Bílnúmera uppflettingar:</strong> Notað í verkskráningu til að fylla út bílupplýsingar og eiganda</p>
          <p>• <strong>Cache:</strong> Upplýsingar eru geymdar í 1 klukkustund til að flýta fyrir endurteknum uppflettingum</p>
          <p>• <strong>Árángurshlutfall:</strong> Hlutfall uppflettinga þar sem rogg.is skilar upplýsingum</p>
        </CardContent>
      </Card>

      {/* Audit-log modal — shows last 100 lookups for selected user */}
      {auditOpenUser && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={closeAudit}
          data-testid="lookup-audit-modal"
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center px-5 py-3 border-b">
              <h3 className="font-semibold text-lg">
                📋 Uppflettingar — {auditOpenUser.email}
                <span className="text-sm text-slate-500 font-normal ml-2">
                  (síðustu 30 dagar, allt að 100 færslur)
                </span>
              </h3>
              <button
                onClick={closeAudit}
                className="text-slate-500 hover:text-slate-700 text-2xl leading-none"
                data-testid="lookup-audit-close-btn"
              >
                ×
              </button>
            </div>
            <div className="overflow-auto p-5">
              {auditLoading ? (
                <div className="text-center py-8 text-slate-500">Hleður...</div>
              ) : auditRows.length === 0 ? (
                <div className="text-center py-8 text-slate-500">Engar færslur.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b text-slate-600 sticky top-0 bg-white">
                      <th className="py-2 pr-3">Tími</th>
                      <th className="py-2 pr-3">Plata</th>
                      <th className="py-2 pr-3">Staða</th>
                      <th className="py-2 pr-3">Cache</th>
                      <th className="py-2 pr-3">Kostn.</th>
                      <th className="py-2 pr-3">IP</th>
                      <th className="py-2 pr-3">Tæki</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditRows.map((row) => (
                      <tr
                        key={row.id}
                        className={`border-b ${
                          row.status === 'invalid' || row.status === 'rate_limited'
                            ? 'bg-red-50'
                            : ''
                        }`}
                      >
                        <td className="py-1.5 pr-3 whitespace-nowrap text-slate-700">
                          {formatTs(row.ts)}
                        </td>
                        <td className="py-1.5 pr-3 font-mono font-semibold">
                          {row.plate}
                        </td>
                        <td className="py-1.5 pr-3">
                          {row.status === 'ok' && (row.found
                            ? <span className="text-green-700">✓ fundinn</span>
                            : <span className="text-amber-700">⚠ ekki fundinn</span>)}
                          {row.status === 'invalid' && <span className="text-red-700">🛑 ógilt</span>}
                          {row.status === 'rate_limited' && <span className="text-red-700">⛔ takmark</span>}
                          {row.status === 'not_found' && <span className="text-amber-700">⚠ ekki fundinn</span>}
                        </td>
                        <td className="py-1.5 pr-3">{row.cached ? '✓' : ''}</td>
                        <td className="py-1.5 pr-3 text-right text-blue-700 font-semibold">
                          {row.cost > 0 ? `${formatNumber(row.cost)} kr` : '-'}
                        </td>
                        <td className="py-1.5 pr-3 text-xs text-slate-600 font-mono">{row.ip}</td>
                        <td className="py-1.5 pr-3 text-xs text-slate-500 truncate max-w-[160px]" title={row.user_agent}>
                          {row.user_agent}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AppWrapper;